{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2024                                      }
{            Email : info@tmssoftware.com                            }
{            Web : https://www.tmssoftware.com                       }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.TMSFNCDataGridRenderer;

interface

{$I WEBLib.TMSFNCDefines.inc}

{$IFDEF FMXLIB}
{$DEFINE FMXWEBLIB}
{$ENDIF}
{$IFDEF CMNLIB}
{$DEFINE CMNWEBLIB}
{$ENDIF}
{$IFDEF WEBLIB}
{$DEFINE CMNWEBLIB}
{$DEFINE FMXWEBLIB}
{$ENDIF}

uses
  {$IFDEF MSWINDOWS}
  Windows,
  {$ENDIF}
  Classes, SysUtils, WEBLib.Graphics, WEBLib.TMSFNCDataGridCell, WEBLib.TMSFNCColorPicker, WEBLib.TMSFNCDataGridData, WEBLib.TMSFNCDataGridCore, WEBLib.TMSFNCGraphics, WEBLib.TMSFNCGraphicsTypes, Types,
  WEBLib.StdCtrls, WEBLib.TMSFNCScrollBar, WEBLib.TMSFNCCustomControl, WEBLib.TMSFNCToolBar, WEBLib.TMSFNCCheckedListBox, WEBLib.TMSFNCListBox, WEBLib.TMSFNCPopup, WEBLib.TMSFNCTypes, Generics.Collections, Generics.Defaults, WEBLib.TMSFNCBitmapContainer, WEBLib.TMSFNCImage,
  WEBLib.Controls, WEBLib.ExtCtrls, WEBLib.TMSFNCEdit, WEBLib.TMSFNCFindDialog, WEBLib.TMSFNCReplaceDialog, WEBLib.Menus, WEBLib.TMSFNCStyles
  {$IFDEF FMXLIB}
  ,FMX.DateTimeCtrls, FMX.ComboEdit, FMX.ListBox, FMX.Edit, FMX.Memo, FMX.SpinBox
  ,FMX.Memo.Types, FMX.Objects, FMX.Types, UITypes
  {$ENDIF}
  {$IFDEF VCLLIB}
  ,ComCtrls, Messages, Spin, UITypes
  {$ENDIF}
  ;

resourcestring
  sTMSFNCDataGridFilterAll = '(All)';
  sTMSFNCDataGridFilterEditPrompt = 'Filter';
  sTMSFNCDataGridFilterSortButtonAscending = 'Sort Ascending';
  sTMSFNCDataGridFilterSortButtonDescending = 'Sort Descending';

type
  TTMSFNCDataGridCellList = class(TObjectList<TTMSFNCDataGridCell>)
  public
    function GetItemForCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCell;
  end;

  TTMSFNCDataGridDisplayList = class(TList<TTMSFNCDataGridCell>)
  public
    function GetItemForCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCell;
  end;

  TTMSFNCDataGridFixedColumnCellDisplayList = class(TTMSFNCDataGridDisplayList);
  TTMSFNCDataGridFixedColumnCellList = class(TTMSFNCDataGridCellList);
  TTMSFNCDataGridFixedRootCellDisplayList = class(TTMSFNCDataGridDisplayList);
  TTMSFNCDataGridFixedRootCellList = class(TTMSFNCDataGridCellList);
  TTMSFNCDataGridFixedRowCellDisplayList = class(TTMSFNCDataGridDisplayList);
  TTMSFNCDataGridFixedRowCellList = class(TTMSFNCDataGridCellList);
  TTMSFNCDataGridNormalCellDisplayList = class(TTMSFNCDataGridDisplayList);
  TTMSFNCDataGridNormalCellList = class(TTMSFNCDataGridCellList);

  TTMSFNCDataGridSelectedCellDictionary = class(TDictionary<TTMSFNCDataGridCellCoord, Boolean>);

  TTMSFNCDataGridFilterAppearance = class(TPersistent)
  private
    FFill: TTMSFNCGraphicsFill;
    FStroke: TTMSFNCGraphicsStroke;
    FFont: TTMSFNCGraphicsFont;
    procedure SetFill(const Value: TTMSFNCGraphicsFill);
    procedure SetStroke(const Value: TTMSFNCGraphicsStroke);
    procedure SetFont(const Value: TTMSFNCGraphicsFont);
  protected
    procedure DoChange(Sender: TObject);
    procedure DoFillChanged(Sender: TObject);
    procedure DoFontChanged(Sender: TObject);
    procedure DoStrokeChanged(Sender: TObject);
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
  published
    property Fill: TTMSFNCGraphicsFill read FFill write SetFill;
    property Font: TTMSFNCGraphicsFont read FFont write SetFont;
    property Stroke: TTMSFNCGraphicsStroke read FStroke write SetStroke;
  end;

  TTMSFNCDataGridDragAppearance = class(TPersistent)
  private
    FFill: TTMSFNCGraphicsFill;
    FStroke: TTMSFNCGraphicsStroke;
    FSize: Single;
    procedure SetFill(const Value: TTMSFNCGraphicsFill);
    procedure SetStroke(const Value: TTMSFNCGraphicsStroke);
    procedure SetSize(const Value: Single);
    function IsSizeStored: Boolean;
  protected
    procedure DoChange(Sender: TObject);
    procedure DoFillChanged(Sender: TObject);
    procedure DoStrokeChanged(Sender: TObject);
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
  published
    property Fill: TTMSFNCGraphicsFill read FFill write SetFill;
    property Stroke: TTMSFNCGraphicsStroke read FStroke write SetStroke;
    property Size: Single read FSize write SetSize stored IsSizeStored nodefault;
  end;

  TTMSFNCDataGridCellAppearance = class(TPersistent)
  private
    FBandLayout: TTMSFNCDataGridCellLayout;
    FFixedLayout: TTMSFNCDataGridCellLayout;
    FFixedSelectedLayout: TTMSFNCDataGridCellLayout;
    FFocusedLayout: TTMSFNCDataGridCellLayout;
    FGroupLayout: TTMSFNCDataGridCellLayout;
    FNormalLayout: TTMSFNCDataGridCellLayout;
    FOnChange: TNotifyEvent;
    FSelectedLayout: TTMSFNCDataGridCellLayout;
    FShowFocus: Boolean;
    FSummaryLayout: TTMSFNCDataGridCellLayout;
    FExpandIcon: TTMSFNCBitmap;
    FCollapseIcon: TTMSFNCBitmap;
    FFilterIcon: TTMSFNCBitmap;
    FFilterActiveIcon: TTMSFNCBitmap;
    FFilterTypeIcon: TTMSFNCBitmap;
    FSortDescendingIcon: TTMSFNCBitmap;
    FSortAscendingIcon: TTMSFNCBitmap;
    FFilterClearIcon: TTMSFNCBitmap;
    procedure SetBandLayout(const Value: TTMSFNCDataGridCellLayout);
    procedure SetFixedLayout(const Value: TTMSFNCDataGridCellLayout);
    procedure SetFixedSelectedLayout(const Value: TTMSFNCDataGridCellLayout);
    procedure SetFocusedLayout(const Value: TTMSFNCDataGridCellLayout);
    procedure SetGroupLayout(const Value: TTMSFNCDataGridCellLayout);
    procedure SetNormalLayout(const Value: TTMSFNCDataGridCellLayout);
    procedure SetSelectedLayout(const Value: TTMSFNCDataGridCellLayout);
    procedure SetShowFocus(const Value: Boolean);
    procedure SetSummaryLayout(const Value: TTMSFNCDataGridCellLayout);
    procedure SetCollapseIcon(const Value: TTMSFNCBitmap);
    procedure SetExpandIcon(const Value: TTMSFNCBitmap);
    procedure SetFilterIcon(const Value: TTMSFNCBitmap);
    procedure SetFilterActiveIcon(const Value: TTMSFNCBitmap);
    procedure SetSortAscendingIcon(const Value: TTMSFNCBitmap);
    procedure SetSortDescendingIcon(const Value: TTMSFNCBitmap);
    procedure SetFilterClearIcon(const Value: TTMSFNCBitmap);
    procedure SetFilterTypeIcon(const Value: TTMSFNCBitmap);
  protected
    procedure LayoutChanged(Sender: TObject);
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
  published
    property ExpandIcon: TTMSFNCBitmap read FExpandIcon write SetExpandIcon;
    property CollapseIcon: TTMSFNCBitmap read FCollapseIcon write SetCollapseIcon;
    property FilterIcon: TTMSFNCBitmap read FFilterIcon write SetFilterIcon;
    property FilterActiveIcon: TTMSFNCBitmap read FFilterActiveIcon write SetFilterActiveIcon;
    property FilterClearIcon: TTMSFNCBitmap read FFilterClearIcon write SetFilterClearIcon;
    property FilterTypeIcon: TTMSFNCBitmap read FFilterTypeIcon write SetFilterTypeIcon;
    property SortAscendingIcon: TTMSFNCBitmap read FSortAscendingIcon write SetSortAscendingIcon;
    property SortDescendingIcon: TTMSFNCBitmap read FSortDescendingIcon write SetSortDescendingIcon;
    property BandLayout: TTMSFNCDataGridCellLayout read FBandLayout write SetBandLayout;
    property FixedLayout: TTMSFNCDataGridCellLayout read FFixedLayout write SetFixedLayout;
    property FixedSelectedLayout: TTMSFNCDataGridCellLayout read FFixedSelectedLayout write SetFixedSelectedLayout;
    property FocusedLayout: TTMSFNCDataGridCellLayout read FFocusedLayout write SetFocusedLayout;
    property GroupLayout: TTMSFNCDataGridCellLayout read FGroupLayout write SetGroupLayout;
    property NormalLayout: TTMSFNCDataGridCellLayout read FNormalLayout write SetNormalLayout;
    property SelectedLayout: TTMSFNCDataGridCellLayout read FSelectedLayout write SetSelectedLayout;
    property ShowFocus: Boolean read FShowFocus write SetShowFocus default True;
    property SummaryLayout: TTMSFNCDataGridCellLayout read FSummaryLayout write SetSummaryLayout;
  end;

  TTMSFNCDataGridEmptySpaceKind = (gekColumnTop, gekColumnBottom, gekRowLeft, gekRowRight, gekColumnBottomRowRight);

  TTMSFNCDataGridAfterLookupEvent = procedure(Sender: TObject; ALookupString: string) of object;
  TTMSFNCDataGridBeforeLookupEvent = procedure(Sender: TObject; var ALookupString: string; var ACanLookup: Boolean) of object;
  TTMSFNCDataGridAfterDrawCellEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ACell: TTMSFNCDataGridCell) of object;
  TTMSFNCDataGridAfterDrawGridEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF) of object;
  TTMSFNCDataGridBeforeDrawCellEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ACell: TTMSFNCDataGridCell; var ACanDraw: Boolean) of object;
  TTMSFNCDataGridBeforeDrawEmptySpaceEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; AKind: TTMSFNCDataGridEmptySpaceKind; var ACanDraw: Boolean) of object;
  TTMSFNCDataGridAfterDrawEmptySpaceEvent = procedure(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF; AKind: TTMSFNCDataGridEmptySpaceKind) of object;
  TTMSFNCDataGridGetCellClassEvent = procedure(Sender: TObject; AColumn, ARow: Integer; var ACellClass: TTMSFNCDataGridCellClass) of object;
  TTMSFNCDataGridGetCellLayoutEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCell) of object;
  TTMSFNCDataGridGetCellLayoutHasPriorityEvent = procedure(Sender: TObject;  ACell: TTMSFNCDataGridCellCoord; var AHasPriority: Boolean) of object;
  TTMSFNCDataGridGetCellHintEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; var AHint: string) of object;
  TTMSFNCDataGridGetCellPropertiesEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCell) of object;
  TTMSFNCDataGridCanEditCellEvent = procedure(Sender: TObject; AColumn, ARow: Integer; var ACanEdit: Boolean) of object;
  TTMSFNCDataGridCellClickEvent = procedure(Sender: TObject; AColumn, ARow: Integer) of object;
  TTMSFNCDataGridCellAnchorClickEvent = procedure(Sender: TObject; AColumn, ARow: Integer; AAnchor: string) of object;

  TTMSFNCDataGridCellConfigureMode = (gccmLayout, gccmProperties, gccmBoth);

  TTMSFNCDataGridMergedCells = TList<TTMSFNCDataGridCellCoord>;

  TTMSFNCDataGridStretchingOptions = class(TPersistent)
  private
    FOnChangeRebuild: TNotifyEvent;
    FEnabled: Boolean;
    FIndex: Integer;
    FMode: TTMSFNCDataGridStretchingMode;
    FRange: TTMSFNCDataGridStretchingRange;
    procedure SetEnabled(const Value: Boolean);
    procedure SetIndex(const Value: Integer);
    procedure SetMode(const Value: TTMSFNCDataGridStretchingMode);
    procedure SetRange(const Value: TTMSFNCDataGridStretchingRange);
  protected
    property OnChangeRebuild: TNotifyEvent read FOnChangeRebuild write FOnChangeRebuild;
  public
    constructor Create;
    procedure Assign(Source: TPersistent); override;
  published
    property Enabled: Boolean read FEnabled write SetEnabled default False;
    property Mode: TTMSFNCDataGridStretchingMode read FMode write SetMode default gstmAll;
    property Range: TTMSFNCDataGridStretchingRange read FRange write SetRange default gstrNormal;
    property &Index: Integer read FIndex write SetIndex default -1;
  end;

  TTMSFNCDataGridColumnRowOptions = class(TPersistent)
  private
    FOnChangeRebuild: TNotifyEvent;
    FStretching: TTMSFNCDataGridStretchingOptions;
    procedure SetStretching(const Value: TTMSFNCDataGridStretchingOptions);
  protected
    procedure DoStretchingChanged(Sender: TObject);
    property OnChangeRebuild: TNotifyEvent read FOnChangeRebuild write FOnChangeRebuild;
  public
    constructor Create; virtual;
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
  published
    property Stretching: TTMSFNCDataGridStretchingOptions read FStretching write SetStretching;
  end;

  TTMSFNCDataGridColumnOptions = class(TTMSFNCDataGridColumnRowOptions)
  private
    FNodeIndex: Integer;
    FFixedAutoCheckAll: Boolean;
    procedure SetNodeIndex(const Value: Integer);
  public
    constructor Create; override;
    procedure Assign(Source: TPersistent); override;
  published
    property FixedAutoCheckAll: Boolean read FFixedAutoCheckAll write FFixedAutoCheckAll default True;
    property NodeIndex: Integer read FNodeIndex write SetNodeIndex default 0;
  end;

  TTMSFNCDataGridRowOptions = class(TTMSFNCDataGridColumnRowOptions);

  TTMSFNCDataGridSelectionMode = (gsmNone, gsmSingleCell, gsmSingleRow, gsmSingleColumn, gsmCellRange, gsmRowRange, gsmColumnRange, gsmDisjunctRow, gsmDisjunctColumn, gsmDisjunctCell,
    gsmDisjunctCellRange);

  TTMSFNCDataGridSelectionOptions = class(TPersistent)
  private
    FMode: TTMSFNCDataGridSelectionMode;
    FOnChange: TNotifyEvent;
    FShowSelectionInFixedCells: Boolean;
    FOnChangeRebuild: TNotifyEvent;
    procedure SetMode(const Value: TTMSFNCDataGridSelectionMode);
    procedure SetShowSelectionInFixedCells(const Value: Boolean);
  protected
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
    property OnChangeRebuild: TNotifyEvent read FOnChangeRebuild write FOnChangeRebuild;
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create;
    procedure Changed;
    procedure ChangedRebuild;
  published
    property Mode: TTMSFNCDataGridSelectionMode read FMode write SetMode default gsmSingleCell;
    property ShowSelectionInFixedCells: Boolean read FShowSelectionInFixedCells write SetShowSelectionInFixedCells default False;
  end;

  TTMSFNCDataGridBandingOptions = class(TPersistent)
  private
    FEnabled: Boolean;
    FOnChange: TNotifyEvent;
    FBandRowCount: integer;
    FNormalRowCount: integer;
    FTotalRowCount: integer;
    procedure SetEnabled(const Value: Boolean);
    procedure SetBandRowCount(const Value: integer);
    procedure SetNormalRowCount(const Value: integer);
  protected
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create;
    procedure Changed; virtual;
    property TotalRowCount: integer read FTotalRowCount;
  published
    property BandRowCount: integer read FBandRowCount write SetBandRowCount default 1;
    property Enabled: Boolean read FEnabled write SetEnabled default False;
    property NormalRowCount: integer read FNormalRowCount write SetNormalRowCount default 1;
  end;

  TTMSFNCDataGridClipboardPasteAction = (gpaReplace, gpaInsert);

  TTMSFNCDataGridClipboardOptions = class(TPersistent)
  private
    FPasteAction: TTMSFNCDataGridClipboardPasteAction;
    FEnabled: Boolean;
    FIgnoreReadOnly: Boolean;
    FAllowRowGrow: Boolean;
    FAllowColumnGrow: Boolean;
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create;
  published
    property AllowColumnGrow: Boolean read FAllowColumnGrow write FAllowColumnGrow default false;
    property AllowRowGrow: Boolean read FAllowRowGrow write FAllowRowGrow default False;
    property Enabled: Boolean read FEnabled write FEnabled default True;
    property IgnoreReadOnly: Boolean read FIgnoreReadOnly write FIgnoreReadOnly default False;
    property PasteAction: TTMSFNCDataGridClipboardPasteAction read FPasteAction write FPasteAction default gpaReplace;
  end;

  TTMSFNCDataGridEditCellStart = (gcsNone, gcsStart, gcsEnd);
  TTMSFNCDataGridTabKeyHandling = (gtkhNextControl, gtkhNextCell, gtkhMixed);
  TTMSFNCDataGridTabKeyDirection = (gtkdNextColumnCell, gtkdNextRowCell);
  TTMSFNCDataGridTabKeyMode = (gtkmNone, gtkmNormal, gtkmShift);

  TTMSFNCDataGridInsertKeyHandling = (gikhNone, gikhInsertRowBefore, gikhInsertRowAfter);

  TTMSFNCDataGridEnterKeyHandling = (gekhNone, gekhNextColumn, gekhNextRow, gekhNextColumnInRow, gekhNextRowInColumn,
    gekhNextColumnAndAppend, gekhNextRowAndAppend, gekhNextRowAndColumnAppend, gekhNextColumnAndRowAppend);

  TTMSFNCDataGridDeleteKeyHandling = (gdkhNone, gdkhDeleteRow);

  TTMSFNCDataGridHomeKeyHandling = (ghkhNone, ghkhFirstRow, ghkhFirstColumn);

  TTMSFNCDataGridEndKeyHandling = (genkhNone, genkhLastRow, genkhLastColumn);

  TTMSFNCDataGridKeyboardOptions = class(TPersistent)
  private
    FTabKeyDirection: TTMSFNCDataGridTabKeyDirection;
    FTabKeyHandling: TTMSFNCDataGridTabKeyHandling;
    FPageScrollSize: Integer;
    FEnterKeyHandling: TTMSFNCDataGridEnterKeyHandling;
    FEnterKeyDirectEdit: Boolean;
    FDeleteKeyHandling: TTMSFNCDataGridDeleteKeyHandling;
    FInsertKeyHandling: TTMSFNCDataGridInsertKeyHandling;
    FTabKeyDirectEdit: Boolean;
    FArrowKeyDirectEdit: Boolean;
    FCellMergeShortcut: Boolean;
    FFindShortcut: Boolean;
    FReplaceShortcut: Boolean;
    FAppendRowOnArrowKeyDown: Boolean;
    FAppendColumnOnArrowKeyRight: Boolean;
    FHomeKeyHandling: TTMSFNCDataGridHomeKeyHandling;
    FEndKeyHandling: TTMSFNCDataGridEndKeyHandling;
    procedure SetPageScrollSize(const Value: Integer);
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create;
  published
    property ArrowKeyDirectEdit: Boolean read FArrowKeyDirectEdit write FArrowKeyDirectEdit default False;
    property AppendRowOnArrowKeyDown: Boolean read FAppendRowOnArrowKeyDown write FAppendRowOnArrowKeyDown default False;
    property AppendColumnOnArrowKeyRight: Boolean read FAppendColumnOnArrowKeyRight write FAppendColumnOnArrowKeyRight default False;
    property CellMergeShortcut: Boolean read FCellMergeShortcut write FCellMergeShortcut default False;
    property DeleteKeyHandling: TTMSFNCDataGridDeleteKeyHandling read FDeleteKeyHandling write FDeleteKeyHandling default gdkhNone;
    property EnterKeyDirectEdit: Boolean read FEnterKeyDirectEdit write FEnterKeyDirectEdit default False;
    property EnterKeyHandling: TTMSFNCDataGridEnterKeyHandling read FEnterKeyHandling write FEnterKeyHandling default gekhNone;
    property FindShortcut: Boolean read FFindShortcut write FFindShortcut default True;
    property InsertKeyHandling: TTMSFNCDataGridInsertKeyHandling read FInsertKeyHandling write FInsertKeyHandling default gikhNone;
    property PageScrollSize: Integer read FPageScrollSize write SetPageScrollSize default 0;
    property ReplaceShortcut: Boolean read FReplaceShortcut write FReplaceShortcut default True;
    property TabKeyDirectEdit: Boolean read FTabKeyDirectEdit write FTabKeyDirectEdit default False;
    property TabKeyDirection: TTMSFNCDataGridTabKeyDirection read FTabKeyDirection write FTabKeyDirection default gtkdNextColumnCell;
    property TabKeyHandling: TTMSFNCDataGridTabKeyHandling read FTabKeyHandling write FTabKeyHandling default gtkhMixed;
    property HomeKeyHandling: TTMSFNCDataGridHomeKeyHandling read FHomeKeyHandling write FHomeKeyHandling default ghkhFirstColumn;
    property EndKeyHandling: TTMSFNCDataGridEndKeyHandling read FEndKeyHandling write FEndKeyHandling default genkhLastColumn;
  end;

  TTMSFNCDataGridFixedCellSelectionMode = (gfcsAll, gfcsRow, gfcsColumn, gfcsRowRange, gfcsColumnRange, gfcsDisjunctRow, gfcsDisjunctColumn);

  TTMSFNCDataGridFixedCellSelection = set of TTMSFNCDataGridFixedCellSelectionMode;

  TTMSFNCDataGridAutoScrollingSpeedMode = (gasmPixels, gasmCell);

  TTMSFNCDataGridIOHTMLOptions = class(TPersistent)
  private
    FExportColors: Boolean;
    FExportImages: Boolean;
    FImageBaseName: string;
    FImageFolder: string;
    FHeaderText: string;
    FConvertSpecialChars: Boolean;
    FTableWidth: Integer;
    FNonBreakingText: Boolean;
    FHeaderFile: string;
    FTableBorderSize: Integer;
    FFooterText: string;
    FTableCellPadding: Integer;
    FPrefixTag: string;
    FSuffixTag: string;
    FTableCellSpacing: Integer;
    FSummary: string;
    FFooterFile: string;
    FXHTML: Boolean;
    FTableStyle: string;
    function IsImageBaseNameStored: Boolean;
    function IsImageFolderStored: Boolean;
  public
    constructor Create; virtual;
    procedure Assign(Source: TPersistent); override;
  published
    property ConvertSpecialChars: Boolean read FConvertSpecialChars write FConvertSpecialChars default False;
    property ExportColors: Boolean read FExportColors write FExportColors default True;
    property ExportImages: Boolean read FExportImages write FExportImages default True;
    property FooterFile: string read FFooterFile write FFooterFile;
    property FooterText: string read FFooterText write FFooterText;
    property HeaderFile: string read FHeaderFile write FHeaderFile;
    property HeaderText: string read FHeaderText write FHeaderText;
    property ImageBaseName: string read FImageBaseName write FImageBaseName stored IsImageBaseNameStored nodefault;
    property ImageFolder: string read FImageFolder write FImageFolder stored IsImageFolderStored nodefault;
    property NonBreakingText: Boolean read FNonBreakingText write FNonBreakingText default False;
    property PrefixTag: string read FPrefixTag write FPrefixTag;
    property SuffixTag: string read FSuffixTag write FSuffixTag;
    property Summary: string read FSummary write FSummary;
    property TableBorderSize: Integer read FTableBorderSize write FTableBorderSize default 1;
    property TableCellPadding: Integer read FTableCellPadding write FTableCellPadding default 0;
    property TableCellSpacing: Integer read FTableCellSpacing write FTableCellSpacing default 0;
    property TableStyle: string read FTableStyle write FTableStyle;
    property TableWidth: Integer read FTableWidth write FTableWidth default 100;
    property XHTML: Boolean read FXHTML write FXHTML default False;
  end;

  TTMSFNCDataGridIOOptions = class(TTMSFNCDataGridDataIOOptions)
  private
    FHTML: TTMSFNCDataGridIOHTMLOptions;
    procedure SetHTML(const Value: TTMSFNCDataGridIOHTMLOptions);
  public
    constructor Create; override;
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
  published
    property HTML: TTMSFNCDataGridIOHTMLOptions read FHTML write SetHTML;
  end;

  TTMSFNCDataGridMouseOptions = class(TPersistent)
  private
    FDirectEdit: Boolean;
    FWheelScrollSize: Integer;
    FAutoDragging: Boolean;
    FFixedRowSizing: Boolean;
    FFixedColumnSizing: Boolean;
    FRowDragging: Boolean;
    FColumnDragging: Boolean;
    FAutoScrollingInterval: Integer;
    FRowSizing: Boolean;
    FColumnSizing: Boolean;
    FAutoScrollingSpeed: Integer;
    FAutoScrolling: Boolean;
    FTouchScrolling: Boolean;
    FTouchScrollingSensitivity: Single;
    FFixedCellSelection: TTMSFNCDataGridFixedCellSelection;
    FRowAutoSizeOnDblClick: Boolean;
    FColumnAutoSizeOnDblClick: Boolean;
    FWheelScrollKeepSelection: Boolean;
    FRowSizeMargin: Integer;
    FColumnSizeMargin: Integer;
    FAutoScrollingSpeedMode: TTMSFNCDataGridAutoScrollingSpeedMode;
    FClickMargin: Integer;
    procedure SetWheelScrollSize(const Value: Integer);
    procedure SetAutoScrollingInterval(const Value: Integer);
    procedure SetTouchScrolling(const Value: Boolean);
    procedure SetWheelScrollKeepSelection(const Value: Boolean);
    function IsTouchScrollingSensitivityStored: Boolean;
    procedure SetColumnSizeMargin(const Value: Integer);
    procedure SetRowSizeMargin(const Value: Integer);
    procedure SetClickMargin(const Value: Integer);
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create;
  published
    property AutoDragging: Boolean read FAutoDragging write FAutoDragging default True;
    property AutoScrolling: Boolean read FAutoScrolling write FAutoScrolling default True;
    property AutoScrollingInterval: Integer read FAutoScrollingInterval write SetAutoScrollingInterval default 100;
    property AutoScrollingSpeed: Integer read FAutoScrollingSpeed write FAutoScrollingSpeed default 1;
    property AutoScrollingSpeedMode: TTMSFNCDataGridAutoScrollingSpeedMode read FAutoScrollingSpeedMode write FAutoScrollingSpeedMode default gasmPixels;
    property ClickMargin: Integer read FClickMargin write SetClickMargin default 2;
    property ColumnAutoSizeOnDblClick: Boolean read FColumnAutoSizeOnDblClick write FColumnAutoSizeOnDblClick default True;
    property ColumnDragging: Boolean read FColumnDragging write FColumnDragging default False;
    property ColumnSizeMargin: Integer read FColumnSizeMargin write SetColumnSizeMargin default 6;
    property ColumnSizing: Boolean read FColumnSizing write FColumnSizing default False;
    property DirectEdit: Boolean read FDirectEdit write FDirectEdit default false;
    property FixedCellSelection: TTMSFNCDataGridFixedCellSelection read FFixedCellSelection write FFixedCellSelection default [];
    property FixedColumnSizing: Boolean read FFixedColumnSizing write FFixedColumnSizing default False;
    property FixedRowSizing: Boolean read FFixedRowSizing write FFixedRowSizing default False;
    property RowAutoSizeOnDblClick: Boolean read FRowAutoSizeOnDblClick write FRowAutoSizeOnDblClick default True;
    property RowDragging: Boolean read FRowDragging write FRowDragging default False;
    property RowSizeMargin: Integer read FRowSizeMargin write SetRowSizeMargin default 6;
    property RowSizing: Boolean read FRowSizing write FRowSizing default False;
    property TouchScrolling: Boolean read FTouchScrolling write SetTouchScrolling default False;
    property TouchScrollingSensitivity: Single read FTouchScrollingSensitivity write FTouchScrollingSensitivity stored IsTouchScrollingSensitivityStored nodefault;
    property WheelScrollKeepSelection: Boolean read FWheelScrollKeepSelection write SetWheelScrollKeepSelection default True;
    property WheelScrollSize: Integer read FWheelScrollSize write SetWheelScrollSize default 4;
  end;

  TTMSFNCDataGridLookupOptions = class(TPersistent)
  private
    FCaseSensitive: Boolean;
    FIncremental: Boolean;
    FEnabled: Boolean;
    FResetInterval: Integer;
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create;
  published
    property CaseSensitive: Boolean read FCaseSensitive write FCaseSensitive default False;
    property Enabled: Boolean read FEnabled write FEnabled default False;
    property Incremental: Boolean read FIncremental write FIncremental default True;
    property ResetInterval: Integer read FResetInterval write FResetInterval default 4000;
  end;

  TTMSFNCDataGridAutoCompleteType = (gactDisabled, gactNormal, gactLookupList);

  TTMSFNCDataGridAutoCompleteOptions = class(TPersistent)
  private
    FType: TTMSFNCDataGridAutoCompleteType;
    FCaseSensitive: Boolean;
    FItems: TStringList;
    procedure SetItems(const Value: TStringList);
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create;
    destructor Destroy; override;
  published
    property &Type: TTMSFNCDataGridAutoCompleteType read FType write FType default gactDisabled;
    property CaseSensitive: Boolean read FCaseSensitive write FCaseSensitive default false;
    property Items: TStringList read FItems write SetItems;
  end;

  TTMSFNCDataGridEditingOptions = class(TPersistent)
  private
    FEnabled: Boolean;
    FDirectDropDown: Boolean;
    FAutoComplete: TTMSFNCDataGridAutoCompleteOptions;
    procedure SetAutoComplete(const Value: TTMSFNCDataGridAutoCompleteOptions);
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create;
    destructor Destroy; override;
  published
    property Enabled: Boolean read FEnabled write FEnabled default True;
    property AutoComplete: TTMSFNCDataGridAutoCompleteOptions read FAutoComplete write SetAutoComplete;
    property DirectDropDown: Boolean read FDirectDropDown write FDirectDropDown default False;
  end;

  TTMSFNCDataGridOptions = class(TTMSFNCDataGridDataOptions)
  private
    FColumn: TTMSFNCDataGridColumnOptions;
    FRow: TTMSFNCDataGridRowOptions;
    FSelection: TTMSFNCDataGridSelectionOptions;
    FBanding: TTMSFNCDataGridBandingOptions;
    FClipboard: TTMSFNCDataGridClipboardOptions;
    FKeyboard: TTMSFNCDataGridKeyboardOptions;
    FMouse: TTMSFNCDataGridMouseOptions;
    FEditing: TTMSFNCDataGridEditingOptions;
    FLookup: TTMSFNCDataGridLookupOptions;
    procedure SetColumn(const Value: TTMSFNCDataGridColumnOptions);
    procedure SetRow(const Value: TTMSFNCDataGridRowOptions);
    procedure SetSelection(const Value: TTMSFNCDataGridSelectionOptions);
    procedure SetBanding(const Value: TTMSFNCDataGridBandingOptions);
    procedure SetClipboard(const Value: TTMSFNCDataGridClipboardOptions);
    procedure SetKeyboard(const Value: TTMSFNCDataGridKeyboardOptions);
    procedure SetMouse(const Value: TTMSFNCDataGridMouseOptions);
    procedure SetEditing(const Value: TTMSFNCDataGridEditingOptions);
    procedure SetLookup(const Value: TTMSFNCDataGridLookupOptions);
    function GetIO: TTMSFNCDataGridIOOptions;
    procedure SetIO(const Value: TTMSFNCDataGridIOOptions);
  protected
    procedure DoColumnChangedRebuild(Sender: TObject);
    procedure DoRowChangedRebuild(Sender: TObject);
    procedure DoCellsChangedRebuild(Sender: TObject);
    procedure DoSelectionChanged(Sender: TObject);
    procedure DoSelectionChangedRebuild(Sender: TObject);
    procedure DoBandingChanged(Sender: TObject);
    function GetIOOptionsClass: TTMSFNCDataGridDataIOOptionsClass; override;
  public
    constructor Create; override;
    procedure Assign(Source: TPersistent); override;
    destructor Destroy; override;
  published
    property Banding: TTMSFNCDataGridBandingOptions read FBanding write SetBanding;
    property Clipboard: TTMSFNCDataGridClipboardOptions read FClipboard write SetClipboard;
    property Column: TTMSFNCDataGridColumnOptions read FColumn write SetColumn;
    property Editing: TTMSFNCDataGridEditingOptions read FEditing write SetEditing;
    property IO: TTMSFNCDataGridIOOptions read GetIO write SetIO;
    property Keyboard: TTMSFNCDataGridKeyboardOptions read FKeyboard write SetKeyboard;
    property Lookup: TTMSFNCDataGridLookupOptions read FLookup write SetLookup;
    property Mouse: TTMSFNCDataGridMouseOptions read FMouse write SetMouse;
    property Row: TTMSFNCDataGridRowOptions read FRow write SetRow;
    property Selection: TTMSFNCDataGridSelectionOptions read FSelection write SetSelection;
  end;

  TTMSFNCDataGridScrollMode = (gsmPixelScrolling, gsmCellScrolling);
  TTMSFNCDataGridScrollUpdate = (gsuContinuous, gsuOnce);
  TTMSFNCDataGridScrollEvent = procedure(Sender: TObject; APosition: Single) of object;
  TTMSFNCDataGridInteractionDirection = (gidNone, gidLeft, gidRight, gidUp, gidDown, gidLeftUp, gidRightDown);

  TTMSFNCDataGridSizeMode = (gsmColumnSizing, gsmRowSizing, gsmPreviousColumnSizing, gsmPreviousRowSizing);

  TTMSFNCDataGridDraggingMode = (gdmNone, gdmRowDragging, gdmColumnDragging);

  TTMSFNCDataGridRendererUpdateCursorEvent = procedure(Sender: TObject; ACursor: TCursor) of object;
  TTMSFNCDataGridRendererIsLoadingEvent = function(Sender: TObject): Boolean of object;
  TTMSFNCDataGridRendererIsDestroyingEvent = function(Sender: TObject): Boolean of object;
  TTMSFNCDataGridRendererIsDesigntimeEvent = function(Sender: TObject): Boolean of object;
  TTMSFNCDataGridRendererIsFocusedEvent = function(Sender: TObject): Boolean of object;

  TTMSFNCDataGridRenderer = class;

  TTMSFNCDataGridScrollBar = class(TTMSFNCScrollBar)
  protected
    procedure DrawEx(AGraphics: TTMSFNCGraphics);
  end;

  {$IFDEF FMXLIB}
  TTMSFNCDataGridInplaceEditor = TControl;
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  TTMSFNCDataGridInplaceEditor = TWinControl;
  {$ENDIF}
  TTMSFNCDataGridInplaceEditorClass = class of TTMSFNCDataGridInplaceEditor;

  TTMSFNCDataGridInplaceEditorType = (getNone, getEdit, getNumericEdit, getSignedNumericEdit, getFloatEdit, getSignedFloatEdit, getUppercaseEdit, getMixedCaseEdit, getLowerCaseEdit,
    getMoneyEdit, getHexEdit, getAlphaNumericEdit, getValidCharsEdit, getComboBox, getComboEdit, getSpinBox, getDatePicker, getColorPicker,
    getTrackBar, getMemo, getHTMLEditor, getCustom);
  TTMSFNCDataGridInplaceEditorTarget = (getCellValue, getFillColor);

  TTMSFNCDataGridBeforeOpenInplaceEditorEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; var AValue: TTMSFNCDataGridCellValue; var ACanOpen: Boolean) of object;
  TTMSFNCDataGridAfterOpenInplaceEditorEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; AValue: TTMSFNCDataGridCellValue) of object;
  TTMSFNCDataGridBeforeSortColumnEvent = procedure(Sender: TObject; AColumn: Integer; var ACanSort: Boolean) of object;
  TTMSFNCDataGridAfterSortColumnEvent = procedure(Sender: TObject; AColumn: Integer) of object;
  TTMSFNCDataGridBeforeApplyFilterEvent = procedure(Sender: TObject; AColumn: Integer; var ACondition: string; var ACanApply: Boolean) of object;
  TTMSFNCDataGridAfterApplyFilterEvent = procedure(Sender: TObject; AColumn: Integer; ACondition: string) of object;
  TTMSFNCDataGridBeforeShowFilterEvent = procedure(Sender: TObject; AColumn: Integer; var ACanShow: Boolean) of object;
  TTMSFNCDataGridAfterShowFilterEvent = procedure(Sender: TObject; AColumn: Integer) of object;
  TTMSFNCDataGridBeforeClipboardEvent = procedure(Sender: TObject; var ACanShow: Boolean) of object;
  TTMSFNCDataGridAfterClipboardEvent = procedure(Sender: TObject) of object;
  TTMSFNCDataGridBeforeShowFindDialogEvent = procedure(Sender: TObject; AFindDialog: TTMSFNCFindDialog; var ACanShow: Boolean) of object;
  TTMSFNCDataGridAfterShowFindDialogEvent = procedure(Sender: TObject; AFindDialog: TTMSFNCFindDialog) of object;
  TTMSFNCDataGridBeforeShowReplaceDialogEvent = procedure(Sender: TObject; AReplaceDialog: TTMSFNCReplaceDialog; var ACanShow: Boolean) of object;
  TTMSFNCDataGridAfterShowReplaceDialogEvent = procedure(Sender: TObject; AReplaceDialog: TTMSFNCReplaceDialog) of object;
  TTMSFNCDataGridBeforeCloseInplaceEditorEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; ACancelled: Boolean; var AValue: TTMSFNCDataGridCellValue; var ACanClose: Boolean) of object;
  TTMSFNCDataGridAfterCloseInplaceEditorEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; ACancel: Boolean; AValue: TTMSFNCDataGridCellValue) of object;
  TTMSFNCDataGridCellEditValidateDataEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; var AValue: TTMSFNCDataGridCellValue; var AValid: Boolean) of object;
  TTMSFNCDataGridCellBeforeShowPopupMenuEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; APopupMenu: TPopupMenu; var ACanShow: Boolean) of object;
  TTMSFNCDataGridCellBeforeNodeExpandEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; var ACanExpand: Boolean) of object;
  TTMSFNCDataGridCellBeforeNodeCollapseEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; var ACanCollapse: Boolean) of object;
  TTMSFNCDataGridCellAfterNodeExpandEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord) of object;
  TTMSFNCDataGridCellAfterNodeCollapseEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord) of object;
  TTMSFNCDataGridNeedFilterDropDownData = procedure(Sender: TObject; AColumn: Integer; AValues: TStrings) of object;
  TTMSFNCDataGridCellEditGetDataEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; var AValue: TTMSFNCDataGridCellValue) of object;
  TTMSFNCDataGridCellEditSetDataEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; var AValue: TTMSFNCDataGridCellValue) of object;
  TTMSFNCDataGridGetInplaceEditorClassEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; var AInplaceEditorClass: TTMSFNCDataGridInplaceEditorClass) of object;
  TTMSFNCDataGridGetInplaceEditorTypeEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; var AInplaceEditorType: TTMSFNCDataGridInplaceEditorType) of object;
  TTMSFNCDataGridGetInplaceEditorTargetEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; var AInplaceEditorTarget: TTMSFNCDataGridInplaceEditorTarget) of object;
  TTMSFNCDataGridGetInplaceEditorRectEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; var AInplaceEditorRect: TRectF) of object;
  TTMSFNCDataGridGetInplaceEditorPropertiesEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; AInplaceEditorType: TTMSFNCDataGridInplaceEditorType) of object;
  TTMSFNCDataGridSelectingCellEvent = procedure(Sender: TObject; AStartCell, AEndCell: TTMSFNCDataGridCellCoord; var ACanSelect: Boolean) of object;
  TTMSFNCDataGridSelectCellEvent = procedure(Sender: TObject; AStartCell, AEndCell: TTMSFNCDataGridCellCoord) of object;
  TTMSFNCDataGridCanAppendColumnEvent = procedure(Sender: TObject; AColumn: Integer; var ACanAppend: Boolean) of object;
  TTMSFNCDataGridCanAppendRowEvent = procedure(Sender: TObject; ARow: Integer; var ACanAppend: Boolean) of object;
  TTMSFNCDataGridAppendColumnEvent = procedure(Sender: TObject; AColumn: Integer) of object;
  TTMSFNCDataGridAppendRowEvent = procedure(Sender: TObject; ARow: Integer) of object;
  TTMSFNCDataGridCanDeleteColumnEvent = procedure(Sender: TObject; AColumn: Integer; var ACanDelete: Boolean) of object;
  TTMSFNCDataGridCanDeleteRowEvent = procedure(Sender: TObject; ARow: Integer; var ACanDelete: Boolean) of object;
  TTMSFNCDataGridDeleteColumnEvent = procedure(Sender: TObject; AColumn: Integer) of object;
  TTMSFNCDataGridDeleteRowEvent = procedure(Sender: TObject; ARow: Integer) of object;
  TTMSFNCDataGridCanDropColumnEvent = procedure(Sender: TObject; AFromColumn, AToColumn: Integer; var ACanDrop: Boolean) of object;
  TTMSFNCDataGridCanDropRowEvent = procedure(Sender: TObject; AFromRow, AToRow: Integer; var ACanDrop: Boolean) of object;
  TTMSFNCDataGridCanDragColumnEvent = procedure(Sender: TObject; AColumn: Integer; var ACanDrag: Boolean) of object;
  TTMSFNCDataGridCanDragRowEvent = procedure(Sender: TObject; ARow: Integer; var ACanDrag: Boolean) of object;
  TTMSFNCDataGridColumnDroppedEvent = procedure(Sender: TObject; AFromColumn, AToColumn: Integer) of object;
  TTMSFNCDataGridRowDroppedEvent = procedure(Sender: TObject; AFromRow, AToColumn: Integer) of object;
  TTMSFNCDataGridCanInsertColumnEvent = procedure(Sender: TObject; AColumn: Integer; var ACanInsert: Boolean) of object;
  TTMSFNCDataGridCanInsertRowEvent = procedure(Sender: TObject; ARow: Integer; var ACanInsert: Boolean) of object;
  TTMSFNCDataGridInsertColumnEvent = procedure(Sender: TObject; AColumn: Integer) of object;
  TTMSFNCDataGridInsertRowEvent = procedure(Sender: TObject; ARow: Integer) of object;
  TTMSFNCDataGridCanSizeColumnEvent = procedure(Sender: TObject; AColumn: Integer; var ASize: Double; var ACanSize: Boolean) of object;
  TTMSFNCDataGridCanSizeRowEvent = procedure(Sender: TObject; ARow: Integer; var ASize: Double; var ACanSize: Boolean) of object;
  TTMSFNCDataGridSizeColumnEvent = procedure(Sender: TObject; AColumn: Integer; ASize: Double) of object;
  TTMSFNCDataGridSizeRowEvent = procedure(Sender: TObject; ARow: Integer; ASize: Double) of object;
  TTMSFNCDataGridColumnSizedEvent = procedure(Sender: TObject; AColumn: Integer; ASize: Double) of object;
  TTMSFNCDataGridRowSizedEvent = procedure(Sender: TObject; ARow: Integer; ASize: Double) of object;

  TTMSFNCDataGridEdit = class(TTMSFNCEdit)
  {$IFDEF VCLLIB}
  private
    procedure WMGetDlgCode(var Message: TWMGetDlgCode); message WM_GETDLGCODE;
  {$ENDIF}
  public
    {$IFDEF FMXLIB}
    function GetDefaultStyleLookupName: string; override;
    {$ENDIF}
    constructor Create(AOwner: TComponent); override;
  end;

  TComboBoxSafe = class(TComboBox)
  strict private
    {$IFDEF VCLLIB}
    FSafeItemIndex: Integer;
    FSafeItems: TArray<string>;
    {$ENDIF}
    function GetSafeItemIndex: Integer;
    function GetSafeItems: TArray<string>;
    procedure SetSafeItemIndex(const AValue: Integer);
    procedure SetSafeItems(const AValue: TArray<string>);
  {$IFDEF VCLLIB}
  strict protected
    procedure CreateWnd; override;
    procedure DestroyWnd; override;
  {$ENDIF}
  public
    constructor Create(AOwner: TComponent); override;
    property SafeItemIndex: Integer read GetSafeItemIndex write SetSafeItemIndex;
    property SafeItems: TArray<string> read GetSafeItems write SetSafeItems;
  end;

  TTMSFNCDataGridComboBox = class(TComboBox);
  {$IFDEF FMXLIB}
  TTMSFNCDataGridComboEdit = class(TComboEdit);
  {$ELSE}
  TTMSFNCDataGridComboEdit = class(TComboBox);
  {$ENDIF}
  TTMSFNCDataGridTrackBar = class(TTrackBar);
  TTMSFNCDataGridColorPicker = class(TTMSFNCColorPicker);
  TTMSFNCDataGridMemo = class(TMemo);

  {$IFDEF FMXLIB}
  TTMSFNCDataGridDatePicker = class(TDateEdit)
  protected
    procedure KeyDown(var Key: Word; var KeyChar: System.WideChar; Shift: TShiftState); override;
  end;
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  TTMSFNCDataGridDatePicker = class(TDateTimePicker);
  {$ENDIF}

  {$IFDEF FMXLIB}
  TTMSFNCDataGridSpinBox = class(TSpinBox);
  TTMSFNCDataGridCaretPosition = TCaretPosition;
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  TTMSFNCDataGridSpinBox = class(TSpinEdit);
  TTMSFNCDataGridCaretPosition = TPoint;
  {$ENDIF}

  TTMSFNCDataGridRendererDataAdapter = class(TTMSFNCDataGridDataAdapter)
  private
    function GetRenderer: TTMSFNCDataGridRenderer;
    procedure SetRenderer(const Value: TTMSFNCDataGridRenderer);
  protected
    procedure GetFilterData(AColumn: Integer; AValues: TStrings); virtual; abstract;
    procedure RowBeforeExpand(ARow: Integer); virtual; abstract;
    procedure RowAfterExpand(ARow: Integer); virtual; abstract;
    procedure RowBeforeCollapse(ARow: Integer); virtual; abstract;
    procedure RowAfterCollapse(ARow: Integer); virtual; abstract;
    procedure GetCellProperties(ACell: TTMSFNCDataGridCell); virtual; abstract;
    procedure GetInplaceEditorType(ACell: TTMSFNCDataGridCellCoord; var AInplaceEditorType: TTMSFNCDataGridInplaceEditorType); virtual; abstract;
    procedure GetInplaceEditorProperties(ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; AInplaceEditorType: TTMSFNCDataGridInplaceEditorType); virtual; abstract;
    procedure ExportNotification(AState: TTMSFNCDataGridExportState; ARow: Integer); virtual; abstract;
    procedure CanEditCell(ACell: TTMSFNCDataGridCellCoord; var ACanEdit: Boolean); virtual; abstract;
    procedure EditCell(ACell: TTMSFNCDataGridCellCoord); virtual; abstract;
    procedure EditSetData(ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; var AValue: TTMSFNCDataGridCellValue); virtual; abstract;
    procedure DeleteRow(ARow: Integer); virtual; abstract;
    procedure InsertRow(ARow: Integer); virtual; abstract;
    procedure UpdateBounds; virtual; abstract;
    procedure Scroll; virtual; abstract;
    procedure SelectCell(ACell: TTMSFNCDataGridCellCoord); virtual; abstract;
    procedure UpdateScrollMode; virtual; abstract;
  public
    property Renderer: TTMSFNCDataGridRenderer read GetRenderer write SetRenderer;
  end;

  TTMSFNCDataGridHilightMode = (ghmCell, ghmColumn, ghmRow, ghmGrid);

  TTMSFNCDataGridHilightOptions = record
    IncludeFixed: Boolean;
    CaseSensitive: Boolean;
    FullText: Boolean;
    Mode: TTMSFNCDataGridHilightMode;
    Column: Integer;
    Row: Integer;
  end;

  TTMSFNCDataGridMarkOptions = TTMSFNCDataGridHilightOptions;

  TTMSFNCCommentPopup = class(TTMSFNCPopup)
  private
    FCell: TTMSFNCDataGridCell;
  end;

  TTMSFNCDataGridColumnSetting = (gcsAppearance, gcsType, gcsEditor, gcsEditorTarget, gcsEditorItems, gcsReadOnly, gcsFormatting,
    gcsControlText, gcsControlWidth, gcsControlHeight, gcsControlAlign, gcsControlPosition, gcsControlMargins,
    gcsFilterButton, gcsFilterEditor, gcsFilterEditorType);

  TTMSFNCDataGridColumnSettings = set of TTMSFNCDataGridColumnSetting;

  TTMSFNCDataGridColumns = class;

  TTMSFNCDataGridColumnFormatting = class(TPersistent)
  private
    FFormat: string;
    FType: TTMSFNCDataGridDataFormattingType;
    FOnChange: TNotifyEvent;
    FConvertSettings: TFormatSettings;
    FBooleanFalseText: string;
    FBooleanTrueText: string;
    procedure SetFormat(const Value: string);
    procedure SetType(const Value: TTMSFNCDataGridDataFormattingType);
    function IsFormatStored: Boolean;
    function IsBooleanFalseTextStored: Boolean;
    function IsBooleanTrueTextStored: Boolean;
  protected
    procedure UpdateColumnFormatting;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  public
    constructor Create; virtual;
    procedure Assign(Source: TPersistent); override;
    property ConvertSettings: TFormatSettings read FConvertSettings write FConvertSettings;
  published
    property &Type: TTMSFNCDataGridDataFormattingType read FType write SetType default gdftDefault;
    property Format: string read FFormat write SetFormat stored IsFormatStored;
    property BooleanTrueText: string read FBooleanTrueText write FBooleanTrueText stored IsBooleanTrueTextStored nodefault;
    property BooleanFalseText: string read FBooleanFalseText write FBooleanFalseText stored IsBooleanFalseTextStored nodefault;
  end;

  TTMSFNCDataGridColumnBinding = class(TPersistent)
  private
    FBooleanFalseValue: string;
    FBooleanTrueValue: string;
    FOnChange: TNotifyEvent;
    function IsBooleanFalseValueStored: Boolean;
    function IsBooleanTrueValueStored: Boolean;
    procedure SetBooleanFalseValue(const Value: string);
    procedure SetBooleanTrueValue(const Value: string);
  protected
    procedure UpdateColumnBinding;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  public
    constructor Create; virtual;
    procedure Assign(Source: TPersistent); override;
  published
    property BooleanTrueValue: string read FBooleanTrueValue write SetBooleanTrueValue stored IsBooleanTrueValueStored nodefault;
    property BooleanFalseValue: string read FBooleanFalseValue write SetBooleanFalseValue stored IsBooleanFalseValueStored nodefault;
  end;

  TTMSFNCDataGridFilterEditorType = (gfetAutomatic, gfetString, gfetBoolean, gfetDateTime, gfetFloat, gfetNumber, gfetCustom);
  TTMSFNCDataGridFilterValueType = (gfvtString, gfvtBoolean, gfvtDateTime, gfvtFloat, gfvtNumber);

  TTMSFNCDataGridColumn = class(TCollectionItem)
  private
    FName: string;
    FVisible: Boolean;
    FColumns: TTMSFNCDataGridColumns;
    FAppearance: TTMSFNCDataGridCellAppearance;
    FSettings: TTMSFNCDataGridColumnSettings;
    FType: TTMSFNCDataGridCellItemType;
    FEditor: TTMSFNCDataGridInplaceEditorType;
    FEditorItems: TStringList;
    FDataPointer: Pointer;
    FDataBoolean: Boolean;
    FDataString: String;
    FDataObject: TObject;
    FDataInteger: NativeInt;
    FReadOnly: Boolean;
    FFormatting: TTMSFNCDataGridColumnFormatting;
    FBinding: TTMSFNCDataGridColumnBinding;
    FEditorTarget: TTMSFNCDataGridInplaceEditorTarget;
    FTypeRange: TTMSFNCDataGridCellItemTypeRange;
    FControlText: string;
    FControlHeight: Integer;
    FControlAlign: TTMSFNCDataGridCellControlAlign;
    FControlWidth: Integer;
    FWidth: Single;
    FHeader: string;
    FControlPosition: TTMSFNCDataGridCellControlPosition;
    FFilterButton: Boolean;
    FFilterEditor: Boolean;
    FControlMargins: TTMSFNCMargins;
    FFilterEditorType: TTMSFNCDataGridFilterEditorType;
    procedure SetHeader(const Value: string);
    function GetRenderer: TTMSFNCDataGridRenderer;
    function GetHeader: string;
    function GetWidth: Single;
    procedure SetWidth(const Value: Single);
    procedure SetAppearance(const Value: TTMSFNCDataGridCellAppearance);
    procedure SetSettings(const Value: TTMSFNCDataGridColumnSettings);
    procedure SetType(const Value: TTMSFNCDataGridCellItemType);
    procedure SetEditorItems(const Value: TStringList);
    procedure SetReadOnly(const Value: Boolean);
    procedure SetFormatting(const Value: TTMSFNCDataGridColumnFormatting);
    procedure SetBinding(const Value: TTMSFNCDataGridColumnBinding);
    procedure SetEditor(const Value: TTMSFNCDataGridInplaceEditorType);
    procedure SetEditorTarget(const Value: TTMSFNCDataGridInplaceEditorTarget);
    function GetValueType: TTypeKind;
    procedure SetTypeRange(const Value: TTMSFNCDataGridCellItemTypeRange);
    function IsControlHeightStored: Boolean;
    function IsControlTextStored: Boolean;
    function IsControlWidthStored: Boolean;
    procedure SetControlAlign(const Value: TTMSFNCDataGridCellControlAlign);
    procedure SetControlHeight(const Value: Integer);
    procedure SetControlText(const Value: string);
    procedure SetControlWidth(const Value: Integer);
    procedure SetControlPosition(
      const Value: TTMSFNCDataGridCellControlPosition);
    function IsHeaderStored: Boolean;
    function IsWidthStored: Boolean;
    procedure SetFilterButton(const Value: Boolean);
    procedure SetFilterEditor(const Value: Boolean);
    procedure SetControlMargins(const Value: TTMSFNCMargins);
    function GetVisible: Boolean;
    procedure SetVisible(const Value: Boolean);
    procedure SetFilterEditorType(const Value: TTMSFNCDataGridFilterEditorType);
  protected
    procedure UpdateSettings(ASetting: TTMSFNCDataGridColumnSetting);
    property Renderer: TTMSFNCDataGridRenderer read GetRenderer;
    procedure UpdateColumn;
    procedure ClearColumn;
    procedure EditorItemsChanged(Sender: TObject);
    procedure MarginsChanged(Sender: TObject);
    procedure AppearanceChanged(Sender: TObject);
    procedure FormattingChanged(Sender: TObject);
    procedure BindingChanged(Sender: TObject);
    function GetDisplayName: string; override;
  public
    constructor Create(ACollection: TCollection); override;
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
    procedure AddSetting(ASetting: TTMSFNCDataGridColumnSetting);
    procedure RemoveSetting(ASetting: TTMSFNCDataGridColumnSetting);
    property DataPointer: Pointer read FDataPointer write FDataPointer;
    property DataBoolean: Boolean read FDataBoolean write FDataBoolean;
    property DataObject: TObject read FDataObject write FDataObject;
    property DataString: String read FDataString write FDataString;
    property DataInteger: NativeInt read FDataInteger write FDataInteger;
  published
    property &ReadOnly: Boolean read FReadOnly write SetReadOnly default False;
    property TypeRange: TTMSFNCDataGridCellItemTypeRange read FTypeRange write SetTypeRange default gcirAll;
    property &Type: TTMSFNCDataGridCellItemType read FType write SetType default gcitNormal;
    property FilterEditorType: TTMSFNCDataGridFilterEditorType read FFilterEditorType write SetFilterEditorType default gfetAutomatic;
    property FilterButton: Boolean read FFilterButton write SetFilterButton default True;
    property FilterEditor: Boolean read FFilterEditor write SetFilterEditor default False;
    property Appearance: TTMSFNCDataGridCellAppearance read FAppearance write SetAppearance;
    property Editor: TTMSFNCDataGridInplaceEditorType read FEditor write SetEditor default getEdit;
    property EditorItems: TStringList read FEditorItems write SetEditorItems;
    property EditorTarget: TTMSFNCDataGridInplaceEditorTarget read FEditorTarget write SetEditorTarget default getCellValue;
    property Binding: TTMSFNCDataGridColumnBinding read FBinding write SetBinding;
    property Formatting: TTMSFNCDataGridColumnFormatting read FFormatting write SetFormatting;
    property Header: string read GetHeader write SetHeader stored IsHeaderStored nodefault;
    property Name: string read FName write FName;
    property Settings: TTMSFNCDataGridColumnSettings read FSettings write SetSettings default [];
    property ValueType: TTypeKind read GetValueType;
    property Visible: Boolean read GetVisible write SetVisible default True;
    property Width: Single read GetWidth write SetWidth stored IsWidthStored nodefault;
    property ControlText: string read FControlText write SetControlText stored IsControlTextStored nodefault;
    property ControlAlign: TTMSFNCDataGridCellControlAlign read FControlAlign write SetControlAlign default gcaNone;
    property ControlPosition: TTMSFNCDataGridCellControlPosition read FControlPosition write SetControlPosition default gcpCenterRight;
    property ControlWidth: Integer read FControlWidth write SetControlWidth stored IsControlWidthStored nodefault;
    property ControlHeight: Integer read FControlHeight write SetControlHeight stored IsControlHeightStored nodefault;
    property ControlMargins: TTMSFNCMargins read FControlMargins write SetControlMargins;
  end;

  TTMSFNCDataGridColumnClass = class of TTMSFNCDataGridColumn;

  {$IFDEF WEBLIB}
  TTMSFNCDataGridColumns = class(TTMSFNCOwnedCollection)
  {$ENDIF}
  {$IFNDEF WEBLIB}
  TTMSFNCDataGridColumns = class(TTMSFNCOwnedCollection<TTMSFNCDataGridColumn>)
  {$ENDIF}
  private
    FRenderer: TTMSFNCDataGridRenderer;
    FOnChange: TNotifyEvent;
    function GetItem(Index: integer): TTMSFNCDataGridColumn;
    procedure SetItem(Index: integer; const Value: TTMSFNCDataGridColumn);
  protected
    property Renderer: TTMSFNCDataGridRenderer read FRenderer;
    procedure UpdateColumns;
    procedure ClearColumn(AIndex: Integer);
    function GetColumnClass: TTMSFNCDataGridColumnClass; virtual;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  public
    function ColumnByName(AName: string): TTMSFNCDataGridColumn;
    function ColumnByHeader(AHeader: string): TTMSFNCDataGridColumn;
    constructor Create(AHost: TPersistent; ARenderer: TTMSFNCDataGridRenderer);
    property Items[Index: integer]: TTMSFNCDataGridColumn read GetItem write SetItem; default;
    function Add: TTMSFNCDataGridColumn;
    function Insert(Index: integer): TTMSFNCDataGridColumn;
  end;

  TTMSFNCDataGridColumnsClass = class of TTMSFNCDataGridColumns;

  TTMSFNCDataGridAutoSizeMode = (gamVisibleCells, gamAllCells);

  TTMSFNCDataGridRendererExportNewPageCallback = reference to procedure;

  TTMSFNCDataGridRendererExportGetPageIndexCallback = reference to function: Integer;

  TTMSFNCDataGridCellControlWrapperList = class(TDictionary<string, TTMSFNCDataGridCellControlWrapper>);

  TTMSFNCDataGridCreateCustomFilterEditorEvent = function(Sender: TObject; ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellControl of object;
  TTMSFNCDataGridBeforeCreateFilterEditorEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; var ACanCreate: Boolean) of object;
  TTMSFNCDataGridAfterCreateFilterEditorEvent = procedure(Sender: TObject; ACell: TTMSFNCDataGridCellCoord; AEditor: TTMSFNCDataGridCellControl) of object;

  TTMSFNCDataGridFilterPanel = class(TTMSFNCCustomControl)
  private
    FBlockChange: Boolean;
    FType: TTMSFNCDataGridFilterEditorType;
    FClearButton: TTMSFNCToolBarButton;
    FCell: TTMSFNCDataGridCellCoord;
    FTypeButton: TTMSFNCToolBarButton;
    FRenderer: TTMSFNCDataGridRenderer;
    FEditor: TTMSFNCDataGridCellControl;
    FTypeListBox: TTMSFNCListBox;
    FEditorTimer: TTimer;
    FEditorTime: Integer;
    FAutoHideControls: Boolean;
    function GetEditorTimer: TTimer;
  protected
    procedure DoEditorTimerChanged(Sender: TObject);
    procedure DoEditorChange(Sender: TObject);
    procedure UpdateControlAfterResize; override;
    procedure DoClearClick(Sender: TObject);
    procedure Notification(AComponent: TComponent; Operation: TOperation); override;
    procedure DoTypeListBoxSelected(Sender: TObject; AItem: TTMSFNCListBoxItem);
    procedure DoTypeButtonClick(Sender: TObject);
    function ItemIndexByType(AType: TTMSFNCDataGridDataFilterType): Integer;
    function GetDefaultFilterType(AType: TTMSFNCDataGridFilterEditorType): TTMSFNCDataGridDataFilterType;
    property EditorTimer: TTimer read GetEditorTimer;
  public
    constructor Create(AOwner: TComponent; ARenderer: TTMSFNCDataGridRenderer; ACell: TTMSFNCDataGridCellCoord; AType: TTMSFNCDataGridFilterEditorType); reintroduce; virtual;
    destructor Destroy; override;
    procedure Reset;
    procedure SetFilter(AFilter: TTMSFNCDataGridDataFilterData);
    property ClearButton: TTMSFNCToolBarButton read FClearButton;
    property TypeButton: TTMSFNCToolBarButton read FTypeButton;
    property TypeListBox: TTMSFNCListBox read FTypeListBox;
    property Editor: TTMSFNCDataGridCellControl read FEditor;
    property AutoHideControls: Boolean read FAutoHideControls write FAutoHideControls default True;
  end;

  TTMSFNCDataGridFilterTimerMode = (gftmDropDown, gftmEditor);

  TTMSFNCDataGridRenderer = class(TTMSFNCDataGridData, ITMSFNCAppearanceGlobalFont, ITMSFNCBitmapContainer, ITMSFNCAdaptToStyle)
  private
    FStyleApplied: Boolean;
    FSaveClipRect, FClipRect: TRectF;
    FUseClipRect: Boolean;
    FCommentPopup: TTMSFNCCommentPopup;
    FBitmapContainer: TTMSFNCBitmapContainer;
    FScale: Single;
    FLoadFilter: Boolean;
    FFilterPopup: TTMSFNCPopup;
    FFilterTypeComboBox: TComboBoxSafe;
    FFilterListBox: TTMSFNCCheckedListBox;
    FFilterSortAscendingButton: TTMSFNCToolBarButton;
    FFilterSortDescendingButton: TTMSFNCToolBarButton;
    FFilterClearButton: TTMSFNCToolBarButton;
    FFilterContainer: TTMSFNCCustomControl;
    FFilterEdit: TTMSFNCEdit;
    FFilterTimer: TTimer;
    FFilterExitTimer: TTimer;
    FDroppedDown: Boolean;
    FKeyDown: Boolean;
    FCapturedScrollbar: TTMSFNCDataGridScrollBar;
    FOldCursor: TCursor;
    FSizing: Boolean;
    FColumnStretchingActive: Boolean;
    FColumnStretchingApplied: Boolean;
    FRowStretchingActive: Boolean;
    FRowStretchingApplied: Boolean;
    FTimeStart, FTimeStop: Double;
    FAnimateVerticalPos, FAnimateHorizontalPos: Boolean;
    FAnimating: Boolean;
    FScrollTimer, FDragTimer, FAnimateTimer: TTimer;
    FDblClick, FClearMouseDown: Boolean;
    FDoTouchScroll, FTouchScrolling: Boolean;
    FDoubleSelection: Boolean;
    FTabKey: TTMSFNCDataGridTabKeyMode;
    FPrevX, FPrevY, FSizeX, FSizeY, FDragX, FDragY, FDownX, FDownY: Single;
    FSpY, FSpX, FX, FY, FTouchX, FTouchY, FScrollX, FScrollY, FMouseX, FMouseY: Single;
    FScrollHTo, FScrollVTo: Double;
    FCellAnchor, FDragScrollCell: TTMSFNCDataGridCellCoord;
    FCellAnchorString: string;
    FMouseDblClick, FMouseSizing, FMouseDown, FMouseUp, FDragFixed, FMouseScrollingUp, FMouseDragging: Boolean;
    FDraggingMode: TTMSFNCDataGridDraggingMode;
    FSizeMode: TTMSFNCDataGridSizeMode;
    FDragCell, FDragCellTo: TTMSFNCDataGridCellCoord;
    FDragCellToObj: TTMSFNCDataGridCell;
    FLookupString: string;
    FLookupTimer: TTimer;
    FEditing: Boolean;
    FFixedColumnCellList: TTMSFNCDataGridFixedColumnCellList;
    FHoverCell, FFocusedCell, FFirstCell, FSaveCell, FRealCell: TTMSFNCDataGridCellCoord;
    FFixedColumnCellDisplay: TTMSFNCDataGridFixedColumnCellDisplayList;
    FOnDisplay: TNotifyEvent;
    FFixedRootCellDisplay: TTMSFNCDataGridFixedRootCellDisplayList;
    FFixedRowCellDisplay: TTMSFNCDataGridFixedRowCellDisplayList;
    FFixedRowCellList: TTMSFNCDataGridFixedRowCellList;
    FFixedRootCellList: TTMSFNCDataGridFixedRootCellList;
    FOnAfterDrawGrid: TTMSFNCDataGridAfterDrawGridEvent;
    FOnBeforeDrawCell: TTMSFNCDataGridBeforeDrawCellEvent;
    FHost: TObject;
    FGlobalFont: TTMSFNCAppearanceGlobalFont;
    FOnVScroll: TTMSFNCDataGridScrollEvent;
    FOnHScroll: TTMSFNCDataGridScrollEvent;
    FNormalCellDisplay: TTMSFNCDataGridNormalCellDisplayList;
    FMergedCells: TTMSFNCDataGridMergedCells;
    FOnAfterDrawCell: TTMSFNCDataGridAfterDrawCellEvent;
    FNormalCellList: TTMSFNCDataGridNormalCellList;
    FCustomScrollBars: Boolean;
    FOnGetCellClass: TTMSFNCDataGridGetCellClassEvent;
    FStroke: TTMSFNCGraphicsStroke;
    FOnGetCellLayout: TTMSFNCDataGridGetCellLayoutEvent;
    FCellAppearance: TTMSFNCDataGridCellAppearance;
    FBlockScrollingUpdate: Boolean;
    FLocalHScrollValue: Double;
    FLocalVScrollValue: Double;
    FIsMouseDown: Boolean;
    FOnGetCellProperties: TTMSFNCDataGridGetCellPropertiesEvent;
    FCustomVerticalScrollBar, FCustomHorizontalScrollBar: TTMSFNCDataGridScrollBar;
    FScrollUpdate: TTMSFNCDataGridScrollUpdate;
    FSaveHScrollPos, FSaveVScrollPos: Single;
    FScrollMode: TTMSFNCDataGridScrollMode;
    FStretchScrollBars: Boolean;
    FVerticalScrollBarVisible: Boolean;
    FBlockSynchronizeColumns: Boolean;
    FHorizontalScrollBarVisible: Boolean;
    FVerticalScrollBar, FHorizontalScrollBar: TScrollBar;
    FOnFocus: TNotifyEvent;
    FOnUpdateCursor: TTMSFNCDataGridRendererUpdateCursorEvent;
    FOnIsFocused: TTMSFNCDataGridRendererIsFocusedEvent;
    FOnIsDestroying: TTMSFNCDataGridRendererIsDestroyingEvent;
    FClosing: Boolean;
    FEditingCell: TTMSFNCDataGridCellCoord;
    FInplaceEditor: TTMSFNCDataGridInplaceEditor;
    FInplaceEditorActive: Boolean;
    FInplaceEditorCustomClass: TTMSFNCDataGridInplaceEditorClass;
    FInplaceEditorType: TTMSFNCDataGridInplaceEditorType;
    FInplaceEditorTarget: TTMSFNCDataGridInplaceEditorTarget;
    FOnAfterOpenInplaceEditor: TTMSFNCDataGridAfterOpenInplaceEditorEvent;
    FOnBeforeOpenInplaceEditor: TTMSFNCDataGridBeforeOpenInplaceEditorEvent;
    FOnBeforeCloseInplaceEditor: TTMSFNCDataGridBeforeCloseInplaceEditorEvent;
    FOnAfterCloseInplaceEditor: TTMSFNCDataGridAfterCloseInplaceEditorEvent;
    FOnGetInplaceEditorClass: TTMSFNCDataGridGetInplaceEditorClassEvent;
    FOnSelectCell: TTMSFNCDataGridSelectCellEvent;
    FOnSelectingCell: TTMSFNCDataGridSelectingCellEvent;
    FOnCanEditCell: TTMSFNCDataGridCanEditCellEvent;
    FOnCanAppendRow: TTMSFNCDataGridCanAppendRowEvent;
    FOnCanAppendColumn: TTMSFNCDataGridCanAppendColumnEvent;
    FOnAppendRow: TTMSFNCDataGridAppendRowEvent;
    FOnAppendColumn: TTMSFNCDataGridAppendColumnEvent;
    FOnCanDeleteRow: TTMSFNCDataGridCanDeleteRowEvent;
    FOnCanDeleteColumn: TTMSFNCDataGridCanDeleteColumnEvent;
    FOnCanInsertRow: TTMSFNCDataGridCanInsertRowEvent;
    FOnCanInsertColumn: TTMSFNCDataGridCanInsertColumnEvent;
    FOnDeleteRow: TTMSFNCDataGridDeleteRowEvent;
    FOnDeleteColumn: TTMSFNCDataGridDeleteColumnEvent;
    FOnInsertRow: TTMSFNCDataGridInsertRowEvent;
    FOnInsertColumn: TTMSFNCDataGridInsertColumnEvent;
    FOnCanSizeRow: TTMSFNCDataGridCanSizeRowEvent;
    FOnCanSizeColumn: TTMSFNCDataGridCanSizeColumnEvent;
    FOnSizeRow: TTMSFNCDataGridSizeRowEvent;
    FOnSizeColumn: TTMSFNCDataGridSizeColumnEvent;
    FOnCanDropRow: TTMSFNCDataGridCanDropRowEvent;
    FOnCanDropColumn: TTMSFNCDataGridCanDropColumnEvent;
    FOnRowDropped: TTMSFNCDataGridRowDroppedEvent;
    FOnColumnDropped: TTMSFNCDataGridColumnDroppedEvent;
    FOnRowSized: TTMSFNCDataGridRowSizedEvent;
    FOnColumnSized: TTMSFNCDataGridColumnSizedEvent;
    FOnCanDragRow: TTMSFNCDataGridCanDragRowEvent;
    FOnCanDragColumn: TTMSFNCDataGridCanDragColumnEvent;
    FOnCellRightClick: TTMSFNCDataGridCellClickEvent;
    FOnCellDblClick: TTMSFNCDataGridCellClickEvent;
    FOnCellClick: TTMSFNCDataGridCellClickEvent;
    FOnCellAnchorClick: TTMSFNCDataGridCellAnchorClickEvent;
    FOnGetInplaceEditorProperties: TTMSFNCDataGridGetInplaceEditorPropertiesEvent;
    FSelectedColumnDictionary: TTMSFNCDataGridBooleanDictionary;
    FSelectedRowDictionary: TTMSFNCDataGridBooleanDictionary;
    FSelection: TTMSFNCDataGridCellCoordRange;
    FSelectedCellDictionary: TTMSFNCDataGridSelectedCellDictionary;
    FOnCellEditSetData: TTMSFNCDataGridCellEditSetDataEvent;
    FOnCellEditValidateData: TTMSFNCDataGridCellEditValidateDataEvent;
    FOnCellEditGetData: TTMSFNCDataGridCellEditGetDataEvent;
    FOnCellButtonClick: TTMSFNCDataGridCellClickEvent;
    FOnCellRadioButtonChange: TTMSFNCDataGridCellClickEvent;
    FOnCellCheckBoxChange: TTMSFNCDataGridCellClickEvent;
    FOnAfterSortColumn: TTMSFNCDataGridAfterSortColumnEvent;
    FOnBeforeSortColumn: TTMSFNCDataGridBeforeSortColumnEvent;
    FOnAfterApplyFilter: TTMSFNCDataGridAfterApplyFilterEvent;
    FOnBeforeApplyFilter: TTMSFNCDataGridBeforeApplyFilterEvent;
    FOnAfterShowFilter: TTMSFNCDataGridAfterShowFilterEvent;
    FOnBeforeShowFilter: TTMSFNCDataGridBeforeShowFilterEvent;
    FOnNeedFilterDropDownData: TTMSFNCDataGridNeedFilterDropDownData;
    FOnGetInplaceEditorType: TTMSFNCDataGridGetInplaceEditorTypeEvent;
    FOnGetInplaceEditorRect: TTMSFNCDataGridGetInplaceEditorRectEvent;
    FFilterTime: Integer;
    FColumns: TTMSFNCDataGridColumns;
    FCellLayout: TTMSFNCDataGridCellLayout;
    FOnIsDesigntime: TTMSFNCDataGridRendererIsDesigntimeEvent;
    FOnIsLoading: TTMSFNCDataGridRendererIsLoadingEvent;
    FOnAfterShowFindDialog: TTMSFNCDataGridAfterShowFindDialogEvent;
    FOnAfterShowReplaceDialog: TTMSFNCDataGridAfterShowReplaceDialogEvent;
    FOnBeforeShowFindDialog: TTMSFNCDataGridBeforeShowFindDialogEvent;
    FOnBeforeShowReplaceDialog: TTMSFNCDataGridBeforeShowReplaceDialogEvent;
    FReplaceDialog: TTMSFNCReplaceDialog;
    FFindDialog: TTMSFNCFindDialog;
    FOnGetInplaceEditorTarget: TTMSFNCDataGridGetInplaceEditorTargetEvent;
    FFilterAppearance: TTMSFNCDataGridFilterAppearance;
    FDragAppearance: TTMSFNCDataGridDragAppearance;
    FOnAfterCutToClipboard: TTMSFNCDataGridAfterClipboardEvent;
    FOnBeforePasteFromClipboard: TTMSFNCDataGridBeforeClipboardEvent;
    FOnBeforeCopyToClipboard: TTMSFNCDataGridBeforeClipboardEvent;
    FOnAfterPasteFromClipboard: TTMSFNCDataGridAfterClipboardEvent;
    FOnBeforeCutToClipboard: TTMSFNCDataGridBeforeClipboardEvent;
    FOnAfterCopyToClipboard: TTMSFNCDataGridAfterClipboardEvent;
    FCellPopupMenu: TPopupMenu;
    FOnCellBeforeShowPopupMenu: TTMSFNCDataGridCellBeforeShowPopupMenuEvent;
    FOnBeforeDrawEmptySpace: TTMSFNCDataGridBeforeDrawEmptySpaceEvent;
    FOnAfterDrawEmptySpace: TTMSFNCDataGridAfterDrawEmptySpaceEvent;
    FOnAfterLookup: TTMSFNCDataGridAfterLookupEvent;
    FOnBeforeLookup: TTMSFNCDataGridBeforeLookupEvent;
    FLocalRectChanged: Boolean;
    FOnCellBeforeNodeExpand: TTMSFNCDataGridCellBeforeNodeExpandEvent;
    FOnCellBeforeNodeCollapse: TTMSFNCDataGridCellBeforeNodeCollapseEvent;
    FOnCellAfterNodeExpand: TTMSFNCDataGridCellAfterNodeExpandEvent;
    FOnCellAfterNodeCollapse: TTMSFNCDataGridCellAfterNodeCollapseEvent;
    FAdaptToStyle: Boolean;
    FAdaptedToStyle: Boolean;
    FPadding: TTMSFNCMargins;
    FOnGetCellHint: TTMSFNCDataGridGetCellHintEvent;
    FOnAfterCloseInplaceEditorInternal: TTMSFNCDataGridAfterCloseInplaceEditorEvent;
    FControlWrapperList: TTMSFNCDataGridCellControlWrapperList;
    FOnAfterCreateFilterEditor: TTMSFNCDataGridAfterCreateFilterEditorEvent;
    FOnCreateCustomFilterEditor: TTMSFNCDataGridCreateCustomFilterEditorEvent;
    FOnBeforeCreateFilterEditor: TTMSFNCDataGridBeforeCreateFilterEditorEvent;
    FOnGetCellLayoutHasPriority: TTMSFNCDataGridGetCellLayoutHasPriorityEvent;
    function GetAnimateTimer: TTimer;
    function GetBitmapContainer: TTMSFNCBitmapContainer;
    function GetBitmapNames(AColumn, ARow: Integer): string;
    function GetBitmaps(AColumn, ARow: Integer): TTMSFNCBitmap;
    function GetBottomRow: Integer;
    function GetCommentPopup: TTMSFNCCommentPopup;
    function GetComments(AColumn, ARow: Integer): string;
    function GetCustomHorizontalScrollBar: TTMSFNCDataGridScrollBar;
    function GetCustomVerticalScrollBar: TTMSFNCDataGridScrollBar;
    function GetDragTimer: TTimer;
    function GetFilterContainer: TTMSFNCCustomControl;
    function GetFilterEdit: TTMSFNCEdit;
    function GetFilterListBox: TTMSFNCCheckedListBox;
    function GetFilterPopup: TTMSFNCPopup;
    function GetFilterSortAscendingButton: TTMSFNCToolBarButton;
    function GetFilterSortDescendingButton: TTMSFNCToolBarButton;
    function GetFilterTimer: TTimer;
    function GetFindDialog: TTMSFNCFindDialog;
    function GetFocusedCell: TTMSFNCDataGridCellCoord;
    function GetGrids(AColumn, ARow: Integer): TTMSFNCDataGridRenderer;
    function GetHasBitmap(AColumn, ARow: Integer): Boolean;
    function GetHasButton(AColumn, ARow: Integer): Boolean;
    function GetHasCheckBox(AColumn, ARow: Integer): Boolean;
    function GetHasDataCheckBox(AColumn, ARow: Integer): Boolean;
    function GetHasGrid(AColumn, ARow: Integer): Boolean;
    function GetHasLayout(AColumn, ARow: Integer): Boolean;
    function GetHasProgressBar(AColumn, ARow: Integer): Boolean;
    function GetHasDataProgressBar(AColumn, ARow: Integer): Boolean;
    function GetHasRadioButton(AColumn, ARow: Integer): Boolean;
    function GetHasDataRadioButton(AColumn, ARow: Integer): Boolean;
    function GetHints(AColumn, ARow: Integer): string;
    function GetLayouts(AColumn, ARow: Integer): TTMSFNCDataGridCellLayout;
    function GetLeftColumn: Integer;
    function GetOptions: TTMSFNCDataGridOptions;
    function GetProgressValue(AColumn, ARow: Integer): Integer;
    function GetRadioGroupIndex(AColumn, ARow: Integer): Integer;
    function GetReadOnlys(AColumn, ARow: Integer): Boolean;
    function GetReplaceDialog: TTMSFNCReplaceDialog;
    function GetRightColumn: Integer;
    function GetScrollTimer: TTimer;
    function GetSelectedColumnDictionary: TTMSFNCDataGridBooleanDictionary;
    function GetSelectedCell(AColumn, ARow: Integer): Boolean;
    function GetSelectedCellDictionary: TTMSFNCDataGridSelectedCellDictionary;
    function GetSelectedColumn(AColumn: Integer): Boolean;
    function GetSelectedRowDictionary: TTMSFNCDataGridBooleanDictionary;
    function GetSelectedRow(ARow: Integer): Boolean;
    function GetStates(AColumn, ARow: Integer): TTMSFNCDataGridCellState;
    function GetTopRow: Integer;
    procedure SetBitmapContainer(const Value: TTMSFNCBitmapContainer);
    procedure SetCellAppearance(const Value: TTMSFNCDataGridCellAppearance);
    procedure SetColumns(const Value: TTMSFNCDataGridColumns);
    procedure SetComments(AColumn, ARow: Integer; const Value: string);
    procedure SetCustomScrollBars(const Value: Boolean);
    procedure SetFocusedCell(const Value: TTMSFNCDataGridCellCoord);
    procedure SetGlobalFont(const Value: TTMSFNCAppearanceGlobalFont);
    procedure SetHorizontalScrollBarVisible(const Value: Boolean);
    procedure SetHScrollValue(AValue: Single);
    procedure SetHints(AColumn, ARow: Integer; const Value: string);
    procedure SetLeftColumn(const Value: Integer);
    procedure SetOptions(const Value: TTMSFNCDataGridOptions);
    procedure SetRadioGroupIndex(AColumn, ARow: Integer; const Value: Integer);
    procedure SetReadOnlys(AColumn, ARow: Integer; const Value: Boolean);
    procedure SetScrollMode(const Value: TTMSFNCDataGridScrollMode);
    procedure SetScrollUpdate(const Value: TTMSFNCDataGridScrollUpdate);
    procedure SetSelectedCell(AColumn, ARow: Integer; const Value: Boolean);
    procedure SetSelectedColumn(AColumn: Integer; const Value: Boolean);
    procedure SetSelectedRow(ARow: Integer; const Value: Boolean);
    procedure SetStretchScrollBars(const Value: Boolean);
    procedure SetTopRow(const Value: Integer);
    procedure SetVerticalScrollBarVisible(const Value: Boolean);
    procedure SetVScrollValue(AValue: Single);
    procedure SetFilterAppearance(const Value: TTMSFNCDataGridFilterAppearance);
    procedure SetDragAppearance(const Value: TTMSFNCDataGridDragAppearance);
    function GetControlAligns(AColumn,
      ARow: Integer): TTMSFNCDataGridCellControlAlign;
    function GetControlPositions(AColumn,
      ARow: Integer): TTMSFNCDataGridCellControlPosition;
    function GetControlHeights(AColumn, ARow: Integer): Integer;
    function GetControlTexts(AColumn, ARow: Integer): string;
    function GetControlWidths(AColumn, ARow: Integer): Integer;
    procedure SetControlAligns(AColumn, ARow: Integer;
      const Value: TTMSFNCDataGridCellControlAlign);
    procedure SetControlPositions(AColumn, ARow: Integer;
      const Value: TTMSFNCDataGridCellControlPosition);
    procedure SetControlHeights(AColumn, ARow: Integer; const Value: Integer);
    procedure SetControlTexts(AColumn, ARow: Integer; const Value: string);
    procedure SetControlWidths(AColumn, ARow: Integer; const Value: Integer);
    function GetLookupTimer: TTimer;
    procedure SetAdaptToStyle(const Value: Boolean);
    function GetAdaptToStyle: Boolean;
    procedure SetPadding(const Value: TTMSFNCMargins);
    function GetFilterClearButton: TTMSFNCToolBarButton;
    function GetControlMargins(AColumn, ARow: Integer): TTMSFNCMargins;
    function GetFilterExitTimer: TTimer;
    function GetControlWrapperList: TTMSFNCDataGridCellControlWrapperList;
    function GetFilterEditors(AColumn,
      ARow: Integer): TTMSFNCDataGridCellControl;
    function GetFilterTypeComboBox: TComboBoxSafe;
    function GetColors(AColumn, ARow: Integer): TTMSFNCGraphicsColor;
    function GetFontColors(AColumn, ARow: Integer): TTMSFNCGraphicsColor;
    function GetFontNames(AColumn, ARow: Integer): string;
    function GetFontSizes(AColumn, ARow: Integer): Single;
    function GetFontStyles(AColumn, ARow: Integer): TFontStyles;
    function GetTextAligns(AColumn, ARow: Integer): TTMSFNCGraphicsTextAlign;
    function GetVerticalTextAligns(AColumn, ARow: Integer): TTMSFNCGraphicsTextAlign;
    procedure SetColors(AColumn, ARow: Integer; const Value: TTMSFNCGraphicsColor);
    procedure SetFontColors(AColumn, ARow: Integer; const Value: TTMSFNCGraphicsColor);
    procedure SetFontNames(AColumn, ARow: Integer; const Value: string);
    procedure SetFontSizes(AColumn, ARow: Integer; const Value: Single);
    procedure SetFontStyles(AColumn, ARow: Integer; const Value: TFontStyles);
    procedure SetTextAligns(AColumn, ARow: Integer; const Value: TTMSFNCGraphicsTextAlign);
    procedure SetVerticalTextAligns(AColumn, ARow: Integer; const Value: TTMSFNCGraphicsTextAlign);
    function GetTextAngles(AColumn, ARow: Integer): Double;
    procedure SetTextAngles(AColumn, ARow: Integer; const Value: Double);
    function GetHasFilterEditor(AColumn, ARow: Integer): Boolean;
    function GetPageCount: Integer;
    function GetPageIndex: Integer;
    procedure SetPageIndex(const Value: Integer);
  protected
    function ConfigureScrollBarsRect(ARect: TRectF): TRectF;
    function CreateFilterEditor(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellControl; virtual;
    function CheckRange(ARange: TTMSFNCDataGridCellItemTypeRange; ARow: Integer): Boolean; virtual;
    function CloseInplaceEditor(ACancel: Boolean; AFlagClose: Boolean = False; AEnterKey: Boolean = False): Boolean; virtual;
    function ColumnStretchingActive: Boolean;
    function RowStretchingActive: Boolean;
    function DoCreateCustomFilterEditor(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellControl; virtual;
    function DoIsDesigntimeGrid(Sender: TObject): Boolean;
    function DoIsDestroyingGrid(Sender: TObject): Boolean;
    function DoIsFocusedGrid(Sender: TObject): Boolean;
    function DoIsLoadingGrid(Sender: TObject): Boolean;
    function NeedsItemExtended(ACell: TTMSFNCDataGridCellCoord): Boolean; override;
    function GetBaseCellClass(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellClass;
    function GetBooleans(AColumn, ARow: Integer): Boolean; override;
    function GetCalculationRect: TRectF; virtual;
    function GetCellAppearance(AState: TTMSFNCDataGridCellState; ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellLayout;
    function GetCellState(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellState; virtual;
    function GetColumn(AColumn: Integer): TTMSFNCDataGridColumn; virtual;
    function GetColumnsClass: TTMSFNCDataGridColumnsClass; virtual;
    function GetColumnViewPortSize: Double;
    function GetContentRect: TRectF; override;
    function LocalRect: TRectF; override;
    function GetFullRect: TRectF; virtual;
    function GetHorizontalScrollBar: TScrollBar;
    function GetHorizontalScrollPosition: Double; override;
    function GetHVisible: Boolean;
    function GetHintString: string; virtual;
    function HasHint: Boolean; virtual;
    function GetInplaceEditor: TTMSFNCDataGridInplaceEditor;
    function GetInplaceEditorRect(ACell: TTMSFNCDataGridCellCoord): TRectF;
    function GetIsDestroying: Boolean; override;
    function GetIsLoading: Boolean; override;
    function GetOptionsClass: TTMSFNCDataGridDataOptionsClass; override;
    function GetPageScrollSize: Integer;
    function GetRowViewPortSize: Double;
    function GetSelectedCellCount: Integer;
    function GetSelectedColumnCount: Integer;
    function GetSelectedRowCount: Integer;
    function GetSelection: TTMSFNCDataGridCellCoordRange; override;
    function GetTotalColumnOffset: Double;
    function GetTotalRowOffset: Double;
    function GetVerticalScrollBar: TScrollBar;
    function GetVerticalScrollPosition: Double; override;
    function GetVVisible: Boolean;
    function IsBlocked: Boolean; override;
    function IsDesigntime: Boolean;
    function IsFocused: Boolean;
    function ProcessEnterKey(ACell: TTMSFNCDataGridCellCoord; AKeyHandling: TTMSFNCDataGridEnterKeyHandling): TTMSFNCDataGridCellCoord; virtual;
    function SavetoHTMLString(ARange: TTMSFNCDataGridCellCoordRange; AImageDirectory: string = ''): string;
    function OnlyLastFullVisibleRow: Boolean; override;

    procedure Animate(Sender: TObject);
    procedure ApplyFilterFromValue(AColumn: Integer; AValue: TTMSFNCDataGridCellValue; AType: TTMSFNCDataGridDataFilterType);
    procedure ApplyCellScale(ACell: TTMSFNCDataGridCell; AScale: Single);
    {$IFDEF FMXLIB}
    procedure ApplyInplaceEditorStyleLookup(Sender: TObject);
    {$ENDIF}
    procedure AddCellToDisplay(ACell: TTMSFNCDataGridCell; ADisplay: TTMSFNCDataGridDisplayList);
    procedure BuildDisplay(AList: TTMSFNCDataGridCellList; ADisplay: TTMSFNCDataGridDisplayList);
    procedure CellAppearanceChanged(Sender: TObject);
    procedure ConfigureCell(ACell: TTMSFNCDataGridCell; AMode: TTMSFNCDataGridCellConfigureMode = gccmBoth); virtual;
    procedure ConfigureLayout(ACell: TTMSFNCDataGridCellCoord; ALayout: TTMSFNCDataGridCellLayout); virtual;
    procedure DeleteColumnData(AColumn: Integer); override;
    procedure DeleteRowData(ARow: Integer); override;
    procedure DestroyEditor;
    procedure DoAfterCreateFilterEditor(ACell: TTMSFNCDataGridCellCoord; AEditor: TTMSFNCDataGridCellControl); virtual;
    procedure DoAfterApplyFilter(AColumn: Integer; ACondition: string); virtual;
    procedure DoAfterCloseInplaceEditor(ACell: TTMSFNCDataGridCellCoord; ACancelled: Boolean; AValue: TTMSFNCDataGridCellValue); virtual;
    procedure DoAfterCopyToClipboard; virtual;
    procedure DoAfterCutToClipboard; virtual;
    procedure DoAfterDrawEmptySpace(AGraphics: TTMSFNCGraphics; ARect: TRectF; AKind: TTMSFNCDataGridEmptySpaceKind); virtual;
    procedure DoAfterDrawGrid(AGraphics: TTMSFNCGraphics; ARect: TRectF); virtual;
    procedure DoAfterOpenInplaceEditor(ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; AValue: TTMSFNCDataGridCellValue); virtual;
    procedure DoAfterLookup(ALookupString: string); virtual;
    procedure DoAfterPasteFromClipboard; virtual;
    procedure DoAfterShowFilter(AColumn: Integer); virtual;
    procedure DoAfterShowFindDialog(AFindDialog: TTMSFNCFindDialog); virtual;
    procedure DoAfterShowReplaceDialog(AReplaceDialog: TTMSFNCReplaceDialog); virtual;
    procedure DoAfterSortColumn(AColumn: Integer); virtual;
    procedure DoAppendColumn(AColumn: Integer); virtual;
    procedure DoAppendRow(ARow: Integer); virtual;
    procedure DoBeforeCreateFilterEditor(ACell: TTMSFNCDataGridCellCoord; var ACanCreate: Boolean); virtual;
    procedure DoBeforeApplyFilter(AColumn: Integer; var ACondition: string; var ACanApply: Boolean); virtual;
    procedure DoBeforeCloseInplaceEditor(ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; ACancelled: Boolean; var AValue: TTMSFNCDataGridCellValue; var ACanClose: Boolean); virtual;
    procedure DoBeforeCopyToClipboard(var ACanCopy: Boolean); virtual;
    procedure DoBeforeCutToClipboard(var ACanCut: Boolean); virtual;
    procedure DoBeforeDrawEmptySpace(AGraphics: TTMSFNCGraphics; ARect: TRectF; AKind: TTMSFNCDataGridEmptySpaceKind; var ACanDraw: Boolean); virtual;
    procedure DoBeforeOpenInplaceEditor(ACell: TTMSFNCDataGridCellCoord; var AValue: TTMSFNCDataGridCellValue; var ACanOpen: Boolean); virtual;
    procedure DoBeforeLookup(var ALookupString: string; var ACanLookup: Boolean); virtual;
    procedure DoBeforePasteFromClipboard(var ACanPaste: Boolean); virtual;
    procedure DoBeforeShowFilter(AColumn: Integer; var ACanShow: Boolean); virtual;
    procedure DoBeforeShowFindDialog(AFindDialog: TTMSFNCFindDialog; var ACanShow: Boolean); virtual;
    procedure DoBeforeShowReplaceDialog(AReplaceDialog: TTMSFNCReplaceDialog; var ACanShow: Boolean); virtual;
    procedure DoBeforeSortColumn(AColumn: Integer; var ACanSort: Boolean); virtual;
    procedure DoCanAppendColumn(AColumn: Integer; var ACanAppend: Boolean); virtual;
    procedure DoCanAppendRow(ARow: Integer; var ACanAppend: Boolean); virtual;
    procedure DoCanDeleteColumn(AColumn: Integer; var ACanDelete: Boolean); virtual;
    procedure DoCanDeleteRow(ARow: Integer; var ACanDelete: Boolean); virtual;
    procedure DoCanDragColumn(AColumn: Integer; var ACanDrag: Boolean); virtual;
    procedure DoCanDragRow(ARow: Integer; var ACanDrag: Boolean); virtual;
    procedure DoCanDropColumn(AFromColumn, AToColumn: Integer; var ACanDrop: Boolean); virtual;
    procedure DoCanDropRow(AFromRow, AToRow: Integer; var ACanDrop: Boolean); virtual;
    procedure DoCanEditCell(AColumn, ARow: Integer; var ACanEdit: Boolean); virtual;
    procedure DoCanInsertColumn(AColumn: Integer; var ACanInsert: Boolean); virtual;
    procedure DoCanInsertRow(ARow: Integer; var ACanInsert: Boolean); virtual;
    procedure DoCanSizeColumn(AColumn: Integer; var ASize: Double; var ACanSize: Boolean); virtual;
    procedure DoCanSizeRow(ARow: Integer; var ASize: Double; var ACanSize: Boolean); virtual;
    procedure DoCellAnchorClick(AColumn, ARow: Integer; AAnchor: string); virtual;
    procedure DoCellButtonClick(Sender: TObject);
    procedure DoCellCheckChange(Sender: TObject);
    procedure DoCellClick(ACell: TTMSFNCDataGridCellCoord; AMouseInfo: TTMSFNCDataGridMouseInfo); virtual;
    procedure DoCellCommentIndicatorClick(Sender: TObject);
    procedure DoCellDblClick(AColumn, ARow: Integer); virtual;
    procedure DoCellEditGetData(ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; var AValue: TTMSFNCDataGridCellValue); virtual;
    procedure DoCellEditSetData(ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; var AValue: TTMSFNCDataGridCellValue); virtual;
    procedure DoCellEditValidateData(ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; var AValue: TTMSFNCDataGridCellValue; var AValid: Boolean); virtual;
    procedure DoCellFilterButtonClick(Sender: TObject);
    procedure DoCellNodeCollapse(Sender: TObject);
    procedure DoCellNodeExpand(Sender: TObject);
    procedure DoCellBeforeDraw(Sender: TObject; AGraphics: TTMSFNCGraphics; var ACanDraw: Boolean); virtual;
    procedure DoCellAfterDraw(Sender: TObject; AGraphics: TTMSFNCGraphics); virtual;
    procedure DoCellBeforeNodeExpand(ACell: TTMSFNCDataGridCellCoord; var ACanExpand: Boolean); virtual;
    procedure DoCellAfterNodeExpand(ACell: TTMSFNCDataGridCellCoord); virtual;
    procedure DoCellBeforeNodeCollapse(ACell: TTMSFNCDataGridCellCoord; var ACanCollapse: Boolean); virtual;
    procedure DoCellAfterNodeCollapse(ACell: TTMSFNCDataGridCellCoord); virtual;
    procedure DoCellBeforeShowPopupMenu(ACell: TTMSFNCDataGridCellCoord; APopupMenu: TPopupMenu; var ACanShow: Boolean); virtual;
    procedure DoCellRightClick(AColumn, ARow: Integer); virtual;
    procedure DoColumnDropped(AFromColumn, AToColumn: Integer); virtual;
    procedure DoColumnsChanged(Sender: TObject);
    procedure DoColumnSized(AColumn: Integer; ASize: Double); virtual;
    procedure DoDeleteColumn(AColumn: Integer); virtual;
    procedure DoDeleteRow(ARow: Integer); virtual;
    procedure DoDisplay;
    procedure DoLayoutChanged(Sender: TObject);
    procedure DoDisplayGrid(Sender: TObject);
    procedure DoDragTimer(Sender: TObject);
    procedure DoEditorKeyDown(Sender: TObject; var Key: Word; {$IFDEF FMXLIB}var KeyChar: WideChar; {$ENDIF}Shift: TShiftState);
    {$IFNDEF FMXLIB}
    procedure DoEditorKeyPress(Sender: TObject; var Key: Char);
    {$ENDIF}
    procedure DoEditorKeyUp(Sender: TObject; var Key: Word; {$IFDEF FMXLIB}var KeyChar: WideChar; {$ENDIF}Shift: TShiftState);
    {$IFDEF FMXLIB}
    procedure DoFilterEditApplyStyleLookup(Sender: TObject);
    {$ENDIF}
    procedure DoFilterEditChange(Sender: TObject);
    procedure DoFilterTypeChange(Sender: TObject);
    procedure DoFilterEnter(Sender: TObject);
    procedure DoFilterListBoxItemCheckChanged(Sender: TObject; AItem: TTMSFNCCheckedListBoxItem);
    procedure DoFilterSortClick(Sender: TObject);
    procedure DoFilterClearClick(Sender: TObject);
    procedure DoFilterTimer(Sender: TObject);
    procedure DoFilterExitTimer(Sender: TObject);
    procedure DoFind(Sender: TObject; AFindText: string; var AFound: Boolean); virtual;
    procedure DoFindNext(Sender: TObject; AFindText: string; var AFound: Boolean); virtual;
    procedure DoFocusGrid(Sender: TObject);
    procedure DoGetCellLayoutHasPriority(ACell: TTMSFNCDataGridCellCoord; var AHasPriority: Boolean); virtual;
    procedure DoGetCellClass(AColumn, ARow: Integer; var ACellClass: TTMSFNCDataGridCellClass); virtual;
    procedure DoGetCellFormatting(ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellValue; var AFormatting: TTMSFNCDataGridDataFormatting; var AConvertSettings: TFormatSettings; var ACanFormat: Boolean); override;
    procedure DoGetCellHint(ACell: TTMSFNCDataGridCellCoord; var AHint: string); virtual;
    procedure DoGetCellLayout(ACell: TTMSFNCDataGridCell); virtual;
    procedure DoGetCellProperties(ACell: TTMSFNCDataGridCell); virtual;
    procedure DoGetInplaceEditorClass(ACell: TTMSFNCDataGridCellCoord; var AInplaceEditorClass: TTMSFNCDataGridInplaceEditorClass); virtual;
    procedure DoGetInplaceEditorProperties(ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; AInplaceEditorType: TTMSFNCDataGridInplaceEditorType); virtual;
    procedure DoGetInplaceEditorRect(ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; var AInplaceEditorRect: TRectF); virtual;
    procedure DoGetInplaceEditorTarget(ACell: TTMSFNCDataGridCellCoord; var AInplaceEditorTarget: TTMSFNCDataGridInplaceEditorTarget); virtual;
    procedure DoGetInplaceEditorType(ACell: TTMSFNCDataGridCellCoord; var AInplaceEditorType: TTMSFNCDataGridInplaceEditorType); virtual;
    procedure DoHScroll(APosition: Single); virtual;
    procedure DoInsertColumn(AColumn: Integer); virtual;
    procedure DoInsertRow(ARow: Integer); virtual;
    procedure DoNeedFilterDropDownData(AColumn: Integer; AValues: TStrings); virtual;
    procedure DoPaintCommentPopup(Sender: TObject; AGraphics: TTMSFNCGraphics; ARect: TRectF);
    procedure DoReplace(Sender: TObject; AFindText, AReplaceText: string); virtual;
    procedure DoReplaceAll(Sender: TObject; AFindText, AReplaceText: string); virtual;
    procedure DoReplaceFind(Sender: TObject; AReplaceText: string); virtual;
    procedure DoReplaceFindNext(Sender: TObject; AFindText: string); virtual;
    procedure DoRowDropped(AFromRow, AToRow: Integer); virtual;
    procedure DoRowSized(ARow: Integer; ASize: Double); virtual;
    procedure DoScrollTimer(Sender: TObject);
    procedure DoLookupTimer(Sender: TObject);
    procedure DoSelectCell(AStartCell, AEndCell: TTMSFNCDataGridCellCoord);
    procedure DoSelectingCell(AStartCell, AEndCell: TTMSFNCDataGridCellCoord; var ACanSelect: Boolean);
    procedure DoSizeColumn(AColumn: Integer; ASize: Double); virtual;
    procedure DoSizeRow(ARow: Integer; ASize: Double); virtual;
    procedure DoUpdateCursorGrid(Sender: TObject; ACursor: TCursor);
    procedure DoVScroll(APosition: Single); virtual;
    procedure DrawCell(AGraphics: TTMSFNCGraphics; ACache: TTMSFNCDataGridCell);
    procedure DrawDisplay(AGraphics: TTMSFNCGraphics; ADisplay: TTMSFNCDataGridDisplayList);
    procedure DrawDragging(AGraphics: TTMSFNCGraphics); virtual;
    procedure DrawEmptySpaces(AGraphics: TTMSFNCGraphics); virtual;
    procedure DrawScrollBars(AGraphics: TTMSFNCGraphics);
    procedure HCustomScrollChanged(Sender: TObject; {%H-}AValue: Double);
    procedure HorizontalScrollPositionChanged; virtual;
    procedure HScrollChanged(Sender: TObject);
    procedure InternalAutoSizeColumn(AColumn: Integer; const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5; const AGraphics: TTMSFNCGraphics = nil);
    procedure InternalAutoSizeRow(ARow: integer; const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5; const AGraphics: TTMSFNCGraphics = nil);
    procedure InternalClear; override;
    procedure InternalMarkCells(AValue, ATag: string; ACaseSensitive: Boolean; AFromColumn, AFromRow, AToColumn, AToRow: Integer; AFullCell: Boolean = False);
    procedure InternalSaveToHTML(AFileName: string; AEncoding: TEncoding); virtual;
    procedure InternalSaveToHTMLStream(AStream: TStream; AEncoding: TEncoding; AImageDirectory: string = ''); virtual;
    procedure InternalSaveToImage(AFileName: string); virtual;
    procedure InternalSaveToImageStream(AStream: TStream); virtual;
    procedure InternalUnmarkCells(ATag: string; AFromColumn, AFromRow, AToColumn, AToRow: Integer);
    procedure MoveColumnData(AFromColumn, AToColumn: Integer); override;
    procedure MoveRowData(AFromRow, AToRow: Integer); override;
    procedure OptionsChanged(Sender: TObject);
    procedure PaddingChanged(Sender: TObject);
    procedure OptionsChangedRebuild(Sender: TObject);
    procedure ProcessTab(var AKeyInfo: TTMSFNCDataGridKeyInfo; Mode: TTMSFNCDataGridTabKeyDirection; Handling: TTMSFNCDataGridTabKeyHandling; DirectEdit: Boolean; CellStart: TTMSFNCDataGridEditCellStart = gcsNone);
    procedure ResetScrollBarCapture;
    procedure SetBooleans(AColumn, ARow: Integer; const Value: Boolean); override;
    procedure SetClipRect(ARect: TRectF);
    procedure SetFocusCell(ACol, ARow: Integer);
    procedure SetFonts(ASetType: TTMSFNCAppearanceGlobalFontType);
    procedure SetLayout(ACell: TTMSFNCDataGridCell); virtual;
    procedure SetProperties(ACell: TTMSFNCDataGridCell); virtual;
    procedure SetStartCell(AColumn, ARow: Integer);
    procedure SetStopCell(AColumn, ARow: Integer);
    procedure StopAnimationTimer;
    procedure SwapColumnData(AFromColumn, AToColumn: Integer); override;
    procedure SwapRowData(AFromRow, AToRow: Integer); override;
    procedure SynchronizeColumns(ACollectionUpdate: Boolean = False); override;
    procedure UpdateAutoSizing;
    procedure ResetColumnWidths;
    procedure ResetRowHeights;
    procedure UpdateCursor(ACursor: TCursor);
    procedure UpdateFilterList(AColumn: Integer);
    procedure UpdateInplaceEditorPosition;
    procedure RemoveCellControlFromCache(AColumn, ARow: Integer); override;
    procedure UpdateScrollBars(AUpdate: Boolean = True; ACalculate: Boolean = True);
    procedure VCustomScrollChanged(Sender: TObject; {%H-}AValue: Double);
    procedure VerticalScrollPositionChanged; virtual;
    procedure VScrollChanged(Sender: TObject);
    property ControlWrapperList: TTMSFNCDataGridCellControlWrapperList read GetControlWrapperList;
    property AnimateTimer: TTimer read GetAnimateTimer;
    property LookupTimer: TTimer read GetLookupTimer;
    property FilterEditors[AColumn, ARow: Integer]: TTMSFNCDataGridCellControl read GetFilterEditors;
    property HasFilterEditor[AColumn, ARow: Integer]: Boolean read GetHasFilterEditor;
    property BlockScrollingUpdate: Boolean read FBlockScrollingUpdate write FBlockScrollingUpdate;
    property DragTimer: TTimer read GetDragTimer;
    property FilterTimer: TTimer read GetFilterTimer;
    property FilterExitTimer: TTimer read GetFilterExitTimer;
    property OnAfterCloseInplaceEditorInternal: TTMSFNCDataGridAfterCloseInplaceEditorEvent read FOnAfterCloseInplaceEditorInternal write FOnAfterCloseInplaceEditorInternal;
    property ScrollTimer: TTimer read GetScrollTimer;
    property SelectedColumnDictionary: TTMSFNCDataGridBooleanDictionary read GetSelectedColumnDictionary;
    property SelectedCellDictionary: TTMSFNCDataGridSelectedCellDictionary read GetSelectedCellDictionary;
    property SelectedRowDictionary: TTMSFNCDataGridBooleanDictionary read GetSelectedRowDictionary;
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create(AHost: TObject = nil); reintroduce; virtual;
    destructor Destroy; override;

    function AddGrid(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridRenderer; overload;
    function AddGrid(AColumn, ARow: Integer): TTMSFNCDataGridRenderer; overload;
    function CanRegisterRuntimeClasses: Boolean; override;
    function CancelEditing: Boolean;
    function ColumnByName(AName: string): TTMSFNCDataGridColumn;
    function ColumnByHeader(AHeader: string): TTMSFNCDataGridColumn;
    function ColumnIndexByName(AName: string): Integer;
    function ColumnIndexByHeader(AHeader: string): Integer;
    function GetCellObjectForCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCell;
    function GetHMax: Single;
    function GetHScrollValue: Single;
    function GetHViewPortSize: Single;
    function GetParentHost: TTMSFNCDataGridCellParent;
    function GetVMax: Single;
    function GetPageIndexForRow(ARow: Integer): Integer;
    function GetVScrollValue: Single;
    function GetVViewPortSize: Single;
    function HasCustomScrollBars: Boolean;
    function HasValidHost: Boolean;
    function HilightText(ACaseSensitive: Boolean; AValue, AText: string): string;
    function IsCellDisplayList(ADisplay: TTMSFNCDataGridDisplayList): Boolean;
    function IsCellReadOnly(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellSelected(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsEditing: Boolean;
    function IsFixedCellSelected(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function Lookup(AValue: string; AAllRows: Boolean = False; AAutoGoto: Boolean = False): TTMSFNCDataGridCellCoord;
    function LookupInColumn(AColumn: Integer; AValue: string; AAllRows: Boolean = False; AAutoGoto: Boolean = False): TTMSFNCDataGridCellCoord;
    function LookupInColumnFromRow(AColumn, ARow: Integer; AValue: string; AAllRows: Boolean = False; AAutoGoto: Boolean = False): TTMSFNCDataGridCellCoord;
    function MarkText(ACaseSensitive: Boolean; AValue, AText: string): string;
    function NextSelectableColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function NextSelectableRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function PreviousSelectableColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function PreviousSelectableRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function NextVisibleColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function NextVisibleRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function PreviousVisibleColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function PreviousVisibleRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
    function SetGrid(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridRenderer; overload;
    function SetGrid(AColumn, ARow: Integer): TTMSFNCDataGridRenderer; overload;
    function StopEditing: Boolean;
    function UnhilightText(ACaseSensitive: Boolean; AValue: string): string;
    function UnmarkText(AValue: string): string;
    function XYToCell(X, Y: Double): TTMSFNCDataGridCellCoord;
    function XYToRow(X, Y: Double): Integer;
    function XYToColumn(X, Y: Double): Integer;
    function XYToRenderer(X, Y: Double): TTMSFNCDataGridRenderer;
    procedure AddBitmap(ACell: TTMSFNCDataGridCellCoord; ABitmap: TTMSFNCBitmap); overload;
    procedure AddBitmap(ACell: TTMSFNCDataGridCellCoord; AName: string); overload;
    procedure AddBitmap(AColumn, ARow: Integer; ABitmap: TTMSFNCBitmap); overload;
    procedure AddBitmap(AColumn, ARow: Integer; AName: string); overload;
    procedure AddBitmapColumn(AColumn: Integer; ABitmap: TTMSFNCBitmap); overload;
    procedure AddBitmapColumn(AColumn: Integer; AName: string); overload;
    procedure AddButton(ACell: TTMSFNCDataGridCellCoord; AText: string = ''); overload;
    procedure AddButton(AColumn, ARow: Integer; AText: string = ''); overload;
    procedure AddButtonColumn(AColumn: Integer; AText: string = '');
    procedure AddCheckBox(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False); overload;
    procedure AddCheckBox(AColumn, ARow: Integer; AValue: Boolean = False); overload;
    procedure AddCheckBoxColumn(AColumn: Integer; AValue: Boolean = False);
    procedure AddDataCheckBox(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure AddDataCheckBox(AColumn, ARow: Integer); overload;
    procedure AddDataCheckBoxColumn(AColumn: Integer);
    procedure AddProgressBar(ACell: TTMSFNCDataGridCellCoord; AValue: Integer = 0); overload;
    procedure AddProgressBar(AColumn, ARow: Integer; AValue: Integer = 0); overload;
    procedure AddProgressBarColumn(AColumn: Integer; AValue: Integer = 0);
    procedure AddDataProgressBar(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure AddDataProgressBar(AColumn, ARow: Integer); overload;
    procedure AddDataProgressBarColumn(AColumn: Integer);
    procedure AddRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure AddRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure AddRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
    procedure AddDataRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure AddDataRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure AddDataRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
    procedure AfterExport; override;
    procedure ApplyStyle; virtual;
    procedure ApplyExportScale(AScale: Single); virtual;
    procedure ResetExportScale; virtual;
    procedure AutoSizeColumn(AColumn: Integer; const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
    procedure AutoSizeColumns(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
    procedure AutoSizeGrid(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APaddingColumn: Integer = 5; const APaddingRow: Integer = 5);
    procedure AutoSizeRow(ARow: Integer; const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
    procedure AutoSizeRows(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
    procedure BeforeExport; override;
    procedure Calculate;
    procedure ChangeDPIScale(M, D: Integer);
    procedure ClearSelection;
    procedure ClearFocus;
    procedure ClearColumns;
    procedure CopyToClipboard(ASelectedCells: Boolean = True);
    procedure CutToClipboard(ASelectedCells: Boolean = True);
    procedure Display;
    procedure Draw(AGraphics: TTMSFNCGraphics); virtual;
    procedure EditCell(ACell: TTMSFNCDataGridCellCoord; ACellStart: TTMSFNCDataGridEditCellStart = gcsNone; AInsertKey: Boolean = False; AValue: string = ''; AKey: Word = 0); virtual;
    procedure &Export(AGraphics: TTMSFNCGraphics); overload; virtual;
    procedure &Export(AGraphics: TTMSFNCGraphics; ARect: TRectF; ASelection: TTMSFNCDataGridCellCoordRange;
      ARepeatFixedColumns: Boolean = False; ARepeatFixedRows: Boolean = False; AFitToPage: Boolean = True; ASkipSaveBounds: Boolean = False;
        AOnNewPage: TTMSFNCDataGridRendererExportNewPageCallback = nil; AOnGetPageIndex: TTMSFNCDataGridRendererExportGetPageIndexCallback = nil); overload; virtual;
    procedure ExportNotification(AState: TTMSFNCDataGridExportState; ARow: Integer); override;
    procedure Focus;
    procedure GoToCell(ACell: TTMSFNCDataGridCellCoord; ASelect: Boolean = True); override;
    procedure HandleEnter;
    procedure HandleExit;
    procedure HandleKey(var AKeyInfo: TTMSFNCDataGridKeyInfo);
    procedure HandleMouse(var AMouseInfo: TTMSFNCDataGridMouseInfo);
    procedure HilightCells(AHilightText: string; AOptions: TTMSFNCDataGridHilightOptions);
    procedure MarkCells(AHilightText: string; AOptions: TTMSFNCDataGridMarkOptions); overload;
    procedure FirstPage;
    procedure LastPage;
    procedure NextPage;
    procedure GoToPage(APageIndex: Integer);
    procedure PreviousPage;
    procedure Navigate(ACell: TTMSFNCDataGridCellCoord; AForceScroll: Boolean = False; AIgnoreColumn: Boolean = False; AIgnoreRow: Boolean = False);
    procedure PasteFromClipboard;
    procedure RegisterRuntimeClasses; override;
    procedure RemoveBitmap(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveBitmap(AColumn, ARow: Integer); overload;
    procedure RemoveButton(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveButton(AColumn, ARow: Integer); overload;
    procedure RemoveCheckBox(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveCheckBox(AColumn, ARow: Integer); overload;
    procedure RemoveDataCheckBox(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveDataCheckBox(AColumn, ARow: Integer); overload;
    procedure RemoveGrid(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveGrid(AColumn, ARow: Integer); overload;
    procedure RemoveFilterEditor(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveFilterEditor(AColumn, ARow: Integer); overload;
    procedure RemoveLayout(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveLayout(AColumn, ARow: Integer); overload;
    procedure RemoveProgressBar(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveProgressBar(AColumn, ARow: Integer); overload;
    procedure RemoveDataProgressBar(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveDataProgressBar(AColumn, ARow: Integer); overload;
    procedure RemoveRadioButton(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveRadioButton(AColumn, ARow: Integer); overload;
    procedure RemoveDataRadioButton(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure RemoveDataRadioButton(AColumn, ARow: Integer); overload;
    procedure ResetToDefaultStyle; virtual;
    procedure RestoreScrollPosition;
    procedure SaveScrollPosition;
    procedure SaveToHTMLData(AFileName: string); overload; virtual;
    procedure SaveToHTMLData(AFileName: string; AEncoding: TEncoding); overload; virtual;
    procedure SaveToHTMLStreamData(AStream: TStream); overload; virtual;
    procedure SaveToHTMLStreamData(AStream: TStream; AEncoding: TEncoding); overload; virtual;
    procedure SaveToImageData(AFileName: string); overload; virtual;
    procedure SaveToImageStreamData(AStream: TStream); overload; virtual;
    procedure Scroll(AHorizontalPos, AVerticalPos: Double);
    procedure SelectAllCells;
    procedure SelectCell(ACell: TTMSFNCDataGridCellCoord; AShift: TShiftState = []; AMouseDragging: Boolean = False); virtual;
    procedure SelectColumns(AFromColumn, AToColumn: Integer);
    procedure SelectRows(AFromRow, AToRow: Integer);
    procedure SetBitmap(ACell: TTMSFNCDataGridCellCoord; ABitmap: TTMSFNCBitmap); overload;
    procedure SetBitmap(ACell: TTMSFNCDataGridCellCoord; AName: string); overload;
    procedure SetBitmap(AColumn, ARow: Integer; ABitmap: TTMSFNCBitmap); overload;
    procedure SetBitmap(AColumn, ARow: Integer; AName: string); overload;
    procedure SetBitmapColumn(AColumn: Integer; ABitmap: TTMSFNCBitmap); overload;
    procedure SetBitmapColumn(AColumn: Integer; AName: string); overload;
    procedure SetButton(ACell: TTMSFNCDataGridCellCoord; AText: string = ''); overload;
    procedure SetButton(AColumn, ARow: Integer; AText: string = ''); overload;
    procedure SetButtonColumn(AColumn: Integer; AText: string = '');
    procedure SetCheckBox(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False); overload;
    procedure SetCheckBox(AColumn, ARow: Integer; AValue: Boolean = False); overload;
    procedure SetCheckBoxColumn(AColumn: Integer; AValue: Boolean = False);
    procedure SetDataCheckBox(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure SetDataCheckBox(AColumn, ARow: Integer); overload;
    procedure SetDataCheckBoxColumn(AColumn: Integer);
    procedure SetProgressBar(ACell: TTMSFNCDataGridCellCoord; AValue: Integer = 0); overload;
    procedure SetProgressBar(AColumn, ARow: Integer; AValue: Integer = 0); overload;
    procedure SetProgressBarColumn(AColumn: Integer; AValue: Integer = 0);
    procedure SetDataProgressBar(ACell: TTMSFNCDataGridCellCoord); overload;
    procedure SetDataProgressBar(AColumn, ARow: Integer); overload;
    procedure SetDataProgressBarColumn(AColumn: Integer);
    procedure SetRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure SetRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure SetRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
    procedure SetDataRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure SetDataRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0); overload;
    procedure SetDataRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
    procedure SetLocalRect(ARect: TRectF); override;
    procedure ShowFindDialog;
    procedure ShowReplaceDialog;
    procedure UnhilightCells(AOptions: TTMSFNCDataGridHilightOptions);
    procedure UnmarkCells(AOptions: TTMSFNCDataGridMarkOptions); overload;
    procedure Update; override;
    procedure UpdateBounds; virtual;
    property BitmapContainer: TTMSFNCBitmapContainer read GetBitmapContainer write SetBitmapContainer;
    property BitmapNames[AColumn, ARow: Integer]: string read GetBitmapNames;
    property Bitmaps[AColumn, ARow: Integer]: TTMSFNCBitmap read GetBitmaps;
    property BottomRow: Integer read GetBottomRow;
    property CellPopupMenu: TPopupMenu read FCellPopupMenu write FCellPopupMenu;
    property CommentPopup: TTMSFNCCommentPopup read GetCommentPopup;
    property Colors[AColumn, ARow: Integer]: TTMSFNCGraphicsColor read GetColors write SetColors;
    property Comments[AColumn, ARow: Integer]: string read GetComments write SetComments;
    property ControlWidths[AColumn, ARow: Integer]: Integer read GetControlWidths write SetControlWidths;
    property ControlHeights[AColumn, ARow: Integer]: Integer read GetControlHeights write SetControlHeights;
    property ControlAligns[AColumn, ARow: Integer]: TTMSFNCDataGridCellControlAlign read GetControlAligns write SetControlAligns;
    property ControlMargins[AColumn, ARow: Integer]: TTMSFNCMargins read GetControlMargins;
    property ControlPositions[AColumn, ARow: Integer]: TTMSFNCDataGridCellControlPosition read GetControlPositions write SetControlPositions;
    property ControlTexts[AColumn, ARow: Integer]: string read GetControlTexts write SetControlTexts;
    property CustomHorizontalScrollBar: TTMSFNCDataGridScrollBar read GetCustomHorizontalScrollBar;
    property CustomVerticalScrollBar: TTMSFNCDataGridScrollBar read GetCustomVerticalScrollBar;
    property EditingCell: TTMSFNCDataGridCellCoord read FEditingCell;
    property FilterContainer: TTMSFNCCustomControl read GetFilterContainer;
    property FilterEdit: TTMSFNCEdit read GetFilterEdit;
    property FilterListBox: TTMSFNCCheckedListBox read GetFilterListBox;
    property FilterTypeComboBox: TComboBoxSafe read GetFilterTypeComboBox;
    property FilterPopup: TTMSFNCPopup read GetFilterPopup;
    property FilterSortAscendingButton: TTMSFNCToolBarButton read GetFilterSortAscendingButton;
    property FilterSortDescendingButton: TTMSFNCToolBarButton read GetFilterSortDescendingButton;
    property FilterClearButton: TTMSFNCToolBarButton read GetFilterClearButton;
    property FindDialog: TTMSFNCFindDialog read GetFindDialog;
    property FocusedCell: TTMSFNCDataGridCellCoord read GetFocusedCell write SetFocusedCell;
    property TextAligns[AColumn, ARow: Integer]: TTMSFNCGraphicsTextAlign read GetTextAligns write SetTextAligns;
    property VerticalTextAligns[AColumn, ARow: Integer]: TTMSFNCGraphicsTextAlign read GetVerticalTextAligns write SetVerticalTextAligns;
    property FontSizes[AColumn, ARow: Integer]: Single read GetFontSizes write SetFontSizes;
    property FontStyles[AColumn, ARow: Integer]: TFontStyles read GetFontStyles write SetFontStyles;
    property FontNames[AColumn, ARow: Integer]: string read GetFontNames write SetFontNames;
    property FontColors[AColumn, ARow: Integer]: TTMSFNCGraphicsColor read GetFontColors write SetFontColors;
    property TextAngles[AColumn, ARow: Integer]: Double read GetTextAngles write SetTextAngles;
    property Grids[AColumn, ARow: Integer]: TTMSFNCDataGridRenderer read GetGrids;
    property HasBitmap[AColumn, ARow: Integer]: Boolean read GetHasBitmap;
    property HasButton[AColumn, ARow: Integer]: Boolean read GetHasButton;
    property HasCheckBox[AColumn, ARow: Integer]: Boolean read GetHasCheckBox;
    property HasDataCheckBox[AColumn, ARow: Integer]: Boolean read GetHasDataCheckBox;
    property HasGrid[AColumn, ARow: Integer]: Boolean read GetHasGrid;
    property HasLayout[AColumn, ARow: Integer]: Boolean read GetHasLayout;
    property HasProgressBar[AColumn, ARow: Integer]: Boolean read GetHasProgressBar;
    property HasDataProgressBar[AColumn, ARow: Integer]: Boolean read GetHasDataProgressBar;
    property HasRadioButton[AColumn, ARow: Integer]: Boolean read GetHasRadioButton;
    property HasDataRadioButton[AColumn, ARow: Integer]: Boolean read GetHasDataRadioButton;
    property ProgressValue[AColumn, ARow: Integer]: Integer read GetProgressValue;
    property HorizontalScrollBar: TScrollBar read GetHorizontalScrollBar;
    property Host: TObject read FHost;
    property Hints[AColumn, ARow: Integer]: string read GetHints write SetHints;
    property InplaceEditor: TTMSFNCDataGridInplaceEditor read GetInplaceEditor;
    property IsMouseDown: Boolean read FIsMouseDown write FIsMouseDown;
    property Layouts[AColumn, ARow: Integer]: TTMSFNCDataGridCellLayout read GetLayouts;
    property LeftColumn: Integer read GetLeftColumn write SetLeftColumn;
    property OnAfterCreateFilterEditor: TTMSFNCDataGridAfterCreateFilterEditorEvent read FOnAfterCreateFilterEditor write FOnAfterCreateFilterEditor;
    property OnAfterApplyFilter: TTMSFNCDataGridAfterApplyFilterEvent read FOnAfterApplyFilter write FOnAfterApplyFilter;
    property OnAfterCloseInplaceEditor: TTMSFNCDataGridAfterCloseInplaceEditorEvent read FOnAfterCloseInplaceEditor write FOnAfterCloseInplaceEditor;
    property OnAfterCopyToClipboard: TTMSFNCDataGridAfterClipboardEvent read FOnAfterCopyToClipboard write FOnAfterCopyToClipboard;
    property OnAfterCutToClipboard: TTMSFNCDataGridAfterClipboardEvent read FOnAfterCutToClipboard write FOnAfterCutToClipboard;
    property OnAfterDrawCell: TTMSFNCDataGridAfterDrawCellEvent read FOnAfterDrawCell write FOnAfterDrawCell;
    property OnAfterDrawEmptySpace: TTMSFNCDataGridAfterDrawEmptySpaceEvent read FOnAfterDrawEmptySpace write FOnAfterDrawEmptySpace;
    property OnAfterDrawGrid: TTMSFNCDataGridAfterDrawGridEvent read FOnAfterDrawGrid write FOnAfterDrawGrid;
    property OnAfterOpenInplaceEditor: TTMSFNCDataGridAfterOpenInplaceEditorEvent read FOnAfterOpenInplaceEditor write FOnAfterOpenInplaceEditor;
    property OnAfterLookup: TTMSFNCDataGridAfterLookupEvent read FOnAfterLookup write FOnAfterLookup;
    property OnAfterPasteFromClipboard: TTMSFNCDataGridAfterClipboardEvent read FOnAfterPasteFromClipboard write FOnAfterPasteFromClipboard;
    property OnAfterShowFilter: TTMSFNCDataGridAfterShowFilterEvent read FOnAfterShowFilter write FOnAfterShowFilter;
    property OnAfterShowFindDialog: TTMSFNCDataGridAfterShowFindDialogEvent read FOnAfterShowFindDialog write FOnAfterShowFindDialog;
    property OnAfterShowReplaceDialog: TTMSFNCDataGridAfterShowReplaceDialogEvent read FOnAfterShowReplaceDialog write FOnAfterShowReplaceDialog;
    property OnAfterSortColumn: TTMSFNCDataGridAfterSortColumnEvent read FOnAfterSortColumn write FOnAfterSortColumn;
    property OnAppendColumn: TTMSFNCDataGridAppendColumnEvent read FOnAppendColumn write FOnAppendColumn;
    property OnAppendRow: TTMSFNCDataGridAppendRowEvent read FOnAppendRow write FOnAppendRow;
    property OnBeforeCreateFilterEditor: TTMSFNCDataGridBeforeCreateFilterEditorEvent read FOnBeforeCreateFilterEditor write FOnBeforeCreateFilterEditor;
    property OnBeforeApplyFilter: TTMSFNCDataGridBeforeApplyFilterEvent read FOnBeforeApplyFilter write FOnBeforeApplyFilter;
    property OnBeforeCloseInplaceEditor: TTMSFNCDataGridBeforeCloseInplaceEditorEvent read FOnBeforeCloseInplaceEditor write FOnBeforeCloseInplaceEditor;
    property OnBeforeCopyToClipboard: TTMSFNCDataGridBeforeClipboardEvent read FOnBeforeCopyToClipboard write FOnBeforeCopyToClipboard;
    property OnBeforeCutToClipboard: TTMSFNCDataGridBeforeClipboardEvent read FOnBeforeCutToClipboard write FOnBeforeCutToClipboard;
    property OnBeforeDrawCell: TTMSFNCDataGridBeforeDrawCellEvent read FOnBeforeDrawCell write FOnBeforeDrawCell;
    property OnBeforeDrawEmptySpace: TTMSFNCDataGridBeforeDrawEmptySpaceEvent read FOnBeforeDrawEmptySpace write FOnBeforeDrawEmptySpace;
    property OnBeforeOpenInplaceEditor: TTMSFNCDataGridBeforeOpenInplaceEditorEvent read FOnBeforeOpenInplaceEditor write FOnBeforeOpenInplaceEditor;
    property OnBeforeLookup: TTMSFNCDataGridBeforeLookupEvent read FOnBeforeLookup write FOnBeforeLookup;
    property OnBeforePasteFromClipboard: TTMSFNCDataGridBeforeClipboardEvent read FOnBeforePasteFromClipboard write FOnBeforePasteFromClipboard;
    property OnBeforeShowFilter: TTMSFNCDataGridBeforeShowFilterEvent read FOnBeforeShowFilter write FOnBeforeShowFilter;
    property OnBeforeShowFindDialog: TTMSFNCDataGridBeforeShowFindDialogEvent read FOnBeforeShowFindDialog write FOnBeforeShowFindDialog;
    property OnBeforeShowReplaceDialog: TTMSFNCDataGridBeforeShowReplaceDialogEvent read FOnBeforeShowReplaceDialog write FOnBeforeShowReplaceDialog;
    property OnBeforeSortColumn: TTMSFNCDataGridBeforeSortColumnEvent read FOnBeforeSortColumn write FOnBeforeSortColumn;
    property OnCanAppendColumn: TTMSFNCDataGridCanAppendColumnEvent read FOnCanAppendColumn write FOnCanAppendColumn;
    property OnCanAppendRow: TTMSFNCDataGridCanAppendRowEvent read FOnCanAppendRow write FOnCanAppendRow;
    property OnCanDeleteColumn: TTMSFNCDataGridCanDeleteColumnEvent read FOnCanDeleteColumn write FOnCanDeleteColumn;
    property OnCanDeleteRow: TTMSFNCDataGridCanDeleteRowEvent read FOnCanDeleteRow write FOnCanDeleteRow;
    property OnCanDragColumn: TTMSFNCDataGridCanDragColumnEvent read FOnCanDragColumn write FOnCanDragColumn;
    property OnCanDragRow: TTMSFNCDataGridCanDragRowEvent read FOnCanDragRow write FOnCanDragRow;
    property OnCanDropColumn: TTMSFNCDataGridCanDropColumnEvent read FOnCanDropColumn write FOnCanDropColumn;
    property OnCanDropRow: TTMSFNCDataGridCanDropRowEvent read FOnCanDropRow write FOnCanDropRow;
    property OnCanEditCell: TTMSFNCDataGridCanEditCellEvent read FOnCanEditCell write FOnCanEditCell;
    property OnCanInsertColumn: TTMSFNCDataGridCanInsertColumnEvent read FOnCanInsertColumn write FOnCanInsertColumn;
    property OnCanInsertRow: TTMSFNCDataGridCanInsertRowEvent read FOnCanInsertRow write FOnCanInsertRow;
    property OnCanSizeColumn: TTMSFNCDataGridCanSizeColumnEvent read FOnCanSizeColumn write FOnCanSizeColumn;
    property OnCanSizeRow: TTMSFNCDataGridCanSizeRowEvent read FOnCanSizeRow write FOnCanSizeRow;
    property OnCellAnchorClick: TTMSFNCDataGridCellAnchorClickEvent read FOnCellAnchorClick write FOnCellAnchorClick;
    property OnCellButtonClick: TTMSFNCDataGridCellClickEvent read FOnCellButtonClick write FOnCellButtonClick;
    property OnCellCheckBoxChange: TTMSFNCDataGridCellClickEvent read FOnCellCheckBoxChange write FOnCellCheckBoxChange;
    property OnCellClick: TTMSFNCDataGridCellClickEvent read FOnCellClick write FOnCellClick;
    property OnCellDblClick: TTMSFNCDataGridCellClickEvent read FOnCellDblClick write FOnCellDblClick;
    property OnCellEditGetData: TTMSFNCDataGridCellEditGetDataEvent read FOnCellEditGetData write FOnCellEditGetData;
    property OnCellEditSetData: TTMSFNCDataGridCellEditSetDataEvent read FOnCellEditSetData write FOnCellEditSetData;
    property OnCellEditValidateData: TTMSFNCDataGridCellEditValidateDataEvent read FOnCellEditValidateData write FOnCellEditValidateData;
    property OnCellBeforeShowPopupMenu: TTMSFNCDataGridCellBeforeShowPopupMenuEvent read FOnCellBeforeShowPopupMenu write FOnCellBeforeShowPopupMenu;
    property OnCellBeforeNodeExpand: TTMSFNCDataGridCellBeforeNodeExpandEvent read FOnCellBeforeNodeExpand write FOnCellBeforeNodeExpand;
    property OnCellBeforeNodeCollapse: TTMSFNCDataGridCellBeforeNodeCollapseEvent read FOnCellBeforeNodeCollapse write FOnCellBeforeNodeCollapse;
    property OnCellAfterNodeExpand: TTMSFNCDataGridCellAfterNodeExpandEvent read FOnCellAfterNodeExpand write FOnCellAfterNodeExpand;
    property OnCellAfterNodeCollapse: TTMSFNCDataGridCellAfterNodeCollapseEvent read FOnCellAfterNodeCollapse write FOnCellAfterNodeCollapse;
    property OnCellRadioButtonChange: TTMSFNCDataGridCellClickEvent read FOnCellRadioButtonChange write FOnCellRadioButtonChange;
    property OnCellRightClick: TTMSFNCDataGridCellClickEvent read FOnCellRightClick write FOnCellRightClick;
    property OnColumnDropped: TTMSFNCDataGridColumnDroppedEvent read FOnColumnDropped write FOnColumnDropped;
    property OnColumnSized: TTMSFNCDataGridColumnSizedEvent read FOnColumnSized write FOnColumnSized;
    property OnCreateCustomFilterEditor: TTMSFNCDataGridCreateCustomFilterEditorEvent read FOnCreateCustomFilterEditor write FOnCreateCustomFilterEditor;
    property OnDeleteColumn: TTMSFNCDataGridDeleteColumnEvent read FOnDeleteColumn write FOnDeleteColumn;
    property OnDeleteRow: TTMSFNCDataGridDeleteRowEvent read FOnDeleteRow write FOnDeleteRow;
    property OnDisplay: TNotifyEvent read FOnDisplay write FOnDisplay;
    property OnFocus: TNotifyEvent read FOnFocus write FOnFocus;
    property OnGetCellClass: TTMSFNCDataGridGetCellClassEvent read FOnGetCellClass write FOnGetCellClass;
    property OnGetCellLayout: TTMSFNCDataGridGetCellLayoutEvent read FOnGetCellLayout write FOnGetCellLayout;
    property OnGetCellLayoutHasPriority: TTMSFNCDataGridGetCellLayoutHasPriorityEvent read FOnGetCellLayoutHasPriority write FOnGetCellLayoutHasPriority;
    property OnGetCellHint: TTMSFNCDataGridGetCellHintEvent read FOnGetCellHint write FOnGetCellHint;
    property OnGetCellProperties: TTMSFNCDataGridGetCellPropertiesEvent read FOnGetCellProperties write FOnGetCellProperties;
    property OnGetInplaceEditorClass: TTMSFNCDataGridGetInplaceEditorClassEvent read FOnGetInplaceEditorClass write FOnGetInplaceEditorClass;
    property OnGetInplaceEditorProperties: TTMSFNCDataGridGetInplaceEditorPropertiesEvent read FOnGetInplaceEditorProperties write FOnGetInplaceEditorProperties;
    property OnGetInplaceEditorRect: TTMSFNCDataGridGetInplaceEditorRectEvent read FOnGetInplaceEditorRect write FOnGetInplaceEditorRect;
    property OnGetInplaceEditorTarget: TTMSFNCDataGridGetInplaceEditorTargetEvent read FOnGetInplaceEditorTarget write FOnGetInplaceEditorTarget;
    property OnGetInplaceEditorType: TTMSFNCDataGridGetInplaceEditorTypeEvent read FOnGetInplaceEditorType write FOnGetInplaceEditorType;
    property OnHScroll: TTMSFNCDataGridScrollEvent read FOnHScroll write FOnHScroll;
    property OnInsertColumn: TTMSFNCDataGridInsertColumnEvent read FOnInsertColumn write FOnInsertColumn;
    property OnInsertRow: TTMSFNCDataGridInsertRowEvent read FOnInsertRow write FOnInsertRow;
    property OnIsDesigntime: TTMSFNCDataGridRendererIsDesigntimeEvent read FOnIsDesigntime write FOnIsDesigntime;
    property OnIsDestroying: TTMSFNCDataGridRendererIsDestroyingEvent read FOnIsDestroying write FOnIsDestroying;
    property OnIsFocused: TTMSFNCDataGridRendererIsFocusedEvent read FOnIsFocused write FOnIsFocused;
    property OnIsLoading: TTMSFNCDataGridRendererIsLoadingEvent read FOnIsLoading write FOnIsLoading;
    property OnNeedFilterDropDownData: TTMSFNCDataGridNeedFilterDropDownData read FOnNeedFilterDropDownData write FOnNeedFilterDropDownData;
    property OnRowDropped: TTMSFNCDataGridRowDroppedEvent read FOnRowDropped write FOnRowDropped;
    property OnRowSized: TTMSFNCDataGridRowSizedEvent read FOnRowSized write FOnRowSized;
    property OnSelectCell: TTMSFNCDataGridSelectCellEvent read FOnSelectCell write FOnSelectCell;
    property OnSelectingCell: TTMSFNCDataGridSelectingCellEvent read FOnSelectingCell write FOnSelectingCell;
    property OnSizeColumn: TTMSFNCDataGridSizeColumnEvent read FOnSizeColumn write FOnSizeColumn;
    property OnSizeRow: TTMSFNCDataGridSizeRowEvent read FOnSizeRow write FOnSizeRow;
    property OnUpdateCursor: TTMSFNCDataGridRendererUpdateCursorEvent read FOnUpdateCursor write FOnUpdateCursor;
    property OnVScroll: TTMSFNCDataGridScrollEvent read FOnVScroll write FOnVScroll;
    property PageCount: Integer read GetPageCount;
    property PageIndex: Integer read GetPageIndex write SetPageIndex;
    property RadioGroupIndex[AColumn, ARow: Integer]: Integer read GetRadioGroupIndex write SetRadioGroupIndex;
    property ReadOnlys[AColumn, ARow: Integer]: Boolean read GetReadOnlys write SetReadOnlys;
    property ReplaceDialog: TTMSFNCReplaceDialog read GetReplaceDialog;
    property RightColumn: Integer read GetRightColumn;
    property SelectedCellCount: Integer read GetSelectedCellCount;
    property SelectedCells[AColumn, ARow: Integer]: Boolean read GetSelectedCell write SetSelectedCell;
    property SelectedColumnCount: Integer read GetSelectedColumnCount;
    property SelectedColumns[AColumn: Integer]: Boolean read GetSelectedColumn write SetSelectedColumn;
    property SelectedRowCount: Integer read GetSelectedRowCount;
    property SelectedRows[ARow: Integer]: Boolean read GetSelectedRow write SetSelectedRow;
    property Selection: TTMSFNCDataGridCellCoordRange read FSelection write FSelection;
    property States[AColumn, ARow: Integer]: TTMSFNCDataGridCellState read GetStates;
    property TopRow: Integer read GetTopRow write SetTopRow;
    property VerticalScrollBar: TScrollBar read GetVerticalScrollBar;
  published
    property AdaptToStyle: Boolean read GetAdaptToStyle write SetAdaptToStyle default False;
    property CellAppearance: TTMSFNCDataGridCellAppearance read FCellAppearance write SetCellAppearance;
    property Columns: TTMSFNCDataGridColumns read FColumns write SetColumns;
    property CustomScrollBars: Boolean read FCustomScrollBars write SetCustomScrollBars default False;
    property DragAppearance: TTMSFNCDataGridDragAppearance read FDragAppearance write SetDragAppearance;
    property FilterAppearance: TTMSFNCDataGridFilterAppearance read FFilterAppearance write SetFilterAppearance;
    property GlobalFont: TTMSFNCAppearanceGlobalFont read FGlobalFont write SetGlobalFont;
    property HorizontalScrollBarVisible: Boolean read FHorizontalScrollBarVisible write SetHorizontalScrollBarVisible default True;
    property Options: TTMSFNCDataGridOptions read GetOptions write SetOptions;
    property Padding: TTMSFNCMargins read FPadding write SetPadding;
    property ScrollMode: TTMSFNCDataGridScrollMode read FScrollMode write SetScrollMode default gsmPixelScrolling;
    property ScrollUpdate: TTMSFNCDataGridScrollUpdate read FScrollUpdate write SetScrollUpdate default gsuContinuous;
    property StretchScrollBars: Boolean read FStretchScrollBars write SetStretchScrollBars default False;
    property Stroke: TTMSFNCGraphicsStroke read FStroke;
    property VerticalScrollBarVisible: Boolean read FVerticalScrollBarVisible write SetVerticalScrollBarVisible default True;
  end;

  TTMSFNCDataGridRendererClass = class of TTMSFNCDataGridRenderer;

  TTMSFNCDataGridRendererCell = class(TTMSFNCDataGridCell)
  private
    FRenderRect: TRectF;
    FExportRenderer: TTMSFNCDataGridRenderer;
    function GetRenderer: TTMSFNCDataGridRenderer;
  protected
    function GetRendererRect: TRectF; virtual;
    function GetDrawElements(AIsEditing: Boolean): TTMSFNCDataGridCellDrawElements; override;
    function GetRequiredSize(AGraphics: TTMSFNCGraphics = nil): TSizeF; override;
    procedure ApplyScale(AScale: Single); override;
  public
    constructor Create(ARect: TRectF; AColumn, ARow: Integer; AData: TTMSFNCDataGridCellItem; AKind: TTMSFNCDataGridCellKind; ARoot: TObject = nil); override;
    destructor Destroy; override;
    procedure BeforeExport; override;
    procedure AfterExport; override;
    procedure DrawContent(AGraphics: TTMSFNCGraphics); override;
    property Renderer: TTMSFNCDataGridRenderer read GetRenderer;
  end;

  function DefaultHilightOptions: TTMSFNCDataGridHilightOptions;
  function DefaultMarkOptions: TTMSFNCDataGridMarkOptions;
  procedure SetEditorValue(AControl: TTMSFNCDataGridCellControl; AValue: TTMSFNCDataGridCellValue);
  function GetEditorValue(AControl: TTMSFNCDataGridCellControl): TTMSFNCDataGridCellValue;

implementation

uses
  WEBLib.TMSFNCHTMLEditor, WEBLib.TMSFNCColorSelector, WEBLib.TMSFNCDataGridBase, WEBLib.TMSFNCCustomPicker, WEBLib.TMSFNCHTMLEngine, WEBLib.TMSFNCDataGridUtil,
  WEBLib.TMSFNCCustomComponent, WEBLib.Forms, Math, WEBLib.TMSFNCUtils, WEBLib.TMSFNCEditorsTools
  {$IFDEF FMXLIB}
  ,FMX.Styles.Objects
  {$ENDIF}
  ,Rtti
  ;

{$R TMSFNCDataGrid.res}

{$IFDEF WEBLIB}
const
  TTMSFNCDATAGRIDFILTERACTIVE=
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiI+PGRlZnM+PHN0eWxlPi5jbHMtMXtmaWxsOm5vbmU7fS5jbHMtMntmaWxsOiM0N'+
  'jgyQjQ7fS5jbHMtM3tmaWxsOnVybCgjTmV3X1BhdHRlcm5fU3dhdGNoXzIpO30uY2xzLTR7ZmlsbDojM2EzYTM4O308L3N0eWxlPjxwYXR0ZXJuIGlkPSJOZXdfUGF0dGVybl9Td2F0Y2hfMiIgZGF0YS1uYW1lPSJOZXcgUGF0dGVybiBTd2F0Y2ggMiIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4IiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9u'+
  'VXNlIiB2aWV3Qm94PSIwIDAgNjggNjgiPjxyZWN0IGNsYXNzPSJjbHMtMSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PHJlY3QgY2xhc3M9ImNscy0yIiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48L3BhdHRlcm4+PC9kZWZzPjx0aXRsZT5GaWx0ZXI8L3RpdGxlPjxnIGlkPSJJY29uIj48cG9seWdvbiBjbGFzcz0iY2xzLTMiIHBvaW5'+
  '0cz0iMS41IDYgMTIuNSAxNSAxMi41IDMwLjUgMTguNSAzMC41IDE4LjUgMTUgMjkuNSA2IDI5LjUgMS41IDEuNSAxLjUgMS41IDYiLz48cGF0aCBjbGFzcz0iY2xzLTQiIGQ9Ik0xOSwzMUgxMlYxNS4yNGwtMTEtOVYxSDMwVjYuMjRsLTExLDlabS02LTFoNVYxNC43NmwxMS05VjJIMlY1Ljc2bDExLDlaIi8+PC9nPjwvc3ZnPg';
  TTMSFNCDATAGRIDFILTER =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiI+PGRlZnM+PHN0eWxlPi5jbHMtMXtmaWxsOm5vbmU7fS5jbHMtMntmaWxsOiNmY'+
  'WZhZmE7fS5jbHMtM3tmaWxsOnVybCgjTmV3X1BhdHRlcm5fU3dhdGNoXzIpO30uY2xzLTR7ZmlsbDojM2EzYTM4O308L3N0eWxlPjxwYXR0ZXJuIGlkPSJOZXdfUGF0dGVybl9Td2F0Y2hfMiIgZGF0YS1uYW1lPSJOZXcgUGF0dGVybiBTd2F0Y2ggMiIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4IiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9u'+
  'VXNlIiB2aWV3Qm94PSIwIDAgNjggNjgiPjxyZWN0IGNsYXNzPSJjbHMtMSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PHJlY3QgY2xhc3M9ImNscy0yIiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48L3BhdHRlcm4+PC9kZWZzPjx0aXRsZT5GaWx0ZXI8L3RpdGxlPjxnIGlkPSJJY29uIj48cG9seWdvbiBjbGFzcz0iY2xzLTMiIHBvaW5'+
  '0cz0iMS41IDYgMTIuNSAxNSAxMi41IDMwLjUgMTguNSAzMC41IDE4LjUgMTUgMjkuNSA2IDI5LjUgMS41IDEuNSAxLjUgMS41IDYiLz48cGF0aCBjbGFzcz0iY2xzLTQiIGQ9Ik0xOSwzMUgxMlYxNS4yNGwtMTEtOVYxSDMwVjYuMjRsLTExLDlabS02LTFoNVYxNC43NmwxMS05VjJIMlY1Ljc2bDExLDlaIi8+PC9nPjwvc3ZnPg';
  TTMSFNCDATAGRIDFILTERCLEAR =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiI+PGRlZnM+PHN0eWxlPi5jbHMtMXtmaWxsOm5vbmU7fS5jbHMtMntmaWxsOiNhN'+
  'zRhYjA7fS5jbHMtM3tmaWxsOiNkMzk0ZDY7fS5jbHMtNHtmaWxsOiNmYWZhZmE7fS5jbHMtNXtmaWxsOnVybCgjTmV3X1BhdHRlcm5fU3dhdGNoXzgpO30uY2xzLTZ7ZmlsbDp1cmwoI05ld19QYXR0ZXJuX1N3YXRjaF83KTt9PC9zdHlsZT48cGF0dGVybiBpZD0iTmV3X1BhdHRlcm5fU3dhdGNoXzgiIGRhdGEtbmFtZT0iTmV3IFBhdHRl'+
  'cm4gU3dhdGNoIDgiIHdpZHRoPSI2OCIgaGVpZ2h0PSI2OCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgdmlld0JveD0iMCAwIDY4IDY4Ij48cmVjdCBjbGFzcz0iY2xzLTEiIHdpZHRoPSI2OCIgaGVpZ2h0PSI2OCIvPjxyZWN0IGNsYXNzPSJjbHMtMyIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PC9wYXR0ZXJuPjxwYXR0ZXJ'+
  'uIGlkPSJOZXdfUGF0dGVybl9Td2F0Y2hfNyIgZGF0YS1uYW1lPSJOZXcgUGF0dGVybiBTd2F0Y2ggNyIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4IiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIiB2aWV3Qm94PSIwIDAgNjggNjgiPjxyZWN0IGNsYXNzPSJjbHMtMSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PHJlY3QgY2xhc3M9Im'+
  'Nscy0yIiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48L3BhdHRlcm4+PC9kZWZzPjx0aXRsZT5DbGVhcjwvdGl0bGU+PGcgaWQ9Ikljb24iPjxyZWN0IGNsYXNzPSJjbHMtNCIgeD0iMS44NiIgeT0iMTAuMzQiIHdpZHRoPSIyOC4yOCIgaGVpZ2h0PSIxMS4zMSIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTYuNjMgMTYpIHJvdGF0ZSgtNDUpI'+
  'i8+PHBvbHlnb24gY2xhc3M9ImNscy01IiBwb2ludHM9IjIuNzEgMjIgMTAgMjkuMjkgMTUuNzkgMjMuNSA4LjUgMTYuMjEgMi43MSAyMiIvPjxwYXRoIGNsYXNzPSJjbHMtNiIgZD0iTTEwLjcxLDMwbDIwLTIwTDIyLDEuMjksMS4yOSwyMiwxMCwzMC43MVYzMUgzMVYzMFpNMjIsMi43MSwyOS4yOSwxMCwxNi41LDIyLjc5LDkuMjEsMTUu'+
  'NVpNMi43MSwyMiw4LjUsMTYuMjFsNy4yOSw3LjI5TDEwLDI5LjI5WiIvPjwvZz48L3N2Zz4';
  TTMSFNCDATAGRIDCOLLAPSE =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDMyIA0KMzIiPjxkZWZzPjxzdHlsZT4uY2xzLTF7ZmlsbDojMDAwMDA7fTwvc3R5bGU+PC9kZWZzPjx0aXRsZT5QbHVzPC90aXRsZT4gPGcgDQppZD0iSWNvbiI+PHJlY'+
  '3QgY2xhc3M9ImNscy0xIiB4PSI3IiB5PSIxNSIgd2lkdGg9IjE4IiBoZWlnaHQ9IjEiLz48cmVjdCBjbGFzcz0iY2xzLTEiIHg9IjEiIA0KeT0iMSIgd2lkdGg9IjEiIGhlaWdodD0iMzAiLz48cmVjdCBjbGFzcz0iY2xzLTEiIHg9IjEiIHk9IjEiIHdpZHRoPSIzMCIgDQpoZWlnaHQ9IjEiLz48cmVjdCBjbGFzcz0iY2xzLTEiIHg9IjMw'+
  'IiB5PSIxIiB3aWR0aD0iMSIgaGVpZ2h0PSIzMCIvPjxyZWN0IA0KY2xhc3M9ImNscy0xIiB4PSIxIiB5PSIzMCIgd2lkdGg9IjMwIiBoZWlnaHQ9IjEiLz48L2c+PC9zdmc+';
  TTMSFNCDATAGRIDEXPAND =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDMyIA0KMzIiPjxkZWZzPjxzdHlsZT4uY2xzLTF7ZmlsbDojMDAwMDA7fTwvc3R5bGU+PC9kZWZzPjx0aXRsZT5QbHVzPC90aXRsZT4gPGcgDQppZD0iSWNvbiI+PHJlY'+
  '3QgY2xhc3M9ImNscy0xIiB4PSI3IiB5PSIxNSIgd2lkdGg9IjE4IiBoZWlnaHQ9IjEiLz48cmVjdCANCmNsYXNzPSJjbHMtMSIgeD0iMTUiIHk9IjciIHdpZHRoPSIxIiBoZWlnaHQ9IjE4Ii8+PHJlY3QgY2xhc3M9ImNscy0xIiB4PSIxIiANCnk9IjEiIHdpZHRoPSIxIiBoZWlnaHQ9IjMwIi8+PHJlY3QgY2xhc3M9ImNscy0xIiB4PSIx'+
  'IiB5PSIxIiB3aWR0aD0iMzAiIA0KaGVpZ2h0PSIxIi8+PHJlY3QgY2xhc3M9ImNscy0xIiB4PSIzMCIgeT0iMSIgd2lkdGg9IjEiIGhlaWdodD0iMzAiLz48cmVjdCANCmNsYXNzPSJjbHMtMSIgeD0iMSIgeT0iMzAiIHdpZHRoPSIzMCIgaGVpZ2h0PSIxIi8+PC9nPjwvc3ZnPg';
  TTMSFNCDATAGRIDSORTASC =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiI+PGRlZnM+PHN0eWxlPi5jbHMtMXtmaWxsOm5vbmU7fS5jbHMtMntmaWxsOiNhN'+
  'zRhYjA7fS5jbHMtM3tmaWxsOnVybCgjTmV3X1BhdHRlcm5fU3dhdGNoXzcpO30uY2xzLTR7ZmlsbDojMWU4YmNkO30uY2xzLTV7ZmlsbDojM2EzYTM4O308L3N0eWxlPjxwYXR0ZXJuIGlkPSJOZXdfUGF0dGVybl9Td2F0Y2hfNyIgZGF0YS1uYW1lPSJOZXcgUGF0dGVybiBTd2F0Y2ggNyIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4IiBwYXR0'+
  'ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIiB2aWV3Qm94PSIwIDAgNjggNjgiPjxyZWN0IGNsYXNzPSJjbHMtMSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PHJlY3QgY2xhc3M9ImNscy0yIiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48L3BhdHRlcm4+PC9kZWZzPjx0aXRsZT5Tb3J0LUFaPC90aXRsZT48ZyBpZD0iSWNvbiI+PHBhdGg'+
  'gY2xhc3M9ImNscy0zIiBkPSJNMiwyOS42NGw3LTEwVjIwSDNWMThoOXYxLjQ0TDUsMjkuMzZWMjloN3YySDJaIi8+PHBhdGggY2xhc3M9ImNscy00IiBkPSJNNS43LDExLDQuNTIsMTVIMS45M0w2LjMzLDFIOS41NEwxNCwxNWgtMi43bC0xLjI0LTRaTTkuNjQsOS4xMSw4LjU2LDUuNjZjLS4yNy0uODUtLjQ5LTEuOC0uNjktMi42MWgwYy'+
  '0uMi44MS0uNCwxLjc4LS42NSwyLjYxTDYuMTEsOS4xMVoiLz48cG9seWdvbiBjbGFzcz0iY2xzLTUiIHBvaW50cz0iMjMgMjUuMTIgMjMgNiAyMiA2IDIyIDI1LjEyIDE2LjQ0IDE5LjU2IDE1LjU2IDIwLjQ0IDIyLjUgMjcuMzggMjkuNDQgMjAuNDQgMjguNTYgMTkuNTYgMjMgMjUuMTIiLz48L2c+PC9zdmc+';
  TTMSFNCDATAGRIDSORTDESC =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiI+PGRlZnM+PHN0eWxlPi5jbHMtMXtmaWxsOm5vbmU7fS5jbHMtMntmaWxsOiNhN'+
  'zRhYjA7fS5jbHMtM3tmaWxsOnVybCgjTmV3X1BhdHRlcm5fU3dhdGNoXzcpO30uY2xzLTR7ZmlsbDojMWU4YmNkO30uY2xzLTV7ZmlsbDojM2EzYTM4O308L3N0eWxlPjxwYXR0ZXJuIGlkPSJOZXdfUGF0dGVybl9Td2F0Y2hfNyIgZGF0YS1uYW1lPSJOZXcgUGF0dGVybiBTd2F0Y2ggNyIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4IiBwYXR0'+
  'ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIiB2aWV3Qm94PSIwIDAgNjggNjgiPjxyZWN0IGNsYXNzPSJjbHMtMSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PHJlY3QgY2xhc3M9ImNscy0yIiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48L3BhdHRlcm4+PC9kZWZzPjx0aXRsZT5Tb3J0LVpBPC90aXRsZT48ZyBpZD0iSWNvbiI+PHBhdGg'+
  'gY2xhc3M9ImNscy0zIiBkPSJNMiwxMi42NGw3LTEwVjNIM1YxaDlWMi40NEw1LDEyLjM2VjEyaDd2MkgyWiIvPjxwYXRoIGNsYXNzPSJjbHMtNCIgZD0iTTUuNywyNyw0LjUyLDMxSDEuOTNsNC40LTE0SDkuNTRMMTQsMzFoLTIuN2wtMS4yNC00Wm0zLjk0LTEuOTNMOC41NiwyMS42NmMtLjI3LS44NS0uNDktMS44LS42OS0yLjYxaDBjLS'+
  '4yLjgxLS40LDEuNzgtLjY1LDIuNjFMNi4xMSwyNS4xMVoiLz48cG9seWdvbiBjbGFzcz0iY2xzLTUiIHBvaW50cz0iMjMgMjUuMTIgMjMgNiAyMiA2IDIyIDI1LjEyIDE2LjQ0IDE5LjU2IDE1LjU2IDIwLjQ0IDIyLjUgMjcuMzggMjkuNDQgMjAuNDQgMjguNTYgMTkuNTYgMjMgMjUuMTIiLz48L2c+PC9zdmc+';
  TTMSFNCDATAGRIDFILTERTYPE =
  'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiI+PGRlZnM+PHN0eWxlPi5jbHMtMXtmaWxsOm5vbmU7fS5jbHMtMntmaWxsOiM3O'+
  'Tc3NzQ7fS5jbHMtM3tmaWxsOiNmYWZhZmE7fS5jbHMtNHtmaWxsOiMzYTNhMzg7fS5jbHMtNXtmaWxsOnVybCgjTmV3X1BhdHRlcm5fU3dhdGNoXzQpO308L3N0eWxlPjxwYXR0ZXJuIGlkPSJOZXdfUGF0dGVybl9Td2F0Y2hfNCIgZGF0YS1uYW1lPSJOZXcgUGF0dGVybiBTd2F0Y2ggNCIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4IiBwYXR0'+
  'ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIiB2aWV3Qm94PSIwIDAgNjggNjgiPjxyZWN0IGNsYXNzPSJjbHMtMSIgd2lkdGg9IjY4IiBoZWlnaHQ9IjY4Ii8+PHJlY3QgY2xhc3M9ImNscy0yIiB3aWR0aD0iNjgiIGhlaWdodD0iNjgiLz48L3BhdHRlcm4+PC9kZWZzPjx0aXRsZT5UZXh0LUZpbHRlcjwvdGl0bGU+PGcgaWQ9Ik1hc2siPjx'+
  'wb2x5Z29uIGNsYXNzPSJjbHMtMyIgcG9pbnRzPSIyNS41IDkgMTkgMi41IDE5IDkgMjUuNSA5Ii8+PHBhdGggY2xhc3M9ImNscy0zIiBkPSJNMTgsMTBWMkg2VjMwSDIxVjI2LjE4TDE5Ljg0LDI1SDhWMjRIMTguODVsLTItMkg4VjIxaDhWMTlIOFYxOGg4VjE2SDhWMTVIMjR2MWgyVjEwWk04LDloOHYxSDhabTAsNFYxMkgyNHYxWiIvPj'+
  'xwb2x5Z29uIGNsYXNzPSJjbHMtNCIgcG9pbnRzPSI1IDEgNSAzMSA2IDMxIDIxIDMxIDIxIDMwIDYgMzAgNiAyIDE4IDIgMTguNSAyIDE5IDIgMTkgMi41IDI1LjUgOSAyNiA5IDI2IDkuNSAyNiAxMCAyNiAxNiAyNyAxNiAyNyA5LjcgMjcgOSAxOSAxIDUgMSIvPjxwb2x5Z29uIGNsYXNzPSJjbHMtNCIgcG9pbnRzPSIyNiAxMCAyNiA5L'+
  'jUgMjYgOSAyNS41IDkgMTkgOSAxOSAyLjUgMTkgMiAxOC41IDIgMTggMiAxOCAxMCAyNiAxMCIvPjxyZWN0IGNsYXNzPSJjbHMtMyIgeD0iOCIgeT0iOSIgd2lkdGg9IjgiIGhlaWdodD0iMSIvPjxyZWN0IGNsYXNzPSJjbHMtNSIgeD0iOCIgeT0iOSIgd2lkdGg9IjgiIGhlaWdodD0iMSIvPjxyZWN0IGNsYXNzPSJjbHMtMyIgeD0iOCIg'+
  'eT0iMTIiIHdpZHRoPSIxNiIgaGVpZ2h0PSIxIi8+PHJlY3QgY2xhc3M9ImNscy01IiB4PSI4IiB5PSIxMiIgd2lkdGg9IjE2IiBoZWlnaHQ9IjEiLz48cG9seWdvbiBjbGFzcz0iY2xzLTMiIHBvaW50cz0iOCAxNSA4IDE2IDE2IDE2IDI0IDE2IDI0IDE1IDggMTUiLz48cG9seWdvbiBjbGFzcz0iY2xzLTUiIHBvaW50cz0iOCAxNSA4IDE'+
  '2IDE2IDE2IDI0IDE2IDI0IDE1IDggMTUiLz48cmVjdCBjbGFzcz0iY2xzLTMiIHg9IjgiIHk9IjE4IiB3aWR0aD0iOCIgaGVpZ2h0PSIxIi8+PHJlY3QgY2xhc3M9ImNscy01IiB4PSI4IiB5PSIxOCIgd2lkdGg9IjgiIGhlaWdodD0iMSIvPjxwb2x5Z29uIGNsYXNzPSJjbHMtMyIgcG9pbnRzPSI4IDIyIDE2Ljg3IDIyIDE2IDIxLjEyID'+
  'E2IDIxIDggMjEgOCAyMiIvPjxwb2x5Z29uIGNsYXNzPSJjbHMtNSIgcG9pbnRzPSI4IDIyIDE2Ljg3IDIyIDE2IDIxLjEyIDE2IDIxIDggMjEgOCAyMiIvPjxwb2x5Z29uIGNsYXNzPSJjbHMtMyIgcG9pbnRzPSI4IDI1IDE5Ljg0IDI1IDE4Ljg1IDI0IDggMjQgOCAyNSIvPjxwb2x5Z29uIGNsYXNzPSJjbHMtNSIgcG9pbnRzPSI4IDI1I'+
  'DE5Ljg0IDI1IDE4Ljg1IDI0IDggMjQgOCAyNSIvPjwvZz48ZyBpZD0iT3ZlcmxheSI+PHBvbHlnb24gY2xhc3M9ImNscy0zIiBwb2ludHM9IjMxLjUgMTcuNSAxNy41IDE3LjUgMTcuNSAyMC41IDIyLjUgMjUuNTYgMjIuNSAzMS41IDI2LjUgMzEuNSAyNi41IDI1LjU2IDMxLjUgMjAuNSAzMS41IDE3LjUiLz48cGF0aCBjbGFzcz0iY2xz'+
  'LTQiIGQ9Ik0yNywzMkgyMlYyNS43N2wtNS01LjA2VjE3SDMydjMuNzFsLTUsNS4wNlptLTQtMWgzVjI1LjM2bDUtNS4wN1YxOEgxOHYyLjI5bDUsNS4wN1oiLz48L2c+PC9zdmc+';  
{$ENDIF}

type
  TTMSFNCDataGridBaseOpen = class(TTMSFNCDataGridBase);
  TTMSFNCDataGridCellOpen = class(TTMSFNCDataGridCell);
  {$IFDEF FMXLIB}
  TTMSFNCDataGridCellControlOpen = class(TTMSFNCDataGridCellControl);
  {$ENDIF}
  TTMSFNCDataGridNodeCellOpen = class(TTMSFNCDataGridNodeCell);
  TTMSFNCDataGridCellItemExtendedOpen = class(TTMSFNCDataGridCellItemExtended);
  TTMSFNCDataGridInplaceEditorOpen = class(TTMSFNCDataGridInplaceEditor);
  TFNCControlOpen = class(TTMSFNCCustomControl);
  TCustomEditOpen = class(TCustomEdit);
  TCustomMemoOpen = class(TCustomMemo);
  TCustomComboBoxOpen = class(TCustomComboBox);

const
  SCROLLINGDELAY = 0;
  {$IFDEF MSWINDOWS}
  SWIPECOUNT = 300;
  {$ENDIF}
  {$IFDEF MACOS}
  {$IFDEF IOS}
  SWIPECOUNT = 300;
  {$ELSE}
  SWIPECOUNT = 300;
  {$ENDIF}
  {$ENDIF}
  {$IFDEF ANDROID}
  SWIPECOUNT = 300;
  {$ENDIF}
  {$IFDEF FMXLIB}
  {$IFDEF LINUX}
  SWIPECOUNT = 300;
  {$ENDIF}
  {$ENDIF}
  {$IFDEF WEBLIB}
  SWIPECOUNT = 300;
  {$ENDIF}
  FILTERDELAY = 40;

function GetTickCountX: DWORD;
var
  h, m, s, ms: Word;
begin
  DecodeTime(Now, h, m, s, ms);
  Result := ms + s * 1000 + m * 60 * 1000 + h * 60 * 60 * 1000;
end;

function AnimateDouble(var Start, Stop: Double; Delta, Margin: Double): Boolean;
begin
  Result := true;
  if (Start > Stop - Margin) and (Start < Stop + Margin) then
  begin
    Start := Stop;
    Result := false;
  end
  else
  begin
    Delta := Max(Margin, Delta);
    if Start < Stop then
      Start := Start + Delta
    else
      Start := Start - Delta;
  end;
end;

function DefaultHilightOptions: TTMSFNCDataGridHilightOptions;
begin
  Result.IncludeFixed := False;
  Result.CaseSensitive := False;
  Result.FullText := False;
  Result.Mode := ghmGrid;
  Result.Column := -1;
  Result.Row := -1;
end;

function DefaultMarkOptions: TTMSFNCDataGridMarkOptions;
begin
  Result := DefaultHilightOptions;
end;

{ TTMSFNCDataGridRenderer }

{$IFDEF FMXLIB}
procedure TTMSFNCDataGridRenderer.ApplyInplaceEditorStyleLookup(Sender: TObject);
var
  obj: TFmxObject;
begin
  if (Sender = FInplaceEditor) {$IFDEF FMXLIB} and not (FInplaceEditor is TCustomComboEdit) {$ENDIF} then
  begin
    obj := FInplaceEditor.FindStyleResource('background');
    if Assigned(obj) and (obj is TActiveStyleObject) then
      TActiveStyleObject(obj).Source := nil;
  end;
end;
{$ENDIF}

procedure TTMSFNCDataGridRenderer.DoAfterOpenInplaceEditor(ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; AValue: TTMSFNCDataGridCellValue);
begin
  if Assigned(OnAfterOpenInplaceEditor) then
    OnAfterOpenInplaceEditor(Self, ACell, AInplaceEditor, AValue);
end;

procedure TTMSFNCDataGridRenderer.DoAfterPasteFromClipboard;
begin
  if Assigned(OnAfterPasteFromClipboard) then
    OnAfterPasteFromClipboard(Self);
end;

procedure TTMSFNCDataGridRenderer.DoAppendColumn(AColumn: Integer);
begin
  if Assigned(OnAppendColumn) then
    OnAppendColumn(Self, AColumn);
end;

procedure TTMSFNCDataGridRenderer.DoAppendRow(ARow: Integer);
begin
  if Assigned(OnAppendRow) then
    OnAppendRow(Self, ARow);
end;

procedure TTMSFNCDataGridRenderer.DoDeleteColumn(AColumn: Integer);
begin
  if Assigned(OnDeleteColumn) then
    OnDeleteColumn(Self, AColumn);
end;

procedure TTMSFNCDataGridRenderer.DoDeleteRow(ARow: Integer);
begin
  if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).DeleteRow(ARow);

  if Assigned(OnDeleteRow) then
    OnDeleteRow(Self, ARow);
end;

procedure TTMSFNCDataGridRenderer.DoColumnDropped(AFromColumn, AToColumn: Integer);
begin
  if Assigned(OnColumnDropped) then
    OnColumnDropped(Self, AFromColumn, AToColumn);
end;

procedure TTMSFNCDataGridRenderer.DoColumnsChanged(Sender: TObject);
begin
  SynchronizeColumns(True);

  if (UpdateCount > 0) or GetIsDestroying then
    Exit;

  RefreshData;
  Update;
end;

procedure TTMSFNCDataGridRenderer.DoRowDropped(AFromRow, AToRow: Integer);
begin
  if Assigned(OnRowDropped) then
    OnRowDropped(Self, AFromRow, AToRow);
end;

procedure TTMSFNCDataGridRenderer.DoInsertColumn(AColumn: Integer);
begin
  if Assigned(OnInsertColumn) then
    OnInsertColumn(Self, AColumn);
end;

procedure TTMSFNCDataGridRenderer.DoInsertRow(ARow: Integer);
begin
  if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).InsertRow(ARow);

  if Assigned(OnInsertRow) then
    OnInsertRow(Self, ARow);
end;

procedure TTMSFNCDataGridRenderer.DoBeforeOpenInplaceEditor(ACell: TTMSFNCDataGridCellCoord; var AValue: TTMSFNCDataGridCellValue; var ACanOpen: Boolean);
begin
  if Assigned(OnBeforeOpenInplaceEditor) then
    OnBeforeOpenInplaceEditor(Self, ACell, AValue, ACanOpen);
end;

procedure TTMSFNCDataGridRenderer.DoBeforePasteFromClipboard(
  var ACanPaste: Boolean);
begin
  if Assigned(OnBeforePasteFromClipboard) then
    OnBeforePasteFromClipboard(Self, ACanPaste);
end;

procedure TTMSFNCDataGridRenderer.DoAfterSortColumn(AColumn: Integer);
begin
  if Assigned(OnAfterSortColumn) then
    OnAfterSortColumn(Self, AColumn);
end;

procedure TTMSFNCDataGridRenderer.DoBeforeSortColumn(AColumn: Integer; var ACanSort: Boolean);
begin
  if Assigned(OnBeforeSortColumn) then
    OnBeforeSortColumn(Self, AColumn, ACanSort);
end;

procedure TTMSFNCDataGridRenderer.DoGetInplaceEditorClass(ACell: TTMSFNCDataGridCellCoord; var AInplaceEditorClass: TTMSFNCDataGridInplaceEditorClass);
begin
  if Assigned(OnGetInplaceEditorClass) then
    OnGetInplaceEditorClass(Self, ACell, AInplaceEditorClass);
end;

procedure TTMSFNCDataGridRenderer.DoGetInplaceEditorTarget(
  ACell: TTMSFNCDataGridCellCoord;
  var AInplaceEditorTarget: TTMSFNCDataGridInplaceEditorTarget);
var
  cl: TTMSFNCDataGridColumn;
begin
  cl := GetColumn(ACell.Column);
  if Assigned(cl) and (gcsEditor in cl.Settings) then
    AInplaceEditorTarget := cl.EditorTarget
  else
    AInplaceEditorTarget := getCellValue;

  if Assigned(OnGetInplaceEditorTarget) then
    OnGetInplaceEditorTarget(Self, ACell, AInplaceEditorTarget);
end;

procedure TTMSFNCDataGridRenderer.DoGetInplaceEditorType(ACell: TTMSFNCDataGridCellCoord; var AInplaceEditorType: TTMSFNCDataGridInplaceEditorType);
var
  cl: TTMSFNCDataGridColumn;
begin
  cl := GetColumn(ACell.Column);
  if Assigned(cl) and (gcsEditor in cl.Settings) then
    AInplaceEditorType := cl.Editor
  else
    AInplaceEditorType := getEdit;

  if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).GetInplaceEditorType(ACell, AInplaceEditorType);

  if Assigned(OnGetInplaceEditorType) then
    OnGetInplaceEditorType(Self, ACell, AInplaceEditorType);
end;

procedure TTMSFNCDataGridRenderer.DoGetInplaceEditorProperties(
  ACell: TTMSFNCDataGridCellCoord;
  AInplaceEditor: TTMSFNCDataGridInplaceEditor; AInplaceEditorType: TTMSFNCDataGridInplaceEditorType);
begin
  if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).GetInplaceEditorProperties(ACell, AInplaceEditor, AInplaceEditorType);

  if Assigned(OnGetInplaceEditorProperties) then
    OnGetInplaceEditorProperties(Self, ACell, AInplaceEditor, AInplaceEditorType);
end;

procedure TTMSFNCDataGridRenderer.DoGetInplaceEditorRect(
  ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; var AInplaceEditorRect: TRectF);
begin
  if Assigned(OnGetInplaceEditorRect) then
    OnGetInplaceEditorRect(Self, ACell, AInplaceEditor, AInplaceEditorRect);
end;

procedure TTMSFNCDataGridRenderer.DoCanAppendColumn(AColumn: Integer;
  var ACanAppend: Boolean);
begin
  if Assigned(OnCanAppendColumn) then
    OnCanAppendColumn(Self, AColumn, ACanAppend);
end;

procedure TTMSFNCDataGridRenderer.DoCanAppendRow(ARow: Integer;
  var ACanAppend: Boolean);
begin
  if Assigned(OnCanAppendRow) then
    OnCanAppendRow(Self, ARow, ACanAppend);
end;

procedure TTMSFNCDataGridRenderer.DoCanDeleteColumn(AColumn: Integer;
  var ACanDelete: Boolean);
begin
  if Assigned(OnCanDeleteColumn) then
    OnCanDeleteColumn(Self, AColumn, ACanDelete);
end;

procedure TTMSFNCDataGridRenderer.DoCanDeleteRow(ARow: Integer;
  var ACanDelete: Boolean);
begin
  if Assigned(OnCanDeleteRow) then
    OnCanDeleteRow(Self, ARow, ACanDelete);
end;

procedure TTMSFNCDataGridRenderer.DoCanDropColumn(AFromColumn, AToColumn: Integer;
  var ACanDrop: Boolean);
begin
  if Assigned(OnCanDropColumn) then
    OnCanDropColumn(Self, AFromColumn, AToColumn, ACanDrop);
end;

procedure TTMSFNCDataGridRenderer.DoCanDropRow(AFromRow, AToRow: Integer;
  var ACanDrop: Boolean);
begin
  if Assigned(OnCanDropRow) then
    OnCanDropRow(Self, AFromRow, AToRow, ACanDrop);
end;

procedure TTMSFNCDataGridRenderer.DoCanDragColumn(AColumn: Integer;
  var ACanDrag: Boolean);
begin
  if Assigned(OnCanDragColumn) then
    OnCanDragColumn(Self, AColumn, ACanDrag);
end;

procedure TTMSFNCDataGridRenderer.DoCanDragRow(ARow: Integer;
  var ACanDrag: Boolean);
begin
  if Assigned(OnCanDragRow) then
    OnCanDragRow(Self, ARow, ACanDrag);
end;

procedure TTMSFNCDataGridRenderer.DoCanEditCell(AColumn, ARow: Integer;
  var ACanEdit: Boolean);
begin
  ACanEdit := ACanEdit and Options.Editing.Enabled;
  ACanEdit := ACanEdit and not IsCellReadOnly(MakeCell(AColumn, ARow));

  if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).CanEditCell(MakeCell(AColumn, ARow), ACanEdit);

  if Assigned(OnCanEditCell) then
    OnCanEditCell(Self, AColumn, ARow, ACanEdit);
end;

procedure TTMSFNCDataGridRenderer.DoCanInsertColumn(AColumn: Integer;
  var ACanInsert: Boolean);
begin
  if Assigned(OnCanInsertColumn) then
    OnCanInsertColumn(Self, AColumn, ACanInsert);
end;

procedure TTMSFNCDataGridRenderer.DoCanInsertRow(ARow: Integer;
  var ACanInsert: Boolean);
begin
  if Assigned(OnCanInsertRow) then
    OnCanInsertRow(Self, ARow, ACanInsert);
end;

procedure TTMSFNCDataGridRenderer.DoCanSizeColumn(AColumn: Integer;
  var ASize: Double; var ACanSize: Boolean);
begin
  if Assigned(OnCanSizeColumn) then
    OnCanSizeColumn(Self, AColumn, ASize, ACanSize);
end;

procedure TTMSFNCDataGridRenderer.DoCanSizeRow(ARow: Integer;
  var ASize: Double; var ACanSize: Boolean);
begin
  if Assigned(OnCanSizeRow) then
    OnCanSizeRow(Self, ARow, ASize, ACanSize);
end;

procedure TTMSFNCDataGridRenderer.DoCellAfterDraw(Sender: TObject;
  AGraphics: TTMSFNCGraphics);
begin
  if Assigned(OnAfterDrawCell) and (Sender is TTMSFNCDataGridCell) then
    OnAfterDrawCell(Self, AGraphics, Sender as TTMSFNCDataGridCell);
end;

procedure TTMSFNCDataGridRenderer.DoCellAfterNodeCollapse(
  ACell: TTMSFNCDataGridCellCoord);
begin
  if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).RowAfterCollapse(ACell.Row);

  if Assigned(OnCellAfterNodeCollapse) then
    OnCellAfterNodeCollapse(Self, ACell);
end;

procedure TTMSFNCDataGridRenderer.DoCellAfterNodeExpand(
  ACell: TTMSFNCDataGridCellCoord);
begin
  if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).RowAfterExpand(ACell.Row);

  if Assigned(OnCellAfterNodeExpand) then
    OnCellAfterNodeExpand(Self, ACell);
end;

procedure TTMSFNCDataGridRenderer.DoCellAnchorClick(AColumn, ARow: Integer;
  AAnchor: string);
begin
  if Options.URL.AutoOpen then
    TTMSFNCUtils.OpenURL(AAnchor);

  if Assigned(OnCellAnchorClick) then
    OnCellAnchorClick(Self, AColumn, ARow, AAnchor);
end;

procedure TTMSFNCDataGridRenderer.DoCellButtonClick(Sender: TObject);
var
  cl: TTMSFNCDataGridCell;
begin
  if Assigned(Sender) and (Sender is TTMSFNCDataGridCell) then
  begin
    cl := (Sender as TTMSFNCDataGridCell);
    if Assigned(OnCellButtonClick) then
      OnCellButtonClick(Self, cl.Column, cl.Row);
  end;
end;

procedure TTMSFNCDataGridRenderer.DoCellCheckChange(Sender: TObject);
var
  cl: TTMSFNCDataGridCell;
  I, g: Integer;
  b: Boolean;

  procedure TriggerEvents(ACell: TTMSFNCDataGridCellCoord);
  var
    cll: TTMSFNCDataGridCell;
  begin
    cll := GetCellObjectForCell(ACell);
    if Assigned(cll) then
    begin
      if cll is TTMSFNCDataGridCheckBoxCell then
      begin
        if Assigned(OnCellCheckBoxChange) then
          OnCellCheckBoxChange(Self, cll.Column, cll.Row);
      end
      else if cll is TTMSFNCDataGridRadioButtonCell then
      begin
        if Assigned(OnCellRadioButtonChange) then
          OnCellRadioButtonChange(Self, cll.Column, cll.Row);
      end;
    end;
  end;
begin
  if Assigned(Sender) and (Sender is TTMSFNCDataGridCell) then
  begin
    cl := (Sender as TTMSFNCDataGridCell);
    if cl is TTMSFNCDataGridCheckedCell then
    begin
      IncUpdateCount;

      b := (cl as TTMSFNCDataGridCheckedCell).Checked;

      if (cl is TTMSFNCDataGridCheckBoxCell) then
      begin
        if (cl.Row <= FixedRowCount - 1) and Options.Column.FixedAutoCheckAll then
        begin
          for I := cl.Row to RowCount - FixedBottomRowCount - 1 do
          begin
            if IsCellVisible(MakeCell(cl.Column, I)) then
            begin
              if Booleans[cl.Column, I] <> b then
              begin
                Booleans[cl.Column, I] := b;
                TriggerEvents(MakeCell(cl.Column, I));
              end;
            end;
          end;
        end;
      end
      else if cl is TTMSFNCDataGridRadioButtonCell then
      begin
        g := RadioGroupIndex[cl.Column, cl.Row];
        for I := 0 to RowCount - 1 do
        begin
          if (I <> cl.Row) and IsCellVisible(MakeCell(cl.Column, I)) then
          begin
            if RadioGroupIndex[cl.Column, I] = g then
            begin
              if Booleans[cl.Column, I] <> False then
              begin
                Booleans[cl.Column, I] := False;
                TriggerEvents(MakeCell(cl.Column, I));
              end;
            end;
          end;
        end;
      end;

      if b <> Booleans[cl.Column, cl.Row] then
      begin
        Booleans[cl.Column, cl.Row] := b;
        TriggerEvents(MakeCell(cl.Column, cl.Row));
      end;

      DecUpdateCount;
      Display;
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.DoCellClick(ACell: TTMSFNCDataGridCellCoord; AMouseInfo: TTMSFNCDataGridMouseInfo);
var
  c: Boolean;
  sd, nsd: TTMSFNCDataGridSortDirection;
  cl: TTMSFNCDataGridCell;
begin
  if IsCellFixed(ACell) and (ACell.Row = Options.Sorting.Row) and Options.Sorting.Enabled then
  begin
    c := True;
    DoBeforeSortColumn(ACell.Column, c);

    if c then
    begin
      cl := GetCellObjectForCell(ACell);

      if Assigned(cl) then
      begin
        if not (ssShift in AMouseInfo.Shift) then
          SortIndexDictionary.Clear;

        case cl.SortIndicator of
          gsiAscending: sd := gsdDescending;
          else sd := gsdAscending;
        end;

        nsd := gsdNone;
        if not SortIndexDictionary.TryGetValue(ACell.Column, nsd) then
          SortIndexDictionary.AddOrSetValue(ACell.Column, sd)
        else
        begin
          case nsd of
            gsdAscending: SortIndexDictionary[ACell.Column] := gsdDescending;
            gsdDescending: SortIndexDictionary[ACell.Column] := gsdAscending;
          end;          
        end;

        if IsGrouped then
          GroupSortIndexed
        else
          SortIndexed;
      end;

      DoAfterSortColumn(ACell.Column);
    end;
  end;

  if Assigned(OnCellClick) then
    OnCellClick(Self, ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.DoCellDblClick(AColumn, ARow: Integer);
begin
  if Assigned(OnCellDblClick) then
    OnCellDblClick(Self, AColumn, ARow);
end;

procedure TTMSFNCDataGridRenderer.DoCellEditGetData(
  ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor;
  var AValue: TTMSFNCDataGridCellValue);
begin
  if Assigned(OnCellEditGetData) then
    OnCellEditGetData(Self, ACell, AInplaceEditor, AValue);
end;

procedure TTMSFNCDataGridRenderer.DoCellEditSetData(
  ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor;
  var AValue: TTMSFNCDataGridCellValue);
begin
  if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).EditSetData(ACell, AInplaceEditor, AValue);

  if Assigned(OnCellEditSetData) then
    OnCellEditSetData(Self, ACell, AInplaceEditor, AValue);
end;

procedure TTMSFNCDataGridRenderer.DoCellEditValidateData(
  ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor;
  var AValue: TTMSFNCDataGridCellValue; var AValid: Boolean);
begin
  if Assigned(OnCellEditValidateData) then
    OnCellEditValidateData(Self, ACell, AInplaceEditor, AValue, AValid);
end;

procedure TTMSFNCDataGridRenderer.DoCellRightClick(AColumn, ARow: Integer);
begin
  if Assigned(OnCellRightClick) then
    OnCellRightClick(Self, AColumn, ARow);
end;

procedure TTMSFNCDataGridRenderer.DoBeforeApplyFilter(AColumn: Integer;
  var ACondition: string; var ACanApply: Boolean);
begin
  if Assigned(OnBeforeApplyFilter) then
    OnBeforeApplyFilter(Self, AColumn, ACondition, ACanApply);
end;

procedure TTMSFNCDataGridRenderer.DoBeforeShowFilter(AColumn: Integer; var ACanShow: Boolean);
begin
  if Assigned(OnBeforeShowFilter) then
    OnBeforeShowFilter(Self, AColumn, ACanShow);
end;

procedure TTMSFNCDataGridRenderer.DoBeforeShowFindDialog(
  AFindDialog: TTMSFNCFindDialog; var ACanShow: Boolean);
begin
  if Assigned(OnBeforeShowFindDialog) then
    OnBeforeShowFindDialog(Self, AFindDialog, ACanShow);
end;

procedure TTMSFNCDataGridRenderer.DoBeforeShowReplaceDialog(
  AReplaceDialog: TTMSFNCReplaceDialog; var ACanShow: Boolean);
begin
  if Assigned(OnBeforeShowReplaceDialog) then
    OnBeforeShowReplaceDialog(Self, AReplaceDialog, ACanShow);
end;

procedure TTMSFNCDataGridRenderer.DoBeforeCloseInplaceEditor(ACell: TTMSFNCDataGridCellCoord; AInplaceEditor: TTMSFNCDataGridInplaceEditor; ACancelled: Boolean; var AValue: TTMSFNCDataGridCellValue; var ACanClose: Boolean);
begin
  if Assigned(OnBeforeCloseInplaceEditor) then
    OnBeforeCloseInplaceEditor(Self, ACell, AInplaceEditor, ACancelled, AValue, ACanClose);
end;

procedure TTMSFNCDataGridRenderer.DoBeforeCopyToClipboard(var ACanCopy: Boolean);
begin
  if Assigned(OnBeforeCopyToClipboard) then
    OnBeforeCopyToClipboard(Self, ACanCopy);
end;

procedure TTMSFNCDataGridRenderer.DoBeforeCreateFilterEditor(
  ACell: TTMSFNCDataGridCellCoord; var ACanCreate: Boolean);
begin
  if Assigned(OnBeforeCreateFilterEditor) then
    OnBeforeCreateFilterEditor(Self, ACell, ACanCreate);
end;

procedure TTMSFNCDataGridRenderer.DoBeforeCutToClipboard(var ACanCut: Boolean);
begin
  if Assigned(OnBeforeCutToClipboard) then
    OnBeforeCutToClipboard(Self, ACanCut);
end;

procedure TTMSFNCDataGridRenderer.DoAfterApplyFilter(AColumn: Integer;
  ACondition: string);
begin
  if Assigned(OnAfterApplyFilter) then
    OnAfterApplyFilter(Self, AColumn, ACondition);
end;

procedure TTMSFNCDataGridRenderer.DoAfterShowFilter(AColumn: Integer);
begin
  if Assigned(OnAfterShowFilter) then
    OnAfterShowFilter(Self, AColumn);
end;

procedure TTMSFNCDataGridRenderer.DoAfterShowFindDialog(
  AFindDialog: TTMSFNCFindDialog);
begin
  if Assigned(OnAfterShowFindDialog) then
    OnAfterShowFindDialog(Self, AFindDialog);
end;

procedure TTMSFNCDataGridRenderer.DoAfterShowReplaceDialog(
  AReplaceDialog: TTMSFNCReplaceDialog);
begin
  if Assigned(OnAfterShowReplaceDialog) then
    OnAfterShowReplaceDialog(Self, AReplaceDialog);
end;

procedure TTMSFNCDataGridRenderer.DoAfterCloseInplaceEditor(ACell: TTMSFNCDataGridCellCoord; ACancelled: Boolean; AValue: TTMSFNCDataGridCellValue);
begin
  if Assigned(OnAfterCloseInplaceEditorInternal) then
    OnAfterCloseInplaceEditorInternal(Self, ACell, ACancelled, AValue);

  if Assigned(OnAfterCloseInplaceEditor) then
    OnAfterCloseInplaceEditor(Self, ACell, ACancelled, AValue);
end;

procedure TTMSFNCDataGridRenderer.DoAfterCopyToClipboard;
begin
  if Assigned(OnAfterCopyToClipboard) then
    OnAfterCopyToClipboard(Self);
end;

procedure TTMSFNCDataGridRenderer.DoAfterCreateFilterEditor(
  ACell: TTMSFNCDataGridCellCoord; AEditor: TTMSFNCDataGridCellControl);
begin
  if Assigned(OnAfterCreateFilterEditor) then
    OnAfterCreateFilterEditor(Self, ACell, AEditor);
end;

procedure TTMSFNCDataGridRenderer.DoAfterCutToClipboard;
begin
  if Assigned(OnAfterCutToClipboard) then
    OnAfterCutToClipboard(Self);
end;

procedure TTMSFNCDataGridRenderer.UpdateInplaceEditorPosition;
var
  r: TRectF;
begin
  if (UpdateCount > 0) or GetIsDestroying then
    Exit;

  if Assigned(FInplaceEditor) then
  begin
    r := GetInplaceEditorRect(FEditingCell);
    FInplaceEditor.BoundsRect := {$IFNDEF FMXLIB}ConvertToRect{$ENDIF}(r);
  end;
end;

function GetEditorValue(AControl: TTMSFNCDataGridCellControl): TTMSFNCDataGridCellValue;
var
  AContext: TRttiContext;
  rt: TRttiType;
  prop, prop2: TRttiProperty;
  propv, prop2v: TValue;
  idx: Integer;
  sl: TStrings;
  s: string;
begin
  if not Assigned(AControl) then
  begin
    Result := TValueToCellValue(TValue.Empty);
    Exit;
  end;

  AContext := TRttiContext.Create;
  try
    propv := TValue.Empty;
    rt := AContext.GetType(AControl.ClassInfo);

    prop := rt.GetProperty('Value');
    {$IFNDEF FMXLIB}
    if not Assigned(prop) then
      prop := rt.GetProperty('Position');
    {$ENDIF}

    if Assigned(Prop) and Prop.IsReadable then
    begin
      propv := prop.GetValue(AControl);
      Result := TValueToCellValue(propv);
      Exit;
    end;

    prop := rt.GetProperty('Date');
    if Assigned(Prop) and Prop.IsReadable then
    begin
      propv := prop.GetValue(AControl);
      Result := TValueToCellValue(propv);
      Exit;
    end;

    prop := rt.GetProperty('SelectedColor');
    if Assigned(Prop) and Prop.IsReadable then
    begin
      propv := prop.GetValue(AControl);
      Result := TValueToCellValue(propv);
      Exit;
    end;

    prop := rt.GetProperty('ItemIndex');
    prop2 := rt.GetProperty('Items');
    if Assigned(prop) and prop.IsReadable then
    begin
      propv := prop.GetValue(AControl);
      if Assigned(prop2) and prop2.IsReadable then
      begin
        prop2v := prop2.GetValue(AControl);
        if prop2v.IsType<TStrings> and propv.IsType<Integer> then
        begin
          idx := propv.AsInteger;
          sl := prop2v.AsType<TStrings>;
          if (idx >= 0) and (idx <= sl.Count - 1) then
            s := sl[idx]
          else
            s := '';

          Result := s;
          Exit;
        end;
      end;
    end;

    prop := rt.GetProperty({$IFDEF FMXLIB}'IsChecked'{$ELSE}'Checked'{$ENDIF});
    if Assigned(Prop) and Prop.IsReadable then
    begin
      propv := prop.GetValue(AControl);
      Result := TValueToCellValue(propv);
      Exit;
    end;

    prop := rt.GetProperty('Text');
    if Assigned(Prop) and Prop.IsReadable then
    begin
      propv := prop.GetValue(AControl);
      Result := TValueToCellValue(propv);
      Exit;
    end;

  finally
    AContext.Free;
  end;
end;

function TTMSFNCDataGridRenderer.CloseInplaceEditor(ACancel: Boolean; AFlagClose: Boolean = False; AEnterKey: Boolean = False): Boolean;
var
  ivl: TValue;
  vl: TTMSFNCDataGridCellValue;
  vlv, vlt: TValue;
  cc, a: Boolean;
  nav: Boolean;
  cl, rl: TTMSFNCDataGridCellCoord;
  Allow: Boolean;
begin
  Result := True;

  if FClosing then
    Exit;

  FClosing := True;

  try
    vl := GetEditorValue(FInplaceEditor);
    vlv := CellValueToTValue(vl);

    case FInplaceEditorTarget of
      getCellValue: vlt := CellValueToTValue(Cells[FEditingCell.Column, FEditingCell.Row]);
      getFillColor:
      begin
        vlt := CellValueToTValue(gcNull);
        if HasLayout[FEditingCell.Column, FEditingCell.Row] then
          vlt := CellValueToTValue(Layouts[FEditingCell.Column, FEditingCell.Row].Fill.Color);
      end;
    end;

    if vlt.Kind <> vlv.Kind then
    begin
      case vlt.Kind of
        tkInteger, tkEnumeration: vl := ValueToInteger(vl, FormatSettings);
        tkFloat: vl := ValueToFloat(vl, FormatSettings);
        tkString{$IFNDEF WEBLIB}, tkWString, tkLString, tkUString{$ENDIF}: vl := ValueToString(vl);
        {$IFNDEF WEBLIB}
        tkInt64: vl := ValueToInt64(vl, FormatSettings);
        {$ENDIF}
        else
          vl := ValueToString(vl);
      end;
    end;

    cc := True;
    DoBeforeCloseInplaceEditor(FEditingCell, FInplaceEditor, ACancel, vl, cc);
    if cc then
    begin
      if not ACancel then
      begin
        if IsCellValid(FEditingCell) then
        begin
          a := True;
          DoCellEditValidateData(FEditingCell, FInplaceEditor, vl, a);
          if a then
          begin
            DoCellEditSetData(FEditingCell, FInplaceEditor, vl);

            case FInplaceEditorTarget of
              getCellValue: Cells[FEditingCell.Column, FEditingCell.Row] := vl;
              getFillColor:
              begin
                ivl := CellValueToTValue(vl);
                if ivl.IsType<TTMSFNCGraphicsColor> then
                begin
                  Layouts[FEditingCell.Column, FEditingCell.Row].Fill.Kind := gfkSolid;
                  Layouts[FEditingCell.Column, FEditingCell.Row].Fill.Color := ivl.AsType<TTMSFNCGraphicsColor>;
                end;
              end;
            end;
          end;
        end;
      end;

      DestroyEditor;

      nav := False;
      cl := FocusedCell;
      rl := FRealCell;

      if AEnterKey and (Options.Keyboard.EnterKeyHandling <> gekhNone) then
      begin
        nav := True;
        cl := ProcessEnterKey(cl, Options.Keyboard.EnterKeyHandling);
      end;

      DoAfterCloseInplaceEditor(FEditingCell, ACancel, vl);

      FEditingCell := MakeCell(-1, -1);

      Focus;

      if nav then
      begin
        SelectCell(cl);
        if AEnterKey and Options.Keyboard.EnterKeyDirectEdit then
        begin
          Allow := True;
          DoCanEditCell(FocusedCell.Column, FocusedCell.Row, Allow);
          if Allow then
            EditCell(FocusedCell);
        end;
      end;

      Result := True;
    end
    else
    begin
      Result := False;
      if FInplaceEditor.CanFocus then
        TTMSFNCDataGridInplaceEditorOpen(FInplaceEditor).SetFocus;
    end;
  finally
    FClosing := False;
  end;
end;

procedure TTMSFNCDataGridRenderer.DoAfterDrawEmptySpace(
  AGraphics: TTMSFNCGraphics; ARect: TRectF;
  AKind: TTMSFNCDataGridEmptySpaceKind);
begin
  if Assigned(OnAfterDrawEmptySpace) then
    OnAfterDrawEmptySpace(AGraphics, AGraphics, ARect, AKind);
end;

procedure TTMSFNCDataGridRenderer.DoBeforeDrawEmptySpace(
  AGraphics: TTMSFNCGraphics; ARect: TRectF;
  AKind: TTMSFNCDataGridEmptySpaceKind; var ACanDraw: Boolean);
begin
  if Assigned(OnBeforeDrawEmptySpace) then
    OnBeforeDrawEmptySpace(Self, AGraphics, ARect, AKind, ACanDraw);
end;

procedure TTMSFNCDataGridRenderer.DoBeforeLookup(var ALookupString: string;
  var ACanLookup: Boolean);
begin
  if Assigned(OnBeforeLookup) then
    OnBeforeLookup(Self, ALookupString, ACanLookup);
end;

procedure TTMSFNCDataGridRenderer.DoGetCellClass(AColumn, ARow: Integer;
  var ACellClass: TTMSFNCDataGridCellClass);
begin
  if Assigned(OnGetCellClass) then
    OnGetCellClass(Self, AColumn, ARow, ACellClass);
end;

procedure TTMSFNCDataGridRenderer.DoGetCellFormatting(
  ACell: TTMSFNCDataGridCellCoord; AData: TTMSFNCDataGridCellValue;
  var AFormatting: TTMSFNCDataGridDataFormatting; var AConvertSettings: TFormatSettings; var ACanFormat: Boolean);
var
  cl: TTMSFNCDataGridColumn;
begin
  cl := GetColumn(ACell.Column);
  if Assigned(cl) and (gcsFormatting in cl.Settings) then
  begin
    AFormatting.Format := cl.Formatting.Format;
    AFormatting.&Type := cl.Formatting.&Type;
    AFormatting.BooleanTrueText := cl.Formatting.BooleanTrueText;
    AFormatting.BooleanFalseText := cl.Formatting.BooleanFalseText;
    AConvertSettings := cl.Formatting.ConvertSettings;
  end;
  inherited;
end;

procedure TTMSFNCDataGridRenderer.DoGetCellHint(ACell: TTMSFNCDataGridCellCoord;
  var AHint: string);
begin
  if Assigned(OnGetCellHint) then
    OnGetCellHint(Self, ACell, AHint);
end;

procedure TTMSFNCDataGridRenderer.DoGetCellLayout(ACell: TTMSFNCDataGridCell);
begin
  if Assigned(OnGetCellLayout) then
    OnGetCellLayout(Self, ACell);
end;

procedure TTMSFNCDataGridRenderer.DoGetCellLayoutHasPriority(ACell: TTMSFNCDataGridCellCoord; var AHasPriority: Boolean);
begin
  if Assigned(OnGetCellLayoutHasPriority) then
    OnGetCellLayoutHasPriority(Self, ACell, AHasPriority);
end;

procedure TTMSFNCDataGridRenderer.DoGetCellProperties(ACell: TTMSFNCDataGridCell);
begin
  if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).GetCellProperties(ACell);

  if Assigned(OnGetCellProperties) then
    OnGetCellProperties(Self, ACell);
end;

procedure TTMSFNCDataGridRenderer.DoHScroll(APosition: Single);
begin
  if Assigned(OnHScroll) then
    OnHScroll(Self, APosition);
end;

function TTMSFNCDataGridRenderer.DoIsDesigntimeGrid(Sender: TObject): Boolean;
begin
  Result := False;
  if Assigned(OnIsDesigntime) then
    Result := OnIsDesigntime(Sender);
end;

function TTMSFNCDataGridRenderer.DoIsDestroyingGrid(Sender: TObject): Boolean;
begin
  Result := False;
  if Assigned(OnIsDestroying) then
    Result := OnIsDestroying(Sender);
end;

function TTMSFNCDataGridRenderer.DoIsLoadingGrid(Sender: TObject): Boolean;
begin
  Result := False;
  if Assigned(OnIsLoading) then
    Result := OnIsLoading(Sender);
end;

procedure TTMSFNCDataGridRenderer.DoLayoutChanged(Sender: TObject);
begin
  DoUpdate;
end;

procedure TTMSFNCDataGridRenderer.DoLookupTimer(Sender: TObject);
begin
  FLookupString := '';
  LookupTimer.Enabled := False;
end;

function TTMSFNCDataGridRenderer.DoIsFocusedGrid(Sender: TObject): Boolean;
begin
  Result := False;
  if Assigned(OnIsFocused) then
    Result := OnIsFocused(Sender);
end;

procedure TTMSFNCDataGridRenderer.DoNeedFilterDropDownData(AColumn: Integer;
  AValues: TStrings);
begin
  if Assigned(OnNeedFilterDropDownData) then
    OnNeedFilterDropDownData(Self, AColumn, AValues);
end;

procedure TTMSFNCDataGridRenderer.DoPaintCommentPopup(Sender: TObject;
  AGraphics: TTMSFNCGraphics; ARect: TRectF);
var
  c: TTMSFNCCommentPopup;
  cl: TTMSFNCDataGridCell;
  r: TRectF;
begin
  if Sender is TTMSFNCCommentPopup then
  begin
    c := (Sender as TTMSFNCCommentPopup);
    cl := c.FCell;
    if Assigned(cl) then
    begin
      r := ARect;
      InflateRectEx(r, -4, -4);
      AGraphics.DrawText(r, cl.Comment, True, gtaLeading, gtaLeading);
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.DoCellCommentIndicatorClick(Sender: TObject);
var
  g: TTMSFNCGraphics;
  sz: TSizeF;
  pt: TPointF;
  {$IFNDEF FMXLIB}
  ptx: TPoint;
  {$ENDIF}
  p: TTMSFNCDataGridCellParent;
  cl: TTMSFNCDataGridCell;
  cr: TRectF;
  cm: string;
begin
  inherited;

  if Sender is TTMSFNCDataGridCell then
  begin
    cl := Sender as TTMSFNCDataGridCell;

    p := GetParentHost;

    if p is TTMSFNCDataGridCellControl then
      CommentPopup.PlacementControl := p as TTMSFNCDataGridCellControl;

    cr := TTMSFNCDataGridCellOpen(cl).GetCommentIndicatorRect;

    pt := PointF(cr.Left, cr.Bottom);

    if p is TTMSFNCDataGridCellControl then
    begin
      {$IFDEF FMXLIB}
      pt := TTMSFNCDataGridCellControlOpen(p as TTMSFNCDataGridCellControl).LocalToScreen(pt);
      {$ELSE}
      ptx := (p as TTMSFNCDataGridCellControl).ClientToScreen(Point(Round(pt.X), Round(pt.Y)));
      pt := PointF(ptx.X, ptx.Y);
      {$ENDIF}
    end;

    cm := cl.Comment;

    CommentPopup.FCell := cl;

    g := TTMSFNCGraphics.CreateBitmapCanvas;
    g.BeginScene;
    g.BitmapContainer := BitmapContainer;
    //g.OptimizedHTMLDrawing := OptimizedHTMLDrawing;
    try
      sz := g.CalculateTextSize(cm, RectF(0, 0, 10000, 10000), True, True);
      CommentPopup.DropDownHeight := sz.cy + 10;
      CommentPopup.DropDownWidth := sz.cx + 10;
    finally
      g.EndScene;
      g.Free;
    end;

    //pt := PointF(pt.X  - FCommentPopup.DropDownWidth, ACell.Top - FCommentPopup.DropDownHeight));
    CommentPopup.PlacementRectangle.Left := pt.X;
    CommentPopup.PlacementRectangle.Top := pt.Y;
    CommentPopup.PlacementRectangle.Right := pt.X + CommentPopup.DropDownWidth;
    CommentPopup.PlacementRectangle.Bottom := pt.Y + CommentPopup.DropDownHeight;

    CommentPopup.Popup;

    //if Assigned(OnCellCommentClick) then
    //  OnCellCommentClick(Self, ACol, ARow, ACell);
  end;
end;

procedure TTMSFNCDataGridRenderer.DoCellFilterButtonClick(Sender: TObject);
var
  pt: TPointF;
  cl: TTMSFNCDataGridCell;
  fr: TRectF;
  p: TTMSFNCDataGridCellParent;
  c: Boolean;
  {$IFNDEF FMXLIB}
  ptx: TPoint;
  {$ENDIF}
begin
  if Assigned(Sender) and (Sender is TTMSFNCDataGridCell) then
  begin
    cl := (Sender as TTMSFNCDataGridCell);
    if (Options.Filtering.Enabled) and (Options.Filtering.Row = cl.Row) then
    begin
      c := True;
      DoBeforeShowFilter(cl.Column, c);

      if c then
      begin
        UpdateFilterList(cl.Column);

        p := GetParentHost;

        if p is TTMSFNCDataGridCellControl then
          FilterPopup.PlacementControl := p as TTMSFNCDataGridCellControl;

        fr := TTMSFNCDataGridCellOpen(cl).GetFilterButtonRect;

        pt := PointF(fr.Left, fr.Bottom);

        if p is TTMSFNCDataGridCellControl then
        begin
          {$IFDEF FMXLIB}
          pt := TTMSFNCDataGridCellControlOpen(p as TTMSFNCDataGridCellControl).LocalToScreen(pt);
          {$ELSE}
          ptx := (p as TTMSFNCDataGridCellControl).ClientToScreen(Point(Round(pt.X), Round(pt.Y)));
          pt := PointF(ptx.X, ptx.Y);
          {$ENDIF}
        end;

        FilterPopup.PlacementRectangle.Left := pt.X;
        FilterPopup.PlacementRectangle.Top := pt.Y;
        FilterPopup.PlacementRectangle.Right := pt.X + FilterPopup.DropDownWidth;
        FilterPopup.PlacementRectangle.Bottom := pt.Y + FilterPopup.DropDownHeight;

        {$IFDEF FMXLIB}
        if not FStyleApplied then
        {$ENDIF}
        begin
          FilterPopup.FillColor := FilterAppearance.Fill.Color;
          FilterPopup.StrokeColor := FilterAppearance.Stroke.Color;
          TFNCControlOpen(FilterContainer).Fill.Assign(FilterAppearance.Fill);
          TFNCControlOpen(FilterContainer).Stroke.Assign(FilterAppearance.Stroke);
        end;

        FilterContainer.Width := Options.Filtering.DropDownWidth;
        FilterContainer.Height := Options.Filtering.DropDownHeight;
        FilterPopup.DropDownHeight := Options.Filtering.DropDownHeight;
        FilterPopup.DropDownWidth := Options.Filtering.DropDownWidth;

        FilterClearButton.DataInteger := cl.Column;
        FilterSortAscendingButton.DataInteger := cl.Column;
        FilterSortDescendingButton.DataInteger := cl.Column;
        FilterSortAscendingButton.Visible := Options.Sorting.Enabled;
        FilterSortDescendingButton.Visible := Options.Sorting.Enabled;

        if not FStyleApplied then
        begin
          FilterSortAscendingButton.Font.Assign(FilterAppearance.Font);
          FilterSortAscendingButton.Appearance.NormalFill.Assign(FilterAppearance.Fill);
          FilterSortAscendingButton.Appearance.NormalStroke.Assign(FilterAppearance.Stroke);
          FilterSortDescendingButton.Font.Assign(FilterAppearance.Font);
          FilterSortDescendingButton.Appearance.NormalFill.Assign(FilterAppearance.Fill);
          FilterSortDescendingButton.Appearance.NormalStroke.Assign(FilterAppearance.Stroke);
        end
        else
        begin
          FilterSortAscendingButton.Appearance.NormalStroke.Kind := gskNone;
          FilterSortDescendingButton.Appearance.NormalStroke.Kind := gskNone;
        end;
        
        FilterEdit.Tag := cl.Column;

        FilterPopup.ContentControl := FilterContainer;
        FilterPopup.FocusedControl := FilterEdit;
        {$IFDEF FMXMOBILE}
        FilterPopup.StaysOpen := True;
        {$ENDIF}
        FilterPopup.Popup;
        {$IFDEF FMXMOBILE}
        FilterExitTimer.Enabled := True;
        {$ENDIF}

        DoAfterShowFilter(cl.Column);
      end;
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.DoCellNodeCollapse(Sender: TObject);
var
  cl: TTMSFNCDataGridCell;
  cc: Boolean;
  c: TTMSFNCDataGridCellCoord;
begin
  if Assigned(Sender) and (Sender is TTMSFNCDataGridCell) then
  begin
    cl := (Sender as TTMSFNCDataGridCell);
    cc := True;
    c := cl.Coordinate;
    DoCellBeforeNodeCollapse(c, cc);
    if cc then
    begin
      CollapseNode(c.Row);
      DoCellAfterNodeCollapse(c);
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.DoCellNodeExpand(Sender: TObject);
var
  cl: TTMSFNCDataGridCell;
  c: TTMSFNCDataGridCellCoord;
  ce: Boolean;
begin
  if Assigned(Sender) and (Sender is TTMSFNCDataGridCell) then
  begin
    cl := (Sender as TTMSFNCDataGridCell);
    ce := True;
    c := cl.Coordinate;
    DoCellBeforeNodeExpand(c, ce);
    if ce then
    begin
      ExpandNode(c.Row);
      DoCellAfterNodeExpand(c);
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.DoCellBeforeDraw(Sender: TObject;
  AGraphics: TTMSFNCGraphics; var ACanDraw: Boolean);
begin
  if Assigned(OnBeforeDrawCell) and (Sender is TTMSFNCDataGridCell) then
    OnBeforeDrawCell(Self, AGraphics, Sender as TTMSFNCDataGridCell, ACanDraw);
end;

procedure TTMSFNCDataGridRenderer.DoCellBeforeNodeCollapse(
  ACell: TTMSFNCDataGridCellCoord; var ACanCollapse: Boolean);
begin
  if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).RowBeforeCollapse(ACell.Row);

  if Assigned(OnCellBeforeNodeCollapse) then
    OnCellBeforeNodeCollapse(Self, ACell, ACanCollapse);
end;

procedure TTMSFNCDataGridRenderer.DoCellBeforeNodeExpand(
  ACell: TTMSFNCDataGridCellCoord; var ACanExpand: Boolean);
begin
  if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).RowBeforeExpand(ACell.Row);

  if Assigned(OnCellBeforeNodeExpand) then
    OnCellBeforeNodeExpand(Self, ACell, ACanExpand);
end;

procedure TTMSFNCDataGridRenderer.DoCellBeforeShowPopupMenu(ACell: TTMSFNCDataGridCellCoord; APopupMenu: TPopupMenu; var ACanShow: Boolean);
begin
  if Assigned(OnCellBeforeShowPopupMenu) then
    OnCellBeforeShowPopupMenu(Self, ACell, APopupMenu, ACanShow);
end;

procedure TTMSFNCDataGridRenderer.DoSelectCell(AStartCell,
  AEndCell: TTMSFNCDataGridCellCoord);
begin
  if Assigned(OnSelectCell) then
    OnSelectCell(Self, AStartCell, AEndCell);
end;

procedure TTMSFNCDataGridRenderer.DoSelectingCell(AStartCell,
  AEndCell: TTMSFNCDataGridCellCoord; var ACanSelect: Boolean);
begin
  if Assigned(OnSelectingCell) then
    OnSelectingCell(Self, AStartCell, AEndCell, ACanSelect);
end;

procedure TTMSFNCDataGridRenderer.DoColumnSized(AColumn: Integer; ASize: Double);
begin
  if Assigned(OnColumnSized) then
    OnColumnSized(Self, AColumn, ASize);
end;

function TTMSFNCDataGridRenderer.DoCreateCustomFilterEditor(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellControl;
begin
  Result := nil;
  if Assigned(OnCreateCustomFilterEditor) then
    Result := OnCreateCustomFilterEditor(Self, ACell);
end;

procedure TTMSFNCDataGridRenderer.DoRowSized(ARow: Integer; ASize: Double);
begin
  if Assigned(OnRowSized) then
    OnRowSized(Self, ARow, ASize);
end;

procedure TTMSFNCDataGridRenderer.DoSizeColumn(AColumn: Integer; ASize: Double);
begin
  if Assigned(OnSizeColumn) then
    OnSizeColumn(Self, AColumn, ASize);
end;

procedure TTMSFNCDataGridRenderer.DoSizeRow(ARow: Integer; ASize: Double);
begin
  if Assigned(OnSizeRow) then
    OnSizeRow(Self, ARow, ASize);
end;

procedure TTMSFNCDataGridRenderer.DoUpdateCursorGrid(Sender: TObject;
  ACursor: TCursor);
begin
  if Assigned(OnUpdateCursor) then
    OnUpdateCursor(Sender, ACursor);
end;

procedure TTMSFNCDataGridRenderer.DoVScroll(APosition: Single);
begin
  if Assigned(OnVScroll) then
    OnVScroll(Self, APosition);
end;

procedure TTMSFNCDataGridRenderer.DoDisplay;
begin
  if Assigned(OnDisplay) then
    OnDisplay(Self);
end;

procedure TTMSFNCDataGridRenderer.DoDisplayGrid(Sender: TObject);
begin
  if Assigned(OnDisplay) then
    OnDisplay(Sender);
end;

procedure TTMSFNCDataGridRenderer.DoEditorKeyDown(Sender: TObject; var Key: Word;
  {$IFDEF FMXLIB}var KeyChar: WideChar; {$ENDIF}Shift: TShiftState);
begin
  if Shift <> [] then
    Exit;

  FKeyDown := True;

  case Key of
    KEY_TAB: if FInplaceEditorActive then Key := 0;
    KEY_RETURN, KEY_F2:
    begin
      FDroppedDown := ((FInplaceEditor is TCustomComboBox) and (FInplaceEditor as TCustomComboBox).DroppedDown);
      FDroppedDown := FDroppedDown or ((FInplaceEditor is TTMSFNCDataGridEdit) and (FInplaceEditor as TTMSFNCDataGridEdit).LookupList.IsOpen);
      FDroppedDown := FDroppedDown or ((FInplaceEditor is TTMSFNCCustomPicker) and (FInplaceEditor as TTMSFNCCustomPicker).Popup.IsOpen);
      {$IFDEF FMXLIB}
      FDroppedDown := FDroppedDown or ((FInplaceEditor is TCustomComboEdit) and (FInplaceEditor as TCustomComboEdit).DroppedDown);
      {$ENDIF}

      if not ((FInplaceEditor is TCustomComboBox) and (FInplaceEditor as TCustomComboBox).DroppedDown)
        and not ((FInplaceEditor is TTMSFNCDataGridEdit) and (FInplaceEditor as TTMSFNCDataGridEdit).LookupList.IsOpen)
        and not ((FInplaceEditor is TTMSFNCCustomPicker) and (FInplaceEditor as TTMSFNCCustomPicker).Popup.IsOpen)
        {$IFDEF FMXLIB}
        and not ((FInplaceEditor is TCustomComboEdit) and (FInplaceEditor as TCustomComboEdit).DroppedDown)
        {$ENDIF}
        then
        Key := 0;
    end;
    KEY_UP, KEY_DOWN:
    begin
      if not (FInplaceEditor is TCustomMemo) and not ((FInplaceEditor is TCustomComboBox) and (FInplaceEditor as TCustomComboBox).DroppedDown)
        and not ((FInplaceEditor is TTMSFNCDataGridEdit) and (FInplaceEditor as TTMSFNCDataGridEdit).LookupList.IsOpen)
        and not ((FInplaceEditor is TTMSFNCCustomPicker) and (FInplaceEditor as TTMSFNCCustomPicker).Popup.IsOpen)
        {$IFDEF FMXLIB}
        and not ((FInplaceEditor is TCustomComboEdit) and (FInplaceEditor as TCustomComboEdit).DroppedDown)
        {$ENDIF}
        then
        Key := 0;
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.DoEditorKeyUp(Sender: TObject; var Key: Word;
  {$IFDEF FMXLIB}var KeyChar: WideChar; {$ENDIF}Shift: TShiftState);
var
  ki: TTMSFNCDataGridKeyInfo;
  a, Allow: Boolean;
begin
  if not FKeyDown then
    Exit;

  FKeyDown := False;

  case Key of
    KEY_TAB:
    begin
      ki := DefaultKeyInfo;
      ki.Kind := gkkDialog;
      ki.Key := Key;
      ki.Shift := Shift;
      ki.Blocked := FInplaceEditorActive;
      HandleKey(ki);
    end;
  end;

  if Shift <> [] then
    Exit;

  case Key of
    KEY_ESCAPE: CancelEditing;
    KEY_RETURN, KEY_F2:
    begin
      if not FDroppedDown then
        CloseInplaceEditor(False, False, Key = KEY_RETURN);
    end;
    KEY_LEFT:
    begin
      if Options.Keyboard.ArrowKeyDirectEdit then
      begin
        if ((FInplaceEditor is TCustomEdit) and ((FInplaceEditor as TCustomEdit).SelStart = 0))
          or ((FInplaceEditor is TCustomMemo) and ((FInplaceEditor as TCustomMemo).SelStart = 0)) then
        begin
          ki := DefaultKeyInfo;
          ki.Key := KEY_TAB;
          ki.Shift := [ssShift];
          ProcessTab(ki, gtkdNextColumnCell, gtkhNextCell, True, gcsEnd);
        end;
      end;
    end;
    KEY_RIGHT:
    begin
      if Options.Keyboard.ArrowKeyDirectEdit then
      begin
        if ((FInplaceEditor is TCustomEdit) and ((FInplaceEditor as TCustomEdit).SelStart = Length((FInplaceEditor as TCustomEdit).Text)))
          or ((FInplaceEditor is TCustomMemo) and ((FInplaceEditor as TCustomMemo).SelStart = Length((FInplaceEditor as TCustomMemo).Text))) then
        begin
          ki := DefaultKeyInfo;
          ki.Key := KEY_TAB;
          ProcessTab(ki, gtkdNextColumnCell, gtkhNextCell, True, gcsStart);
        end;
      end;
    end;
    KEY_UP, KEY_DOWN:
    begin
      if not (FInplaceEditor is TCustomMemo) and not ((FInplaceEditor is TCustomComboBox) and (FInplaceEditor as TCustomComboBox).DroppedDown)
        and not ((FInplaceEditor is TTMSFNCDataGridEdit) and (FInplaceEditor as TTMSFNCDataGridEdit).LookupList.IsOpen)
        and not ((FInplaceEditor is TTMSFNCCustomPicker) and (FInplaceEditor as TTMSFNCCustomPicker).Popup.IsOpen)
        {$IFDEF FMXLIB}
        and not ((FInplaceEditor is TCustomComboEdit) and (FInplaceEditor as TCustomComboEdit).DroppedDown)
        {$ENDIF}
        and not (FInplaceEditorType in [getSpinBox])
        then
      begin
        a := StopEditing;
        if a then
        begin
          ki := DefaultKeyInfo;
          ki.Key := Key;
          ki.Shift := Shift;
          ki.Kind := gkkDown;
          HandleKey(ki);

          if Options.Keyboard.ArrowKeyDirectEdit then
          begin
            Allow := True;
            DoCanEditCell(FocusedCell.Column, FocusedCell.Row, Allow);
            if Allow then
              EditCell(FocusedCell);
          end;
        end;
      end;
    end;
  end;

  FDroppedDown := False;
end;

{$IFNDEF FMXLIB}
procedure TTMSFNCDataGridRenderer.DoEditorKeyPress(Sender: TObject; var Key: Char);
begin
  if Ord(Key) in [KEY_ESCAPE, KEY_RETURN, KEY_TAB] then
    Key := #0;
end;
{$ENDIF}

procedure TTMSFNCDataGridRenderer.DoFocusGrid(Sender: TObject);
begin
  if Assigned(OnFocus) then
    OnFocus(Sender);
end;

procedure TTMSFNCDataGridRenderer.BeforeExport;
begin
  inherited;
  FSaveClipRect := FClipRect;
end;

procedure TTMSFNCDataGridRenderer.BuildDisplay(AList: TTMSFNCDataGridCellList; ADisplay: TTMSFNCDataGridDisplayList);
var
  I: Integer;
begin
  if (UpdateCount > 0) or GetIsDestroying or not Assigned(AList) or not Assigned(ADisplay) then
    Exit;

  ADisplay.Clear;
  for I := 0 to AList.Count - 1 do
    AddCellToDisplay(AList[I], ADisplay);
end;

procedure TTMSFNCDataGridRenderer.Display;
begin
  if (UpdateCount > 0) or GetIsDestroying then
    Exit;

  BuildDisplay(FNormalCellList, FNormalCellDisplay);
  BuildDisplay(FFixedRowCellList, FFixedRowCellDisplay);
  BuildDisplay(FFixedColumnCellList, FFixedColumnCellDisplay);
  BuildDisplay(FFixedRootCellList, FFixedRootCellDisplay);

  UpdateInplaceEditorPosition;

  DoDisplay;
end;

procedure TTMSFNCDataGridRenderer.CellAppearanceChanged(Sender: TObject);
begin
  Display;
end;

constructor TTMSFNCDataGridRenderer.Create(AHost: TObject = nil);
var
  p: TTMSFNCDataGridCellParent;
begin
  FHost := AHost;

  inherited Create;

  if Assigned(AHost) and (AHost is TPersistent) then
    FColumns := GetColumnsClass.Create(AHost as TPersistent, Self)
  else
    FColumns := GetColumnsClass.Create(nil, Self);

  FColumns.OnChange := @DoColumnsChanged;

  FScale := 1.0;

  FSelection := MakeCellRange(-1, -1, -1, -1);

  FStretchScrollBars := False;

  FScrollMode := gsmPixelScrolling;
  FScrollUpdate := gsuContinuous;
  FCustomScrollBars := False;

  FPadding := TTMSFNCMargins.Create;
  FPadding.OnChange := @PaddingChanged;

  FVerticalScrollBarVisible := True;
  FHorizontalScrollBarVisible := True;

  FEditingCell := MakeCell(-1, -1);
  FFocusedCell := MakeCell(-1, -1);
  FFirstCell := FFocusedCell;
  FRealCell := FFocusedCell;
  FSaveCell := FFocusedCell;

  Options.OnChange := @OptionsChanged;
  Options.OnChangeRebuild := OptionsChangedRebuild;
  FStroke := TTMSFNCGraphicsStroke.Create;
  FMergedCells := TTMSFNCDataGridMergedCells.Create;
  FNormalCellList := TTMSFNCDataGridNormalCellList.Create;
  FNormalCellDisplay := TTMSFNCDataGridNormalCellDisplayList.Create;
  FFixedColumnCellList := TTMSFNCDataGridFixedColumnCellList.Create;
  FFixedColumnCellDisplay := TTMSFNCDataGridFixedColumnCellDisplayList.Create;
  FFixedRowCellList := TTMSFNCDataGridFixedRowCellList.Create;
  FFixedRowCellDisplay := TTMSFNCDataGridFixedRowCellDisplayList.Create;
  FFixedRootCellList := TTMSFNCDataGridFixedRootCellList.Create;
  FFixedRootCellDisplay := TTMSFNCDataGridFixedRootCellDisplayList.Create;
  FCellAppearance := TTMSFNCDataGridCellAppearance.Create;
  FCellAppearance.OnChange := @CellAppearanceChanged;

  FStyleApplied := False;
  FFilterAppearance := TTMSFNCDataGridFilterAppearance.Create;
  FDragAppearance := TTMSFNCDataGridDragAppearance.Create;

  FGlobalFont := TTMSFNCAppearanceGlobalFont.Create(Self);

  p := nil;
  if Assigned(AHost) and (AHost is TTMSFNCDataGridCellParent) then
  begin
    p := AHost as TTMSFNCDataGridCellParent;

    FVerticalScrollBar := TScrollBar.Create(p);
    FVerticalScrollBar.Parent := p;
    FVerticalScrollBar.TabStop := False;
    {$IFNDEF CMNWEBLIB}
    FVerticalScrollBar.CanFocus := False;
    {$ENDIF}

    FHorizontalScrollBar := TScrollBar.Create(p);
    FHorizontalScrollBar.Parent := p;
    FHorizontalScrollBar.TabStop := False;
    {$IFNDEF CMNWEBLIB}
    FHorizontalScrollBar.CanFocus := False;
    {$ENDIF}

    {$IFDEF FMXLIB}
    FVerticalScrollBar.Stored := False;
    FHorizontalScrollBar.Stored := False;
    FVerticalScrollBar.Orientation := TOrientation.Vertical;
    {$ENDIF}

    {$IFDEF CMNWEBLIB}
    FVerticalScrollBar.DoubleBuffered := False;
    FHorizontalScrollBar.DoubleBuffered := False;
    FVerticalScrollBar.Kind := sbVertical;
    {$ENDIF}

    FVerticalScrollBar.OnChange := @VScrollChanged;
    FHorizontalScrollBar.OnChange := @HScrollChanged;
  end;

  FCustomVerticalScrollBar := TTMSFNCDataGridScrollBar.Create(p);
  FCustomVerticalScrollBar.Parent := p;
  FCustomVerticalScrollBar.TabStop := False;
  {$IFNDEF CMNWEBLIB}
  FCustomVerticalScrollBar.CanFocus := False;
  {$ENDIF}

  FCustomHorizontalScrollBar := TTMSFNCDataGridScrollBar.Create(p);
  FCustomHorizontalScrollBar.Parent := p;
  FCustomHorizontalScrollBar.TabStop := False;
  {$IFNDEF CMNWEBLIB}
  FCustomHorizontalScrollBar.CanFocus := False;
  {$ENDIF}

  CustomVerticalScrollBar.Stroke.Color := Stroke.Color;
  CustomVerticalScrollBar.Appearance.ThumbStroke.Color := Stroke.Color;
  CustomVerticalScrollBar.Appearance.ScrollButtonLeftStroke.Color := Stroke.Color;
  CustomVerticalScrollBar.Appearance.ScrollButtonRightStroke.Color := Stroke.Color;
  CustomVerticalScrollBar.Appearance.ThumbButtonLeftStroke.Color := Stroke.Color;
  CustomVerticalScrollBar.Appearance.ThumbButtonRightStroke.Color := Stroke.Color;

  CustomHorizontalScrollBar.Appearance.Assign(CustomVerticalScrollBar.Appearance);
  FCustomVerticalScrollBar.Appearance.ThumbButtonSize := 0;
  FCustomHorizontalScrollBar.Appearance.ThumbButtonSize := 0;
  {$IFDEF FMXLIB}
  FCustomVerticalScrollBar.Stored := False;
  FCustomHorizontalScrollBar.Stored := False;
  {$ENDIF}
  FCustomHorizontalScrollBar.Kind := sbkHorizontal;
  FCustomVerticalScrollBar.Visible := False;
  FCustomHorizontalScrollBar.Visible := False;
  FCustomHorizontalScrollBar.OnValueChanged := HCustomScrollChanged;
  FCustomVerticalScrollBar.OnValueChanged := VCustomScrollChanged;

  if Assigned(AHost) then
  begin
    if (AHost is TTMSFNCDataGridRenderer) then
    begin
      OnDisplay := (AHost as TTMSFNCDataGridRenderer).DoDisplayGrid;
      OnFocus := (AHost as TTMSFNCDataGridRenderer).DoFocusGrid;
      OnUpdateCursor := (AHost as TTMSFNCDataGridRenderer).DoUpdateCursorGrid;
      OnIsDestroying := (AHost as TTMSFNCDataGridRenderer).DoIsDestroyingGrid;
      OnIsLoading := (AHost as TTMSFNCDataGridRenderer).DoIsLoadingGrid;
      OnIsDesigntime := (AHost as TTMSFNCDataGridRenderer).DoIsDesigntimeGrid;
      OnIsFocused := (AHost as TTMSFNCDataGridRenderer).DoIsFocusedGrid;
    end
    else if AHost is TTMSFNCDataGridBase then
    begin
      OnDisplay := TTMSFNCDataGridBaseOpen(AHost as TTMSFNCDataGridBase).DoRootDisplay;
      OnFocus := TTMSFNCDataGridBaseOpen(AHost as TTMSFNCDataGridBase).DoRootFocus;
      OnUpdateCursor := TTMSFNCDataGridBaseOpen(AHost as TTMSFNCDataGridBase).DoRootUpdateCursor;
      OnIsDestroying := TTMSFNCDataGridBaseOpen(AHost as TTMSFNCDataGridBase).DoRootIsDestroying;
      OnIsLoading := TTMSFNCDataGridBaseOpen(AHost as TTMSFNCDataGridBase).DoRootIsLoading;
      OnIsDesigntime := TTMSFNCDataGridBaseOpen(AHost as TTMSFNCDataGridBase).DoRootIsDesigntime;
      OnIsFocused := TTMSFNCDataGridBaseOpen(AHost as TTMSFNCDataGridBase).DoRootIsFocused;
    end;
  end;
end;

function TTMSFNCDataGridRenderer.CreateFilterEditor(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellControl;
var
  a: Boolean;
  cl: TTMSFNCDataGridColumn;
  fet, ft: TTMSFNCDataGridFilterEditorType;
  v: TTMSFNCDataGridCellValue;
  i: Integer;
  c: Boolean;
  f: TTMSFNCDataGridDataFormatting;
  fmt: TFormatSettings;

  I64val: Int64;
  Ival: Integer;
  Dval: Double;
  dt: TDateTime;
  vl: TValue;
begin
  Result := nil;

  i := FixedRowCount;
  while not IsRowNormal(i) and (i < RowCount) do
    Inc(i);

  v := Cells[ACell.Column, i];

  a := True;
  DoBeforeCreateFilterEditor(ACell, a);
  if a then
  begin
    cl := GetColumn(ACell.Column);
    if Assigned(cl) and (gcsFilterEditorType in cl.Settings) then
      fet := cl.FilterEditorType
    else
      fet := gfetAutomatic;

    ft := gfetString;

    if fet = gfetAutomatic then
    begin
      f := DefaultFormatting;
      fmt := FormatSettings;
      c := True;
      DoGetCellFormatting(ACell, v, f, fmt, c);

      if c then
      begin
        case f.&Type of
          gdftBoolean: ft := gfetBoolean;
          gdftDateTime, gdftDate, gdftTime: ft := gfetDateTime;
          gdftFloat: ft := gfetFloat;
          gdftNumber: ft := gfetNumber;
          gdftDefault:
          begin
            if ValueIsString(v) then
            begin
              vl := CellValueToTValue(v);

              if TryStrToFloat(vl.AsString, Dval, fmt) then
                ft := gfetFloat
              else if TryStrToInt64(vl.AsString, I64val) then
                ft := gfetNumber
              else if TryStrToInt(vl.AsString, Ival) then
                ft := gfetNumber
              else if TryStrToDateTime(vl.AsString, dt) then
                ft := gfetDateTime;
            end
            else if ValueIsFloat(v) then
              ft := gfetFloat
            else if ValueIsInteger(v) then
              ft := gfetNumber
          end;
        end;
      end
      else
        ft := gfetString;
    end
    else
      ft := fet;

   case ft of
      gfetCustom: Result := DoCreateCustomFilterEditor(ACell);
      else
        Result := TTMSFNCDataGridFilterPanel.Create(nil, Self, ACell, ft);
    end;

    if Assigned(Result) then
    begin
      Result.Tag := ACell.Column;
      DoAfterCreateFilterEditor(ACell, Result);
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.ConfigureCell(ACell: TTMSFNCDataGridCell; AMode: TTMSFNCDataGridCellConfigureMode = gccmBoth);
begin
  if AMode in [gccmLayout, gccmBoth] then
  begin
    SetLayout(ACell);
    DoGetCellLayout(ACell);
  end;

  if AMode in [gccmProperties, gccmBoth] then
  begin
    SetProperties(ACell);
    DoGetCellProperties(ACell);
  end;

  ApplyCellScale(ACell, FScale);
end;

function TTMSFNCDataGridRenderer.GetAdaptToStyle: Boolean;
begin
  Result := FAdaptToStyle;
end;

function TTMSFNCDataGridRenderer.GetAnimateTimer: TTimer;
begin
  if not Assigned(FAnimateTimer) then
  begin
    FAnimateTimer := TTimer.Create(nil);
    FAnimateTimer.Interval := 1;
    FAnimateTimer.Enabled := False;
    FAnimateTimer.OnTimer := Animate;
  end;

  Result := FAnimateTimer;
end;

function TTMSFNCDataGridRenderer.GetBaseCellClass(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellClass;
var
  r: TTMSFNCDataGridRenderer;
  cl: TTMSFNCDataGridColumn;
  fe: Boolean;
begin
  Result := TTMSFNCDataGridCell;

  fe := False;
  if (Options.Filtering.Enabled) and (Options.Filtering.Row = ACell.Row) and (ACell.Column >= FixedColumnCount)
    and (ACell.Column <= ColumnCount - FixedRightColumnCount - 1) then
  begin
    cl := GetColumn(ACell.Column);
    if Assigned(cl) and (gcsFilterEditor in cl.Settings) then
      fe := cl.FilterEditor
    else
      fe := gfcEditor in Options.Filtering.Controls;
  end;

  if HasGrid[ACell.Column, ACell.Row] then
  begin
    r := Grids[ACell.Column, ACell.Row];
    if Assigned(r) then
      Result := TTMSFNCDataGridRendererCell
  end
  else if fe then
    Result := TTMSFNCDataGridFilterEditorCell
  else if HasCheckBox[ACell.Column, ACell.Row] then
    Result := TTMSFNCDataGridCheckBoxCell
  else if HasDataCheckBox[ACell.Column, ACell.Row] then
    Result := TTMSFNCDataGridDataCheckBoxCell
  else if HasButton[ACell.Column, ACell.Row] then
    Result := TTMSFNCDataGridButtonCell
  else if HasBitmap[ACell.Column, ACell.Row] then
    Result := TTMSFNCDataGridBitmapCell
  else if HasProgressBar[ACell.Column, ACell.Row] then
    Result := TTMSFNCDataGridProgressBarCell
  else if HasDataProgressBar[ACell.Column, ACell.Row] then
    Result := TTMSFNCDataGridDataProgressBarCell
  else if HasRadioButton[ACell.Column, ACell.Row] then
    Result := TTMSFNCDataGridRadioButtonCell
  else if HasDataRadioButton[ACell.Column, ACell.Row] then
    Result := TTMSFNCDataGridDataRadioButtonCell
  else if (RowTypes[ACell.Row] = grtNode) and (ACell.Column = Options.Column.NodeIndex) then
    Result := TTMSFNCDataGridNodeCell;
end;

function TTMSFNCDataGridRenderer.GetBitmapContainer: TTMSFNCBitmapContainer;
begin
  Result := FBitmapContainer;
end;

function TTMSFNCDataGridRenderer.GetBitmapNames(AColumn, ARow: Integer): string;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := '';
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) and (AsExtendedCell(cd).&Type = gcitBitmap) then
    Result := AsExtendedCell(cd).BitmapName;
end;

function TTMSFNCDataGridRenderer.GetBitmaps(AColumn,
  ARow: Integer): TTMSFNCBitmap;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := nil;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) and (AsExtendedCell(cd).&Type = gcitBitmap) then
    Result := AsExtendedCell(cd).Bitmap;
end;

procedure TTMSFNCDataGridRenderer.VCustomScrollChanged(Sender: TObject;
  AValue: Double);
begin
  if FBlockScrollingUpdate or not HasCustomScrollBars then
    Exit;

  if not IsMouseDown then
    StopAnimationTimer;

  VerticalScrollPositionChanged;
  SetVScrollValue(GetVScrollValue);
end;

procedure TTMSFNCDataGridRenderer.VerticalScrollPositionChanged;
begin
  BlockScrollingUpdate := True;
  Update;
  BlockScrollingUpdate := False;
  DoVScroll(GetVScrollValue);
  if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).Scroll;
end;

procedure TTMSFNCDataGridRenderer.DestroyEditor;
begin
  if Assigned(FInplaceEditor) then
  begin
    FInplaceEditor.Parent := nil;
    FInplaceEditor.Free;
    FInplaceEditor := nil;
    FInplaceEditorActive := False;
  end;
end;

procedure TTMSFNCDataGridRenderer.DoScrollTimer(Sender: TObject);
var
  c: TTMSFNCDataGridCellCoord;
  contentr: TRectF;
begin
  if not Options.Mouse.AutoScrolling then
  begin
    ScrollTimer.Enabled := False;
    Exit;
  end;

  contentr := GetContentRect;

  if (FX >= (contentr.Right - contentr.Left) - GetTotalFixedRightColumnWidth) then
  begin
    c := FocusedCell;
    if Options.Mouse.AutoScrollingSpeedMode = gasmPixels then
      c.Column := c.Column + Round(FX - ((contentr.Right - contentr.Left) - GetTotalFixedRightColumnWidth)) * Options.Mouse.AutoScrollingSpeed
    else
      c.Column := c.Column + Options.Mouse.AutoScrollingSpeed;
    SetFocusCell(c.Column, c.Row);
    SelectCell(FocusedCell, [], FMouseDragging);
  end
  else if FX <= GetTotalFixedColumnWidth then
  begin
    c := FocusedCell;
    if Options.Mouse.AutoScrollingSpeedMode = gasmPixels then
      c.Column := c.Column + Round(FX - GetTotalFixedColumnWidth) * Options.Mouse.AutoScrollingSpeed
    else
      c.Column := c.Column - Options.Mouse.AutoScrollingSpeed;
    SetFocusCell(c.Column, c.Row);
    SelectCell(FocusedCell, [], FMouseDragging);
  end;

  if (FY >= (contentr.Bottom - contentr.Top) - GetTotalFixedBottomRowHeight) then
  begin
    c := FocusedCell;
    if Options.Mouse.AutoScrollingSpeedMode = gasmPixels then
      c.Row := c.Row + Round(FY - ((contentr.Bottom - contentr.Top) - GetTotalFixedBottomRowHeight)) * Options.Mouse.AutoScrollingSpeed
    else
      c.Row := c.Row + Options.Mouse.AutoScrollingSpeed;
    SetFocusCell(c.Column, c.Row);
    SelectCell(FocusedCell, [], FMouseDragging);
  end
  else if FY <= GetTotalFixedRowHeight then
  begin
    c := FocusedCell;
    if Options.Mouse.AutoScrollingSpeedMode = gasmPixels then
      c.Row := c.Row + Round(FY - GetTotalFixedRowHeight) * Options.Mouse.AutoScrollingSpeed
    else
      c.Row := c.Row - Options.Mouse.AutoScrollingSpeed;
    SetFocusCell(c.Column, c.Row);
    SelectCell(FocusedCell, [], FMouseDragging);
  end;
end;

procedure TTMSFNCDataGridRenderer.VScrollChanged(Sender: TObject);
begin
  if FBlockScrollingUpdate or HasCustomScrollBars then
    Exit;

  if not IsMouseDown then
    StopAnimationTimer;

  VerticalScrollPositionChanged;
  SetVScrollValue(GetVScrollValue);
end;

procedure TTMSFNCDataGridRenderer.HCustomScrollChanged(Sender: TObject;
  AValue: Double);
begin
  if FBlockScrollingUpdate or not HasCustomScrollBars then
    Exit;

  if not IsMouseDown then
    StopAnimationTimer;

  HorizontalScrollPositionChanged;
  SetHScrollValue(GetHScrollValue);
end;

procedure TTMSFNCDataGridRenderer.InternalMarkCells(AValue: string; ATag: string; ACaseSensitive: Boolean; AFromColumn: Integer; AFromRow: Integer; AToColumn: Integer; AToRow: Integer; AFullCell: Boolean = False);
var
  r, c: Integer;
  ct, h: string;
begin
  AValue := TTMSFNCUtils.FixMarkup(AValue, False);

  for r := AFromRow to AToRow do
  begin
    for c := AFromColumn to AToColumn do
    begin
      if IsCellBase(MakeCell(c, r)) then
      begin
        ct := Strings[c, r];
        h := Hilight(ct, AValue, ATag, ACaseSensitive, AFullCell);

        if h <> ct then
          Cells[c, r] := h;
      end;
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.InternalUnmarkCells(ATag: string; AFromColumn, AFromRow, AToColumn, AToRow: Integer);
var
  r,c: Integer;
  ct: string;
begin
  for r := AFromRow to AToRow do
  begin
    for c := AFromColumn to AToColumn do
    begin
      if IsCellBase(MakeCell(c,r)) then
      begin
        ct := Strings[c, r];
        ct := TTMSFNCUtils.UnFixMarkup(ct, False);
        Cells[c,r] := Unhilight(ct, ATag);
      end;
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.HilightCells(AHilightText: string;
  AOptions: TTMSFNCDataGridHilightOptions);
begin
  case AOptions.Mode of
    ghmCell: InternalMarkCells(AHilightText, 'hi', AOptions.CaseSensitive, AOptions.Column, AOptions.Row, AOptions.Column, AOptions.Row, AOptions.FullText);
    ghmColumn:
    begin
      if AOptions.IncludeFixed then
        InternalMarkCells(AHilightText, 'hi', AOptions.CaseSensitive, AOptions.Column, 0, AOptions.Column, RowCount - 1, AOptions.FullText)
      else
        InternalMarkCells(AHilightText, 'hi', AOptions.CaseSensitive, AOptions.Column, FixedRowCount, AOptions.Column, RowCount - 1 - FixedBottomRowCount, AOptions.FullText);
    end;
    ghmRow:
    begin
      if AOptions.IncludeFixed then
        InternalMarkCells(AHilightText, 'hi', AOptions.CaseSensitive, 0, AOptions.Row, ColumnCount - 1, AOptions.Row, AOptions.FullText)
      else
        InternalMarkCells(AHilightText, 'hi', AOptions.CaseSensitive, FixedColumnCount, AOptions.Row, ColumnCount - 1 - FixedRightColumnCount, AOptions.Row, AOptions.FullText);
    end;
    ghmGrid:
    begin
      if AOptions.IncludeFixed then
        InternalMarkCells(AHilightText, 'hi', AOptions.CaseSensitive, 0, 0, ColumnCount - 1, RowCount - 1, AOptions.FullText)
      else
        InternalMarkCells(AHilightText, 'hi', AOptions.CaseSensitive, FixedColumnCount, FixedRowCount, ColumnCount - 1 - FixedRightColumnCount, RowCount - 1 - FixedBottomRowCount, AOptions.FullText);
    end;
  end;
end;

function TTMSFNCDataGridRenderer.HilightText(ACaseSensitive: Boolean; AValue,
  AText: string): string;
begin
  Result := Hilight(AValue, AText, 'hi', ACaseSensitive, False);
end;

procedure TTMSFNCDataGridRenderer.HorizontalScrollPositionChanged;
begin
  BlockScrollingUpdate := True;
  Update;
  BlockScrollingUpdate := False;
  DoHScroll(GetHScrollValue);
end;

procedure TTMSFNCDataGridRenderer.HScrollChanged(Sender: TObject);
begin
  if FBlockScrollingUpdate or HasCustomScrollBars then
    Exit;

  if not IsMouseDown then
    StopAnimationTimer;

  HorizontalScrollPositionChanged;
  SetHScrollValue(GetHScrollValue);
end;

procedure TTMSFNCDataGridRenderer.InternalClear;
begin
  if Assigned(ControlWrapperList) then
    ControlWrapperList.Clear;

  SelectedCellDictionary.Clear;
  SelectedColumnDictionary.Clear;
  SelectedRowDictionary.Clear;

  FNormalCellDisplay.Clear;
  FFixedColumnCellDisplay.Clear;
  FFixedRowCellDisplay.Clear;
  FFixedRootCellDisplay.Clear;

  FNormalCellList.Clear;
  FFixedColumnCellList.Clear;
  FFixedRowCellList.Clear;
  FFixedRootCellList.Clear;

  if Assigned(TTMSFNCDataGridCellOpen.ControlCacheList) then
    TTMSFNCDataGridCellOpen.ControlCacheList.Clear;
  inherited;
end;

procedure TTMSFNCDataGridRenderer.ApplyFilterFromValue(AColumn: Integer; AValue: TTMSFNCDataGridCellValue; AType: TTMSFNCDataGridDataFilterType);
var
  fd: TTMSFNCDataGridDataFilterData;
  cnd, s, cs: string;
  i, filtercol: integer;
  c: Boolean;
begin
  filtercol := AColumn;

  if Options.Filtering.MultiColumn then
  begin
    for i := Filter.Count - 1 downto 0 do
    begin
      if Filter[i].Column = filtercol then
        Filter.Delete(i);
    end;
  end
  else
    Filter.Clear;

  i := FixedRowCount;
  while not IsRowNormal(i) and (i < RowCount) do
    Inc(i);

  s := ValueToString(AValue);
  if (Pos('&', s) = 0) and (Pos('|', s) = 0) and (Pos('<', s) = 0) and (Pos('>', s) = 0)
    and (Pos('=', s) = 0) and (Pos('!', s) = 0) and (Pos('*', s) = 0) and (Pos('?', s) = 0) then
      s := FormatDisplayValue(MakeCell(AColumn, i), AValue);

  fd := nil;
  if s <> '' then
  begin
    fd := Filter.Add;
    fd.Column := filtercol;
    fd.&Type := AType;
    fd.DataString := s;
    fd.BuildCondition(s);
    cnd := fd.Condition;
  end;

  c := True;
  if Assigned(fd) then
  begin
    cs := cnd;
    DoBeforeApplyFilter(fd.Column, cnd, c);
  end;

  if c then
  begin
    if Assigned(fd) and (cnd <> cs) then
      fd.Condition := cnd;

    IncUpdateCount;
    RemoveFilter;
    DecUpdateCount;
    ApplyFilter;

    if Assigned(fd) then
      DoAfterApplyFilter(fd.Column, cnd);
  end;
end;

procedure TTMSFNCDataGridRenderer.PreviousPage;
begin
  PageIndex := PageIndex - 1;
end;

function TTMSFNCDataGridRenderer.PreviousSelectableColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
var
  c, r, rorig, corig: Integer;
begin
  c := AColumn;
  r := ARow;
  corig := c;
  rorig := r;

  Result.Column := AColumn;
  Result.Row := ARow;

  if (Result.Column - 1 >= FixedColumnCount) then
  begin
    Result.Column := Result.Column - 1;

    while IsCellDisabled(Result) and (Result.Column > FixedColumnCount) do
      Result.Column := Result.Column - 1;
  end;

  if IsCellDisabled(Result) then
  begin
    Result.Column := corig;
    Result.Row := rorig;
  end;
end;

function TTMSFNCDataGridRenderer.PreviousSelectableRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
var
  c, r, corig, rorig: Integer;
begin
  c := AColumn;
  r := ARow;
  corig := c;
  rorig := r;

  Result.Column := AColumn;
  Result.Row := ARow;

  if (Result.Row - 1 >= FixedRowCount) then
  begin
    Result.Row := Result.Row - 1;

    while IsCellDisabled(Result) and (Result.Row > FixedRowCount) do
      Result.Row := Result.Row - 1;
  end;

  if IsCellDisabled(Result) then
  begin
    Result.Column := corig;
    Result.Row := rorig;
  end;
end;

function TTMSFNCDataGridRenderer.NeedsItemExtended(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
var
  cl: TTMSFNCDataGridColumn;
begin
  Result := inherited NeedsItemExtended(ACell);

  if Options.Filtering.Enabled and (ACell.Row = Options.Filtering.Row) and (ACell.Column >= FixedColumnCount)
    and (ACell.Column <= ColumnCount - FixedRightColumnCount - 1) then
  begin
    cl := GetColumn(ACell.Column);
    if Assigned(cl) and (gcsFilterEditor in cl.Settings) then
      Result := Result or cl.FilterEditor
    else
      Result := Result or (gfcEditor in Options.Filtering.Controls);
  end;
end;

procedure TTMSFNCDataGridRenderer.NextPage;
begin
  PageIndex := PageIndex + 1;
end;

function TTMSFNCDataGridRenderer.NextSelectableColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
var
  c, corig, r, rorig: Integer;
  cs: Integer;
  chk: Boolean;
begin
  c := AColumn;
  r := ARow;

  corig := c;
  rorig := r;

  Result.Column := AColumn;
  Result.Row := ARow;

  cs := ColumnSpan(MakeCell(c, r));
  if cs > 0 then
  begin
    chk := (Result.Column + cs <= ColumnCount - 1 - FixedRightColumnCount);
    if chk then
      Result.Column := Result.Column + cs
  end
  else
  begin
    chk := (Result.Column + 1 <= ColumnCount - 1 - FixedRightColumnCount);
    if chk then
      Result.Column := Result.Column + 1;
  end;

  if chk then
  begin
    while IsCellDisabled(Result) and (Result.Column < ColumnCount - 1 - FixedRightColumnCount) do
    begin
      cs := ColumnSpan(MakeCell(c, r));
      if cs > 0 then
        Result.Column := Result.Column + cs
      else
        Result.Column := Result.Column + 1;
    end;
  end;

  if IsCellDisabled(Result) then
  begin
    Result.Column := corig;
    Result.Row := rorig;
  end;
end;

function TTMSFNCDataGridRenderer.NextSelectableRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
var
  c, r, corig, rorig: Integer;
  rs: Integer;
  chk: Boolean;
begin
  c := AColumn;
  r := ARow;
  corig := c;
  rorig := r;

  Result.Column := AColumn;
  Result.Row := ARow;

  rs := RowSpan(MakeCell(c, r));
  if rs > 0 then
  begin
    chk := (Result.Row + rs <= RowCount - 1 - FixedBottomRowCount);
    if chk then
      Result.Row := Result.Row + rs
  end
  else
  begin
    chk := (Result.Row + 1 <= RowCount - 1 - FixedBottomRowCount);
    if chk then
      Result.Row := Result.Row + 1;
  end;

  if chk then
  begin
    while IsCellDisabled(Result) and (Result.Row < RowCount - 1 - FixedBottomRowCount) do
    begin
      rs := RowSpan(MakeCell(c, r));
      if rs > 0 then
        Result.Row := Result.Row + rs
      else
        Result.Row := Result.Row + 1;
    end;
  end;

  if IsCellDisabled(Result) then
  begin
    Result.Column := corig;
    Result.Row := rorig;
  end;
end;

function TTMSFNCDataGridRenderer.PreviousVisibleColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
var
  c, r, rorig, corig: Integer;
begin
  c := AColumn;
  r := ARow;
  corig := c;
  rorig := r;

  Result.Column := AColumn;
  Result.Row := ARow;

  if (Result.Column - 1 >= FixedColumnCount) then
  begin
    Result.Column := Result.Column - 1;

    while not IsCellVisible(Result) and (Result.Column > FixedColumnCount) do
      Result.Column := Result.Column - 1;
  end;

  if not IsCellVisible(Result) then
  begin
    Result.Column := corig;
    Result.Row := rorig;
  end;
end;

function TTMSFNCDataGridRenderer.PreviousVisibleRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
var
  c, r, corig, rorig: Integer;
begin
  c := AColumn;
  r := ARow;
  corig := c;
  rorig := r;

  Result.Column := AColumn;
  Result.Row := ARow;

  if (Result.Row - 1 >= FixedRowCount) then
  begin
    Result.Row := Result.Row - 1;

    while not IsCellVisible(Result) and (Result.Row > FixedRowCount) do
      Result.Row := Result.Row - 1;
  end;

  if not IsCellVisible(Result) then
  begin
    Result.Column := corig;
    Result.Row := rorig;
  end;
end;

function TTMSFNCDataGridRenderer.NextVisibleColumn(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
var
  c, corig, r, rorig: Integer;
  cs: Integer;
  chk: Boolean;
begin
  c := AColumn;
  r := ARow;

  corig := c;
  rorig := r;

  Result.Column := AColumn;
  Result.Row := ARow;

  cs := ColumnSpan(MakeCell(c, r));
  if cs > 0 then
  begin
    chk := (Result.Column + cs <= ColumnCount - 1 - FixedRightColumnCount);
    if chk then
      Result.Column := Result.Column + cs
  end
  else
  begin
    chk := (Result.Column + 1 <= ColumnCount - 1 - FixedRightColumnCount);
    if chk then
      Result.Column := Result.Column + 1;
  end;

  if chk then
  begin
    while not IsCellVisible(Result) and (Result.Column < ColumnCount - 1 - FixedRightColumnCount) do
    begin
      cs := ColumnSpan(MakeCell(c, r));
      if cs > 0 then
        Result.Column := Result.Column + cs
      else
        Result.Column := Result.Column + 1;
    end;
  end;

  if not IsCellVisible(Result) then
  begin
    Result.Column := corig;
    Result.Row := rorig;
  end;
end;

function TTMSFNCDataGridRenderer.NextVisibleRow(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
var
  c, r, corig, rorig: Integer;
  rs: Integer;
  chk: Boolean;
begin
  c := AColumn;
  r := ARow;
  corig := c;
  rorig := r;

  Result.Column := AColumn;
  Result.Row := ARow;

  rs := RowSpan(MakeCell(c, r));
  if rs > 0 then
  begin
    chk := (Result.Row + rs <= RowCount - 1 - FixedBottomRowCount);
    if chk then
      Result.Row := Result.Row + rs
  end
  else
  begin
    chk := (Result.Row + 1 <= RowCount - 1 - FixedBottomRowCount);
    if chk then
      Result.Row := Result.Row + 1;
  end;

  if chk then
  begin
    while not IsCellVisible(Result) and (Result.Row < RowCount - 1 - FixedBottomRowCount) do
    begin
      rs := RowSpan(MakeCell(c, r));
      if rs > 0 then
        Result.Row := Result.Row + rs
      else
        Result.Row := Result.Row + 1;
    end;
  end;

  if not IsCellVisible(Result) then
  begin
    Result.Column := corig;
    Result.Row := rorig;
  end;
end;

function TTMSFNCDataGridRenderer.IsCellSelected(ACell: TTMSFNCDataGridCellCoord): Boolean;
var
  sc, stc: TTMSFNCDataGridCellCoord;
begin
  case Options.Selection.Mode of
    gsmDisjunctColumn: Result := SelectedColumns[ACell.Column];
    gsmDisjunctRow: Result := SelectedRows[ACell.Row];
    gsmDisjunctCell, gsmDisjunctCellRange: Result := SelectedCells[ACell.Column, ACell.Row];
    else
    begin
      sc := Selection.StartCell;
      stc := Selection.StopCell;
      Result := (ACell.Column >= sc.Column) and (ACell.Column <= stc.Column) and (ACell.Row >= sc.Row) and (ACell.Row <= stc.Row);
    end;
  end;
end;

function TTMSFNCDataGridRenderer.IsDesigntime: Boolean;
begin
  Result := False;
  if Assigned(OnIsDesigntime) then
    Result := OnIsDesigntime(Self);
end;

function TTMSFNCDataGridRenderer.IsFixedCellSelected(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := False;
  if IsCellFixed(ACell) then
  begin
    Result := ((ACell.Column >= Selection.StartCell.Column) and (ACell.Column <= Selection.StopCell.Column) and (Options.Selection.Mode <> gsmSingleRow) and (Options.Selection.Mode <> gsmDisjunctRow) and not (Options.Selection.Mode in [gsmDisjunctCell, gsmDisjunctCellRange])
      and (Options.Selection.Mode <> gsmRowRange)) or ((ACell.Row >= Selection.StartCell.Row) and (ACell.Row <= Selection.StopCell.Row)
        and (Options.Selection.Mode <> gsmSingleColumn) and (Options.Selection.Mode <> gsmDisjunctColumn) and not (Options.Selection.Mode in [gsmDisjunctCell, gsmDisjunctCellRange]) and (Options.Selection.Mode <> gsmColumnRange));

    if Options.Selection.Mode = gsmDisjunctRow then
      Result := SelectedRows[ACell.Row]
    else if Options.Selection.Mode = gsmDisjunctColumn then
      Result := SelectedColumns[ACell.Column]
    else if Options.Selection.Mode in [gsmDisjunctCell, gsmDisjunctCellRange] then
      Result := SelectedCells[ACell.Column, ACell.Row];
  end;
end;

function TTMSFNCDataGridRenderer.GetInplaceEditor: TTMSFNCDataGridInplaceEditor;
begin
  Result := FInplaceEditor;
end;

function TTMSFNCDataGridRenderer.IsEditing: Boolean;
begin
  Result := FInplaceEditorActive;
end;

function TTMSFNCDataGridRenderer.GetInplaceEditorRect(ACell: TTMSFNCDataGridCellCoord): TRectF;
var
  cr: TRectF;
  c: TTMSFNCDataGridCell;
begin
  Result := RectF(0, 0, 0, 0);

  c := GetCellObjectForCell(ACell);
  if Assigned(c) then
  begin
    if (FInplaceEditorType <> getNone) and Assigned(FInplaceEditor) then
    begin
      if ((FInplaceEditor is TCustomEdit) or (FInplaceEditor is TCustomMemo)) and not (FInplaceEditorType in [getCustom, getComboEdit]) then
      begin
        Result := TTMSFNCDataGridCellOpen(c).GetActualTextRect;
        {$IFDEF FMXLIB}
        InflateRectEx(Result, 2, 2);
        {$ENDIF}
        {$IFDEF WEBLIB}
        InflateRectEx(Result, 2, 0);
        OffsetRectEx(Result, 0, -2);
        {$ENDIF}
      end
      else
        Result := c.Rect;

      cr := GetContentClipRect;
      Result.Top := Max(Result.Top, cr.Top);
      Result.Bottom := Min(Result.Bottom, cr.Bottom);
      Result.Left := Max(Result.Left, cr.Left);
      Result.Right := Min(Result.Right, cr.Right);
      Result := RectF(Floor(Result.Left), Floor(Result.Top), Floor(Result.Right), Floor(Result.Bottom));

      DoGetInplaceEditorRect(ACell, FInplaceEditor, Result);
    end;
  end;
end;

function TTMSFNCDataGridRenderer.GetIsDestroying: Boolean;
begin
  Result := False;
  if Assigned(OnIsDestroying) then
    Result := OnIsDestroying(Self);
end;

function TTMSFNCDataGridRenderer.GetIsLoading: Boolean;
begin
  Result := False;
  if Assigned(OnIsLoading) then
    Result := OnIsLoading(Self);
end;

function TTMSFNCDataGridRenderer.IsFocused: Boolean;
begin
  Result := False;
  if Assigned(OnIsFocused) then
    Result := OnIsFocused(Self);
end;

procedure TTMSFNCDataGridRenderer.ChangeDPIScale(M: Integer; D: Integer);
begin
  inherited;
  BeginUpdate;
  try
    CellAppearance.FixedLayout.Font.Height := TTMSFNCUtils.MulDivInt(CellAppearance.FixedLayout.Font.Height, M, D);
    CellAppearance.NormalLayout.Font.Height := TTMSFNCUtils.MulDivInt(CellAppearance.NormalLayout.Font.Height, M, D);
    CellAppearance.GroupLayout.Font.Height := TTMSFNCUtils.MulDivInt(CellAppearance.GroupLayout.Font.Height, M, D);
    CellAppearance.SummaryLayout.Font.Height := TTMSFNCUtils.MulDivInt(CellAppearance.SummaryLayout.Font.Height, M, D);
    CellAppearance.SelectedLayout.Font.Height := TTMSFNCUtils.MulDivInt(CellAppearance.SelectedLayout.Font.Height, M, D);
    CellAppearance.FocusedLayout.Font.Height := TTMSFNCUtils.MulDivInt(CellAppearance.FocusedLayout.Font.Height, M, D);
    CellAppearance.FixedSelectedLayout.Font.Height := TTMSFNCUtils.MulDivInt(CellAppearance.FixedSelectedLayout.Font.Height, M, D);
    CellAppearance.BandLayout.Font.Height := TTMSFNCUtils.MulDivInt(CellAppearance.BandLayout.Font.Height, M, D);
    DefaultRowHeight := TTMSFNCUtils.MulDivSingle(DefaultRowHeight, M, D);
    DefaultColumnWidth := TTMSFNCUtils.MulDivSingle(DefaultColumnWidth, M, D);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.ClearColumns;
begin
  Columns.Clear;
end;

procedure TTMSFNCDataGridRenderer.ClearFocus;
begin
  {$IFDEF FMXMOBILE}
  if FilterPopup.IsOpen then
  begin
    if FilterListBox.IsFocused then
      FilterListBox.ResetFocus;

    if FilterListBox.TreeView.IsFocused then
      FilterListBox.TreeView.ResetFocus;

    if FilterEdit.IsFocused then
      FilterEdit.ResetFocus;

    if FilterTypeComboBox.IsFocused then
      FilterTypeComboBox.ResetFocus;

    if FilterClearButton.IsFocused then
      FilterClearButton.ResetFocus;

    if FilterSortAscendingButton.IsFocused then
      FilterSortAscendingButton.ResetFocus;

    if FilterSortDescendingButton.IsFocused then
      FilterSortDescendingButton.ResetFocus;

    if FilterContainer.IsFocused then
      FilterContainer.ResetFocus;
  end;
  {$ENDIF}
end;

procedure TTMSFNCDataGridRenderer.ClearSelection;
begin
  FFocusedCell := MakeCell(-1, -1);
  FFirstCell := MakeCell(-1, -1);
  FSaveCell := FFocusedCell;
  FRealCell := FFocusedCell;
  SelectedCellDictionary.Clear;
  SelectedColumnDictionary.Clear;
  SelectedRowDictionary.Clear;
  Selection := MakeCellRange(-1, -1, -1, -1);
  Display;
end;

procedure TTMSFNCDataGridRenderer.DeleteColumnData(AColumn: Integer);
var
  k: TTMSFNCDataGridCellCoord;
begin
  inherited;
  SelectedColumnDictionary.Remove(AColumn);
  for k in SelectedCellDictionary.Keys do
  begin
    if k.Column = AColumn then
      SelectedCellDictionary.Remove(k);
  end;
end;

procedure TTMSFNCDataGridRenderer.DeleteRowData(ARow: Integer);
var
  k: TTMSFNCDataGridCellCoord;
begin
  inherited;
  SelectedRowDictionary.Remove(ARow);
  for k in SelectedCellDictionary.Keys do
  begin
    if k.Row = ARow then
      SelectedCellDictionary.Remove(k);
  end;
end;

destructor TTMSFNCDataGridRenderer.Destroy;
begin
  FHost := nil;
  FColumns.Free;

  FreeAndNil(FCellLayout);
  FreeAndNil(FFindDialog);
  FreeAndNil(FReplaceDialog);
  FreeAndNil(FInplaceEditor);
  FreeAndNil(FFilterTimer);
  FreeAndNil(FFilterExitTimer);
  FreeAndNil(FScrollTimer);
  FreeAndNil(FAnimateTimer);
  FreeAndNil(FDragTimer);
  FreeAndNil(FLookupTimer);
  FreeAndNil(FFilterTypeComboBox);
  FreeAndNil(FFilterListBox);
  FreeAndNil(FFilterEdit);
  FreeAndNil(FFilterClearButton);
  FreeAndNil(FFilterSortAscendingButton);
  FreeAndNil(FFilterSortDescendingButton);
  FreeAndNil(FFilterContainer);
  FreeAndNil(FFilterPopup);
  FreeAndNil(FCommentPopup);

  FFilterAppearance.Free;
  FDragAppearance.Free;

  FPadding.Free;
  FStroke.Free;
  FGlobalFont.Free;
  FCellAppearance.Free;
  FNormalCellDisplay.Free;
  FNormalCellList.Free;
  FMergedCells.Free;
  FreeAndNil(FSelectedCellDictionary);
  FreeAndNil(FSelectedColumnDictionary);
  FreeAndNil(FSelectedRowDictionary);
  FFixedRowCellDisplay.Free;
  FFixedRowCellList.Free;
  FFixedRootCellDisplay.Free;
  FFixedRootCellList.Free;
  FFixedColumnCellDisplay.Free;
  FFixedColumnCellList.Free;

  if Assigned(FCustomVerticalScrollBar) then
    FCustomVerticalScrollBar.Free;

  if Assigned(FCustomHorizontalScrollBar) then
    FCustomHorizontalScrollBar.Free;

  if Assigned(FVerticalScrollBar) then
    FVerticalScrollBar.Free;

  if Assigned(FHorizontalScrollBar) then
    FHorizontalScrollBar.Free;

  FreeAndNil(FControlWrapperList);

  inherited;
end;

function TTMSFNCDataGridRenderer.GetTotalColumnOffset: Double;
var
  I: Integer;
begin
  Result := 0;
  if StartColumn > ColumnCount div 2 then
  begin
    for I := ColumnCount - 1 downto StartColumn do
      Result := Result + ColumnWidths[I];

    Result := TotalColumnWidth - Result;
  end
  else
  begin
    for I := 0 to StartColumn - 1 do
      Result := Result + ColumnWidths[I];
  end;
end;

function TTMSFNCDataGridRenderer.GetVerticalScrollPosition: Double;
var
  vVal, scrollv: Double;
begin
  vVal := GetVScrollValue;

  if ScrollMode = gsmCellScrolling then
  begin
    scrollv := RowPositions[Round(vval)];
    vVal := scrollv;
  end;

  Result := vVal;
end;

function TTMSFNCDataGridRenderer.GetVScrollValue: Single;
begin
  Result := FLocalVScrollValue;

  if HasCustomScrollBars and Assigned(CustomVerticalScrollBar) then
    Result := Min(CustomVerticalScrollBar.Max - CustomVerticalScrollBar.PageSize, Max(0, CustomVerticalScrollBar.Value))
  else if Assigned(VerticalScrollBar) then
  begin
    {$IFDEF FMXLIB}
    Result := Min(VerticalScrollBar.Max - VerticalScrollBar.ViewportSize, Max(0, VerticalScrollBar.Value));
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    Result := Min(VerticalScrollBar.Max - VerticalScrollBar.PageSize, Max(0, Round(VerticalScrollBar.Position)));
    {$ENDIF}
  end;
end;

function TTMSFNCDataGridRenderer.GetVVisible: Boolean;
begin
  Result := False;
  if HasCustomScrollBars and Assigned(CustomVerticalScrollBar) then
    Result := CustomVerticalScrollBar.Visible
  else if Assigned(VerticalScrollBar) then
    Result := VerticalScrollBar.Visible;
end;

procedure TTMSFNCDataGridRenderer.GoToCell(ACell: TTMSFNCDataGridCellCoord;
  ASelect: Boolean);
begin
  inherited;
  if ASelect then
    SelectCell(ACell)
  else
    Navigate(ACell, True);
end;

procedure TTMSFNCDataGridRenderer.GoToPage(APageIndex: Integer);
var
  PageSize: Integer;
  TargetTopRow: Integer;
begin
  PageSize := GetPageScrollSize;

  if (PageSize = 0) or (RowCount = 0) then
    Exit;

  APageIndex := Max(0, Min(APageIndex, GetPageCount - 1));

  TargetTopRow := APageIndex * PageSize;
  TopRow := TargetTopRow;
end;

function TTMSFNCDataGridRenderer.GetVMax: Single;
begin
  Result := 0;
  if HasCustomScrollBars and Assigned(CustomVerticalScrollBar) then
    Result := CustomVerticalScrollBar.Max
  else if Assigned(VerticalScrollBar) then
    Result := VerticalScrollBar.Max
end;

function TTMSFNCDataGridRenderer.GetVViewPortSize: Single;
begin
  Result := 0;
  if HasCustomScrollBars and Assigned(CustomVerticalScrollBar) then
    Result := CustomVerticalScrollBar.PageSize
  else if Assigned(VerticalScrollBar) then
  begin
    {$IFDEF FMXLIB}
    Result := VerticalScrollBar.ViewportSize;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    Result := VerticalScrollBar.PageSize;
    {$ENDIF}
  end;
end;

function TTMSFNCDataGridRenderer.GetVerticalTextAligns(AColumn,
  ARow: Integer): TTMSFNCGraphicsTextAlign;
var
  l: TTMSFNCDataGridCellLayout;
begin
  Result := gtaLeading;
  l := GetCellAppearance(GetCellState(MakeCell(AColumn, ARow)), MakeCell(AColumn, ARow));
  if Assigned(l) then
    Result := l.VerticalTextAlign;
end;

function TTMSFNCDataGridRenderer.GetVerticalScrollBar: TScrollBar;
begin
  Result := FVerticalScrollBar;
end;

function TTMSFNCDataGridRenderer.GetTotalRowOffset: Double;
var
  I: Integer;
begin
  Result := 0;
  if StartRow > RowCount div 2 then
  begin
    for I := RowCount - 1 downto StartRow do
      Result := Result + RowHeights[I];

    Result := TotalRowHeight - Result;
  end
  else
  begin
    for I := 0 to StartRow - 1 do
      Result := Result + RowHeights[I];
  end;
end;

procedure TTMSFNCDataGridRenderer.DrawDisplay(AGraphics: TTMSFNCGraphics; ADisplay: TTMSFNCDataGridDisplayList);
var
  I: Integer;
  cache: TTMSFNCDataGridCell;
begin
  if ADisplay.Count = 0 then
    Exit;

  for I := 0 to ADisplay.Count - 1 do
  begin
    cache := ADisplay[I];
    if IsCellDisplayList(ADisplay) then
      DrawCell(AGraphics, cache);
  end;
end;

procedure TTMSFNCDataGridRenderer.DrawScrollBars(AGraphics: TTMSFNCGraphics);
begin
  if {$IFNDEF WEBLIB}not BlockScrollingUpdate and {$ENDIF}HasCustomScrollBars and not HasValidHost and Assigned(FCustomVerticalScrollBar) and Assigned(FCustomHorizontalScrollBar) then
  begin
    if FCustomVerticalScrollBar.Visible then
      FCustomVerticalScrollBar.DrawEx(AGraphics);

    if FCustomHorizontalScrollBar.Visible then
      FCustomHorizontalScrollBar.DrawEx(AGraphics);
  end;
end;

procedure TTMSFNCDataGridRenderer.EditCell(ACell: TTMSFNCDataGridCellCoord; ACellStart: TTMSFNCDataGridEditCellStart = gcsNone; AInsertKey: Boolean = False; AValue: string = ''; AKey: Word = 0);
var
  ins, c, a: Boolean;
  r: TRectF;
  v: TValue;
  cl: TTMSFNCDataGridColumn;
  ct: TTMSFNCGraphicsColor;
  vl: TTMSFNCDataGridCellValue;
  p: TTMSFNCDataGridCellParent;
  ctl: TTMSFNCDataGridCell;
  {$IFNDEF FMXLIB}
  fc: TTMSFNCGraphicsColor;
  {$ENDIF}

  function CreateCaretPos(ALine, APos: Integer): TTMSFNCDataGridCaretPosition;
  begin
    {$IFDEF FMXLIB}
    Result.Line := ALine;
    Result.Pos := APos;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    Result.X := ALine;
    Result.Y := APos;
    {$ENDIF}
  end;

  function TryExecuteEditor: TValue;
  var
    AContext: TRttiContext;
    rt: TRttiType;
    m: TRttiMethod;
  begin
    Result := TValue.Empty;
    AContext := TRttiContext.Create;
    try
      rt := AContext.GetType(FInplaceEditor.ClassInfo);
      m := rt.GetMethod('Execute');
      if Assigned(m) then
        Result := m.Invoke(FInplaceEditor, []);
    finally
      AContext.Free;
    end;
  end;

begin
  if IsCellDisabled(ACell) then
    Exit;

  p := GetParentHost;

  FInplaceEditorTarget := getCellValue;
  DoGetInplaceEditorTarget(ACell, FInplaceEditorTarget);

  case FInplaceEditorTarget of
    getCellValue:
    begin
      if AInsertKey then
        vl := AValue
      else
        vl := Cells[ACell.Column, ACell.Row];
    end;
    getFillColor:
    begin
      if HasLayout[ACell.Column, ACell.Row] then
      begin
        if Layouts[ACell.Column, ACell.Row].Fill.Kind = gfkSolid then
        begin
          ct := Layouts[ACell.Column, ACell.Row].Fill.Color;
          vl := CellValueToTValue(ct);
        end;
      end;
    end;
  end;

  ins := true;
  DoBeforeOpenInplaceEditor(ACell, vl, ins);
  if ins then
  begin
    if Assigned(FInplaceEditor) then
    begin
      FInplaceEditor.Free;
      FInplaceEditor := nil;
    end;

    DoGetInplaceEditorType(ACell, FInplaceEditorType);

    case FInplaceEditorType of
      getMixedCaseEdit, getUppercaseEdit, getSignedFloatEdit, getFloatEdit, getSignedNumericEdit,
      getHexEdit, getMoneyEdit, getLowerCaseEdit, getNumericEdit,
      getValidCharsEdit, getAlphaNumericEdit, getEdit:
      begin
        FInplaceEditor := TTMSFNCDataGridEdit.Create(p);
        case FInplaceEditorType of
          getNumericEdit: (FInplaceEditor as TTMSFNCDataGridEdit).EditType := etNumeric;
          getSignedNumericEdit: (FInplaceEditor as TTMSFNCDataGridEdit).EditType := etSignedNumeric;
          getFloatEdit: (FInplaceEditor as TTMSFNCDataGridEdit).EditType := etFloat;
          getSignedFloatEdit: (FInplaceEditor as TTMSFNCDataGridEdit).EditType := etSignedFloat;
          getUppercaseEdit: (FInplaceEditor as TTMSFNCDataGridEdit).EditType := etUppercase;
          getMixedCaseEdit: (FInplaceEditor as TTMSFNCDataGridEdit).EditType := etMixedCase;
          getLowerCaseEdit: (FInplaceEditor as TTMSFNCDataGridEdit).EditType := etLowerCase;
          getMoneyEdit: (FInplaceEditor as TTMSFNCDataGridEdit).EditType := etMoney;
          getHexEdit: (FInplaceEditor as TTMSFNCDataGridEdit).EditType := etHex;
          getAlphaNumericEdit: (FInplaceEditor as TTMSFNCDataGridEdit).EditType := etAlphaNumeric;
          getValidCharsEdit: (FInplaceEditor as TTMSFNCDataGridEdit).EditType := etValidChars;
        end;
      end;
      getComboBox:
      begin
        FInplaceEditor := TTMSFNCDataGridComboBox.Create(p);
        {$IFDEF CMNWEBLIB}
        (FInplaceEditor as TTMSFNCDataGridComboBox).Style := csDropDownList;
        {$ENDIF}
      end;
      getComboEdit:
      begin
        FInplaceEditor := TTMSFNCDataGridComboEdit.Create(p);
        {$IFDEF CMNWEBLIB}
        (FInplaceEditor as TTMSFNCDataGridComboEdit).Style := csDropDown;
        {$ENDIF}
      end;
      getSpinBox: FInplaceEditor := TTMSFNCDataGridSpinBox.Create(p);
      getDatePicker: FInplaceEditor := TTMSFNCDataGridDatePicker.Create(p);
      getColorPicker:
      begin
        FInplaceEditor := TTMSFNCDataGridColorPicker.Create(p);
        (FInplaceEditor as TTMSFNCDataGridColorPicker).Mode := csmExtended;
      end;
      getTrackBar: FInplaceEditor := TTMSFNCDataGridTrackBar.Create(p);
      getMemo: FInplaceEditor := TTMSFNCDataGridMemo.Create(p);
      getHTMLEditor: FInplaceEditor := TTMSFNCHTMLEditor.Create(p);
      getCustom:
      begin
        FInplaceEditorCustomClass := nil;
        DoGetInplaceEditorClass(ACell, FInplaceEditorCustomClass);
        if Assigned(FInplaceEditorCustomClass) then
          FInplaceEditor := FInplaceEditorCustomClass.Create(p);
      end;
    end;

    a := True;
    if Assigned(FInplaceEditor) and not (FInplaceEditor is TTMSFNCCustomComponent) then
    begin
      r := GetInplaceEditorRect(ACell);
      a := a and not RectIsEmpty(r);
    end;

    if Assigned(FInplaceEditor) and a then
    begin
      {$IFDEF FMXLIB}
      FInplaceEditor.OnApplyStyleLookup := ApplyInplaceEditorStyleLookup;
      {$ENDIF}

      if FInplaceEditor is TCustomEdit then
      begin
        ctl := GetCellObjectForCell(ACell);
        if Assigned(ctl) then
        begin
          {$IFDEF VCLLIB}
          FInplaceEditor.Visible := False;
          FInplaceEditor.Parent := p;
          TCustomEditOpen(FInplaceEditor as TCustomEdit).BorderStyle := bsNone;
          {$ENDIF}
          {$IFNDEF FMXLIB}
          fc := TCustomEditOpen(FInplaceEditor as TCustomEdit).Font.Color;
          {$ENDIF}

          TCustomEditOpen(FInplaceEditor as TCustomEdit).Font.Assign(ctl.Layout.Font);

          {$IFNDEF FMXLIB}
          TCustomEditOpen(FInplaceEditor as TCustomEdit).Font.Color := fc;
          case ctl.Layout.TextAlign of
            gtaCenter: TCustomEditOpen(FInplaceEditor as TCustomEdit).Alignment := taCenter;
            gtaLeading: TCustomEditOpen(FInplaceEditor as TCustomEdit).Alignment := taLeftJustify;
            gtaTrailing: TCustomEditOpen(FInplaceEditor as TCustomEdit).Alignment := taRightJustify;
          end;
          {$ENDIF}

          {$IFDEF FMXLIB}
          TCustomEditOpen(FInplaceEditor as TCustomEdit).VertTextAlign := TTextAlign.Leading;

          case ctl.Layout.TextAlign of
            gtaCenter: TCustomEditOpen(FInplaceEditor as TCustomEdit).TextSettings.HorzAlign := TTextAlign.Center;
            gtaLeading: TCustomEditOpen(FInplaceEditor as TCustomEdit).TextSettings.HorzAlign := TTextAlign.Leading;
            gtaTrailing: TCustomEditOpen(FInplaceEditor as TCustomEdit).TextSettings.HorzAlign := TTextAlign.Trailing;
          end;
          {$ENDIF}

          {$IFDEF VCLLIB}
          FInplaceEditor.Parent := nil;
          FInplaceEditor.Visible := True;
          {$ENDIF}
        end;
      end;

      if FInplaceEditor is TCustomMemo then
      begin
        ctl := GetCellObjectForCell(ACell);
        if Assigned(ctl) then
        begin
          {$IFDEF VCLLIB}
          FInplaceEditor.Visible := False;
          FInplaceEditor.Parent := p;
          {$ENDIF}
          {$IFNDEF FMXLIB}
          fc := TCustomMemoOpen(FInplaceEditor as TCustomMemo).Font.Color;
          {$ENDIF}

          TCustomMemoOpen(FInplaceEditor as TCustomMemo).Font.Assign(ctl.Layout.Font);

          {$IFNDEF FMXLIB}
          TCustomMemoOpen(FInplaceEditor as TCustomMemo).Font.Color := fc;
          {$ENDIF}

          {$IFDEF VCLLIB}
          FInplaceEditor.Parent := nil;
          FInplaceEditor.Visible := True;
          {$ENDIF}
        end;
      end;

      cl := GetColumn(ACell.Column);
      if Assigned(cl) and (gcsEditorItems in cl.Settings) then
      begin
        if (FInplaceEditor is TCustomComboBox) {$IFDEF FMXLIB}or (FInplaceEditor is TCustomComboEdit){$ENDIF} then
        begin
          {$IFDEF VCLLIB}
          FInplaceEditor.Visible := False;
          FInplaceEditor.Parent := p;
          {$ENDIF}

          if FInplaceEditor is TCustomComboBox then
            TCustomComboBoxOpen(FInplaceEditor as TCustomComboBox).Items.Assign(cl.EditorItems);

          {$IFDEF FMXLIB}
          if FInplaceEditor is TCustomComboEdit then
            (FInplaceEditor as TCustomComboEdit).Items.Assign(cl.EditorItems);
          {$ENDIF}

          {$IFDEF VCLLIB}
          FInplaceEditor.Parent := nil;
          FInplaceEditor.Visible := True;
          {$ENDIF}
        end
      end;

      if not (FInplaceEditor is TTMSFNCCustomComponent) then
      begin
        if FInplaceEditor is TCustomMemo then
        begin
          {$IFDEF FMXLIB}
          (FInplaceEditor as TCustomMemo).StyledSettings := [];
          {$ENDIF}
        end;

        if FInplaceEditor is TCustomEdit then
        begin
          {$IFDEF FMXLIB}
          (FInplaceEditor as TCustomEdit).StyledSettings := [];
          {$ENDIF}
        end;

        TTMSFNCDataGridInplaceEditorOpen(FInplaceEditor).OnKeyDown := @DoEditorKeyDown;
        TTMSFNCDataGridInplaceEditorOpen(FInplaceEditor).OnKeyUp := @DoEditorKeyUp;
        {$IFNDEF FMXLIB}
        TTMSFNCDataGridInplaceEditorOpen(FInplaceEditor).OnKeyPress := DoEditorKeyPress;
        {$ENDIF}
      end;

      if (FInplaceEditor is TTMSFNCDataGridEdit) and (Options.Editing.AutoComplete.&Type <> gactDisabled) then
      begin
        (FInplaceEditor as TTMSFNCDataGridEdit).Lookup.DisplayList.Assign(Options.Editing.AutoComplete.Items);
        (FInplaceEditor as TTMSFNCDataGridEdit).AutoComplete := Options.Editing.AutoComplete.&Type = gactNormal;
        (FInplaceEditor as TTMSFNCDataGridEdit).Lookup.Enabled := Options.Editing.AutoComplete.&Type <> gactNormal;
      end;

      FEditingCell := ACell;

      if not (FInplaceEditor is TTMSFNCCustomComponent) then
      begin
        FInplaceEditor.Parent := p;
        UpdateInplaceEditorPosition;
      end;

      DoGetInplaceEditorProperties(ACell, FInplaceEditor, FInplaceEditorType);
      DoCellEditGetData(ACell, FInplaceEditor, vl);

      SetEditorValue(FInplaceEditor, vl);

      if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
        (Adapter as TTMSFNCDataGridRendererDataAdapter).EditCell(ACell);

      c := False;

      if (FInplaceEditor is TTMSFNCCustomComponent) then
      begin
        v := TryExecuteEditor;
        if v.IsType<TModalResult> then
        begin
          FMouseDown := False;
          CloseInplaceEditor(v.AsType<TModalResult> = mrCancel);
          c := True;
        end;
      end
      else
      begin
        if (Options.Editing.DirectDropDown) then
        begin
          if (FInplaceEditor is TCustomComboBox) then
          begin
            {$IFDEF FMXLIB}
            (FInplaceEditor as TCustomComboBox).DropDown;
            {$ENDIF}

            {$IFDEF CMNWEBLIB}
            (FInplaceEditor as TCustomComboBox).DroppedDown := True;
            {$ENDIF}
          end
          {$IFDEF FMXLIB}
          else if (FInplaceEditor is TCustomComboEdit) then
          begin
            (FInplaceEditor as TCustomComboEdit).DropDown;
          end
          {$ENDIF}
          else if FInplaceEditor is TTMSFNCCustomPicker then
          begin
            (FInplaceEditor as TTMSFNCCustomPicker).DropDown;
          end;
        end;

        if FInplaceEditor.CanFocus then
          TTMSFNCDataGridInplaceEditorOpen(FInplaceEditor).SetFocus;

        if FInplaceEditor is TCustomEdit then
        begin
          try
            if AInsertKey then
            begin
              (FInplaceEditor as TCustomEdit).SelStart := Length((FInplaceEditor as TCustomEdit).Text);
              (FInplaceEditor as TCustomEdit).SelLength := 0;
            end
            else
            begin
              case ACellStart of
                gcsNone:
                begin
                  (FInplaceEditor as TCustomEdit).SelStart := 0;
                  (FInplaceEditor as TCustomEdit).SelLength := Length((FInplaceEditor as TCustomEdit).Text);
                end;
                gcsStart:
                begin
                  (FInplaceEditor as TCustomEdit).SelStart := 0;
                  (FInplaceEditor as TCustomEdit).SelLength := 0;
                end;
                gcsEnd:
                begin
                  (FInplaceEditor as TCustomEdit).SelStart := Length((FInplaceEditor as TCustomEdit).Text);
                  (FInplaceEditor as TCustomEdit).SelLength := 0;
                end;
              end;
            end;
          finally
          end;
        end
        else if FInplaceEditor is TCustomMemo then
        begin
          if AInsertKey then
          begin
            if (FInplaceEditor as TCustomMemo).Lines.Count > 0 then
              (FInplaceEditor as TCustomMemo).CaretPosition := CreateCaretPos((FInplaceEditor as TCustomMemo).Lines.Count - 1, Length((FInplaceEditor as TCustomMemo).Lines[(FInplaceEditor as TCustomMemo).Lines.Count - 1]))
            else
              (FInplaceEditor as TCustomMemo).CaretPosition := CreateCaretPos(0, 0);

            (FInplaceEditor as TCustomMemo).SelLength := 0;
          end
          else
          begin
            case ACellStart of
              gcsNone:
              begin
                (FInplaceEditor as TCustomMemo).CaretPosition := CreateCaretPos(0, 0);
                (FInplaceEditor as TCustomMemo).SelLength := Length((FInplaceEditor as TCustomMemo).Text);
              end;
              gcsStart:
              begin
                (FInplaceEditor as TCustomMemo).CaretPosition := CreateCaretPos(0, 0);
                (FInplaceEditor as TCustomMemo).SelLength := 0;
              end;
              gcsEnd:
              begin
                if (FInplaceEditor as TCustomMemo).Lines.Count > 0 then
                  (FInplaceEditor as TCustomMemo).CaretPosition := CreateCaretPos((FInplaceEditor as TCustomMemo).Lines.Count - 1, Length((FInplaceEditor as TCustomMemo).Lines[(FInplaceEditor as TCustomMemo).Lines.Count - 1]))
                else
                  (FInplaceEditor as TCustomMemo).CaretPosition := CreateCaretPos(0, 0);

                (FInplaceEditor as TCustomMemo).SelLength := 0;
              end;
            end;
          end;
        end;
      end;

      FInplaceEditorActive := not c;
    end;

    if FInplaceEditorActive then
      DoAfterOpenInplaceEditor(ACell, FInplaceEditor, vl);
  end;
end;

procedure TTMSFNCDataGridRenderer.&Export(AGraphics: TTMSFNCGraphics; ARect: TRectF; ASelection: TTMSFNCDataGridCellCoordRange;
  ARepeatFixedColumns: Boolean = False; ARepeatFixedRows: Boolean = False; AFitToPage: Boolean = True; ASkipSaveBounds: Boolean = False;
  AOnNewPage: TTMSFNCDataGridRendererExportNewPageCallback = nil; AOnGetPageIndex: TTMSFNCDataGridRendererExportGetPageIndexCallback = nil);
var
  c, r: Integer;
  cellstr: String;
  cwr, rhr, cw, rh, cwrt, rhrt: Single;
  pgi: Integer;
  I: Integer;
  xr, yr: Single;
  bc, br: Integer;
  cs, rs: Integer;
  cellr: TRectF;
  cls: TTMSFNCDataGridCellClass;
  off: Single;
  x, y: Single;
  rSt, csave, cSt: Integer;
  chk: Boolean;
  FFixedRowsSave, FFixedColumnsSave: Integer;
  FCSave, FRSave: Integer;
  FFixedRowGet, FFixedColGet: Boolean;
  FSetRSave, FSetCSave: Boolean;
  xs, ys: Single;
  ph, pw: Single;
  xts, yts: Single;
  FSaveXBounds: Single;
  FSaveYBounds: Single;
  sc: Single;
  ci: TTMSFNCDataGridCell;
  d: TTMSFNCDataGridCellItem;
  ck: TTMSFNCDataGridCellKind;
  mcpp: TDictionary<TTMSFNCDataGridCellCoord, Integer>;
  needspage: Boolean;

  function GetFixedPrintWidth: Single;
  var
    I: Integer;
  begin
    Result := 0;
    for I := 0 to FixedColumnCount - 1 do
      Result := Result + ColumnWidths[I] * sc;
  end;

  function GetFixedPrintHeight: Single;
  var
    I: Integer;
  begin
    Result := 0;
    for I := 0 to FixedRowCount - 1 do
      Result := Result + RowHeights[I] * sc;
  end;

  function GetScale: Single;
  var
    w, K: Integer;
  begin
    Result := 1;
    if AFitToPage then
    begin
      w := 0;
      for K := 0 to ColumnCount - 1 do
        w := w + Round(ColumnWidths[K]);

      if w > 0 then
        Result := (ARect.Right - ARect.Left) / w;
    end;
  end;

  procedure GetCellMergeInfo(AColumn, ARow: Integer; var ABaseColumn, ABaseRow, AColumnSpan, ARowSpan: Integer);
  var
    mi: TTMSFNCDataGridCellMergeInfo;
  begin
    if HasMergeInfo[AColumn, ARow] then
    begin
      mi := MergeInfo[AColumn, ARow];
      ABaseColumn := c - mi.ColumnOffset;
      ABaseRow := r - mi.RowOffset;
      AColumnSpan := mi.ColumnSpan;
      ARowSpan := mi.RowSpan;
    end;
  end;

begin
  mcpp := TDictionary<TTMSFNCDataGridCellCoord, Integer>.Create;
  ExportNotification(gesExportStart,-1);
  try
    AGraphics.BitmapContainer := BitmapContainer;
    sc := GetScale;
    if Assigned(AOnNewPage) then
      AOnNewPage();

    needspage := False;

    x := ARect.Left;
    y := ARect.Top;

    rSt := ASelection.StartCell.Row;
    FFixedRowsSave := 0;
    FFixedColumnsSave := 0;
    cSave := -1;
    cSt := -1;
    FFixedRowGet := False;
    FFixedColGet := False;
    FSetRSave := True;
    FSetCSave := True;

    pw := GetFixedPrintWidth;
    ph := GetFixedPrintHeight;

    FSaveXBounds := ARect.Left;

    for I := ASelection.StartCell.Column to ASelection.StopCell.Column do
    begin
      if (CompareValue(Floor(FSaveXBounds + ColumnWidths[I] * sc), ARect.Right) = GreaterThanValue) then
        Break;

      FSaveXBounds := FSaveXBounds + ColumnWidths[I] * sc;
    end;

    FSaveYBounds := ARect.Top;

    for I := ASelection.StartCell.Row to ASelection.StopCell.Row do
    begin
      if (CompareValue(Floor(FSaveYBounds + RowHeights[I] * sc), ARect.Bottom) = GreaterThanValue) then
        Break;

      FSaveYBounds := FSaveYBounds + RowHeights[I] * sc;
    end;

    c := ASelection.StartCell.Column;

    FCSave := -1;
    while c <= ASelection.StopCell.Column do
    begin
      if cSt = -1 then
        cSt := c;

      if ARepeatFixedColumns and (FixedColumnCount > 0) then
      begin
        if (FFixedColumnsSave < FixedColumnCount) then
        begin
          FFixedColGet := True;
          if FSetCSave then
          begin
            FSetCSave := False;
            FCSave := cSt;
          end;

          cst := FFixedColumnsSave;
          c := cst;
          Inc(FFixedColumnsSave);
        end
        else if FFixedColGet then
        begin
          FFixedColGet := False;
          cSt := FCSave;
          c := FCSave;

          if cst < FixedColumnCount then
          begin
            cst := FixedColumnCount;
            c := cst;
          end;
        end;
      end;

      cw := ColumnWidths[c] * sc;

      r := rst;
      FRSave := rst;
      while r <= ASelection.StopCell.Row do
      begin
        if needspage then
        begin
          if Assigned(AOnNewPage) then
            AOnNewPage();

          needspage := False;
        end;

        if ARepeatFixedRows and (FixedRowCount > 0) then
        begin
          if (FFixedRowsSave < FixedRowCount) then
          begin
            FFixedRowGet := True;
            if FSetRSave then
            begin
              FSetRSave := False;
              FRSave := rSt;
            end;

            rSt := FFixedRowsSave;
            r := rst;
            Inc(FFixedRowsSave);
          end
          else if FFixedRowGet then
          begin
            FFixedRowGet := False;
            rSt := FRSave;
            r := FRSave;

            if rst < FixedRowCount then
            begin
              rst := FixedRowCount;
              r := rst;
            end;
          end;
        end;

        ExportNotification(gesExportNewRow, r);

        bc := c;
        br := r;
        cs := 0;
        rs := 0;

        rh := RowHeights[r] * sc;

        GetCellMergeInfo(c, r, bc, br, cs, rs);

        chk := (c = bc) and (r = br);

        if Assigned(AOnGetPageIndex) then
        begin
          pgi := AOnGetPageIndex();
          if chk then
            mcpp.AddOrSetValue(MakeCell(bc, br), pgi)
          else if (bc <> c) and (br <> r) then
            chk := mcpp.ContainsKey(MakeCell(bc, br)) and (mcpp[MakeCell(bc, br)] <> pgi);
        end;

        if chk then
        begin
          cellstr := Strings[bc, br];
          cwr := cw;
          rhr := rh;

          cwrt := 0;
          for I := bc to bc + cs - 1 do
            cwrt := cwrt + ColumnWidths[I] * sc;

          if cwrt > 0 then
            cwr := cwrt;

          rhrt := 0;
          for I := br to br + rs - 1 do
            rhrt := rhrt + RowHeights[I] * sc;

          if rhrt > 0 then
            rhr := rhrt;

          xr := 0;
          for I := bc to c - 1 do
            xr := xr + ColumnWidths[I] * sc;

          yr := 0;
          for I := br to r - 1 do
            yr := yr + RowHeights[I] * sc;

          xts := x - xr;
          yts := y - yr;
          xs := xts + cwr;
          ys := yts + rhr;

          if not ASkipSaveBounds then
          begin
            if FSaveXBounds > -1 then
              xs := Min(FSaveXBounds, xs);
          end;

          pgi := 0;
          if Assigned(AOnGetPageIndex) then
            pgi := AOnGetPageIndex;

          if ((pgi = 0) or ARepeatFixedColumns) and (c > FixedColumnCount) then
            xts := Max(ARect.Left + pw, xts)
          else
            xts := Max(ARect.Left, xts);

          if not ASkipSaveBounds then
          begin
            if FSaveYBounds > -1 then
              ys := Min(FSaveYBounds, ys);
          end;

          if ((pgi = 0) or ARepeatFixedRows) and (r > FixedRowCount) then
            yts := Max(ARect.Top + ph, yts)
          else
            yts := Max(ARect.Top, yts);

          if (bc = FixedColumnCount) then
          begin
            if RowTypes[br] = grtDefault then
              off := (20 * Max(0, (RowNodeLevel[br] - 1)))
            else
              off := (20 * Max(0, RowNodeLevel[br]));

            xts := xts + off;
          end;

          cellr := RectF(xts, yts, xs, ys);

          if IsCellVisible(MakeCell(bc, br)) then
          begin
            cls := GetBaseCellClass(MakeCell(bc, br));
            DoGetCellClass(bc, br, cls);
            if Assigned(cls) then
            begin
              ck := GetCellKind(MakeCell(bc, br));
              d := CellData[bc, br];
              ci := cls.Create(cellr, bc, br, d, ck, Self);

              if Assigned(ci) then
              begin
                ci.BeforeExport;
                try
                  ConfigureCell(ci);
                  ApplyCellScale(ci, sc);
                  DrawCell(AGraphics, ci);
                finally
                  ci.AfterExport;
                  ci.Free;
                end;
              end;
            end;
          end;
        end;

        y := y + rh;
        if CompareValue(Floor(y + RowHeights[r + 1] * sc), ARect.Bottom) = GreaterThanValue then
        begin
          FSaveYBounds := ARect.Bottom;
          Break;
        end;

        Inc(r);
      end;

      y := ARect.Top;
      x := x + cw;
      Inc(c);

      FFixedRowsSave := 0;

      if (bc + cs = ASelection.StopCell.Column) and (br + rs = ASelection.StopCell.Row) then
        Break;

      if (CompareValue(Floor(x + ColumnWidths[c] * sc) , ARect.Right) = GreaterThanValue) or (c = ASelection.StopCell.Column + 1) then
      begin
        FSaveXBounds := ARect.Right;

        rst := r + 1;

        if cSave = -1 then
          cSave := c;

        if r = ASelection.StopCell.Row + 1 then
        begin
          c := cSave;
          cst := -1;
          rst := ASelection.StartCell.Row;
          cSave := -1;
        end
        else
          c := cst;

        FFixedRowsSave := 0;
        FFixedColumnsSave := 0;
        FSetRSave := True;
        FSetCSave := True;

        if (c < ASelection.StopCell.Column + 1) then
        begin
          needspage := True;

          x := ARect.Left;
          y := ARect.Top;
        end;
      end;
    end;
  finally
    mcpp.Free;
    ExportNotification(gesExportDone,-1);
  end;
end;

procedure TTMSFNCDataGridRenderer.&Export(AGraphics: TTMSFNCGraphics);
begin
  &Export(AGraphics, RectF(0, 0, Width, Height), MakeCellRange(0, 0, ColumnCount - 1, RowCount - 1));
end;

procedure TTMSFNCDataGridRenderer.ExportNotification(
  AState: TTMSFNCDataGridExportState; ARow: Integer);
begin
  inherited;

  if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).ExportNotification(AState, ARow);
end;

procedure TTMSFNCDataGridRenderer.HandleEnter;
var
  cl: TTMSFNCDataGridCellCoord;
begin
  if (FocusedCell.Column = -1) and (FocusedCell.Row = -1) then
  begin
    if (FTabKey = gtkmNormal) then
      FocusedCell := MakeCell(FixedColumnCount, FixedRowCount)
    else if FTabKey = gtkmShift then
    begin
      cl.Column := ColumnCount - 1 - FixedRightColumnCount;
      cl.Row := RowCount - 1 - FixedBottomRowCount;
      FocusedCell := cl;
    end
    else
      Display;

    FTabKey := gtkmNone;
  end;
end;

procedure TTMSFNCDataGridRenderer.HandleExit;
begin
  ResetScrollBarCapture;
  FClearMouseDown := FClosing;
  FDblClick := False;
  FMouseDown := False;
  FMouseDblClick := False;
  FDragCell := MakeCell(-1, -1);
  FDragCellTo := MakeCell(-1, -1);
  FDragCellToObj := nil;
  Display;
end;

procedure TTMSFNCDataGridRenderer.DoDragTimer(Sender: TObject);
var
  contentr: TRectF;
begin
  if not Options.Mouse.AutoDragging then
  begin
    DragTimer.Enabled := False;
    Exit;
  end;

  contentr := GetContentRect;

  if (FX >= (contentr.Right - contentr.Left)) then
  begin
    if Options.Mouse.AutoScrollingSpeedMode = gasmPixels then
      FDragScrollCell.Column := FDragScrollCell.Column + Round(FX - (contentr.Right - contentr.Left)) * Options.Mouse.AutoScrollingSpeed
    else
      FDragScrollCell.Column := FDragScrollCell.Column + Options.Mouse.AutoScrollingSpeed;
    Navigate(FDragScrollCell);
  end
  else if FX <= 0 then
  begin
    if Options.Mouse.AutoScrollingSpeedMode = gasmPixels then
      FDragScrollCell.Column := FDragScrollCell.Column + Round(FX) * Options.Mouse.AutoScrollingSpeed
    else
      FDragScrollCell.Column := FDragScrollCell.Column - Options.Mouse.AutoScrollingSpeed;

    Navigate(FDragScrollCell);
  end;

  if (FY >= (contentr.Bottom - contentr.Top)) then
  begin
    if Options.Mouse.AutoScrollingSpeedMode = gasmPixels then
      FDragScrollCell.Row := FDragScrollCell.Row + Round(FY - (contentr.Bottom - contentr.Top)) * Options.Mouse.AutoScrollingSpeed
    else
      FDragScrollCell.Row := FDragScrollCell.Row + Options.Mouse.AutoScrollingSpeed;
    Navigate(FDragScrollCell);
  end
  else if FY <= 0 then
  begin
    if Options.Mouse.AutoScrollingSpeedMode = gasmPixels then
      FDragScrollCell.Row := FDragScrollCell.Row + Round(FY) * Options.Mouse.AutoScrollingSpeed
    else
      FDragScrollCell.Row := FDragScrollCell.Row - Options.Mouse.AutoScrollingSpeed;
    Navigate(FDragScrollCell);
  end;
end;

procedure TTMSFNCDataGridRenderer.DrawDragging(AGraphics: TTMSFNCGraphics);
var
  r: TRectF;
  pth: TTMSFNCGraphicsPath;
  sz: Single;
begin
  if Assigned(FDragCellToObj) then
  begin
    sz := DragAppearance.Size;

    case FDraggingMode of
      gdmRowDragging:
      begin
        r := FDragCellToObj.Rect;
        r := RectF(r.Right, r.Top + (r.Bottom - r.Top) / 2 - sz / 2, r.Right + sz, r.Top + (r.Bottom - r.Top) / 2 + sz / 2);

        AGraphics.Fill.Assign(DragAppearance.Fill);
        AGraphics.Stroke.Assign(DragAppearance.Stroke);
        pth := TTMSFNCGraphicsPath.Create;
        try
          pth.MoveTo(PointF(r.Left, r.Top + (r.Bottom - r.Top) / 2));
          pth.LineTo(PointF(r.Right, r.Top));
          pth.LineTo(PointF(r.Right, r.Bottom));
          pth.ClosePath;
          AGraphics.DrawPath(pth);
        finally
          pth.Free;
        end;
      end;
      gdmColumnDragging:
      begin
        r := FDragCellToObj.Rect;
        r := RectF(r.Left + (r.Right - r.Left) / 2 - sz / 2, r.Bottom, r.Left + (r.Right - r.Left) / 2 + sz / 2, r.Bottom + sz);

        AGraphics.Fill.Assign(DragAppearance.Fill);
        AGraphics.Stroke.Assign(DragAppearance.Stroke);
        pth := TTMSFNCGraphicsPath.Create;
        try
          pth.MoveTo(PointF(r.Left + (r.Right - r.Left) / 2, r.Top));
          pth.LineTo(PointF(r.Right, r.Bottom));
          pth.LineTo(PointF(r.Left, r.Bottom));
          pth.ClosePath;
          AGraphics.DrawPath(pth);
        finally
          pth.Free;
        end;
      end;
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.DrawEmptySpaces(AGraphics: TTMSFNCGraphics);
var
  lr, fr, r: TRectF;
  vs, hs: TScrollBar;
  vsc, hsc: TTMSFNCScrollBar;
  vsb, hsb, a: Boolean;
begin
  if not StretchScrollBars then
  begin
    lr := GetContentClipRect;
    fr := GetFullRect;
    AGraphics.Fill.Assign(CellAppearance.FixedLayout.Fill);
    AGraphics.Stroke.Assign(Stroke);

    vsc := CustomVerticalScrollBar;
    hsc := CustomHorizontalScrollBar;
    vs := VerticalScrollBar;
    hs := HorizontalScrollBar;

    if HasCustomScrollBars then
      vsb := Assigned(vsc) and vsc.Visible
    else
      vsb := Assigned(vs) and vs.Visible;

    if HasCustomScrollBars then
      hsb := Assigned(hsc) and hsc.Visible
    else
      hsb := Assigned(hs) and hs.Visible;

    if vsb then
    begin
      r := RectF({$IFDEF FMXWEBLIB}Int{$ENDIF}(lr.Right - 1){$IFDEF FMXWEBLIB} + 0.5{$ENDIF}, {$IFDEF FMXWEBLIB}Int{$ENDIF}(fr.Top){$IFDEF FMXWEBLIB} + 0.5{$ENDIF}, {$IFDEF FMXWEBLIB}Int{$ENDIF}(fr.Right + 1){$IFDEF FMXWEBLIB} + 0.5{$ELSE} + 1{$ENDIF}, {$IFDEF FMXWEBLIB}Int{$ENDIF}(fr.Top + 1) {$IFDEF FMXWEBLIB} + 0.5{$ELSE}{$ENDIF} + TotalFixedRowHeight);
      if (r.Right - r.Left > 0) and (r.Bottom - r.Top > 0) then
      begin
        a := True;
        DoBeforeDrawEmptySpace(AGraphics, r, gekColumnTop, a);
        if a then
        begin
          AGraphics.DrawRectangle(r);
          DoAfterDrawEmptySpace(AGraphics, r, gekColumnTop);
        end;
      end;

      r := RectF({$IFDEF FMXWEBLIB}Int{$ENDIF}(lr.Right - 1){$IFDEF FMXWEBLIB} + 0.5{$ENDIF}, {$IFDEF FMXWEBLIB}Int{$ENDIF}(lr.Bottom){$IFDEF FMXWEBLIB} + 0.5{$ENDIF} - TotalFixedBottomRowHeight, {$IFDEF FMXWEBLIB}Int{$ENDIF}(fr.Right + 1){$IFDEF FMXWEBLIB} + 0.5{$ELSE} + 1{$ENDIF}, {$IFDEF FMXWEBLIB}Int{$ENDIF}(lr.Bottom) {$IFDEF FMXWEBLIB} + 0.5{$ELSE}{$ENDIF});

      if (r.Right - r.Left > 0) and (r.Bottom - r.Top > 0) then
      begin
        a := True;
        DoBeforeDrawEmptySpace(AGraphics, r, gekColumnBottom, a);
        if a then
        begin
          AGraphics.DrawRectangle(r);
          DoAfterDrawEmptySpace(AGraphics, r, gekColumnBottom);
        end;
      end;
    end;

    if hsb then
    begin
      r := RectF({$IFDEF FMXWEBLIB}Int{$ENDIF}(lr.Right){$IFDEF FMXWEBLIB} + 0.5{$ENDIF} - TotalFixedRightColumnWidth, {$IFDEF FMXWEBLIB}Int{$ENDIF}(lr.Bottom - 1){$IFDEF FMXWEBLIB} + 0.5{$ENDIF}, {$IFDEF FMXWEBLIB}Int{$ENDIF}(lr.Right){$IFDEF FMXWEBLIB} + 0.5{$ELSE}{$ENDIF}, {$IFDEF FMXWEBLIB}Int{$ENDIF}(fr.Bottom + 1) {$IFDEF FMXWEBLIB} + 0.5{$ELSE} + 1{$ENDIF});
      if (r.Right - r.Left > 0) and (r.Bottom - r.Top > 0) then
      begin
        a := True;
        DoBeforeDrawEmptySpace(AGraphics, r, gekRowRight, a);
        if a then
        begin
          AGraphics.DrawRectangle(r);
          DoAfterDrawEmptySpace(AGraphics, r, gekRowRight);
        end;
      end;

      r := RectF({$IFDEF FMXWEBLIB}Int{$ENDIF}(fr.Left){$IFDEF FMXWEBLIB} + 0.5{$ENDIF}, {$IFDEF FMXWEBLIB}Int{$ENDIF}(lr.Bottom - 1){$IFDEF FMXWEBLIB} + 0.5{$ENDIF}, {$IFDEF FMXWEBLIB}Int{$ENDIF}(lr.Left + 1){$IFDEF FMXWEBLIB} + 0.5{$ELSE}{$ENDIF} + TotalFixedColumnWidth, {$IFDEF FMXWEBLIB}Int{$ENDIF}(fr.Bottom + 1) {$IFDEF FMXWEBLIB} + 0.5{$ELSE} + 1{$ENDIF});
      if (r.Right - r.Left > 0) and (r.Bottom - r.Top > 0) then
      begin
        a := True;
        DoBeforeDrawEmptySpace(AGraphics, r, gekRowLeft, a);
        if a then
        begin
          AGraphics.DrawRectangle(r);
          DoAfterDrawEmptySpace(AGraphics, r, gekRowLeft);
        end;
      end;
    end;

    if hsb and vsb then
    begin
      r := RectF({$IFDEF FMXWEBLIB}Int{$ENDIF}(lr.Right - 1){$IFDEF FMXWEBLIB} + 0.5{$ENDIF}, {$IFDEF FMXWEBLIB}Int{$ENDIF}(lr.Bottom - 1){$IFDEF FMXWEBLIB} + 0.5{$ENDIF}, {$IFDEF FMXWEBLIB}Int{$ENDIF}(fr.Right + 1){$IFDEF FMXWEBLIB} + 0.5{$ELSE} + 1{$ENDIF}, {$IFDEF FMXWEBLIB}Int{$ENDIF}(fr.Bottom + 1) {$IFDEF FMXWEBLIB} + 0.5{$ELSE} + 1{$ENDIF});
      if (r.Right - r.Left > 0) and (r.Bottom - r.Top > 0) then
      begin
        a := True;
        DoBeforeDrawEmptySpace(AGraphics, r, gekColumnBottomRowRight, a);
        if a then
        begin
          AGraphics.DrawRectangle(r);
          DoAfterDrawEmptySpace(AGraphics, r, gekColumnBottomRowRight);
        end;
      end;
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.Draw(AGraphics: TTMSFNCGraphics);
var
  r: TRectF;
  st: TTMSFNCGraphicsSaveState;
begin
  st := AGraphics.SaveState;

  try
    r := GetContentClipRect;
    AGraphics.ClipRect(r);

    DrawDisplay(AGraphics, FNormalCellDisplay);
    DrawDisplay(AGraphics, FFixedColumnCellDisplay);
    DrawDisplay(AGraphics, FFixedRowCellDisplay);
    DrawDisplay(AGraphics, FFixedRootCellDisplay);

    DrawDragging(AGraphics);

    if not IsExporting then
    begin
      AGraphics.Fill.Kind := gfkNone;
      AGraphics.Stroke.Assign(Stroke);
      AGraphics.DrawRectangle(r);
    end;

    DoAfterDrawGrid(AGraphics, r);
  finally
    AGraphics.RestoreState(st);
  end;

  DrawEmptySpaces(AGraphics);
  DrawScrollBars(AGraphics);
end;

procedure TTMSFNCDataGridRenderer.DoAfterDrawGrid(AGraphics: TTMSFNCGraphics; ARect: TRectF);
begin
  if Assigned(OnAfterDrawGrid) then
    OnAfterDrawGrid(Self, AGraphics, ARect);
end;

procedure TTMSFNCDataGridRenderer.DoAfterLookup(ALookupString: string);
begin
  if Assigned(OnAfterLookup) then
    OnAfterLookup(Self, ALookupString);
end;

procedure TTMSFNCDataGridRenderer.DrawCell(AGraphics: TTMSFNCGraphics; ACache: TTMSFNCDataGridCell);
var
  e: Boolean;
begin
  e := IsEditing and (FEditingCell.Column = ACache.Column) and (FEditingCell.Row = ACache.Row);
  ACache.DrawElements := TTMSFNCDataGridCellOpen(ACache).GetDrawElements(e);
  TTMSFNCDataGridCellOpen(ACache).Draw(AGraphics);
end;

function TTMSFNCDataGridRenderer.GetCellAppearance(AState: TTMSFNCDataGridCellState;
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellLayout;
var
  c, cll: TTMSFNCDataGridCellLayout;
  ap: TTMSFNCDataGridCellAppearance;
  cl: TTMSFNCDataGridColumn;
  rr: Integer;
  b: Boolean;
begin
  cll := nil;
  if HasLayout[ACell.Column, ACell.Row] then
    cll := Layouts[ACell.Column, ACell.Row];

  cl := GetColumn(ACell.Column);
  if Assigned(cl) and (gcsAppearance in cl.Settings) then
    ap := cl.Appearance
  else
    ap := CellAppearance;

  b := False;

  case AState of
    gcsNormal:
    begin
      rr := ACell.Row;
      if IsRowNode(rr) and IsGrouped then
        c := ap.GroupLayout
      else if IsRowSummary(rr) and IsGrouped then
        c := ap.SummaryLayout
      else
      begin
        if IsRowBand(ACell.Row) and Options.Banding.Enabled then
          c := ap.BandLayout
        else
        begin
          c := ap.NormalLayout;
          b := True;
        end;
      end;
    end;
    gcsFocused: c := ap.FocusedLayout;
    gcsFixed: c := ap.FixedLayout;
    gcsFixedSelected: c := ap.FixedSelectedLayout;
    gcsSelected: c := ap.SelectedLayout;
    else
      c := nil;
  end;

  if not Assigned(FCellLayout) then
    FCellLayout := TTMSFNCDataGridCellLayout.Create;

  if Assigned(c) then
  begin
    FCellLayout.Assign(c);
    if Assigned(cll) then
    begin
      DoGetCellLayoutHasPriority(ACell, b);
      if b then
        FCellLayout.Assign(cll)
      else
      begin
        FCellLayout.Sides := cll.Sides;
        FCellLayout.Font.Name := cll.Font.Name;
        FCellLayout.Font.Size := cll.Font.Size;
        FCellLayout.Font.Style := cll.Font.Style;
        FCellLayout.TextAlign := cll.TextAlign;
        FCellLayout.TextAngle := cll.TextAngle;
        FCellLayout.VerticalTextAlign := cll.VerticalTextAlign;
        FCellLayout.WordWrapping := cll.WordWrapping;
      end;
    end;

    Result := FCellLayout;
  end
  else
    Result := nil;
end;

function TTMSFNCDataGridRenderer.GetCellObjectForCell(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCell;
begin
  Result := nil;
  if (ACell.Column = -1) or (ACell.Row = -1) then
    Exit;

  if IsCellFixed(ACell) or IsCellFreeze(ACell) then
  begin
    if IsCellFixedRoot(ACell) or IsCellFreezeRoot(ACell) then
      Result := FFixedRootCellDisplay.GetItemForCell(ACell)
    else
    begin
      Result := FFixedColumnCellDisplay.GetItemForCell(ACell);
      if not Assigned(Result) then
        Result := FFixedRowCellDisplay.GetItemForCell(ACell);
    end;
  end
  else
    Result := FNormalCellDisplay.GetItemForCell(ACell);
end;

function TTMSFNCDataGridRenderer.GetCellState(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellState;
begin
  if (Options.Selection.Mode <> gsmNone) and ((FocusedCell.Row = ACell.Row) and (FocusedCell.Column = ACell.Column)) and IsFocused then
    Result := gcsFocused
  else
  begin
    if IsCellFixed(ACell) then
    begin
      if (Options.Selection.Mode <> gsmNone) and Options.Selection.ShowSelectionInFixedCells and IsFixedCellSelected(MakeCell(ACell.Column, ACell.Row)) then
        Result := gcsFixedSelected
      else
        Result := gcsFixed;
    end
    else if (Options.Selection.Mode <> gsmNone) and IsCellSelected(MakeCell(ACell.Column, ACell.Row)) then
      Result := gcsSelected
    else
      Result := gcsNormal;
  end;
end;

function TTMSFNCDataGridRenderer.GetRadioGroupIndex(AColumn,
  ARow: Integer): Integer;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := -1;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
    Result := AsExtendedCell(cd).RadioGroupIndex;
end;

procedure TTMSFNCDataGridRenderer.SetLeftColumn(const Value: Integer);
var
  cl: TTMSFNCDataGridCellCoord;
begin
  cl.Row := Max(FixedRowCount, Min(StartRow, RowCount - 1 - FixedBottomRowCount));
  cl.Column := Max(FixedColumnCount, Min(Value, ColumnCount - 1 - FixedRightColumnCount));
  Navigate(cl,  True);
end;

procedure TTMSFNCDataGridRenderer.SetLocalRect(ARect: TRectF);
begin
  inherited;
  FLocalRectChanged := True;
end;

function TTMSFNCDataGridRenderer.GetBooleans(AColumn, ARow: Integer): Boolean;
var
  cl: TTMSFNCDataGridColumn;
begin
  Result := False;
  if HasDataCheckBox[AColumn, ARow] or HasDataRadioButton[AColumn, ARow] then
  begin
    cl := GetColumn(AColumn);
    if Assigned(cl) then
      Result := Strings[AColumn, ARow] = cl.Binding.BooleanTrueValue;
  end
  else
    Result := inherited GetBooleans(AColumn, ARow);
end;

function TTMSFNCDataGridRenderer.GetBottomRow: Integer;
begin
  Result := StopRow;
end;

function TTMSFNCDataGridRenderer.GetRightColumn: Integer;
begin
  Result := StopColumn;
end;

function TTMSFNCDataGridRenderer.GetLayouts(AColumn,
  ARow: Integer): TTMSFNCDataGridCellLayout;
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  Result := nil;
  cd := CellDataExtended[AColumn, ARow];
  if Assigned(cd) then
  begin
    if not Assigned(TTMSFNCDataGridCellItemExtendedOpen(cd).Layout) then
    begin
      TTMSFNCDataGridCellItemExtendedOpen(cd).Layout := TTMSFNCDataGridCellLayout.Create;
      ConfigureLayout(MakeCell(AColumn, ARow), TTMSFNCDataGridCellItemExtendedOpen(cd).Layout);
      TTMSFNCDataGridCellItemExtendedOpen(cd).Layout.OnChange := @DoLayoutChanged;
    end;

    Result := TTMSFNCDataGridCellItemExtendedOpen(cd).Layout;
  end;
end;

function TTMSFNCDataGridRenderer.GetLeftColumn: Integer;
begin
  Result := StartColumn;
end;

function TTMSFNCDataGridRenderer.GetLookupTimer: TTimer;
begin
  if not Assigned(FLookupTimer) then
  begin
    FLookupTimer := TTimer.Create(nil);
    FLookupTimer.OnTimer := DoLookupTimer;
    FLookupTimer.Interval := Options.Lookup.ResetInterval;
    FLookupTimer.Enabled := False;
  end;

  Result := FLookupTimer;
end;

procedure TTMSFNCDataGridRenderer.SetTextAngles(AColumn, ARow: Integer;
  const Value: Double);
begin
  Layouts[AColumn, ARow].TextAngle := Value;
end;

procedure TTMSFNCDataGridRenderer.SetTopRow(const Value: Integer);
var
  cl: TTMSFNCDataGridCellCoord;
begin
  cl.Row := Max(FixedRowCount, Min(Value, RowCount - 1 - FixedBottomRowCount));
  cl.Column := Max(FixedColumnCount, Min(StartColumn, ColumnCount - 1 - FixedRightColumnCount));
  Navigate(cl, True);
end;

function TTMSFNCDataGridRenderer.GetScrollTimer: TTimer;
begin
  if not Assigned(FScrollTimer) then
  begin
    FScrollTimer := TTimer.Create(nil);
    FScrollTimer.OnTimer := DoScrollTimer;
    FScrollTimer.Interval := Options.Mouse.AutoScrollingInterval;
    FScrollTimer.Enabled := False;
  end;

  Result := FScrollTimer;
end;

function TTMSFNCDataGridRenderer.GetSelectedColumnDictionary: TTMSFNCDataGridBooleanDictionary;
begin
  if not Assigned(FSelectedColumnDictionary) then
    FSelectedColumnDictionary := TTMSFNCDataGridBooleanDictionary.Create;

  Result := FSelectedColumnDictionary;
end;

function TTMSFNCDataGridRenderer.GetSelectedCell(AColumn, ARow: Integer): Boolean;
begin
  case Options.Selection.Mode of
    gsmDisjunctRow: Result := SelectedRows[ARow];
    gsmDisjunctColumn: Result := SelectedColumns[AColumn];
    gsmDisjunctCell, gsmDisjunctCellRange:
    begin
      if SelectedCellDictionary.ContainsKey(MakeCell(AColumn, ARow)) then
        Result := SelectedCellDictionary[MakeCell(AColumn, ARow)]
      else
        Result := False;
    end
    else
    begin
      Result := (AColumn >= Selection.StartCell.Column) and (AColumn <= Selection.StopCell.Column)
        and (ARow >= Selection.StartCell.Row) and (ARow <= Selection.StopCell.Row);
    end;
  end;
end;

function TTMSFNCDataGridRenderer.GetSelectedCellDictionary: TTMSFNCDataGridSelectedCellDictionary;
begin
  if not Assigned(FSelectedCellDictionary) then
    FSelectedCellDictionary := TTMSFNCDataGridSelectedCellDictionary.Create;

  Result := FSelectedCellDictionary;
end;

function TTMSFNCDataGridRenderer.GetSelectedRowDictionary: TTMSFNCDataGridBooleanDictionary;
begin
  if not Assigned(FSelectedRowDictionary) then
    FSelectedRowDictionary := TTMSFNCDataGridBooleanDictionary.Create;

  Result := FSelectedRowDictionary;
end;

function TTMSFNCDataGridRenderer.GetSelectedRow(ARow: Integer): Boolean;
begin
  if Options.Selection.Mode in [gsmDisjunctRow, gsmDisjunctCell, gsmDisjunctCellRange] then
  begin
    if SelectedRowDictionary.ContainsKey(ARow) then
      Result := SelectedRowDictionary[ARow]
    else
      Result := False;
  end
  else
    Result := (ARow >= Selection.StartCell.Row) and (ARow <= Selection.StopCell.Row)
end;

function TTMSFNCDataGridRenderer.GetSelection: TTMSFNCDataGridCellCoordRange;
begin
  Result := Selection;
end;

function TTMSFNCDataGridRenderer.GetStates(AColumn,
  ARow: Integer): TTMSFNCDataGridCellState;
begin
  Result := GetCellState(MakeCell(AColumn, ARow));
end;

function TTMSFNCDataGridRenderer.GetSelectedColumn(AColumn: Integer): Boolean;
begin
  if Options.Selection.Mode in [gsmDisjunctColumn, gsmDisjunctCell, gsmDisjunctCellRange] then
  begin
    if SelectedColumnDictionary.ContainsKey(AColumn) then
      Result := SelectedColumnDictionary[AColumn]
    else
      Result := False;
  end
  else
    Result := (AColumn >= Selection.StartCell.Column) and (AColumn <= Selection.StopCell.Column)
end;

function TTMSFNCDataGridRenderer.GetReadOnlys(AColumn, ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := False;

  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsReadOnly in cl.Settings) then
    Result := cl.ReadOnly
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).ReadOnly;
  end;
end;

function TTMSFNCDataGridRenderer.GetReplaceDialog: TTMSFNCReplaceDialog;
begin
  if not Assigned(FReplaceDialog) then
  begin
    FReplaceDialog := TTMSFNCReplaceDialog.Create(nil);
    FReplaceDialog.Options := [rdoCaseSensitive,
      rdoFindEnabled, rdoFindNextEnabled, rdoReplaceAllEnabled, rdoReplaceEnabled,
      rdoCloseEnabled, rdoDown];
    FReplaceDialog.VisibleOptions := [rdovCaseSensitive, rdovExpression,
      rdovWholeWordOnly, rdovReplace, rdovReplaceAll, rdovFind, rdovFindNext,
      rdovClose, rdovFindCombo, rdovFindMemo, rdovReplaceCombo, rdovDirection];
    FReplaceDialog.OnFind := DoReplaceFind;
    FReplaceDialog.OnFindNext := DoReplaceFindNext;
    FReplaceDialog.OnReplace := DoReplace;
    FReplaceDialog.OnReplaceAll := DoReplaceAll;
  end;

  Result := FReplaceDialog;
end;

function TTMSFNCDataGridRenderer.GetTextAngles(AColumn, ARow: Integer): Double;
var
  l: TTMSFNCDataGridCellLayout;
begin
  Result := 0.0;
  l := GetCellAppearance(GetCellState(MakeCell(AColumn, ARow)), MakeCell(AColumn, ARow));
  if Assigned(l) then
    Result := l.TextAngle;
end;

function TTMSFNCDataGridRenderer.GetTopRow: Integer;
begin
  Result := StartRow;
end;

function TTMSFNCDataGridRenderer.GetOptions: TTMSFNCDataGridOptions;
begin
  Result := TTMSFNCDataGridOptions(inherited Options);
end;

function TTMSFNCDataGridRenderer.GetPageCount: Integer;
var
  PageSize: Integer;
begin
  PageSize := GetPageScrollSize;
  if PageSize > 0 then
    Result := ((RowCount - FixedBottomRowCount - FixedRowCount) + PageSize - 1) div PageSize
  else
    Result := 0;
end;

function TTMSFNCDataGridRenderer.GetPageIndex: Integer;
begin
  Result := GetPageIndexforRow(BottomRow);
end;

function TTMSFNCDataGridRenderer.GetPageIndexForRow(ARow: Integer): Integer;
var
  PageSize: Integer;
begin
  PageSize := GetPageScrollSize; // Number of rows per page
  if (PageSize > 0) and (RowCount > 0) then
    Result := ARow div PageSize
  else
    Result := 0; // Default to the first page if invalid
end;

function TTMSFNCDataGridRenderer.GetPageScrollSize: Integer;
var
  vm: Single;
begin
  vm := GetVMax;
  if (Options.Keyboard.PageScrollSize = 0) and (vm * RowCount > 0) then
    Result := Round(GetVViewPortSize / vm * RowCount)
  else
    Result := Options.Keyboard.PageScrollSize;
end;

function TTMSFNCDataGridRenderer.GetParentHost: TTMSFNCDataGridCellParent;
var
  h: TObject;
begin
  Result := nil;

  h := Host;

  if h is TTMSFNCDataGridCellParent then
  begin
    Result := h as TTMSFNCDataGridCellParent;
    Exit;
  end;

  while Assigned(h) and not (h is TTMSFNCDataGridCellParent) do
  begin
    if h is TTMSFNCDataGridRenderer then
      h := (h as TTMSFNCDataGridRenderer).Host;

    if h is TTMSFNCDataGridCellParent then
    begin
      Result := h as TTMSFNCDataGridCellParent;
      Break;
    end;
  end;
end;

function TTMSFNCDataGridRenderer.GetProgressValue(AColumn,
  ARow: Integer): Integer;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := 0;
  if HasDataProgressBar[AColumn, ARow] then
    Result := Ints[AColumn, ARow]
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) and (AsExtendedCell(cd).&Type = gcitProgressBar) then
      Result := AsExtendedCell(cd).IntegerValue;
  end;
end;

function TTMSFNCDataGridRenderer.GetCustomHorizontalScrollBar: TTMSFNCDataGridScrollBar;
begin
  Result := FCustomHorizontalScrollBar;
end;

function TTMSFNCDataGridRenderer.GetCustomVerticalScrollBar: TTMSFNCDataGridScrollBar;
begin
  Result := FCustomVerticalScrollBar;
end;

function TTMSFNCDataGridRenderer.GetDragTimer: TTimer;
begin
  if not Assigned(FDragTimer) then
  begin
    FDragTimer := TTimer.Create(nil);
    FDragTimer.OnTimer := DoDragTimer;
    FDragTimer.Interval := Options.Mouse.AutoScrollingInterval;
    FDragTimer.Enabled := False;
  end;

  Result := FDragTimer;
end;

function TTMSFNCDataGridRenderer.GetContentRect: TRectF;
begin
  Result := inherited GetContentRect;
  Result.Right := Result.Left + Max(0, Result.Right - Result.Left);
  Result.Bottom := Result.Top + Max(0, Result.Bottom - Result.Top);
  Result := ConfigureScrollBarsRect(Result);
end;

function TTMSFNCDataGridRenderer.GetControlAligns(AColumn,
  ARow: Integer): TTMSFNCDataGridCellControlAlign;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := gcaNone;
  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsControlAlign in cl.Settings) then
    Result := cl.ControlAlign
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).ControlAlign;
  end;
end;

function TTMSFNCDataGridRenderer.GetControlPositions(AColumn,
  ARow: Integer): TTMSFNCDataGridCellControlPosition;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := gcpCenterRight;
  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsControlPosition in cl.Settings) then
    Result := cl.ControlPosition
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).ControlPosition;
  end;
end;

function TTMSFNCDataGridRenderer.GetControlHeights(AColumn,
  ARow: Integer): Integer;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := -1;
  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsControlHeight in cl.Settings) then
    Result := cl.ControlHeight
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).ControlHeight;
  end;
end;

function TTMSFNCDataGridRenderer.GetControlMargins(AColumn,
  ARow: Integer): TTMSFNCMargins;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := nil;
  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsControlMargins in cl.Settings) then
    Result := cl.ControlMargins
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).ControlMargins;
  end;
end;

function TTMSFNCDataGridRenderer.GetControlTexts(AColumn,
  ARow: Integer): string;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := '';
  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsControlText in cl.Settings) then
    Result := cl.ControlText
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).ControlText;
  end;
end;

function TTMSFNCDataGridRenderer.GetControlWidths(AColumn,
  ARow: Integer): Integer;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := -1;
  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsControlWidth in cl.Settings) then
    Result := cl.ControlWidth
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).ControlWidth;
  end;
end;

function TTMSFNCDataGridRenderer.GetControlWrapperList: TTMSFNCDataGridCellControlWrapperList;
begin
  if not Assigned(FControlWrapperList) then
    FControlWrapperList := TTMSFNCDataGridCellControlWrapperList.Create;

  Result := FControlWrapperList;
end;

function TTMSFNCDataGridRenderer.GetCalculationRect: TRectF;
begin
  Result := LocalRect;
  Result.Right := Result.Left + Max(0, Result.Right - Result.Left);
  Result.Bottom := Result.Top + Max(0, Result.Bottom - Result.Top);
  Result := ConfigureScrollBarsRect(Result);
end;

function TTMSFNCDataGridRenderer.GetHorizontalScrollPosition: Double;
var
  hVal, scrollh: Double;
begin
  hVal := GetHScrollValue;
  if ScrollMode = gsmCellScrolling then
  begin
    scrollh := ColumnPositions[Round(hval)];
    hVal := scrollh;
  end;

  Result := hVal;
end;

function TTMSFNCDataGridRenderer.GetTextAligns(AColumn, ARow: Integer): TTMSFNCGraphicsTextAlign;
var
  l: TTMSFNCDataGridCellLayout;
begin
  Result := gtaLeading;
  l := GetCellAppearance(GetCellState(MakeCell(AColumn, ARow)), MakeCell(AColumn, ARow));
  if Assigned(l) then
    Result := l.TextAlign;
end;

function TTMSFNCDataGridRenderer.GetHorizontalScrollBar: TScrollBar;
begin
  Result := FHorizontalScrollBar;
end;

function TTMSFNCDataGridRenderer.GetHViewPortSize: Single;
begin
  Result := 0;
  if HasCustomScrollBars and Assigned(CustomHorizontalScrollBar) then
    Result := CustomHorizontalScrollBar.PageSize
  else if Assigned(HorizontalScrollBar) then
  begin
    {$IFDEF FMXLIB}
    Result := HorizontalScrollBar.ViewportSize;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    Result := HorizontalScrollBar.PageSize;
    {$ENDIF}
  end;
end;

function TTMSFNCDataGridRenderer.GetRowViewPortSize: Double;
var
  I,cnt: Integer;
  h: Double;
  ch: Double;
  cr: TRectF;
begin
  cr := GetContentRect;
  ch := cr.Bottom - cr.Top;
  Result := 0;
  h := 0;
  cnt := 0;
  for I := RowCount - 1 downto 0 do
  begin
    h := h + RowHeights[I];
    if h > ch then
    begin
      Result := Max(1, cnt);
      Break;
    end;
    Inc(cnt);
  end;
end;

function TTMSFNCDataGridRenderer.GetHScrollValue: Single;
begin
  Result := FLocalHScrollValue;

  if HasCustomScrollBars and Assigned(CustomHorizontalScrollBar) then
    Result := Min(CustomHorizontalScrollBar.Max - CustomHorizontalScrollBar.PageSize, Max(0, CustomHorizontalScrollBar.Value))
  else if Assigned(HorizontalScrollBar) then
  begin
    {$IFDEF FMXLIB}
    Result := Min(HorizontalScrollBar.Max - HorizontalScrollBar.ViewportSize, Max(0, HorizontalScrollBar.Value));
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    Result := Min(HorizontalScrollBar.Max - HorizontalScrollBar.PageSize, Max(0, Round(HorizontalScrollBar.Position)));
    {$ENDIF}
  end;
end;

function TTMSFNCDataGridRenderer.GetColors(AColumn: Integer; ARow: Integer): TTMSFNCGraphicsColor;
var
  l: TTMSFNCDataGridCellLayout;
begin
  Result := gcNull;
  l := GetCellAppearance(GetCellState(MakeCell(AColumn, ARow)), MakeCell(AColumn, ARow));
  if Assigned(l) then
    Result := l.Fill.Color;
end;

function TTMSFNCDataGridRenderer.GetColumn(
  AColumn: Integer): TTMSFNCDataGridColumn;
begin
  Result := nil;
  if (AColumn >= 0) and (AColumn <= Columns.Count - 1) then
    Result := Columns[AColumn];
end;

function TTMSFNCDataGridRenderer.GetColumnsClass: TTMSFNCDataGridColumnsClass;
begin
  Result := TTMSFNCDataGridColumns;
end;

function TTMSFNCDataGridRenderer.GetColumnViewPortSize: Double;
var
  I,cnt: Integer;
  w: Double;
  cw: Double;
  cr: TRectF;
begin
  cr := GetContentRect;
  cw := cr.Right - cr.Left;
  Result := 0;
  w := 0;
  cnt := 0;
  for I := ColumnCount - 1 downto 0 do
  begin
    w := w + ColumnWidths[I];
    if w > cw then
    begin
      Result := Max(1, cnt);
      Break;
    end;
    Inc(cnt);
  end;
end;

function TTMSFNCDataGridRenderer.GetCommentPopup: TTMSFNCCommentPopup;
begin
  if not Assigned(FCommentPopup) then
  begin
    FCommentPopup := TTMSFNCCommentPopup.Create(nil);
    FCommentPopup.Placement := ppAbsolute;
    FCommentPopup.OnPopupPaint := DoPaintCommentPopup;
  end;

  Result := FCommentPopup;
end;

function TTMSFNCDataGridRenderer.GetComments(AColumn, ARow: Integer): string;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := '';
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
    Result := AsExtendedCell(cd).Comment;
end;

function TTMSFNCDataGridRenderer.GetHints(AColumn, ARow: Integer): string;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := '';
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
    Result := AsExtendedCell(cd).Hint;
end;

function TTMSFNCDataGridRenderer.GetHintString: string;
begin
  Result := '';
  if IsCellValid(FHoverCell) then
    Result := Hints[FHoverCell.Column, FHoverCell.Row];
end;

function TTMSFNCDataGridRenderer.CheckRange(ARange: TTMSFNCDataGridCellItemTypeRange; ARow: Integer): Boolean;
begin
  Result := True;
  case ARange of
    gcirNormal: Result := Result and ((ARow >= FixedRowCount) and (ARow <= RowCount - 1 - FixedBottomRowCount));
    gcirFixed: Result := Result and ((ARow < FixedRowCount) or (ARow > RowCount - 1 - FixedBottomRowCount));
  end;
end;

function TTMSFNCDataGridRenderer.GetHasDataCheckBox(AColumn,
  ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := False;
  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsType in cl.Settings) then
  begin
    Result := cl.&Type = gcitDataCheckBox;
    Result := Result and CheckRange(cl.TypeRange, ARow);
  end
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).&Type = gcitDataCheckBox;
  end;
end;

function TTMSFNCDataGridRenderer.GetHasCheckBox(AColumn,
  ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := False;
  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsType in cl.Settings) then
  begin
    Result := cl.&Type = gcitCheckBox;
    Result := Result and CheckRange(cl.TypeRange, ARow);
  end
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).&Type = gcitCheckBox;
  end;
end;

function TTMSFNCDataGridRenderer.GetHasButton(AColumn,
  ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := False;
  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsType in cl.Settings) then
  begin
    Result := cl.&Type = gcitButton;
    Result := Result and CheckRange(cl.TypeRange, ARow);
  end
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).&Type = gcitButton;
  end;
end;

function TTMSFNCDataGridRenderer.GetHasBitmap(AColumn,
  ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := False;

  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsType in cl.Settings) then
  begin
    Result := cl.&Type = gcitBitmap;
    Result := Result and CheckRange(cl.TypeRange, ARow);
  end
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).&Type = gcitBitmap;
  end;
end;

function TTMSFNCDataGridRenderer.GetHasProgressBar(AColumn,
  ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := False;

  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsType in cl.Settings) then
  begin
    Result := cl.&Type = gcitProgressBar;
    Result := Result and CheckRange(cl.TypeRange, ARow);
  end
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).&Type = gcitProgressBar;
  end;
end;

function TTMSFNCDataGridRenderer.GetHasDataProgressBar(AColumn,
  ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := False;

  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsType in cl.Settings) then
  begin
    Result := cl.&Type = gcitDataProgressBar;
    Result := Result and CheckRange(cl.TypeRange, ARow);
  end
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).&Type = gcitDataProgressBar;
  end;
end;

function TTMSFNCDataGridRenderer.GetHasRadioButton(AColumn,
  ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := False;
  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsType in cl.Settings) then
  begin
    Result := cl.&Type = gcitRadioButton;
    Result := Result and CheckRange(cl.TypeRange, ARow);
  end
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).&Type = gcitRadioButton;
  end;
end;

function TTMSFNCDataGridRenderer.GetHasDataRadioButton(AColumn,
  ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
  cl: TTMSFNCDataGridColumn;
begin
  Result := False;
  cl := GetColumn(AColumn);
  if Assigned(cl) and (gcsType in cl.Settings) then
  begin
    Result := cl.&Type = gcitDataRadioButton;
    Result := Result and CheckRange(cl.TypeRange, ARow);
  end
  else
  begin
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
      Result := AsExtendedCell(cd).&Type = gcitDataRadioButton;
  end;
end;

function TTMSFNCDataGridRenderer.GetHasFilterEditor(AColumn,
  ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := False;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
    Result := Assigned(TTMSFNCDataGridCellItemExtendedOpen(AsExtendedCell(cd)).FilterEditor);
end;

function TTMSFNCDataGridRenderer.GetHasLayout(AColumn, ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := False;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
    Result := Assigned(TTMSFNCDataGridCellItemExtendedOpen(AsExtendedCell(cd)).Layout);
end;

function TTMSFNCDataGridRenderer.GetHasGrid(AColumn, ARow: Integer): Boolean;
var
  cd: TTMSFNCDataGridCellItem;
  o: TObject;
begin
  Result := False;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
  begin
    o := TTMSFNCDataGridCellItemExtendedOpen(AsExtendedCell(cd)).Renderer;
    if Assigned(o) and (o is TTMSFNCDataGridRenderer) then
      Result := True;
  end;
end;

function TTMSFNCDataGridRenderer.GetHMax: Single;
begin
  Result := 0;
  if HasCustomScrollBars and Assigned(CustomHorizontalScrollBar) then
    Result := CustomHorizontalScrollBar.Max
  else if Assigned(HorizontalScrollBar) then
    Result := HorizontalScrollBar.Max;
end;

function TTMSFNCDataGridRenderer.GetHVisible: Boolean;
begin
  Result := False;
  if HasCustomScrollBars and Assigned(CustomHorizontalScrollBar) then
    Result := CustomHorizontalScrollBar.Visible
  else if Assigned(HorizontalScrollBar) then
    Result := HorizontalScrollBar.Visible;
end;

function TTMSFNCDataGridRenderer.GetSelectedColumnCount: Integer;
var
  k: Integer;
begin
  Result := 0;
  if Options.Selection.Mode in [gsmDisjunctColumn, gsmDisjunctCell, gsmDisjunctCellRange] then
  begin
    for k in SelectedColumnDictionary.Keys do
    begin
      if IsColumnValid(k) then
        Inc(Result);
    end;
  end
  else if (Selection.StartCell.Row <> -1) and (Selection.StopCell.Row <> -1)
    and (Selection.StartCell.Column <> -1) and (Selection.StopCell.Column <> -1) then
  begin
    for k := Selection.StartCell.Column to Selection.StopCell.Column do
      Inc(Result);
  end;
end;

procedure TTMSFNCDataGridRenderer.SetSelectedCell(AColumn, ARow: Integer;
  const Value: Boolean);
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  if SelectedCellDictionary.ContainsKey(MakeCell(AColumn, ARow)) then
  begin
    if Value = False then
      SelectedCellDictionary.Remove(MakeCell(AColumn, ARow))
    else
      SelectedCellDictionary[MakeCell(AColumn, ARow)] := Value;
  end
  else if Value <> False then
    SelectedCellDictionary.Add(MakeCell(AColumn, ARow), Value);

  SelectedRows[ARow] := Value;
  SelectedColumns[AColumn] := Value;

  DoUpdate;
end;

procedure TTMSFNCDataGridRenderer.SetSelectedColumn(AColumn: Integer;
  const Value: Boolean);
begin
  if not IsColumnValid(AColumn) then
    Exit;

  if SelectedColumnDictionary.ContainsKey(AColumn) then
  begin
    if Value = False then
      SelectedColumnDictionary.Remove(AColumn)
    else
      SelectedColumnDictionary[AColumn] := Value;
  end
  else if Value <> False then
    SelectedColumnDictionary.Add(AColumn, Value);

  DoUpdate;
end;

procedure TTMSFNCDataGridRenderer.SetSelectedRow(ARow: Integer; const Value: Boolean);
begin
  if not IsRowValid(ARow) then
    Exit;

  if SelectedRowDictionary.ContainsKey(ARow) then
  begin
    if Value = False then
      SelectedRowDictionary.Remove(ARow)
    else
      SelectedRowDictionary[ARow] := Value;
  end
  else if Value <> False then
    SelectedRowDictionary.Add(ARow, Value);

  DoUpdate;
end;

function TTMSFNCDataGridRenderer.GetSelectedCellCount: Integer;
begin
  case Options.Selection.Mode of
    gsmDisjunctCell, gsmDisjunctCellRange: Result := SelectedCellDictionary.Count;
    else
      Result := SelectedRowCount * SelectedColumnCount;
  end;
end;

function TTMSFNCDataGridRenderer.GetSelectedRowCount: Integer;
var
  k: Integer;
begin
  Result := 0;
  if Options.Selection.Mode in [gsmDisjunctRow, gsmDisjunctCell, gsmDisjunctCellRange] then
  begin
    for k in SelectedRowDictionary.Keys do
    begin
      if IsRowValid(k) then
        Inc(Result);
    end;
  end
  else if (Selection.StartCell.Row <> -1) and (Selection.StopCell.Row <> -1)
    and (Selection.StartCell.Column <> -1) and (Selection.StopCell.Column <> -1) then
  begin
    for k := Selection.StartCell.Row to Selection.StopCell.Row do
      Inc(Result);
  end;
end;

function TTMSFNCDataGridRenderer.ColumnByHeader(
  AHeader: string): TTMSFNCDataGridColumn;
begin
  Result := Columns.ColumnByHeader(AHeader);
end;

function TTMSFNCDataGridRenderer.ColumnByName(
  AName: string): TTMSFNCDataGridColumn;
begin
  Result := Columns.ColumnByName(AName);
end;

function TTMSFNCDataGridRenderer.ColumnIndexByHeader(AHeader: string): Integer;
var
  c: TTMSFNCDataGridColumn;
begin
  Result := -1;
  c := ColumnByHeader(AHeader);
  if Assigned(c) then
    Result := c.Index;
end;

function TTMSFNCDataGridRenderer.ColumnIndexByName(AName: string): Integer;
var
  c: TTMSFNCDataGridColumn;
begin
  Result := -1;
  c := ColumnByName(AName);
  if Assigned(c) then
    Result := c.Index;
end;

function TTMSFNCDataGridRenderer.ColumnStretchingActive: Boolean;
begin
  Result := FColumnStretchingActive;
end;

function TTMSFNCDataGridRenderer.GetOptionsClass: TTMSFNCDataGridDataOptionsClass;
begin
  Result := TTMSFNCDataGridOptions;
end;

procedure TTMSFNCDataGridRenderer.AutoSizeColumns(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
var
  i: Integer;
  g: TTMSFNCGraphics;
begin
  BeginUpdate;
  g := TTMSFNCGraphics.CreateBitmapCanvas;
  try
    g.BeginScene;
    for i := 0 to ColumnCount - 1 do
      InternalAutoSizeColumn(i, AMode, APadding, g);
    g.EndScene;
  finally
    g.Free;
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.AutoSizeGrid(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APaddingColumn: Integer = 5; const APaddingRow: Integer = 5);
var
  g: TTMSFNCGraphics;
  i: Integer;
begin
  BeginUpdate;
  g := TTMSFNCGraphics.CreateBitmapCanvas;
  try
    g.BeginScene;

    for i := 0 to ColumnCount - 1 do
      InternalAutoSizeColumn(i, AMode, APaddingColumn, g);

    for i := 0 to ColumnCount - 1 do
      InternalAutoSizeRow(i, AMode, APaddingRow, g);

    g.EndScene;
  finally
    g.free;
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.AddButton(AColumn, ARow: Integer; AText: string = '');
begin
  SetButton(AColumn, ARow, AText);
end;

procedure TTMSFNCDataGridRenderer.AddBitmap(ACell: TTMSFNCDataGridCellCoord;
  AName: string);
begin
  SetBitmap(ACell, AName);
end;

procedure TTMSFNCDataGridRenderer.AddBitmap(AColumn, ARow: Integer;
  AName: string);
begin
  SetBitmap(AColumn, ARow, AName);
end;

procedure TTMSFNCDataGridRenderer.AddBitmap(ACell: TTMSFNCDataGridCellCoord;
  ABitmap: TTMSFNCBitmap);
begin
  SetBitmap(ACell, ABitmap);
end;

procedure TTMSFNCDataGridRenderer.AddBitmap(AColumn, ARow: Integer;
  ABitmap: TTMSFNCBitmap);
begin
  SetBitmap(AColumn, ARow, ABitmap);
end;

procedure TTMSFNCDataGridRenderer.AddBitmapColumn(AColumn: Integer;
  AName: string);
begin
  SetBitmapColumn(AColumn, AName);
end;

procedure TTMSFNCDataGridRenderer.AddBitmapColumn(AColumn: Integer;
  ABitmap: TTMSFNCBitmap);
begin
  SetBitmapColumn(AColumn, ABitmap);
end;

procedure TTMSFNCDataGridRenderer.AddButton(ACell: TTMSFNCDataGridCellCoord; AText: string = '');
begin
  SetButton(ACell, AText);
end;

procedure TTMSFNCDataGridRenderer.AddButtonColumn(AColumn: Integer; AText: string = '');
begin
  SetButtonColumn(AColumn, AText);
end;

procedure TTMSFNCDataGridRenderer.AddCellToDisplay(ACell: TTMSFNCDataGridCell;
  ADisplay: TTMSFNCDataGridDisplayList);
var
  x, y: Double;
  cell: TTMSFNCDataGridCellOpen;
  r, rc, rg, rcc: TRectF;
begin
  if (UpdateCount > 0) or GetIsDestroying or not Assigned(ACell) or not Assigned(ADisplay) then
    Exit;

  x := -GetHorizontalScrollPosition;
  y := -GetVerticalScrollPosition;
  r := GetContentClipRect;

  rc := RectF(r.Left + TotalFixedColumnWidth + 1, r.Top + TotalFixedRowHeight + 1, r.Right - TotalFixedRightColumnWidth - 1, r.Bottom - TotalFixedBottomRowHeight - 1);

  if FUseClipRect then
  begin
    rcc := FClipRect;
    rc := RectF(Max(rc.Left, rcc.Left), Max(rc.Top, rcc.Top), Min(rc.Right, rcc.Right), Min(rc.Bottom, rcc.Bottom));
  end;

  cell := TTMSFNCDataGridCellOpen(ACell);
  rg := cell.CalcRect;
  case cell.Kind of
    gckCell: OffsetRectEx(rg, {$IFDEF FMXWEBLIB}Int{$ENDIF}(x) + r.Left, {$IFDEF FMXWEBLIB}Int{$ENDIF}(y) + r.Top);
    gckFixedRowCell, gckFreezeRowCell: OffsetRectEx(rg, {$IFDEF FMXWEBLIB}Int{$ENDIF}(x) + r.Left, r.Top);
    gckFixedColumnCell, gckFreezeColumnCell: OffsetRectEx(rg, r.Left, {$IFDEF FMXWEBLIB}Int{$ENDIF}(y) + r.Top);
    gckFixedRootCell, gckFreezeRootCell, gckFreezeFixedRowCell, gckFreezeFixedColumnCell: OffsetRectEx(rg, r.Left, r.Top);
  end;

  if RectIntersectsWithEx(rg, r) and IsCellVisible(MakeCell(cell.Column, cell.Row)) then
  begin
    cell.Rect := rg;
    case cell.Kind of
      gckCell: cell.ClipRect := RectF(Max(rc.Left, rg.Left), Max(rc.Top, rg.Top), Min(rc.Right, rg.Right), Min(rc.Bottom, rg.Bottom));
      gckFixedRowCell, gckFreezeRowCell: cell.ClipRect := RectF(Max(rc.Left, rg.Left), rg.Top, Min(rc.Right, rg.Right), rg.Bottom);
      gckFixedColumnCell, gckFreezeColumnCell: cell.ClipRect := RectF(rg.Left, Max(rc.Top, rg.Top), rg.Right, Min(rc.Bottom, rg.Bottom));
      gckFixedRootCell, gckFreezeRootCell, gckFreezeFixedRowCell, gckFreezeFixedColumnCell: cell.Cliprect := rg;
    end;

    BeginUsingCellData(cell.Coordinate, cell.Data);
    try
      ConfigureCell(cell, gccmLayout);
    finally
      EndUsingCellData;
    end;

    ADisplay.Add(cell);
  end;
end;

procedure TTMSFNCDataGridRenderer.AddCheckBox(AColumn, ARow: Integer;
  AValue: Boolean);
begin
  SetCheckBox(AColumn, ARow, AValue);
end;

procedure TTMSFNCDataGridRenderer.AddCheckBox(ACell: TTMSFNCDataGridCellCoord;
  AValue: Boolean);
begin
  SetCheckBox(ACell, AValue);
end;

procedure TTMSFNCDataGridRenderer.AddCheckBoxColumn(AColumn: Integer;
  AValue: Boolean);
begin
  SetCheckBoxColumn(AColumn, AValue);
end;

procedure TTMSFNCDataGridRenderer.AddDataCheckBox(AColumn, ARow: Integer);
begin
  SetDataCheckBox(AColumn, ARow);
end;

procedure TTMSFNCDataGridRenderer.AddDataCheckBox(ACell: TTMSFNCDataGridCellCoord);
begin
  SetDataCheckBox(ACell);
end;

procedure TTMSFNCDataGridRenderer.AddDataCheckBoxColumn(AColumn: Integer);
begin
  SetDataCheckBoxColumn(AColumn);
end;

function TTMSFNCDataGridRenderer.AddGrid(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridRenderer;
begin
  Result := SetGrid(ACell);
end;

function TTMSFNCDataGridRenderer.AddGrid(AColumn, ARow: Integer): TTMSFNCDataGridRenderer;
begin
  Result := SetGrid(AColumn, ARow);
end;

procedure TTMSFNCDataGridRenderer.AddDataProgressBar(AColumn, ARow: Integer);
begin
  SetDataProgressBar(AColumn, ARow);
end;

procedure TTMSFNCDataGridRenderer.AddDataProgressBar(
  ACell: TTMSFNCDataGridCellCoord);
begin
  SetDataProgressBar(ACell);
end;

procedure TTMSFNCDataGridRenderer.AddDataProgressBarColumn(AColumn: Integer);
begin
  SetDataProgressBarColumn(AColumn);
end;

procedure TTMSFNCDataGridRenderer.AddProgressBar(AColumn, ARow,
  AValue: Integer);
begin
  SetProgressBar(AColumn, ARow, AValue);
end;

procedure TTMSFNCDataGridRenderer.AddProgressBar(
  ACell: TTMSFNCDataGridCellCoord; AValue: Integer);
begin
  SetProgressBar(ACell, AValue);
end;

procedure TTMSFNCDataGridRenderer.AddProgressBarColumn(AColumn,
  AValue: Integer);
begin
  SetProgressBarColumn(AColumn, AValue);
end;

procedure TTMSFNCDataGridRenderer.AddRadioButton(AColumn, ARow: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  SetRadioButton(AColumn, ARow, AValue, AGroupIndex);
end;

procedure TTMSFNCDataGridRenderer.AddRadioButton(
  ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  SetRadioButton(ACell, AValue, AGroupIndex);
end;

procedure TTMSFNCDataGridRenderer.AddRadioButtonColumn(AColumn: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  SetRadioButtonColumn(AColumn, AValue, AGroupIndex);
end;

procedure TTMSFNCDataGridRenderer.AddDataRadioButton(AColumn, ARow: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  SetDataRadioButton(AColumn, ARow, AValue, AGroupIndex);
end;

procedure TTMSFNCDataGridRenderer.AddDataRadioButton(
  ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  SetDataRadioButton(ACell, AValue, AGroupIndex);
end;

procedure TTMSFNCDataGridRenderer.AddDataRadioButtonColumn(AColumn: Integer;
  AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  SetDataRadioButtonColumn(AColumn, AValue, AGroupIndex);
end;

procedure TTMSFNCDataGridRenderer.AfterExport;
begin
  inherited;
  FClipRect := FSaveClipRect;
end;

procedure TTMSFNCDataGridRenderer.Animate(Sender: TObject);
var
  dx, dy, posx, posy: Double;
  animh, animv: Boolean;
begin
  posy := GetVScrollValue;
  posx := GetHScrollValue;
  dx := Abs(FScrollHTo - posx) / Max(1, Abs(FSpX) * 6);
  dy := Abs(FScrollVTo - posy) / Max(1, Abs(FSpY) * 6);
  animv := False;
  if FAnimateVerticalPos then
    animv := AnimateDouble(posy, FScrollVTo, dy, 0.01);

  animh := False;
  if FAnimateHorizontalPos then
    animh := AnimateDouble(posx, FScrollHTo, dx, 0.01);

  FAnimating := animv or animh;
  if FAnimating then
    Scroll(posx, posy)
  else
  begin
    FAnimateVerticalPos := False;
    AnimateTimer.Enabled := False;
    FAnimateHorizontalPos := False;
  end;
end;

procedure TTMSFNCDataGridRenderer.ApplyExportScale(AScale: Single);
var
  I: Integer;
begin
  FScale := AScale;
  BeginUpdate;
  try
    for I := 0 to ColumnCount - 1 do
      ColumnWidths[I] := ColumnWidths[I] * AScale;

    for I := 0 to RowCount - 1 do
      RowHeights[I] := RowHeights[I] * AScale;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.ResetColumnWidths;
var
  I: Integer;
  cl: TTMSFNCDataGridColumn;
begin
  BeginUpdate;
  try
    for I := 0 to ColumnCount - 1 do
    begin
      cl := GetColumn(I);
      if Assigned(cl) then
        ColumnWidths[I] := cl.Width
      else
        ColumnWidths[I] := DefaultColumnWidth;        
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.ResetExportScale;
var
  I: Integer;
begin
  BeginUpdate;
  try
    for I := 0 to ColumnCount - 1 do
      ColumnWidths[I] := ColumnWidths[I] / FScale;

    for I := 0 to RowCount - 1 do
      RowHeights[I] := RowHeights[I] / FScale;

    FScale := 1.0;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.ResetRowHeights;
var
  I: Integer;
begin
  BeginUpdate;
  try
    for I := 0 to RowCount - 1 do
      RowHeights[I] := DefaultRowHeight; 
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.ApplyCellScale(ACell: TTMSFNCDataGridCell;
  AScale: Single);
begin
  TTMSFNCDataGridCellOpen(ACell).ApplyScale(AScale);
end;

procedure TTMSFNCDataGridRenderer.ApplyStyle;
var
  c: TTMSFNCGraphicsColor;
  cs, vs: TTMSFNCDataGridScrollBar;
begin
  inherited;

  FStyleApplied := True;

  BeginUpdate;
  try
    c := gcNull;
    if TTMSFNCStyles.GetStyleBackgroundFillColor(c) then
    begin
      CellAppearance.NormalLayout.Fill.Color := c;
      CellAppearance.NormalLayout.Fill.Kind := gfkSolid;
      FilterAppearance.Fill.Color := c;
      FilterAppearance.Fill.Kind := gfkSolid;
    end;

    c := gcNull;
    if TTMSFNCStyles.GetStyleLineFillColor(c) then
    begin
      Stroke.Color := c;
      CellAppearance.FixedLayout.Stroke.Color := c;
      CellAppearance.FixedSelectedLayout.Stroke.Color := c;
      CellAppearance.SelectedLayout.Stroke.Color := c;
      CellAppearance.NormalLayout.Stroke.Color := c;
      CellAppearance.FocusedLayout.Stroke.Color := c;
      CellAppearance.BandLayout.Stroke.Color := c;
      CellAppearance.GroupLayout.Stroke.Color := c;
      CellAppearance.SummaryLayout.Stroke.Color := c;
      FilterAppearance.Stroke.Color := c;
    end;

    c := gcNull;
    if TTMSFNCStyles.GetStyleHeaderFillColor(c) then
    begin
      CellAppearance.FixedLayout.Fill.Kind := gfkSolid;
      CellAppearance.FixedLayout.Fill.Color := c;
    end;

    c := gcNull;
    if TTMSFNCStyles.GetStyleHeaderFillColorTo(c) then
    begin
      CellAppearance.FixedLayout.Fill.Kind := gfkGradient;
      CellAppearance.FixedLayout.Fill.ColorTo := c;
    end;

    c := gcNull;
    if TTMSFNCStyles.GetStyleSelectionFillColor(c) then
    begin
      CellAppearance.FixedSelectedLayout.Fill.Color := c;
      CellAppearance.FocusedLayout.Fill.Color := c;
      CellAppearance.SelectedLayout.Fill.Color := c;
      CellAppearance.GroupLayout.Fill.Color := Lighter(c, 20);
      CellAppearance.SummaryLayout.Fill.Color := Lighter(c, 40);
      CellAppearance.FixedSelectedLayout.Fill.Kind := gfkSolid;
      CellAppearance.FocusedLayout.Fill.Kind := gfkSolid;
      CellAppearance.SelectedLayout.Fill.Kind := gfkSolid;
      CellAppearance.GroupLayout.Fill.Kind := gfkSolid;
      CellAppearance.SummaryLayout.Fill.Kind := gfkSolid;
    end;

    c := gcNull;
    if TTMSFNCStyles.GetStyleSelectionFillColorTo(c) then
    begin
      CellAppearance.FixedSelectedLayout.Fill.ColorTo := c;
      CellAppearance.FocusedLayout.Fill.ColorTo := c;
      CellAppearance.SelectedLayout.Fill.ColorTo := c;
      CellAppearance.GroupLayout.Fill.ColorTo := Lighter(c, 20);
      CellAppearance.SummaryLayout.Fill.ColorTo := Lighter(c, 40);
      CellAppearance.FixedSelectedLayout.Fill.Kind := gfkGradient;
      CellAppearance.FocusedLayout.Fill.Kind := gfkGradient;
      CellAppearance.SelectedLayout.Fill.Kind := gfkGradient;
      CellAppearance.GroupLayout.Fill.Kind := gfkGradient;
      CellAppearance.SummaryLayout.Fill.Kind := gfkGradient;
    end;

    c := gcNull;
    if TTMSFNCStyles.GetStyleTextFontColor(c) then
    begin
      CellAppearance.FixedLayout.Font.Color := c;
      CellAppearance.NormalLayout.Font.Color := c;
      CellAppearance.FixedSelectedLayout.Font.Color := c;
      CellAppearance.SelectedLayout.Font.Color := c;
      CellAppearance.FocusedLayout.Font.Color := c;
      CellAppearance.GroupLayout.Font.Color := Blend(c, gcBlack, 30);
      CellAppearance.SummaryLayout.Font.Color := Blend(c, gcBlack, 30);
      FilterAppearance.Font.Color := c;
    end;

    cs := CustomHorizontalScrollBar;
    if Assigned(cs) then
      cs.ApplyStyle;

    vs := CustomVerticalScrollBar;
    if Assigned(vs) then
      vs.ApplyStyle;

  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.Assign(Source: TPersistent);
begin
  BeginUpdate;
  try
    inherited;
    if Source is TTMSFNCDataGridRenderer then
    begin
      FCellAppearance.Assign((Source as TTMSFNCDataGridRenderer).CellAppearance);
      FCustomScrollBars := (Source as TTMSFNCDataGridRenderer).CustomScrollBars;
      FDragAppearance.Assign((Source as TTMSFNCDataGridRenderer).DragAppearance);
      FFilterAppearance.Assign((Source as TTMSFNCDataGridRenderer).FilterAppearance);
      FGlobalFont.Assign((Source as TTMSFNCDataGridRenderer).GlobalFont);
      FHorizontalScrollBarVisible := (Source as TTMSFNCDataGridRenderer).HorizontalScrollBarVisible;
      FVerticalScrollBarVisible := (Source as TTMSFNCDataGridRenderer).VerticalScrollBarVisible;
      FScrollMode := (Source as TTMSFNCDataGridRenderer).ScrollMode;
      FScrollUpdate := (Source as TTMSFNCDataGridRenderer).ScrollUpdate;
      FStretchScrollBars := (Source as TTMSFNCDataGridRenderer).StretchScrollBars;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.InternalSaveToImageStream(AStream: TStream);
var
  g: TTMSFNCGraphics;
begin
  g := TTMSFNCGraphics.CreateBitmapCanvas(Round(TotalColumnWidth), Round(TotalRowHeight));
  try
    g.BeginScene;
    &Export(g, RectF(0, 0 ,TotalColumnWidth, TotalRowHeight), MakeCellRange(0, 0, ColumnCount - 1, RowCount - 1),
      False, False, True);
    g.EndScene;
    g.Bitmap.SaveToStream(AStream);
  finally
    g.Free;
  end;
end;

procedure TTMSFNCDataGridRenderer.InternalSaveToImage(AFileName: string);
var
  ms: TMemoryStream;
  procedure InternalOutputToImage;
  begin
    ms.Position := 0;
    InternalSaveToImageStream(ms);
  end;
begin
  ms := TMemoryStream.Create;
  try
    InternalOutputToImage;
    ms.SaveToFile(AFileName);
    if Options.IO.OpenAfterCreation then
      TTMSFNCUtils.OpenFile(AFileName);
  finally
    ms.Free;
  end;
end;

procedure TTMSFNCDataGridRenderer.InternalSaveToHTML(AFileName: string; AEncoding: TEncoding);
var
  ms: TMemoryStream;
  procedure InternalOutputToHTML;
  var
    d: string;
  begin
    d := ExtractFilePath(AFileName);
    d := StringReplace(d, '\', '/', [rfReplaceAll]);

    ms.Position := 0;
    InternalSaveToHTMLStream(ms, AEncoding, d);
  end;
begin
  ms := TMemoryStream.Create;
  try
    InternalOutputToHTML;
    ms.SaveToFile(AFileName);
    if Options.IO.OpenAfterCreation then
      TTMSFNCUtils.OpenFile(AFileName);
  finally
    ms.Free;
  end;
end;

procedure TTMSFNCDataGridRenderer.SaveToImageData(AFileName: string);
begin
  InternalSaveToImage(AFileName);
end;

procedure TTMSFNCDataGridRenderer.SaveToImageStreamData(AStream: TStream);
begin
  InternalSaveToImageStream(AStream);
end;

procedure TTMSFNCDataGridRenderer.SaveToHTMLData(AFileName: string; AEncoding: TEncoding);
begin
  InternalSaveToHTML(AFileName, AEncoding);
end;

procedure TTMSFNCDataGridRenderer.SaveToHTMLStreamData(AStream: TStream; AEncoding: TEncoding);
begin
  InternalSaveToHTMLStream(AStream, AEncoding);
end;

procedure TTMSFNCDataGridRenderer.SaveToHTMLData(AFileName: string);
begin
  InternalSaveToHTML(AFileName, TEncoding.Default);
end;

procedure TTMSFNCDataGridRenderer.SaveToHTMLStreamData(AStream: TStream);
begin
  InternalSaveToHTMLStream(AStream, TEncoding.Default);
end;

procedure TTMSFNCDataGridRenderer.AutoSizeColumn(AColumn: Integer; const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
var
  g: TTMSFNCGraphics;
begin
  BeginUpdate;
  g := TTMSFNCGraphics.CreateBitmapCanvas;
  try
    g.BeginScene;
    InternalAutoSizeColumn(AColumn, AMode, APadding, g);
    g.EndScene;
  finally
    g.Free;
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.AutoSizeRow(ARow: Integer; const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
var
  g: TTMSFNCGraphics;
begin
  BeginUpdate;
  g := TTMSFNCGraphics.CreateBitmapCanvas;
  try
    g.BeginScene;
    InternalAutoSizeRow(ARow, AMode, APadding, g);
    g.EndScene;
  finally
    g.Free;
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.InternalAutoSizeColumn(AColumn: Integer; const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5; const AGraphics: TTMSFNCGraphics = nil);
var
  i, k: integer;
  m: single;
  totalh: Single;
  rs: Integer;
  c: TTMSFNCDataGridCellCoord;
  cellr: TRectF;
  cls: TTMSFNCDataGridCellClass;
  ck: TTMSFNCDataGridCellKind;
  ci: TTMSFNCDataGridCell;
  bc, br: Integer;
  d: TTMSFNCDataGridCellItem;
  sz: TSizeF;
  sr, er: Integer;
begin
  m := 0;

  if AMode = gamVisibleCells then
  begin
    sr := StartRow;
    er := StopRow;
  end
  else
  begin
    sr := 0;
    er := RowCount - 1;
  end;

  for i := sr to er do
  begin
    c := MakeCell(AColumn, i);
    c := BaseCell(c);

    if not ((c.Column = AColumn) and (c.Row = i)) then
      Continue;

    rs := RowSpan(c);

    totalh := 0;
    if rs > 0 then
    begin
      for K := c.Row to c.Row + rs - 1 do
        totalh := totalh + RowHeights[K];
    end
    else
      totalh := RowHeights[c.Row];

    bc := c.Column;
    br := c.Row;

    cellr := RectF(0, 0, 10000, totalh - 4);

    cls := GetBaseCellClass(MakeCell(bc, br));
    DoGetCellClass(bc, br, cls);
    if Assigned(cls) then
    begin
      ck := GetCellKind(MakeCell(bc, br));
      d := CellData[bc, br];
      ci := cls.Create(cellr, bc, br, d, ck, Self);

      if Assigned(ci) then
      begin
        try
          ConfigureCell(ci);
          sz := TTMSFNCDataGridCellOpen(ci).GetRequiredSize(AGraphics);
          if sz.cx > m then
            m := sz.cx;
        finally
          ci.Free;
        end;
      end;
    end;
  end;

  ColumnWidths[AColumn] := m + APadding;
end;

procedure TTMSFNCDataGridRenderer.InternalAutoSizeRow(ARow: Integer; const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5; const AGraphics: TTMSFNCGraphics = nil);
var
  i, k: integer;
  m: single;
  totalw: Single;
  cs: Integer;
  c: TTMSFNCDataGridCellCoord;
  cellr: TRectF;
  cls: TTMSFNCDataGridCellClass;
  ck: TTMSFNCDataGridCellKind;
  ci: TTMSFNCDataGridCell;
  bc, br: Integer;
  d: TTMSFNCDataGridCellItem;
  sz: TSizeF;
  sc, ec: Integer;
begin
  m := 0;

  if AMode = gamVisibleCells then
  begin
    sc := StartColumn;
    ec := StopColumn;
  end
  else
  begin
    sc := 0;
    ec := ColumnCount - 1;
  end;

  for i := sc to ec do
  begin
    c := MakeCell(i, ARow);
    c := BaseCell(c);

    if not ((c.Column = i) and (c.Row = ARow)) then
      Continue;

    cs := ColumnSpan(c);

    totalw := 0;
    if cs > 0 then
    begin
      for K := c.Column to c.Column + cs - 1 do
        totalw := totalw + ColumnWidths[K];
    end
    else
      totalw := ColumnWidths[c.Column];

    bc := c.Column;
    br := c.Row;

    cellr := RectF(0, 0, totalw - 4, 10000);

    cls := GetBaseCellClass(MakeCell(bc, br));
    DoGetCellClass(bc, br, cls);
    if Assigned(cls) then
    begin
      ck := GetCellKind(MakeCell(bc, br));
      d := CellData[bc, br];
      ci := cls.Create(cellr, bc, br, d, ck, Self);

      if Assigned(ci) then
      begin
        try
          ConfigureCell(ci);
          sz := TTMSFNCDataGridCellOpen(ci).GetRequiredSize(AGraphics);
          if sz.cy > m then
            m := sz.cy;
        finally
          ci.Free;
        end;
      end;
    end;
  end;

  RowHeights[ARow] := m + APadding;
end;

procedure TTMSFNCDataGridRenderer.AutoSizeRows(const AMode: TTMSFNCDataGridAutoSizeMode = gamAllCells; const APadding: Integer = 5);
var
  i: Integer;
  g: TTMSFNCGraphics;
begin
  BeginUpdate;
  g := TTMSFNCGraphics.CreateBitmapCanvas;
  try
    g.BeginScene;
    for i := 0 to RowCount - 1 do
      InternalAutoSizeRow(i, AMode, APadding, g);
    g.EndScene;
  finally
    g.Free;
    EndUpdate;
  end;
end;

function TTMSFNCDataGridRenderer.XYToCell(X, Y: Double): TTMSFNCDataGridCellCoord;
var
  c: TTMSFNCDataGridCellCoord;

  function FindCell(ADisplayList: TTMSFNCDataGridDisplayList): TTMSFNCDataGridCellCoord;
  var
    cf: TTMSFNCDataGridCellCoord;
    I: Integer;
    cl: TTMSFNCDataGridCell;
  begin
    cf.Column := -1;
    cf.Row := -1;
    Result := cf;
    for I := 0 to ADisplayList.Count - 1 do
    begin
      cl := ADisplayList[I];
      if PtInRectEx(cl.Rect, PointF(X, Y)) then
      begin
        cf.Column := cl.Column;
        cf.Row := cl.Row;
        Result := cf;
        Break;
      end;
    end;
  end;
begin
  c.Column := -1;
  c.Row := -1;

  Result := c;

  c := FindCell(FFixedRootCellDisplay);
  if IsCellValid(c) and (IsCellFixed(c) or IsCellFreeze(c)) then
  begin
    Result := c;
    Exit;
  end;

  c := FindCell(FFixedRowCellDisplay);
  if IsCellValid(c) and (IsCellFixed(c) or IsCellFreeze(c)) then
  begin
    Result := c;
    Exit;
  end;

  c := FindCell(FFixedColumnCellDisplay);
  if IsCellValid(c) and (IsCellFixed(c) or IsCellFreeze(c)) then
  begin
    Result := c;
    Exit;
  end;

  c := FindCell(FNormalCellDisplay);
  if IsCellValid(c) and not (IsCellFixed(c) or IsCellFreeze(c)) then
  begin
    Result := c;
    Exit;
  end;
end;

function TTMSFNCDataGridRenderer.XYToColumn(X, Y: Double): Integer;
begin
  Result := XYToCell(X, Y).Column;
end;

function TTMSFNCDataGridRenderer.XYToRenderer(X,
  Y: Double): TTMSFNCDataGridRenderer;
var
  obj: TTMSFNCDataGridCell;
  pr, r: TTMSFNCDataGridRenderer;
begin
  obj := GetCellObjectForCell(XYToCell(X, Y));

  pr := nil;
  if Assigned(obj) and (obj is TTMSFNCDataGridRendererCell) then
  begin
    r := (obj as TTMSFNCDataGridRendererCell).Renderer;
    while Assigned(r) do
    begin
      pr := r;
      r := pr.XYToRenderer(X, Y);
    end;
  end;

  Result := pr;
end;

function TTMSFNCDataGridRenderer.XYToRow(X, Y: Double): Integer;
begin
  Result := XYToCell(X, Y).Row;
end;

function TTMSFNCDataGridRenderer.SaveToHTMLString(ARange: TTMSFNCDataGridCellCoordRange; AImageDirectory: string): string;
var
  i, j: Integer;
  s, al, av, afs, afe, ass, ase, tablestyle: string;
  VAlignValue: TTMSFNCGraphicsTextAlign;
  slist: TStringlist;
  AlignValue: TTMSFNCGraphicsTextAlign;
  wraptxt, colwtxt, CellText, SpanTxt: string;
  Span: TPoint;
  cssval: string;
  cssidx: integer;
  CSSList: TStringList;
  res: string;
  Pic, bmp: TTMSFNCBitmap;
  images_counter: Integer;
  htmlhdr: string;
  summ: string;
  mi: TTMSFNCDataGridCellMergeInfo;
  v: TTMSFNCDataGridCellValue;
  l: TTMSFNCDataGridCellLayout;

  function HTMLAddImage(Pic: TTMSFNCBitmap; counter: Integer; dir: string): string;
  var
    fname: string;
    tt: string;
  begin
    {$IFNDEF WEBLIB}
    if not DirectoryExists(dir + Options.IO.HTML.ImageFolder) then
      if not CreateDir(dir + Options.IO.HTML.ImageFolder) then
        raise Exception.Create('Cannot create ' + dir + 'images');

    tt := TTMSFNCUtils.GetImageType(Pic);
    if tt = '' then
      tt := '.svg';

    fname := dir + Options.IO.HTML.ImageFolder + '/' + Options.IO.HTML.ImageBaseName + inttostr(counter) + tt;
    Pic.SaveToFile(fname);
    Result := fname;
    {$ENDIF}
  end;

  function MakeHREF(s: string): string;
  begin
    Result := s;

//    if not URLshow then
//      Exit;

    if (Pos('://', s) > 0) and (Pos('</', s) = 0) then
    begin
//      if not URLFull then
//        Result := '<a href=' + s + '>' + copy(s, Pos('://', s) + 3, 255) + '</a>'
//      else
        Result := '<a href=' + s + '>' + s + '</a>';
    end;

    if (Pos('mailto:', s) > 0) and (Pos('</', s) = 0) then
    begin
//      if not URLFull then
//        Result := '<a href=' + s + '>' + copy(s, Pos('mailto:', s) + 7, 255) + '</a>'
//      else
        Result := '<a href=' + s + '>' + s + '</a>';
    end;
  end;

  procedure AppendStr(var s: string; newstr: string);
  begin
    if s = '' then
      s := newstr
    else
      s := s + ';' + newstr;
  end;

  function CSS(ALayout: TTMSFNCDataGridCellLayout): string;
  var
    res: string;
  begin
    res := '';

    if (ALayout.Fill.Color <> gcNull) and (ALayout.Fill.Kind <> gfkNone) and Options.IO.HTML.ExportColors then
      AppendStr(res,'background-color: '+ TTMSFNCGraphics.ColorToHTML(ALayout.Fill.Color)+'');

    if (ALayout.Font.Color <> gcNull) and Options.IO.HTML.ExportColors then
      AppendStr(res,'color: '+ TTMSFNCGraphics.ColorToHTML(ALayout.Font.Color)+'');

    AppendStr(res,'font-size:'+IntTostr(Round(ALayout.Font.Size))+'pt');

    if TFontStyle.fsItalic in ALayout.Font.Style then
      AppendStr(res,'font-style: italic');

    if TFontStyle.fsBold in ALayout.Font.Style then
      AppendStr(res,'font-weight: bold');

    AppendStr(res,'font-family: '+ALayout.Font.Name+'');

    case ALayout.TextAlign of
      gtaCenter: AppendStr(res,'text-align: center');
      gtaTrailing: AppendStr(res,'text-align: right');
    end;

    case ALayout.VerticalTextAlign of
      gtaLeading: AppendStr(res,'vertical-align: top');
      gtaCenter: AppendStr(res,'vertical-align: middle');
      gtaTrailing: AppendStr(res,'vertical-align: bottom');
    end;

    Result := res;
  end;

  function ExportImg(ACol, ARow: integer; lst: TStringList; celltext: string; Pic: TTMSFNCBitmap): string;
  var
    image: string;
  begin
    if Assigned(Pic) and not IsBitmapEmpty(Pic) then
    begin
      image := '<img src= "file://' + HTMLAddImage(Pic, images_counter, AImageDirectory)+'">';
      lst.Add('<td class="c'+inttostr(cssidx)+'"'+spantxt + wraptxt + colwtxt+'>' + s + image + '</td>');
      inc(images_counter);
    end
    else
      lst.Add('<td class="c'+inttostr(cssidx)+'"'+spantxt + wraptxt + colwtxt+'>' +s+'</td>');
  end;

begin
  SList := TStringlist.Create;
  SList.Sorted := False;
  CSSList := TStringList.Create;

  ExportNotification(gesExportStart, ARange.StartCell.Row);

  try

    htmlhdr := '';

    if Options.IO.HTML.XHTML then
    begin
      htmlhdr := htmlhdr + '<?xml version="1.0" encoding="utf-8"?>'#13#10
      +'<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Strict//EN"'#13#10
      +'"http://www.w3.org/TR/xhtml1/DTD/xhtml1-strict.dtd">'#13#10
      +'<HTML xmlns="http://www.w3.org/1999/xhtml" xml:lang="en" lang="en">'#13#10;
    end;

    htmlhdr := htmlhdr + Options.IO.HTML.HeaderText;

    if Options.IO.HTML.PrefixTag <> '' then
      htmlhdr := htmlhdr + Options.IO.HTML.PrefixTag;

    if Options.IO.HTML.TableStyle <> '' then
      TableStyle := ' ' + Options.IO.HTML.TableStyle + ' '
    else
      TableStyle := '';

    if Options.IO.HTML.Summary <> '' then
      summ := 'summary="' + Options.IO.HTML.Summary + '" '
    else
      summ := '';

    SList.Add('<table border="'+IntToStr(Options.IO.HTML.TableBorderSize)+'" '+
        'cellspacing="'+IntToStr(Options.IO.HTML.TableCellSpacing)+'" ' +
        'cellpadding="'+IntToStr(Options.IO.HTML.TableCellPadding)+'" ' +
        summ +
        TableStyle +
        ' width="' + IntToStr(Options.IO.HTML.TableWidth) + '%">');

    images_counter := 1;

    for i := ARange.StartCell.Row to ARange.StopCell.Row do
    begin
      ExportNotification(gesExportNextRow, i);
      SList.Add('<tr>');

      for j := ARange.StartCell.Column to ARange.StopCell.Column do
      begin
        if IsCellBase(MakeCell(j, i)) and IsCellVisible(MakeCell(j, i)) then
        begin
          mi := MergeInfo[j, i];
          Span := Point(mi.ColumnSpan, mi.RowSpan);

          SpanTxt := '';

          if Span.X > 0 then
            SpanTxt := ' colspan="' + IntToStr(Span.X) + '"';

          if Span.Y > 0 then
            SpanTxt := SpanTxt + ' rowspan="' + IntToStr(Span.Y) + '"';

          v := Cells[j, i];

          DoSaveCellData(j, i, v);

          CellText := ValueToString(v);

          s := CellText;

          if Options.IO.HTML.ConvertSpecialChars then
            s := TTMSFNCUtils.FixMarkup(s);

          if Options.IO.HTML.NonBreakingText then
            s := FixNonBreaking(s);

          s := HTMLLineBreaks(s);
          s := MakeHREF(s);

          al := '';
          afs := '';
          afe := '';
          ass := '';
          ase := '';

          l := GetCellAppearance(GetCellState(MakeCell(j, i)), MakeCell(j, i));

          AlignValue := gtaLeading;
          if Assigned(l) then
            AlignValue := l.TextAlign;

          if (AlignValue <> gtaLeading) and (s <> '') then
          begin
            if AlignValue = gtaTrailing then
              al := ' align="right"';

            if AlignValue = gtaCenter then
              al := ' align="center"';
          end;

          VAlignValue := gtaLeading;
          if Assigned(l) then
            VAlignValue := l.VerticalTextAlign;

          if (s <> '') and (VAlignValue <> gtaCenter) then
          begin
            if VAlignValue = gtaLeading then
              av := ' valign="top"';
            if VAlignValue = gtaTrailing then
              av := ' valign="bottom"';
          end;


          cssval := CSS(l);

          cssidx := CSSList.IndexOf(cssval);
          if cssidx = -1 then
            cssidx := CSSList.Add(cssval);

  //        if (FHTMLSettings.ColWidths.Count > j - strtcol) and not DoneColW then
  //          colwtxt := ' width=' + IntToStr(FHTMLSettings.ColWidths.Items[j - strtcol ])+'px'
  //        else
            colwtxt := '';

          if SysUtils.Trim(s) = '' then
            s := '&nbsp;';

          if l.WordWrapping then
            WrapTxt := ''
          else
            WrapTxt := ' nowrap="nowrap"';

          pic := nil;
          if Options.IO.HTML.ExportImages then
          begin
            bmp := Bitmaps[j, i];
            if Assigned(bmp) and not IsBitmapEmpty(bmp) then
              pic := bmp
            else if Assigned(BitmapContainer) and (BitmapNames[j, i] <> '') then
              pic := BitmapContainer.FindBitmap(BitmapNames[j, i]);
          end;

          ExportImg(j,i,SList, celltext, pic);
        end;
      end;

      SList.Add('</tr>');
    end;

    SList.Add('</table>');

    if Options.IO.HTML.SuffixTag <> '' then
      SList.Add(Options.IO.HTML.SuffixTag);

    if Options.IO.HTML.FooterText <> '' then
      SList.Add(Options.IO.HTML.FooterText);

    if Options.IO.HTML.XHTML then
      SList.Add('</HTML>');

    res := '<style>'#13#10;

    for cssidx := 0 to CSSList.Count - 1 do
      res := res + '.c'+inttostr(cssidx)+' {'+csslist[cssidx]+'}'#13#10;

    res := res + '</style>'#13#10;

    Result := htmlhdr + res + SList.Text;
  finally
    SList.Free;
    CSSList.Free;
    ExportNotification(gesExportDone, -1);
  end;
end;

procedure TTMSFNCDataGridRenderer.InternalSaveToHTMLStream(AStream: TStream; AEncoding: TEncoding; AImageDirectory: string = '');
var
  s: string;
  sl: TTMSFNCDataGridDataFileStringList;
begin
  sl := TTMSFNCDataGridDataFileStringList.Create;
  try
    s := SaveToHTMLString(MakeCellRange(Options.IO.StartColumn, Options.IO.StartRow, ColumnCount - 1,
      TTMSFNCUtils.IfElse<Integer>(Options.IO.MaxRows > -1, Options.IO.MaxRows, RowCount - 1)), AImageDirectory);

    sl.WriteLn(s);
    sl.SaveToStream(AStream, AEncoding);
  finally
    sl.Free;
  end;
end;

function TTMSFNCDataGridRenderer.IsBlocked: Boolean;
begin
  Result := FBlockScrollingUpdate;
end;

function TTMSFNCDataGridRenderer.IsCellDisplayList(
  ADisplay: TTMSFNCDataGridDisplayList): Boolean;
begin
  Result := (ADisplay is TTMSFNCDataGridNormalCellDisplayList) or
            (ADisplay is TTMSFNCDataGridFixedColumnCellDisplayList) or
            (ADisplay is TTMSFNCDataGridFixedRootCellDisplayList) or
            (ADisplay is TTMSFNCDataGridFixedRowCellDisplayList);
end;

function TTMSFNCDataGridRenderer.IsCellReadOnly(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := IsCellFixed(ACell) or ReadOnlys[ACell.Column, ACell.Row];
end;

function TTMSFNCDataGridRenderer.OnlyLastFullVisibleRow: Boolean;
begin
  Result := inherited OnlyLastFullVisibleRow;
  if Assigned(Adapter) and (ScrollMode = gsmCellScrolling) then
    Result := True;
end;

procedure TTMSFNCDataGridRenderer.OptionsChanged(Sender: TObject);
begin
  Display;
end;

procedure TTMSFNCDataGridRenderer.OptionsChangedRebuild(Sender: TObject);
begin
  Update;
end;

procedure TTMSFNCDataGridRenderer.RemoveLayout(AColumn, ARow: Integer);
var
  cd: TTMSFNCDataGridCellItem;
  o: TObject;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
    begin
      o := TTMSFNCDataGridCellItemExtendedOpen(AsExtendedCell(cd)).Layout;
      if Assigned(o) then
        TTMSFNCDataGridCellItemExtendedOpen(AsExtendedCell(cd)).DestroyLayout;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.RemoveFilterEditor(AColumn, ARow: Integer);
var
  cd: TTMSFNCDataGridCellItem;
  o: TObject;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
    begin
      o := TTMSFNCDataGridCellItemExtendedOpen(AsExtendedCell(cd)).FilterEditor;
      if Assigned(o) then
        TTMSFNCDataGridCellItemExtendedOpen(AsExtendedCell(cd)).DestroyFilterEditor;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.RemoveGrid(AColumn, ARow: Integer);
var
  cd: TTMSFNCDataGridCellItem;
  o: TObject;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) then
    begin
      o := TTMSFNCDataGridCellItemExtendedOpen(AsExtendedCell(cd)).Renderer;
      if Assigned(o) then
        TTMSFNCDataGridCellItemExtendedOpen(AsExtendedCell(cd)).DestroyRenderer;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.RemoveCheckBox(
  ACell: TTMSFNCDataGridCellCoord);
begin
  RemoveCheckBox(ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.RemoveDataCheckBox(
  ACell: TTMSFNCDataGridCellCoord);
begin
  RemoveDataCheckBox(ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.RemoveDataCheckBox(AColumn, ARow: Integer);
var
  cd: TTMSFNCDataGridCellItem;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) and (AsExtendedCell(cd).&Type = gcitDataCheckBox) then
      AsExtendedCell(cd).&Type := gcitNormal;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.RemoveDataProgressBar(
  ACell: TTMSFNCDataGridCellCoord);
begin
  RemoveDataProgressBar(ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.RemoveDataProgressBar(AColumn, ARow: Integer);
var
  cd: TTMSFNCDataGridCellItem;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) and (AsExtendedCell(cd).&Type = gcitDataProgressBar) then
      AsExtendedCell(cd).&Type := gcitNormal;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.RemoveCellControlFromCache(AColumn,
  ARow: Integer);
var
  cl: TTMSFNCDataGridCell;
begin
  inherited;
  cl := GetCellObjectForCell(MakeCell(AColumn, ARow));
  if Assigned(cl) then
    cl.ClearControlCache;
end;

procedure TTMSFNCDataGridRenderer.RemoveCheckBox(AColumn, ARow: Integer);
var
  cd: TTMSFNCDataGridCellItem;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) and (AsExtendedCell(cd).&Type = gcitCheckBox) then
      AsExtendedCell(cd).&Type := gcitNormal;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.RemoveDataRadioButton(
  ACell: TTMSFNCDataGridCellCoord);
begin
  RemoveDataRadioButton(ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.RemoveDataRadioButton(AColumn, ARow: Integer);
var
  cd: TTMSFNCDataGridCellItem;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) and (AsExtendedCell(cd).&Type = gcitDataRadioButton) then
      AsExtendedCell(cd).&Type := gcitNormal;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.RemoveRadioButton(
  ACell: TTMSFNCDataGridCellCoord);
begin
  RemoveRadioButton(ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.RemoveRadioButton(AColumn, ARow: Integer);
var
  cd: TTMSFNCDataGridCellItem;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) and (AsExtendedCell(cd).&Type = gcitRadioButton) then
      AsExtendedCell(cd).&Type := gcitNormal;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.RemoveProgressBar(
  ACell: TTMSFNCDataGridCellCoord);
begin
  RemoveProgressBar(ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.RemoveProgressBar(AColumn, ARow: Integer);
var
  cd: TTMSFNCDataGridCellItem;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) and (AsExtendedCell(cd).&Type = gcitProgressBar) then
      AsExtendedCell(cd).&Type := gcitNormal;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.RemoveBitmap(ACell: TTMSFNCDataGridCellCoord);
begin
  RemoveBitmap(ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.RegisterRuntimeClasses;
begin
  inherited;
  RegisterClass(TTMSFNCDataGridColumn);
end;

procedure TTMSFNCDataGridRenderer.RemoveBitmap(AColumn, ARow: Integer);
var
  cd: TTMSFNCDataGridCellItem;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) and (AsExtendedCell(cd).&Type = gcitBitmap) then
      AsExtendedCell(cd).&Type := gcitNormal;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.RemoveButton(
  ACell: TTMSFNCDataGridCellCoord);
begin
  RemoveButton(ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.RemoveButton(AColumn, ARow: Integer);
var
  cd: TTMSFNCDataGridCellItem;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellData[AColumn, ARow];
    if Assigned(cd) and IsExtendedCell(cd) and (AsExtendedCell(cd).&Type = gcitButton) then
      AsExtendedCell(cd).&Type := gcitNormal;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.RemoveFilterEditor(ACell: TTMSFNCDataGridCellCoord);
begin
  RemoveFilterEditor(ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.RemoveLayout(ACell: TTMSFNCDataGridCellCoord);
begin
  RemoveLayout(ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.RemoveGrid(ACell: TTMSFNCDataGridCellCoord);
begin
  RemoveGrid(ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.ResetScrollBarCapture;
begin
  if Assigned(FCapturedScrollbar) then
    FCapturedScrollbar.HandleMouseLeave;

  FCapturedScrollbar := nil;
end;

procedure TTMSFNCDataGridRenderer.ResetToDefaultStyle;
var
  cs, vs: TTMSFNCDataGridScrollBar;
begin
  FStyleApplied := False;

  BeginUpdate;
  try
    CellAppearance.BandLayout.Fill.Kind := gfkSolid;
    CellAppearance.BandLayout.Fill.Color := gcLightyellow;
    CellAppearance.FixedLayout.Fill.Kind := gfkSolid;
    CellAppearance.FixedLayout.Fill.Color := Lighter(MakeGraphicsColor(238, 238, 238), 10);
    CellAppearance.NormalLayout.Fill.Kind := gfkSolid;
    CellAppearance.NormalLayout.Fill.Color := gcWhite;
    CellAppearance.GroupLayout.Fill.Kind := gfkSolid;
    CellAppearance.GroupLayout.Fill.Color := Lighter(MakeGraphicsColor(238, 238, 238), 10);
    CellAppearance.SummaryLayout.Fill.Kind := gfkSolid;
    CellAppearance.SummaryLayout.Fill.Color := Lighter(MakeGraphicsColor(245, 245, 230), 10);
    CellAppearance.SelectedLayout.Fill.Kind := gfkSolid;
    CellAppearance.SelectedLayout.Fill.Color := Lighter(gcLightsteelblue, 80);
    CellAppearance.FocusedLayout.Fill.Kind := gfkSolid;
    CellAppearance.FocusedLayout.Fill.Color := Lighter(gcLightsteelblue, 40);
    CellAppearance.FixedSelectedLayout.Fill.Kind := gfkSolid;
    CellAppearance.FixedSelectedLayout.Fill.Color := Lighter(gcLightsteelblue, 80);

    CellAppearance.BandLayout.Font.Color := gcBlack;
    CellAppearance.FixedLayout.Font.Color := gcBlack;
    CellAppearance.NormalLayout.Font.Color := gcBlack;
    CellAppearance.GroupLayout.Font.Color := gcBlack;
    CellAppearance.SummaryLayout.Font.Color := gcBlack;
    CellAppearance.SelectedLayout.Font.Color := gcBlack;
    CellAppearance.FocusedLayout.Font.Color := gcBlack;
    CellAppearance.FixedSelectedLayout.Font.Color := gcBlack;

    FilterAppearance.Fill.Color := gcWhite;
    FilterAppearance.Fill.Kind := gfkSolid;
    FilterAppearance.Stroke.Color := gcSilver;
    FilterAppearance.Stroke.Kind := gskSolid;
    FilterAppearance.Font.Color := gcBlack;

    cs := CustomHorizontalScrollBar;
    if Assigned(cs) then
      cs.ApplyStyle;

    vs := CustomVerticalScrollBar;
    if Assigned(vs) then
      vs.ApplyStyle;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.RestoreScrollPosition;
begin
  Scroll(FSaveHScrollPos, FSaveVScrollPos);
end;

function TTMSFNCDataGridRenderer.RowStretchingActive: Boolean;
begin
  Result := FRowStretchingActive;
end;

procedure TTMSFNCDataGridRenderer.SaveScrollPosition;
begin
  FSaveHScrollPos := GetHScrollValue;
  FSaveVScrollPos := GetVScrollValue;
end;

procedure TTMSFNCDataGridRenderer.CutToClipboard(ASelectedCells: Boolean);
begin
  CopyToClipboard(ASelectedCells);
  if ASelectedCells then
    ClearCells(Selection)
  else
    Clear;
end;

procedure TTMSFNCDataGridRenderer.PaddingChanged(Sender: TObject);
begin
  Update;
end;

procedure TTMSFNCDataGridRenderer.PasteFromClipboard;
var
  cliptext: string;
  endofRow: Integer;
  cr: Integer;
  ct,line, clipt, ec:string;
  s,ns,z,tabpos: Integer;
  Allow: Boolean;
  nc: Boolean;
begin
  BeginUpdate;
  try
    cliptext := TTMSFNCClipBoard.GetText;

    s := Selection.StartCell.Column;
    z := Selection.StartCell.Row;

    if (s = -1) or (z = -1) then
      Exit;

    endofRow := Length(cliptext);

    repeat
      {$IFDEF WEBLIB}
      cr := Pos(#13, cliptext);
      {$ELSE}
      {$IFDEF MSWINDOWS}
      cr := Pos(#13, cliptext);
      {$ELSE}
      cr := Pos(#10, cliptext);
      {$ENDIF}
      {$ENDIF}

      if cr = 0 then
        cr := EndOfRow;
      Line := Copy(cliptext,1, cr - 1);

      if (Length(Line) > 0) and (Options.Clipboard.PasteAction = gpaInsert) then
        InsertRows(z, 1);

      TabPos := -1;
      while (TTMSFNCUtils.VarPos(#9,Line,TabPos) > 0) do
      begin
        ns := s;
        Inc(ns);

        if (ns > ColumnCount) and Options.Clipboard.AllowColumnGrow then
         ColumnCount := ns;

        ct := Copy(line,1,TabPos - 1);

        if (Pos(#10,ct) > 0) then
        begin
          if (1 > 0) then
          begin
            if Pos('"',ct)=1 then Delete(ct,1,1);
            if Pos('"',ct) = Length(ct) then
              Delete(ct,Length(ct),1);

            CSVToLineFeeds(ct);
          end;
        end;

        if (s <= ColumnCount) and (z <= RowCount) then
        begin
          if Options.Clipboard.IgnoreReadOnly or not IsCellReadOnly(MakeCell(s, z)) then
          begin
            Allow := True;

            if Allow then
              Cells[s,z] := ct;
          end;
        end;

        Delete(line,1,tabpos);
        inc(s);
        if (s > ColumnCount) and Options.Clipboard.AllowColumnGrow then
          ColumnCount := s;
      end;

      nc := false;

      if (s <= ColumnCount) and (z <= RowCount) then
      begin
        if Options.Clipboard.IgnoreReadOnly or not IsCellReadOnly(MakeCell(s, z)) then
        begin
          if (cr <> EndOfRow) or (Line <> '') then
          begin
            Allow := True;
            nc := true;

            ns := s;

            if nc then
             Inc(ns);

            if (ns > ColumnCount) and Options.Clipboard.AllowColumnGrow then
             ColumnCount := ns;

            if (Pos(#10,Line) > 0) then
            begin
              if Pos('"',Line)=1 then Delete(Line,1,1);
              if Pos('"',Line) = Length(Line) then
                Delete(Line,Length(Line),1);
              CSVToLineFeeds(Line);
            end;

            if Allow then
            begin
              if (s <= ColumnCount) and (z <= RowCount) then
                Cells[s,z] := Line;
            end;
          end;
        end;
      end;

      if nc then
        Inc(s);

      if (s > ColumnCount) and Options.Clipboard.AllowColumnGrow then
        ColumnCount := s;

      clipt := Copy(cliptext, cr + 1, Length(cliptext) - cr);

      if Copy(clipt, Length(clipt), 1) = #10 then
        clipt := Copy(cliptext, cr + 2, Length(cliptext) - cr - 1);

      cliptext := clipt;

      s := Selection.StartCell.Column;

      ec := copy(cliptext, Length(cliptext), 1);

      if (ec <> #0) then
        Inc(z);

      if (z = RowCount) and (cr <> EndOfRow) and (ec <> '') and Options.Clipboard.AllowRowGrow then
        RowCount := RowCount + 1;

    until cr = EndOfRow;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.CopyToClipboard(ASelectedCells: Boolean);
var
  s,z: Integer;
  ct, cliptext: string;
  ACellRange: TTMSFNCDataGridCellCoordRange;
begin
  if ASelectedCells then
    ACellRange := Selection
  else
    ACellRange := MakeCellRange(0, 0, ColumnCount - 1, RowCount - 1);

  if (ACellRange.StartCell.Row = -1) or (ACellRange.StartCell.Column = -1) or (ACellRange.StopCell.Row = -1) or (ACellRange.StopCell.Column = -1) then
    Exit;

  cliptext := '';

  for z := ACellRange.StartCell.Row to ACellRange.StopCell.Row do
  begin
    for s := ACellRange.StartCell.Column to ACellRange.StopCell.Column do
    begin
      ct := Strings[s,z];

      if (Pos(#13,ct) > 0) then
      begin
        ct := '"' + CRToLF(ct) + '"';
      end;

      if (Pos('</',ct) > 0) then
        ct := TTMSFNCUtils.HTMLStrip(ct);

      if (LinesInText(ct,true)>1) then
        LineFeedsToCSV(ct);

      if s < ACellRange.StopCell.Column then
        cliptext := cliptext + ct + #9
      else
        cliptext := cliptext + ct;
    end;

    if ACellRange.StartCell.Row < ACellRange.StopCell.Row then
      cliptext := cliptext + #13#10;
  end;

  TTMSFNCClipBoard.SetText(cliptext);
end;

procedure TTMSFNCDataGridRenderer.LastPage;
begin
  GoToPage(PageCount - 1);
end;

function TTMSFNCDataGridRenderer.LocalRect: TRectF;
begin
  Result := inherited LocalRect;
  Result := RectF(Padding.Left + Result.Left, Padding.Top + Result.Top, Result.Right - Padding.Right, Result.Bottom - Padding.Bottom);
end;

function TTMSFNCDataGridRenderer.Lookup(AValue: string; AAllRows,
  AAutoGoto: Boolean): TTMSFNCDataGridCellCoord;
var
  I: Integer;
begin
  Result := MakeCell(-1, -1);
  for I := FocusedCell.Column to ColumnCount - 1 do
  begin
    Result := LookupInColumn(I, AValue, AAllRows, AAutoGoto);
    if (Result.Column <> -1) and (Result.Row <> -1) then
      Break;
  end;
end;

function TTMSFNCDataGridRenderer.LookupInColumn(AColumn: Integer; AValue: string;
  AAllRows: Boolean = False; AAutoGoto: Boolean = False): TTMSFNCDataGridCellCoord;
var
  sr: Integer;
begin
  if AAllRows then
    sr := 0
  else
    sr := FixedRowCount;

  Result := LookupInColumnFromRow(AColumn, sr, AValue, AAllRows, AAutoGoto);
end;

function TTMSFNCDataGridRenderer.LookupInColumnFromRow(AColumn, ARow: Integer; AValue: string;
  AAllRows: Boolean = False; AAutoGoto: Boolean = False): TTMSFNCDataGridCellCoord;
var
  sr, er, m, r: Integer;
  s, v: string;
begin
  Result := MakeCell(-1, -1);

  v := AValue;
  if not Options.Lookup.CaseSensitive then
    v := Uppercase(v);

  if AAllRows then
  begin
    sr := FixedRowCount;
    er := RowCount - 1;
  end
  else
  begin
    sr := ARow;
    er := RowCount - 1 - FixedBottomRowCount;
  end;

  r := -1;

  for m := sr to er do
  begin
    s := Strings[AColumn, m];
    if (Pos('</', s) > 0) then
      s := TTMSFNCUtils.HTMLStrip(s);

    if not Options.Lookup.CaseSensitive then
      s := UpperCase(s);

    if Pos(v, s) = 1  then
    begin
      r := m;
      Break;
    end;
  end;

  if r <> -1 then
  begin
    Result := MakeCell(AColumn, r);
    if AAutoGoto then
      FocusedCell := Result;
  end;
end;

procedure TTMSFNCDataGridRenderer.MarkCells(AHilightText: string;
  AOptions: TTMSFNCDataGridMarkOptions);
begin
  case AOptions.Mode of
    ghmCell: InternalMarkCells(AHilightText, 'e', AOptions.CaseSensitive, AOptions.Column, AOptions.Row, AOptions.Column, AOptions.Row, AOptions.FullText);
    ghmColumn:
    begin
      if AOptions.IncludeFixed then
        InternalMarkCells(AHilightText, 'e', AOptions.CaseSensitive, AOptions.Column, 0, AOptions.Column, RowCount - 1, AOptions.FullText)
      else
        InternalMarkCells(AHilightText, 'e', AOptions.CaseSensitive, AOptions.Column, FixedRowCount, AOptions.Column, RowCount - 1 - FixedBottomRowCount, AOptions.FullText);
    end;
    ghmRow:
    begin
      if AOptions.IncludeFixed then
        InternalMarkCells(AHilightText, 'e', AOptions.CaseSensitive, 0, AOptions.Row, ColumnCount - 1, AOptions.Row, AOptions.FullText)
      else
        InternalMarkCells(AHilightText, 'e', AOptions.CaseSensitive, FixedColumnCount, AOptions.Row, ColumnCount - 1 - FixedRightColumnCount, AOptions.Row, AOptions.FullText);
    end;
    ghmGrid:
    begin
      if AOptions.IncludeFixed then
        InternalMarkCells(AHilightText, 'e', AOptions.CaseSensitive, 0, 0, ColumnCount - 1, RowCount - 1, AOptions.FullText)
      else
        InternalMarkCells(AHilightText, 'e', AOptions.CaseSensitive, FixedColumnCount, FixedRowCount, ColumnCount - 1 - FixedRightColumnCount, RowCount - 1 - FixedBottomRowCount, AOptions.FullText);
    end;
  end;
end;

function TTMSFNCDataGridRenderer.MarkText(ACaseSensitive: Boolean; AValue,
  AText: string): string;
begin
  Result := Hilight(AValue, AText, 'e', ACaseSensitive, False);
end;

procedure TTMSFNCDataGridRenderer.MoveColumnData(AFromColumn,
  AToColumn: Integer);
var
  pb: TPair<Integer, Boolean>;
  pk: TPair<TTMSFNCDataGridCellCoord, Boolean>;
  R: Integer;
begin
  inherited;
  if SelectedColumnDictionary.ContainsKey(AFromColumn) then
  begin
    pb := SelectedColumnDictionary.ExtractPair(AFromColumn);
    SelectedColumns[AToColumn] := pb.Value;
    SelectedColumnDictionary.Remove(AFromColumn);

    if SelectedCellDictionary.Count > 0 then
    begin
      for R := 0 to RowCount - 1 do
      begin
        pk := SelectedCellDictionary.ExtractPair(MakeCell(AFromColumn, R));
        SelectedCells[AToColumn, R] := pk.Value;
        SelectedCellDictionary.Remove(MakeCell(AFromColumn, R));
      end;
    end;
  end;

  SynchronizeColumns;
  if (AFromColumn >= 0) and (AFromColumn <= Columns.Count - 1) and
    (AToColumn >= 0) and (AToColumn <= Columns.Count - 1) then
      Columns[AFromColumn].Index := AToColumn;
end;

procedure TTMSFNCDataGridRenderer.MoveRowData(AFromRow, AToRow: Integer);
var
  pb: TPair<Integer, Boolean>;
  pk: TPair<TTMSFNCDataGridCellCoord, Boolean>;
  C: Integer;
begin
  inherited;
  if SelectedRowDictionary.ContainsKey(AFromRow) then
  begin
    pb := SelectedRowDictionary.ExtractPair(AFromRow);
    SelectedRows[AToRow] := pb.Value;
    SelectedRowDictionary.Remove(AFromRow);

    if SelectedCellDictionary.Count > 0 then
    begin
      for C := 0 to ColumnCount - 1 do
      begin
        pk := SelectedCellDictionary.ExtractPair(MakeCell(C, AFromRow));
        SelectedCells[C, AToRow] := pk.Value;
        SelectedCellDictionary.Remove(MakeCell(C, AFromRow));
      end;
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.HandleKey(var AKeyInfo: TTMSFNCDataGridKeyInfo);
var
  sel, real: TTMSFNCDataGridCellCoord;
  cs, rs{, i, j}: Integer;
  Allow: Boolean;
  obj: TTMSFNCDataGridCell;
  cl: Boolean;
  ce, cc: Boolean;
  s, r, c: Integer;
begin
  case AKeyInfo.Kind of
    gkkPress:
    begin
      if AKeyInfo.Key in [KEY_TAB] then
        Exit;

      if not Options.Editing.Enabled and Options.Lookup.Enabled then
      begin
        if AKeyInfo.Key in [KEY_ESCAPE,KEY_INSERT,KEY_DELETE,KEY_UP,KEY_DOWN,KEY_LEFT,KEY_RIGHT,KEY_HOME,KEY_END,KEY_PRIOR,KEY_NEXT,KEY_TAB] then
          FLookupString := ''
        else
        begin
          LookupTimer.Enabled := False;

          if AKeyInfo.KeyChar <> #0 then
          begin
            if Options.Lookup.Incremental then
              FLookupString := FLookupString + AKeyInfo.KeyChar
            else
              FLookupString := AKeyInfo.KeyChar;

            LookupTimer.Enabled := True;

            cl := True;

            DoBeforeLookup(FLookupString, cl);

            if cl then
            begin
              Lookup(FLookupString, True, True);
              DoAfterLookup(FLookupString);
            end;
          end;
        end;
      end;

      if Options.Editing.Enabled then
      begin
        Allow := True;
        DoCanEditCell(FocusedCell.Column, FocusedCell.Row, Allow);

        if Allow then
        begin
          if (AKeyInfo.Key >= 32) then
          begin
            EditCell(FocusedCell, gcsStart, True, AKeyInfo.KeyChar, AKeyInfo.Key);
            AKeyInfo.KeyChar := #0;
            Exit;
          end;
        end;
      end;
    end;
    gkkDown:
    begin
      if AKeyInfo.Key = KEY_TAB then
        Exit;

      if AKeyInfo.Key in [KEY_ESCAPE,KEY_INSERT,KEY_DELETE,KEY_UP,KEY_DOWN,KEY_LEFT,KEY_RIGHT,KEY_HOME,KEY_END,KEY_PRIOR,KEY_NEXT,KEY_TAB] then
        FLookupString := '';

      Allow := True;
      DoCanEditCell(FocusedCell.Column, FocusedCell.Row, Allow);

      if Allow then
      begin
        if (AKeyInfo.Key = KEY_F2) or (AKeyInfo.Key = KEY_RETURN) then
        begin
          EditCell(FocusedCell);
          Exit;
        end;
      end;

      if (AKeyInfo.Key = KEY_SPACE) then
      begin
        obj := GetCellObjectForCell(FocusedCell);
        if Assigned(obj) then
          obj.HandleKey(AKeyInfo);

        Exit;
      end;

      if (ssShift in AKeyInfo.Shift) and not (ssCtrl in AKeyInfo.Shift) {$IFDEF FMXLIB}and not (ssCommand in AKeyInfo.Shift){$ENDIF} and (not (ssAlt in AKeyInfo.Shift)) and
          (AKeyInfo.Key = KEY_INSERT) and not FEditing and Options.Clipboard.Enabled then
            PasteFromClipboard;

      if ((ssCtrl in AKeyInfo.Shift) {$IFDEF FMXLIB}or (ssCommand in AKeyInfo.Shift){$ENDIF}) and not (ssShift in AKeyInfo.Shift) and not (ssAlt in AKeyInfo.Shift) then
      begin
        case AKeyInfo.Key of
          Ord('M'):
          begin
            if Options.Keyboard.CellMergeShortcut then
            begin
              MergeRange(Selection);
              AKeyInfo.Key := 0;
            end;
          end;
          Ord('S'):
          begin
            if Options.Keyboard.CellMergeShortcut then
            begin
              SplitCell(Selection.StartCell);
              AKeyInfo.Key := 0;
            end;
          end;
          Ord('X'):
          begin
            if Options.Clipboard.Enabled and not FEditing then
            begin
              Allow := True;
              DoBeforeCutToClipboard(Allow);
              if Allow then
              begin
                CutToClipboard(True);
                DoAfterCutToClipboard;
              end;
            end;
          end;
          Ord('C'):
          begin
            if Options.Clipboard.Enabled and not FEditing then
            begin
              Allow := True;
              DoBeforeCopyToClipboard(Allow);
              if Allow then
              begin
                CopyToClipboard(True);
                DoAfterCopyToClipboard;
              end;
            end;
          end;
          Ord('V'):
          begin
            if Options.Clipboard.Enabled and not FEditing then
            begin
              Allow := True;
              DoBeforePasteFromClipboard(Allow);
              if Allow then
              begin
                PasteFromClipboard;
                DoAfterPasteFromClipboard;
              end;
            end;
          end;
          Ord('A'): SelectAllCells;
          Ord('F'):
          begin
            if Options.Keyboard.FindShortcut and not FEditing then
            begin
              AKeyInfo.Key := 0;
              ShowFindDialog;
            end;
          end;
          Ord('H'):
          begin
            if Options.Keyboard.ReplaceShortcut and not FEditing then
            begin
              AKeyInfo.Key := 0;
              ShowReplaceDialog;
            end;
          end;
          KEY_DELETE:
          begin
            if Options.Clipboard.Enabled then
              CutToClipboard;
          end;
          KEY_INSERT:
          begin
            if Options.Clipboard.Enabled then
              CopyToClipboard
          end;
          KEY_NEXT: TopRow := TopRow + GetPageScrollSize;
          KEY_PRIOR: TopRow := TopRow - GetPageScrollSize;
          KEY_UP:
          begin
            TopRow := TopRow - 1;
          end;
          KEY_DOWN:
          begin
            rs := RowSpan(MakeCell(LeftColumn, TopRow));
            if rs > 0 then
              TopRow := TopRow + rs
            else
              TopRow := TopRow + 1;
          end;
          KEY_LEFT:
          begin
            sel := FocusedCell;
            if IsRowNode(sel.Row) and (sel.Column = Options.Column.NodeIndex) then
            begin
              cc := True;
              DoCellBeforeNodeCollapse(sel, cc);
              if cc then
              begin
                CollapseNode(sel.Row);
                DoCellAfterNodeCollapse(sel);
              end;
            end
            else
              LeftColumn := LeftColumn - 1;
          end;
          KEY_RIGHT:
          begin
            sel := FocusedCell;
            if IsRowNode(sel.Row) and (sel.Column = Options.Column.NodeIndex) then
            begin
              ce := True;
              DoCellBeforeNodeExpand(sel, ce);
              if ce then
              begin
                ExpandNode(sel.Row);
                DoCellAfterNodeExpand(sel);
              end;
            end
            else
            begin
              cs := ColumnSpan(MakeCell(LeftColumn, TopRow));
              if cs > 0 then
                LeftColumn := LeftColumn + cs
              else
                LeftColumn := LeftColumn + 1;
            end;
          end;
          KEY_HOME:
          begin
            case Options.Keyboard.HomeKeyHandling of
              ghkhFirstColumn: LeftColumn := FixedColumnCount;
              ghkhFirstRow: TopRow := FixedRowCount;
            end;
          end;
          KEY_END:
          begin
            case Options.Keyboard.EndKeyHandling of
              genkhLastColumn: LeftColumn := ColumnCount - 1 - FixedRightColumnCount;
              genkhLastRow: TopRow := RowCount - 1 - FixedBottomRowCount;
            end;
          end;
        end;
      end
      else
      begin
        sel := FocusedCell;
        real := FRealCell;

        case AKeyInfo.Key of
          KEY_DELETE:
          begin
            if AKeyInfo.Shift = [] then
            begin
              if Options.Keyboard.DeleteKeyHandling = gdkhDeleteRow then
              begin
                if RowCount > FixedRowCount + FixedBottomRowCount then
                begin
                  Allow := true;
                  DoCanDeleteRow(sel.Row, Allow);
                  if Allow then
                  begin
                    DeleteRow(sel.Row);
                    s := sel.Row;
                    sel.Row := Max(FixedRowCount, Min(sel.Row, RowCount - 1 - FixedBottomRowCount));
                    DoDeleteRow(s);
                  end;
                end;
              end;
            end;
          end;
          KEY_INSERT:
          begin
            if AKeyInfo.Shift = [] then
            begin
              if sel.Row = -1 then
                sel.Row := FixedRowCount - 1;

              case Options.Keyboard.InsertKeyHandling of
                gikhInsertRowBefore:
                begin
                  Allow := true;
                  DoCanInsertRow(sel.Row,Allow);
                  if Allow then
                  begin
                    InsertRow(sel.Row);
                    s := sel.Row;
                    sel.Row := Max(FixedRowCount, Min(sel.Row, RowCount - 1 - FixedBottomRowCount));
                    DoInsertRow(s);
                  end;
                end;
                gikhInsertRowAfter:
                begin
                  Allow := true;
                  DoCanInsertRow(sel.Row,Allow);
                  if Allow then
                  begin
                    s := sel.Row + 1;
                    InsertRow(s);
                    sel.Row := Max(FixedRowCount, Min(sel.Row + 1, RowCount - 1 - FixedBottomRowCount));
                    DoInsertRow(s);
                  end;
                end;
              end;
            end;
          end;
          KEY_F3:
          begin
            if Options.Keyboard.FindShortcut then
            begin
              FindNext(not (ssShift in AKeyInfo.Shift));
              AKeyInfo.Key := 0;
            end;
          end;
          KEY_NEXT:
          begin
            sel := NextSelectableRow(sel.Column, sel.Row + GetPageScrollSize - 1);
            sel.Column := real.Column;
          end;
          KEY_PRIOR:
          begin
            sel := PreviousSelectableRow(sel.Column, sel.Row - GetPageScrollSize + 1);
            sel.Column := real.Column;
          end;
          KEY_UP:
          begin
            sel := PreviousSelectableRow(sel.Column, sel.Row);
            sel.Column := real.Column;
          end;
          KEY_DOWN:
          begin
            r := sel.Row;
            sel := NextSelectableRow(sel.Column, sel.Row);
            if (sel.Row = r) and Options.Keyboard.AppendRowOnArrowKeyDown then
            begin
              Allow := True;
              sel.Row := sel.Row + 1;
              DoCanAppendRow(sel.Row, Allow);
              if Allow then
              begin
                InsertRow(sel.Row);
                DoAppendRow(sel.Row);
              end;
            end;

            sel.Column := real.Column;
          end;
          KEY_LEFT:
          begin
            sel := PreviousSelectableColumn(sel.Column, sel.Row);
            sel.Row := real.Row;
          end;
          KEY_RIGHT:
          begin
            c := sel.Column;
            sel := NextSelectableColumn(sel.Column, sel.Row);

            if (sel.Column = c) and Options.Keyboard.AppendColumnOnArrowKeyRight then
            begin
              Allow := True;
              sel.Column := sel.Column + 1;
              DoCanAppendColumn(sel.Column, Allow);
              if Allow then
              begin
                InsertColumn(sel.Column);
                DoAppendColumn(sel.Column);
              end;
            end;

            sel.Row := real.Row;
          end;
          KEY_HOME:
          begin
            if Options.Keyboard.HomeKeyHandling = ghkhFirstRow then
            begin
              sel.Row := FixedRowCount;
              sel.Column := real.Column;
              sel := NextSelectableRow(sel.Column, sel.Row - 1);
            end
            else if Options.Keyboard.HomeKeyHandling = ghkhFirstColumn then
            begin
              sel.Column := FixedColumnCount;
              sel.Row := real.Row;
              sel := NextSelectableColumn(sel.Column - 1, sel.Row);
            end;
          end;
          KEY_END:
          begin
            if Options.Keyboard.EndKeyHandling = genkhLastRow then
            begin
              sel.Row := RowCount - 1 - FixedBottomRowCount;
              sel.Column := real.Column;
              sel := PreviousSelectableRow(sel.Column, sel.Row + 1);
            end
            else if Options.Keyboard.EndKeyHandling = genkhLastColumn then
            begin
              sel.Column := ColumnCount - 1 - FixedRightColumnCount;
              sel.Row := real.Row;
              sel := PreviousSelectableColumn(sel.Column + 1, sel.Row);
            end;
          end;
        end;

        case AKeyInfo.Key of
          KEY_UP, KEY_LEFT, KEY_DOWN, KEY_RIGHT, KEY_HOME, KEY_END, KEY_NEXT, KEY_PRIOR, KEY_DELETE, KEY_INSERT:
          begin
            if (ssCtrl in AKeyInfo.Shift) {$IFDEF FMXLIB}or (ssCommand in AKeyInfo.Shift){$ENDIF} then
              Navigate(sel)
            else
              SelectCell(sel, AKeyInfo.Shift);

            AKeyInfo.Key := 0;
          end;
        end;
      end;
    end;
    gkkUp:
    begin
      if (AKeyInfo.Key = KEY_SPACE) then
      begin
        obj := GetCellObjectForCell(FocusedCell);
        if Assigned(obj) then
          obj.HandleKey(AKeyInfo);
      end;
    end;
    gkkDialog:
    begin
      FTabKey := gtkmNone;
      if (AKeyInfo.Key = KEY_TAB) and (Options.Keyboard.TabKeyHandling = gtkhMixed) then
      begin
        if ssShift in AKeyInfo.Shift then
          FTabKey := gtkmShift
        else
          FTabKey := gtkmNormal;
      end;

      if IsFocused or ((FInplaceEditor <> nil) and TTMSFNCDataGridInplaceEditorOpen(FInplaceEditor).IsFocused) then
      begin
        ProcessTab(AKeyInfo, Options.Keyboard.TabKeyDirection, Options.Keyboard.TabKeyHandling, Options.Keyboard.TabKeyDirectEdit);
      end;
    end;
  end;
end;

function TTMSFNCDataGridRenderer.ProcessEnterKey(
  ACell: TTMSFNCDataGridCellCoord; AKeyHandling: TTMSFNCDataGridEnterKeyHandling): TTMSFNCDataGridCellCoord;
var
  c, r: Integer;
  cl: TTMSFNCDataGridCellCoord;
  Allow: Boolean;
begin
  cl := ACell;
  case AKeyHandling of
    gekhNextColumn, gekhNextColumnInRow, gekhNextColumnAndAppend, gekhNextColumnAndRowAppend:
    begin
      c := cl.Column;

      cl := NextSelectableColumn(cl.Column, cl.Row);
      if cl.Column = c then
      begin
        case Options.Keyboard.EnterKeyHandling of
          gekhNextColumn, gekhNextColumnAndRowAppend:
          begin
            cl.Column := FixedColumnCount - 1;
            r := cl.Row;
            cl.Row := Min(RowCount - 1 - FixedBottomRowCount, cl.Row + 1);
            cl := NextSelectableColumn(cl.Column, cl.Row);

            if r = cl.Row then
            begin
              if (Options.Keyboard.EnterKeyHandling = gekhNextColumnAndRowAppend) then
              begin
                Allow := True;
                cl.Row := cl.Row + 1;
                DoCanAppendRow(cl.Row, Allow);
                if Allow then
                begin
                  InsertRow(cl.Row);
                  DoAppendRow(cl.Row);
                end;
              end
              else
              begin
                cl.Row := FixedRowCount;
                cl.Column := FixedColumnCount;
              end;
            end;
          end;
          gekhNextColumnInRow, gekhNextColumnAndAppend:
          begin
            if (Options.Keyboard.EnterKeyHandling = gekhNextColumnInRow) then
              cl.Column := FixedColumnCount - 1;

            r := cl.Row;
            c := cl.Column;
            cl := NextSelectableColumn(cl.Column, cl.Row);

            if (c = cl.Column) and (Options.Keyboard.EnterKeyHandling = gekhNextColumnAndAppend) then
            begin
              Allow := True;
              cl.Column := cl.Column + 1;
              DoCanAppendColumn(cl.Column, Allow);
              if Allow then
              begin
                InsertColumn(cl.Column);
                DoAppendColumn(cl.Column);
              end;
            end
            else if r = cl.Row then
              cl.Column := FixedColumnCount;
          end;
        end;

        if IsCellDisabled(cl) then
          cl := NextSelectableColumn(cl.Column, cl.Row);
      end;
    end;
    gekhNextRow, gekhNextRowInColumn, gekhNextRowAndAppend, gekhNextRowAndColumnAppend:
    begin
      r := cl.Row;

      cl := NextSelectableRow(cl.Column, cl.Row);
      if cl.Row = r then
      begin
        case Options.Keyboard.EnterKeyHandling of
          gekhNextRow, gekhNextRowAndColumnAppend:
          begin
            cl.Row := FixedRowCount - 1;
            c := cl.Column;
            cl.Column := Min(ColumnCount - 1 - FixedRightColumnCount, cl.Column + 1);
            cl := NextSelectableRow(cl.Column, cl.Row);

            if c = cl.Column then
            begin
              if (Options.Keyboard.EnterKeyHandling = gekhNextRowAndColumnAppend) then
              begin
                Allow := True;
                cl.Column := cl.Column + 1;
                DoCanAppendColumn(cl.Column, Allow);
                if Allow then
                begin
                  InsertColumn(cl.Column);
                  DoAppendColumn(cl.Column);
                end;
              end
              else
              begin
                cl.Row := FixedRowCount;
                cl.Column := FixedColumnCount;
              end;
            end;
          end;
          gekhNextRowInColumn, gekhNextRowAndAppend:
          begin
            if (Options.Keyboard.EnterKeyHandling = gekhNextRowInColumn) then
              cl.Row := FixedRowCount - 1;

            c := cl.Column;
            r := cl.Row;
            cl := NextSelectableRow(cl.Column, cl.Row);

            if (r = cl.Row) and (Options.Keyboard.EnterKeyHandling = gekhNextRowAndAppend) then
            begin
              Allow := True;
              cl.Row := cl.Row + 1;
              DoCanAppendRow(cl.Row, Allow);
              if Allow then
              begin
                InsertRow(cl.Row);
                DoAppendRow(cl.Row);
              end;
            end
            else if c = cl.Column then
              cl.Row := FixedRowCount;
          end;
        end;

        if IsCellDisabled(cl) then
          cl := NextSelectableColumn(cl.Column, cl.Row);
      end;
    end;
  end;

  Result := cl;
end;

procedure TTMSFNCDataGridRenderer.ProcessTab(var AKeyInfo: TTMSFNCDataGridKeyInfo; Mode: TTMSFNCDataGridTabKeyDirection; Handling: TTMSFNCDataGridTabKeyHandling; DirectEdit: Boolean; CellStart: TTMSFNCDataGridEditCellStart = gcsNone);
var
  cl, clc, rl: TTMSFNCDataGridCellCoord;
  c, r: integer;
  a, Allow: Boolean;
  SaveFocusCell: TTMSFNCDataGridCellCoord;
begin
  if (AKeyInfo.Key = KEY_TAB) and (RowCount > FixedRowCount + FixedBottomRowCount) and (ColumnCount > FixedColumnCount + FixedRightColumnCount) then
  begin
    SaveFocusCell := FocusedCell;
    a := StopEditing;
    if not a then
    begin
      AKeyInfo.Key := 0;
      Exit;
    end;

    FocusedCell := SaveFocusCell;
    if IsFocused then
    begin
      case Handling of
        gtkhNextCell: AKeyInfo.Key := 0;
        gtkhMixed:
        begin
          if ssShift in AKeyInfo.Shift then
          begin
            cl.Column := FixedColumnCount;
            cl.Row := FixedRowCount;

            if IsCellDisabled(cl) then
            begin
              case Mode of
                gtkdNextColumnCell: cl := NextSelectableColumn(cl.Column, cl.Row);
                gtkdNextRowCell: cl := NextSelectableRow(cl.Column, cl.Row);
              end;
            end;

            if (Handling = gtkhMixed) and not ((FocusedCell.Column = cl.Column) and (FocusedCell.Row = cl.Row)) then
              AKeyInfo.Key := 0;
          end
          else
          begin
            cl.Column := ColumnCount - 1 - FixedRightColumnCount;
            cl.Row := RowCount - 1 - FixedBottomRowCount;

            if IsCellDisabled(cl) then
            begin
              case Mode of
                gtkdNextColumnCell: cl := PreviousSelectableColumn(cl.Column, cl.Row);
                gtkdNextRowCell: cl := PreviousSelectableRow(cl.Column, cl.Row);
              end;
            end;

            if (Handling = gtkhMixed) and not ((FocusedCell.Column = cl.Column) and (FocusedCell.Row = cl.Row)) then
              AKeyInfo.Key := 0;
          end;
        end
      end;

      if AKeyInfo.Key = 0 then
      begin
        case Handling of
          gtkhNextCell, gtkhMixed:
          begin
            cl := FocusedCell;
            rl := FRealCell;
            cl := BaseCell(cl);

            if ssShift in AKeyInfo.Shift then
            begin
              clc := MakeCell(FixedColumnCount, FixedRowCount);
              if (FocusedCell.Column = clc.Column) and (FocusedCell.Row = clc.Row) then
              begin
                clc := MakeCell(ColumnCount - 1 - FixedRightColumnCount, RowCount - 1 - FixedBottomRowCount);
                FocusedCell := clc;
                Exit;
              end;

              case Mode of
                gtkdNextColumnCell:
                begin
                  c := cl.Column;
                  cl := PreviousSelectableColumn(cl.Column, cl.Row);
                  cl.Row := rl.Row;
                  if (c = cl.Column) then
                  begin
                    r := cl.row;
                    cl.Column := ColumnCount - 1 - FixedRightColumnCount;
                    cl := PreviousSelectableColumn(cl.Column, cl.Row);
                    if r = cl.Row then
                    begin
                      cl := PreviousSelectableColumn(ColumnCount - FixedRightColumnCount, cl.Row - 1);
                      while (cl.Column = ColumnCount - FixedRightColumnCount) and (cl.Row = r - 1) do
                      begin
                        r  := cl.Row;
                        cl := PreviousSelectableColumn(ColumnCount - FixedRightColumnCount, cl.Row - 1);
                      end;
                    end;
                    if (Handling = gtkhNextCell) and ((cl.Row < FixedRowCount) or (r = cl.row)) then
                    begin
                      cl.Column := ColumnCount - 1 - FixedRightColumnCount;
                      cl.Row := RowCount - 1 - FixedBottomRowCount;
                      cl := PreviousSelectableColumn(cl.Column, cl.Row);
                    end;
                  end;
                end;
                gtkdNextRowCell:
                begin
                  r := cl.Row;
                  cl := PreviousSelectableRow(cl.Column, cl.Row);
                  cl.Column := rl.Column;
                  if (r = cl.Row) then
                  begin
                    c := cl.Column;
                    cl.Row := RowCount - 1 - FixedBottomRowCount;
                    cl := PreviousSelectableRow(cl.Column, cl.Row);
                    if c = cl.Column then
                    begin
                      cl := PreviousSelectableRow(cl.Column - 1, RowCount - FixedBottomRowCount);
                      while (cl.Row = RowCount - FixedBottomRowCount) and (cl.Column = c - 1) do
                      begin
                        c  := cl.Column;
                        cl := PreviousSelectableRow(cl.Column - 1, RowCount - FixedBottomRowCount);
                      end;
                    end;
                    if (Handling = gtkhNextCell) and ((cl.Column < FixedColumnCount) or (c = cl.Column)) then
                    begin
                      cl.Column := ColumnCount - 1 - FixedRightColumnCount;
                      cl.Row := RowCount - 1 - FixedBottomRowCount;
                      cl := PreviousSelectableRow(cl.Column, cl.Row);
                    end;
                  end;
                end;
              end;
              SelectCell(cl);
              if DirectEdit then
              begin
                Allow := True;
                DoCanEditCell(FocusedCell.Column, FocusedCell.Row, Allow);
                if Allow then
                  EditCell(FocusedCell, CellStart);
              end;
            end
            else
            begin
              clc := MakeCell(ColumnCount - 1 - FixedRightColumnCount, RowCount - 1 - FixedBottomRowCount);
              if (FocusedCell.Column = clc.Column) and (FocusedCell.Row = clc.Row) then
              begin
                clc := MakeCell(FixedColumnCount, FixedRowCount);
                FocusedCell := clc;
                Exit;
              end;

              case Mode of
                gtkdNextColumnCell:
                begin
                  c := cl.Column;
                  cl := NextSelectableColumn(cl.Column, cl.Row);
                  cl.Row := rl.Row;
                  if (c = cl.Column) then
                  begin
                    r := cl.row;
                    cl.Column := FixedColumnCount;
                    cl := NextSelectableColumn(cl.Column, cl.Row);
                    if r = cl.Row then
                    begin
                      cl := NextSelectableColumn(FixedColumnCount - 1, cl.Row + 1);
                      while (cl.Column = FixedColumnCount - 1) and (cl.Row = r + 1) do
                      begin
                        r := cl.Row;
                        cl := NextSelectableColumn(FixedColumnCount - 1, cl.row + 1);
                      end;
                    end;

                    if (Handling = gtkhNextCell) and ((cl.Row > RowCount - 1 - FixedBottomRowCount) or (r = cl.row)) then
                    begin
                      cl.Column := FixedColumnCount;
                      cl.Row := FixedRowCount;
                      cl := NextSelectableColumn(cl.Column, cl.Row);
                    end;
                  end;
                end;
                gtkdNextRowCell:
                begin
                  r := cl.Row;
                  cl := NextSelectableRow(cl.Column, cl.Row);
                  cl.Column := rl.Column;
                  if (r = cl.Row) then
                  begin
                    c := cl.Column;
                    cl.Row := FixedRowCount;
                    cl := NextSelectableRow(cl.Column, cl.Row);
                    if c = cl.Column then
                    begin
                      cl := NextSelectableRow(cl.Column + 1, FixedRowCount - 1);
                      while (cl.Row = FixedRowCount - 1) and (cl.Column = c + 1) do
                      begin
                        c := cl.Column;
                        cl := NextSelectableRow(cl.Column + 1, FixedRowCount - 1);
                      end;
                    end;
                    if (Handling = gtkhNextCell) and ((cl.Column > ColumnCount - 1 - FixedRightColumnCount) or (c = cl.Column)) then
                    begin
                      cl.Column := FixedColumnCount;
                      cl.Row := FixedRowCount;
                      cl := NextSelectableRow(cl.Column, cl.Row);
                    end;
                  end;
                end;
              end;
              SelectCell(cl);
              if DirectEdit then
              begin
                Allow := True;
                DoCanEditCell(FocusedCell.Column, FocusedCell.Row, Allow);
                if Allow then
                  EditCell(FocusedCell, CellStart);
              end;
            end;
          end;
        end;
      end;
    end
    else
    begin
      if Handling <> gtkhNextControl then
      begin
        if ssShift in AKeyInfo.Shift then
        begin
          cl.Column := ColumnCount - 1 - FixedRightColumnCount;
          cl.Row := RowCount - 1 - FixedBottomRowCount;
          if IsCellDisabled(cl) then
          begin
            case Mode of
              gtkdNextColumnCell: cl := PreviousSelectableColumn(cl.Column, cl.Row);
              gtkdNextRowCell: cl := PreviousSelectableRow(cl.Column, cl.Row);
            end;
          end;
          SelectCell(cl);
        end
        else
        begin
          cl.Column := FixedColumnCount;
          cl.Row := FixedRowCount;
          if IsCellDisabled(cl) then
          begin
            case Mode of
              gtkdNextColumnCell: cl := NextSelectableColumn(cl.Column, cl.Row);
              gtkdNextRowCell: cl := NextSelectableRow(cl.Column, cl.Row);
            end;
          end;
          SelectCell(cl)
        end;
      end;
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.HandleMouse(
  var AMouseInfo: TTMSFNCDataGridMouseInfo);
var
  sel, cl, cell, res: TTMSFNCDataGridCellCoord;
  obj: TTMSFNCDataGridCell;
  sx, sy: Single;
  newsz: Double;
  fxd, fixed, rowsz, colsz: Boolean;
  allow: Boolean;
  newposx: Single;
  newposy: Single;
  bctl: TTMSFNCDataGridMouseInfo;
  cr: TCursor;
  r, contentr: TRectF;
  hss, vss: Single;
  enablescroll, b, hld: Boolean;
  h, v, f: Single;
  tr: Integer;
  c: TTMSFNCDataGridScrollBar;
  cp: TPointF;
  sz: Double;
  csz: Boolean;
  {$IFNDEF WEBLIB}
  pt: TPointF;
  {$IFNDEF FMXLIB}
  ptx: TPoint;
  {$ENDIF}
  {$ENDIF}
  p: TTMSFNCDataGridCellParent;
  a: Boolean;

  procedure SetHoveredCell;
  begin
    if (res.Column <> FHoverCell.Column) or (res.Row <> FHoverCell.Row) then
    begin
      FHoverCell := res;
      {$IFNDEF WEBLIB}
      Application.CancelHint;
      {$ENDIF}
    end;
  end;
begin
  if (not PtInRectEx(GetContentClipRect, PointF(AMouseInfo.X, AMouseInfo.Y)) and not HasValidHost) or Assigned(FCapturedScrollbar) then
  begin
    c := nil;
    if not Assigned(FCapturedScrollbar) then
    begin
      if CustomHorizontalScrollBar.Visible and PtInRectEx(ConvertToRectF(CustomHorizontalScrollBar.BoundsRect), PointF(AMouseInfo.X, AMouseInfo.Y)) then
        c := CustomHorizontalScrollBar
      else if CustomVerticalScrollBar.Visible and PtInRectEx(ConvertToRectF(CustomVerticalScrollBar.BoundsRect), PointF(AMouseInfo.X, AMouseInfo.Y)) then
        c := CustomVerticalScrollBar;
    end
    else
      c := FCapturedScrollbar;

    if Assigned(c) then
    begin
      cp := PointF(AMouseInfo.X, AMouseInfo.Y);
      cp := PointF(cp.X - c.BoundsRect.Left, cp.Y - c.BoundsRect.Top);
      case AMouseInfo.Kind of
        gmkDown:
        begin
          c.HandleMouseDown(AMouseInfo.Button, AMouseInfo.Shift, cp.X, cp.Y);
          FCapturedScrollbar := c
        end;
        gmkMove: c.HandleMouseMove(AMouseInfo.Shift, cp.X, cp.Y);
        gmkUp:
        begin
          FCapturedScrollbar := nil;
          c.HandleMouseUp(AMouseInfo.Button, AMouseInfo.Shift, cp.X, cp.Y);
        end;
        gmkWheel: c.HandleMouseWheel(AMouseInfo.Shift, AMouseInfo.WheelDelta, hld);
        gmkEnter: c.HandleMouseEnter;
        gmkLeave: c.HandleMouseLeave;
      end;
    end;

    Exit;
  end;

  case AMouseInfo.Kind of
    gmkDown:
    begin
      Focus;

      if not (AMouseInfo.Button = {$IFNDEF WEBLIB}TTMSFNCMouseButton.{$ENDIF}mbLeft) then
        Exit;

      sx := AMouseInfo.X;
      sy := AMouseInfo.Y;

      cell := XYToCell(sx, sy);
      obj := GetCellObjectForCell(cell);
      if Assigned(obj) then
      begin
        FDragCell := cell;
        fixed := IsCellFixed(cell);

        if fixed then
        begin
          rowsz := Options.Mouse.FixedRowSizing;
          colsz := Options.Mouse.FixedColumnSizing;
        end
        else
        begin
          rowsz := Options.Mouse.RowSizing;
          colsz := Options.Mouse.ColumnSizing;
        end;

        if rowsz and (((cell.Row < RowCount - FixedBottomRowCount) and fixed) or not fixed) and (sy >= obj.Top + obj.Height - Options.Mouse.RowSizeMargin) and (sx > obj.Left + Options.Mouse.ColumnSizeMargin) and (sx < obj.Left + obj.Width - Options.Mouse.ColumnSizeMargin) then
        begin
          allow := True;
          sz := RowHeights[cell.Row];
          DoCanSizeRow(cell.Row, sz, allow);
          if allow then
          begin
            FMouseSizing := True;
            FSizeMode := gsmRowSizing;
          end;
        end
        else if rowsz and (((cell.Row > FixedRowCount) and fixed) or not fixed) and (sy <= obj.Top + Options.Mouse.RowSizeMargin) and (sx > obj.Left + Options.Mouse.ColumnSizeMargin) and (sx < obj.Left + obj.Width - Options.Mouse.ColumnSizeMargin) then
        begin
          Allow := True;
          sz := RowHeights[cell.Row - 1];
          DoCanSizeRow(cell.Row - 1, sz, allow);
          if allow then
          begin
            FMouseSizing := True;
            FSizeMode := gsmPreviousRowSizing;
          end;
        end
        else if colsz and (((cell.Column < ColumnCount - FixedRightColumnCount) and fixed) or not fixed) and (sx >= obj.Left + obj.Width - Options.Mouse.ColumnSizeMargin) and (sy > obj.Top + Options.Mouse.RowSizeMargin) and (sy < obj.Top + obj.Height - Options.Mouse.RowSizeMargin) then
        begin
          Allow := True;
          sz := ColumnWidths[cell.Column];
          DoCanSizeColumn(cell.Column, sz, allow);
          if allow then
          begin
            FMouseSizing := True;
            FSizeMode := gsmColumnSizing;
          end;
        end
        else if colsz and (((cell.Column > FixedColumnCount) and fixed) or not fixed) and (sx <= obj.Left - Options.Mouse.ColumnSizeMargin) and (sy > obj.Top + Options.Mouse.RowSizeMargin) and (sy < obj.Top + obj.Height - Options.Mouse.RowSizeMargin) then
        begin
          Allow := True;
          sz := ColumnWidths[cell.Column - 1];
          DoCanSizeColumn(cell.Column - 1, sz, allow);
          if Allow then
          begin
            FMouseSizing := True;
            FSizeMode := gsmPreviousColumnSizing;
          end;
        end
        else
        begin
          FMouseDown := True;
          FMouseUp := True;

          bctl := AMouseInfo;
          bctl.Blocked := False;
          obj.HandleMouse(bctl);
          if bctl.Blocked then
            Display;

          if not bctl.Blocked then
          begin
            if not IsCellFixed(Cell) then
            begin
              Cell := BaseCell(Cell);
              if ((Cell.Column = FocusedCell.Column) and (Cell.Row = FocusedCell.Row)) and not Options.Mouse.DirectEdit then
              begin
                FMouseUp := False;
                Allow := True;
                DoCanEditCell(FocusedCell.Column, FocusedCell.Row, Allow);
                FMouseUp := not Allow;

                if Allow then
                  EditCell(FocusedCell);
              end
              else
                bctl.Blocked := StopEditing;
            end
            else
            begin
              FDragFixed := (Options.Mouse.RowDragging and ((Cell.Column < FixedColumnCount) or (Cell.Column > ColumnCount - 1 - FixedRightColumnCount)));
              FDragFixed := FDragFixed or ((Options.Sorting.Enabled) or Options.Mouse.ColumnDragging) and ((Cell.Row < FixedRowCount) or (Cell.Row > RowCount - 1 - FixedBottomRowCount));
            end;
          end;
        end;
      end;

      FTouchScrolling := False;

      FPrevX := AMouseInfo.X;
      FPrevY := AMouseInfo.Y;

      FSizeX := FPrevX;
      FSizeY := FPrevY;

      FDragX := FPrevX;
      FDragY := FPrevY;

      FDownX := FPrevX;
      FDownY := FPrevY;

      FTouchX := FPrevX;
      FTouchY := FPrevY;

      FScrollX := FPrevX;
      FScrollY := FPrevY;

      FMouseX := FPrevX;
      FMouseY := FPrevY;

      if GetHVisible then
        FScrollHTo := GetHScrollValue
      else
        FScrollHTo := 0;

      if GetVVisible then
        FScrollVTo := GetVScrollValue
      else
        FScrollVTo := 0;

      FCellAnchor.Column := -1;
      FCellAnchor.Row := -1;
      FFirstCell.Column := -1;
      FFirstCell.Row := -1;
      FCellAnchorString := '';
      FDoubleSelection := not AnimateTimer.Enabled;
      FTimeStart := GetTickCountX;
      FTimeStop := FTimeStart;
      IsMouseDown := True;
      FMouseScrollingUp := False;
    end;
    gmkMove:
    begin
      if FInplaceEditorActive then
        Exit;

      if FClearMouseDown then
      begin
        FMouseDown := False;
        FClearMouseDown := False;
      end;

      if FDblClick then
      begin
        FDblClick := False;
        FMouseDown := False;
      end;

      FCellAnchor := MakeCell(-1, -1);
      FCellAnchorString := '';

      FX := AMouseInfo.X;
      FY := AMouseInfo.Y;

      r := GetContentClipRect;

      if FMouseDown and (((FX < FDownX - Options.Mouse.ClickMargin) or (FX > FDownX + Options.Mouse.ClickMargin))
        or ((FY < FDownY - Options.Mouse.ClickMargin) or (FY > FDownY + Options.Mouse.ClickMargin))) then
      begin
        if not FDragFixed then
        begin
          hss := GetHorizontalScrollPosition;
          vss := GetVerticalScrollPosition;
          contentr := GetContentRect;

          FDoTouchScroll := Options.Mouse.TouchScrolling and ((Options.Selection.Mode = gsmSingleCell) or (Options.Selection.Mode = gsmSingleRow) or (Options.Selection.Mode = gsmSingleColumn) or (Options.Selection.Mode = gsmNone));

          enablescroll := False;
          if (((-hss > 0) and (FX < GetTotalFixedColumnWidth))
           or ((-hss < TotalColumnWidth - GetTotalFixedRightColumnWidth) and (FX > contentr.Left + (contentr.Right - contentr.Left) - GetTotalFixedRightColumnWidth)))
           or (((-vss > 0) and (FY < GetTotalFixedRowHeight))
            or ((-vss < TotalRowHeight - GetTotalFixedBottomRowHeight) and (FY > contentr.Top + (contentr.Bottom - contentr.Top) - GetTotalFixedBottomRowHeight))) then
          enablescroll := Options.Mouse.AutoScrolling and not FDoTouchScroll;

          if (FX > contentr.Left + (contentr.Right - contentr.Left) - GetTotalFixedRightColumnWidth) then
            enablescroll := not (gfcsRow in Options.Mouse.FixedCellSelection) and not (gfcsRowRange in Options.Mouse.FixedCellSelection);

          if (FY > contentr.top + (contentr.Bottom - contentr.Top) - GetTotalFixedBottomRowHeight) then
            enablescroll := not (gfcsColumn in Options.Mouse.FixedCellSelection) and not (gfcsColumnRange in Options.Mouse.FixedCellSelection);

          ScrollTimer.Enabled := enablescroll;

          if not ScrollTimer.Enabled then
          begin
            if Options.Mouse.TouchScrolling and FDoTouchScroll then
            begin
              f := Options.Mouse.TouchScrollingSensitivity;
              if ScrollMode = gsmCellScrolling then
                f := f / 10;

              if (FTouchScrolling or (Abs(FMouseX - AMouseInfo.X) > 3) or (Abs(FMouseY - AMouseInfo.Y) > 3)) then
              begin
                if (Abs(AMouseInfo.X - FTouchX) > SCROLLINGDELAY) or (Abs(AMouseInfo.Y - FTouchY) > SCROLLINGDELAY) then
                begin
                  FTouchScrolling := True;
                  FDoubleSelection := False;
                  if IsMouseDown and not FMouseScrollingUp then
                  begin
                    if GetHVisible then
                      h := GetHScrollValue - (AMouseInfo.X - FTouchX) * f
                    else
                      h := 0;

                    if GetVVisible then
                      v := GetVScrollValue - (AMouseInfo.Y - FTouchY) * f
                    else
                      v := 0;

                    Scroll(h, v);
                    FTouchY := AMouseInfo.Y;
                    FTouchX := AMouseInfo.X;
                  end;
                end;
              end
            end
            else
            begin
              FMouseDragging := True;
              res := XYToCell(AMouseInfo.X, AMouseInfo.Y);
              if (res.Column <> -1) and (res.Row <> -1) then
              begin
                fxd := IsCellFixed(res);
                case Options.Selection.Mode of
                  gsmSingleRow: if (gfcsRow in Options.Mouse.FixedCellSelection) then fxd := False;
                  gsmSingleColumn: if (gfcsColumn in Options.Mouse.FixedCellSelection) then fxd := False;
                  gsmRowRange: if (gfcsRowRange in Options.Mouse.FixedCellSelection) then fxd := False;
                  gsmColumnRange: if (gfcsColumnRange in Options.Mouse.FixedCellSelection) then fxd := False;
                end;

                if not fxd then
                  SelectCell(res, AMouseInfo.Shift, FMouseDragging);
              end;
            end;
          end;
        end
        else
        begin
          if (FDragCell.Column <> -1) and (FDragCell.Row <> -1) then
          begin
            obj := GetCellObjectForCell(FDragCell);
            if Assigned(obj) then
            begin
              Allow := Options.Mouse.ColumnDragging;
              if Allow then
                DoCanDragColumn(FDragCell.Column, Allow);

              if ((FDragCell.Row < FixedRowCount) or (FDragCell.Row > RowCount - 1 - FixedBottomRowCount)) and (Allow) then
              begin
                UpdateCursor(crUpArrow);
                FDraggingMode := gdmColumnDragging;
                FDragCellTo := XYToCell(AMouseInfo.X, AMouseInfo.Y);
                FDragCellToObj := GetCellObjectForCell(MakeCell(FDragCellTo.Column, 0));
                FDragX := FX;
                FDragY := FY;
                Display;
              end
              else
              begin
                Allow := Options.Mouse.RowDragging;
                if Allow then
                  DoCanDragRow(FDragCell.Row, Allow);

                if ((FDragCell.Column < FixedColumnCount) or (FDragCell.Column > ColumnCount - 1 - FixedRightColumnCount)) and (Allow) then
                begin
                  UpdateCursor(crUpArrow);
                  FDraggingMode := gdmRowDragging;
                  FDragCellTo := XYToCell(AMouseInfo.X, AMouseInfo.Y);
                  FDragCellToObj := GetCellObjectForCell(MakeCell(0, FDragCellTo.Row));
                  FDragX := FX;
                  FDragY := FY;
                  Display;
                end;
              end;
            end
            else
            begin
              if (((FDragCell.Row < FixedRowCount) or (FDragCell.Row > RowCount - 1 - FixedBottomRowCount))) and (Options.Mouse.ColumnDragging) then
              begin
                newposx := (FX - FDragX);
                if (newposx <= r.Right - r.Left) and (newposx >= 0) then
                begin
                  DragTimer.Enabled := False;
                  if (newposx > GetTotalFixedColumnWidth) and (newposx < (r.Right - r.Left) - GetTotalFixedRightColumnWidth) then
                    FDragScrollCell := XYToCell(newposx, (r.Bottom - r.Top) / 2);
                end
                else if (newposx > r.Right - r.Left) then
                  DragTimer.Enabled := True
                else if (newposx < 0) then
                   DragTimer.Enabled := True;

                FDragX := FX;
                FDragY := FY;
              end
              else if (((FDragCell.Column < FixedColumnCount) or (FDragCell.Column > ColumnCount - 1 - FixedRightColumnCount))) and (Options.Mouse.RowDragging) then
              begin
                newposy := (FY - FDragY);
                if (newposy <= r.Bottom - r.Top) and (newposy >= 0) then
                begin
                  DragTimer.Enabled := False;
                  if (newposy > GetTotalFixedRowHeight) and (newposy < (r.Bottom - r.Top) - GetTotalFixedBottomRowHeight) then
                    FDragScrollCell := XYToCell((r.Right - r.Left) / 2, newposy);
                end
                else if (newposy > r.Bottom - r.Top) then
                  DragTimer.Enabled := True
                else if (newposy < 0) then
                   DragTimer.Enabled := True;

                FDragX := FX;
                FDragY := FY;
              end;

              if PtInRectEx(LocalRect, PointF(AMouseInfo.X, AMouseInfo.Y)) then
                UpdateCursor(crUpArrow)
              else
                UpdateCursor(crNoDrop);
            end;
          end;
        end;
      end
      else if not FMouseSizing then
      begin
        sx := AMouseInfo.X;
        sy := AMouseInfo.Y;

        res := XYToCell(AMouseInfo.X, AMouseInfo.Y);
        if (res.Column <> -1) and (res.Row <> -1) then
        begin
          obj := GetCellObjectForCell(res);
          if Assigned(obj) then
          begin
            fixed := IsCellFixed(res);

//            if Assigned(ToolBarPopup) and (ToolBarPopupMode <> tpmNone) then
//            begin
//              if ToolBarPopupMode = tpmActiveCell then
//              begin
//                if (res.Col = FocusedCell.Col) and (res.Row = FocusedCell.Row) and not ToolBarPopup.Activated then
//                  ShowToolBar;
//
//                if ((res.Col <> FocusedCell.Col) or (res.Row <> FocusedCell.Row)) and Assigned(ToolBarPopup) and ToolBarPopup.Activated and not ToolBarPopup.DropDownActive then
//                  ToolBarPopup.Deactivate;
//              end;
//
//              if (ToolBarPopupMode in [tpmHoverCell, tpmHoverNormalCell]) then
//              begin
//                if ((res.Col <> FToolBarPopupCell.Col) or (res.Row <> FToolBarPopupCell.Row)) and not ToolBarPopup.Activated then
//                begin
//                  if not fixed or (ToolBarPopupMode = tpmHoverCell) then
//                    ShowToolBar;
//                end;
//
//                if ((res.Col <> FToolBarPopupCell.Col) or (res.Row <> FToolBarPopupCell.Row)) and ToolBarPopup.Activated and not ToolBarPopup.DropDownActive then
//                  ToolBarPopup.Deactivate;
//              end;
//            end;

            if fixed then
            begin
              rowsz := Options.Mouse.FixedRowSizing;
              colsz := Options.Mouse.FixedColumnSizing;
            end
            else
            begin
              rowsz := Options.Mouse.RowSizing;
              colsz := Options.Mouse.ColumnSizing;
            end;

            if rowsz and (res.Row > FixedRowCount) and (sy <= obj.Top + Options.Mouse.RowSizeMargin) and (sx > obj.Left + Options.Mouse.ColumnSizeMargin) and (sx < obj.Left + obj.Width - Options.Mouse.ColumnSizeMargin) then
            begin
              allow := True;
              sz := RowHeights[res.Row - 1];
              DoCanSizeRow(res.Row - 1, sz, allow);
              if allow then
                UpdateCursor(crSizeNS)
            end
            else if rowsz and (((res.Row < RowCount - FixedRowCount) and fixed) or not fixed) and (sy >= obj.Top + obj.Height - Options.Mouse.RowSizeMargin) and (sx > obj.Left + Options.Mouse.ColumnSizeMargin) and (sx < obj.Left + obj.Width - Options.Mouse.ColumnSizeMargin) then
            begin
              allow := True;
              sz := RowHeights[res.Row];
              DoCanSizeRow(res.Row, sz, allow);
              if allow then
                UpdateCursor(crSizeNS)
            end
            else if colsz and (res.Column > FixedColumnCount) and (sx <= obj.Left - Options.Mouse.ColumnSizeMargin) and (sy > obj.Top + Options.Mouse.RowSizeMargin) and (sy < obj.Top + obj.Height - Options.Mouse.RowSizeMargin) then
            begin
              allow := True;
              sz := ColumnWidths[res.Column - 1];
              DoCanSizeColumn(res.Column - 1, sz, allow);
              if allow then
                UpdateCursor(crSizeWE)
            end
            else if colsz and (((res.Column < ColumnCount - FixedColumnCount) and fixed) or not fixed) and (sx >= obj.Left + obj.Width - Options.Mouse.ColumnSizeMargin) and (sy > obj.Top + Options.Mouse.RowSizeMargin) and (sy < obj.Top + obj.Height - Options.Mouse.RowSizeMargin) then
            begin
              allow := True;
              sz := ColumnWidths[res.Column];
              DoCanSizeColumn(res.Column, sz, allow);
              if allow then
                UpdateCursor(crSizeWE)
            end
            else
            begin
              bctl := AMouseInfo;
              bctl.Blocked := False;
              cr := FOldCursor;
              bctl.Cursor := cr;
              obj.HandleMouse(bctl);
              if bctl.Blocked then
              begin
                UpdateCursor(bctl.Cursor);
                Display;
              end;

              if not bctl.Blocked then
              begin
                if obj.IsHTML then
                  FCellAnchorString := obj.XYToAnchor(AMouseInfo.X, AMouseInfo.Y)
                else
                begin
                  FCellAnchorString := obj.Text;
                  if not IsUrl(FCellAnchorString) or (not Options.URL.AutoDetect) then
                    FCellAnchorString := '';
                end;

                if (FCellAnchorString <> '') and Options.URL.AutoDetect then
                  UpdateCursor(crHandPoint)
                else
                begin
                  SetHoveredCell;
                  UpdateCursor(FOldCursor);
                end;
              end;
            end;
          end;
        end
        else
        begin
          SetHoveredCell;
          UpdateCursor(FOldCursor);
        end;
      end
      else
      begin
        if (FDragCell.Column <> -1) and (FDragCell.Column <> -1) then
        begin
          case FSizeMode of
            gsmColumnSizing:
            begin
              newsz := ColumnWidths[FDragCell.Column] + (FX - FSizeX);
              csz := True;
              DoCanSizeColumn(FDragCell.Column, newsz, csz);
              if csz then
              begin
                ColumnWidths[FDragCell.Column] := newsz;
                DoSizeColumn(FDragCell.Column, newsz);
              end;
            end;
            gsmRowSizing:
            begin
              newsz := RowHeights[FDragCell.Row] + (FY - FSizeY);
              csz := True;
              DoCanSizeRow(FDragCell.Row, newsz, csz);
              if csz then
              begin
                RowHeights[FDragCell.Row] := newsz;
                DoSizeRow(FDragCell.Row, newsz);
              end;
            end;
            gsmPreviousColumnSizing:
            begin
              newsz := ColumnWidths[FDragCell.Column - 1] + (FX - FSizeX);
              csz := True;
              DoCanSizeColumn(FDragCell.Column - 1, newsz, csz);
              if csz then
              begin
                ColumnWidths[FDragCell.Column - 1] := newsz;
                DoSizeColumn(FDragCell.Column - 1, newsz);
              end;
            end;
            gsmPreviousRowSizing:
            begin
              newsz := RowHeights[FDragCell.Row - 1] + (FY - FSizeY);
              csz := True;
              DoCanSizeRow(FDragCell.Row - 1, newsz, csz);
              if csz then
              begin
                RowHeights[FDragCell.Row - 1] := newsz;
                DoSizeRow(FDragCell.Row - 1, newsz);
              end;
            end;
          end;

          FSizeX := FX;
          FSizeY := FY;
          Update;
          FSizing := True;
        end;
      end;
    end;
    gmkUp:
    begin
      if FInplaceEditorActive then
        Exit;

      if not (AMouseInfo.Button = {$IFNDEF WEBLIB}TTMSFNCMouseButton.{$ENDIF}mbLeft) then
      begin
        if AMouseInfo.Button = {$IFNDEF WEBLIB}TTMSFNCMouseButton.{$ENDIF}mbRight then
        begin
          res := XYToCell(AMouseInfo.X, AMouseInfo.Y);
          if (res.Column <> -1) and (res.Row <> -1) then
          begin
            DoCellRightClick(res.Column, res.Row);

            p := GetParentHost;
            if Assigned(FCellPopupMenu) and Assigned(p) and (p is TTMSFNCDataGridCellControl) then
            begin
              a := True;
              DoCellBeforeShowPopupMenu(res, FCellPopupMenu, a);
              if a then
              begin
                {$IFNDEF WEBLIB}
                FCellPopupMenu.PopupComponent := p;

                pt := PointF(AMouseInfo.X, AMouseInfo.Y);

                {$IFDEF FMXLIB}
                pt := TTMSFNCDataGridCellControlOpen(p as TTMSFNCDataGridCellControl).LocalToScreen(pt);
                {$ELSE}
                ptx := (p as TTMSFNCDataGridCellControl).ClientToScreen(Point(Round(pt.X), Round(pt.Y)));
                pt := PointF(ptx.X, ptx.Y);
                {$ENDIF}

                FCellPopupMenu.Popup(Round(pt.X), Round(pt.Y));
                {$ENDIF}
              end;
            end;
          end;
        end;
        Exit;
      end;

      if not IsMouseDown then
        Exit;

      FMouseScrollingUp := True;
      IsMouseDown := False;
      ScrollTimer.Enabled := False;
      DragTimer.Enabled := False;

      if FSizing then
      begin
        case FSizeMode of
          gsmColumnSizing: DoColumnSized(FDragCell.Column, ColumnWidths[FDragCell.Column]);
          gsmRowSizing: DoRowSized(FDragCell.Row, RowHeights[FDragCell.Row]);
          gsmPreviousColumnSizing: DoColumnSized(FDragCell.Column - 1, ColumnWidths[FDragCell.Column - 1]);
          gsmPreviousRowSizing: DoRowSized(FDragCell.Row - 1, RowHeights[FDragCell.Row - 1]);
        end;
        Update;
      end;

      if not FDoubleSelection and Options.Mouse.TouchScrolling and FDoTouchScroll then
      begin
        f := Options.Mouse.TouchScrollingSensitivity;
        if ScrollMode = gsmCellScrolling then
          f := f / 10;

        FTimeStop := GetTickCountX;
        if ((FTimeStop - FTimeStart) < SWIPECOUNT) and ((FTimeStop - FTimeStart) > 0) then
        begin
          FSpY := Abs(AMouseInfo.Y - FScrollY) / (FTimeStop - FTimeStart);
          if (FSpY > 0) then
          begin
            if GetVVisible then
            begin
              if (AMouseInfo.Y - FScrollY) > 0 then
                FScrollVTo := Max(0, Min(GetVMax - GetVViewportSize, FScrollVTo - Round(Abs(AMouseInfo.Y - FScrollY) * FSpY * f * 3)))
              else
                FScrollVTo := Max(0, Min(GetVMax - GetVViewportSize, FScrollVTo + Round(Abs(AMouseInfo.Y - FScrollY) * FSpY * f * 3)));
            end
            else
              FScrollVTo := 0;

            FAnimateVerticalPos := True;
            AnimateTimer.Enabled := True;
          end;

          FSpX := Abs(AMouseInfo.X - FScrollX) / (FTimeStop - FTimeStart);
          if (FSpX > 0) then
          begin
            if GetHVisible then
            begin
              if (AMouseInfo.X - FScrollX) > 0 then
                FScrollHTo := Max(0, Min(GetHMax - GetHViewportSize, FScrollHTo - Round(Abs(AMouseInfo.X - FScrollX) * FSpX * f * 3)))
              else
                FScrollHTo := Max(0, Min(GetHMax - GetHViewportSize, FScrollHTo + Round(Abs(AMouseInfo.X - FScrollX) * FSpX * f * 3)));
            end
            else
              FScrollHTo := 0;

            FAnimateHorizontalPos := True;
            AnimateTimer.Enabled := True;
          end;
        end;
      end;

      bctl := AMouseInfo;
      bctl.Blocked := not (FDraggingMode = gdmNone) or FDblClick;
      if FMouseUp then
      begin
        res := XYToCell(AMouseInfo.X, AMouseInfo.Y);
        if (res.Column <> -1) and (res.Row <> -1) then
        begin
          obj := GetCellObjectForCell(res);
          if Assigned(obj) then
          begin
            FCellAnchor.Column := -1;
            FCellAnchor.Row := -1;
            if not FMouseDragging then
            begin
              obj.HandleMouse(bctl);
              if bctl.Blocked then
                Display;

              if not bctl.Blocked then
              begin
                if obj.IsHTML then
                  FCellAnchorString := obj.XYToAnchor(AMouseInfo.X, AMouseInfo.Y)
                else
                begin
                  FCellAnchorString := obj.Text;
                  if not IsUrl(FCellAnchorString) or not Options.URL.AutoDetect then
                    FCellAnchorString := '';
                end;

                if FCellAnchorString <> '' then
                  FCellAnchor := res;
              end;
            end;
          end;

          if not bctl.Blocked then
          begin
            if (FCellAnchor.Column = -1) and (FCellAnchor.Row = -1) and (FCellAnchorString = '') then
            begin
              fxd := IsCellFixed(res);
              case Options.Selection.Mode of
                gsmCellRange:
                begin
                  if (gfcsAll in Options.Mouse.FixedCellSelection) and (res.Column < FixedColumnCount) and (res.Row < FixedRowCount) then
                    fxd := False;
                end;
                gsmSingleRow: if (gfcsRow in Options.Mouse.FixedCellSelection) then fxd := False;
                gsmSingleColumn: if (gfcsColumn in Options.Mouse.FixedCellSelection) then fxd := False;
                gsmRowRange: if (gfcsRowRange in Options.Mouse.FixedCellSelection) then fxd := False;
                gsmColumnRange: if (gfcsColumnRange in Options.Mouse.FixedCellSelection) then fxd := False;
                gsmDisjunctRow: if (gfcsDisjunctRow in Options.Mouse.FixedCellSelection) then fxd := False;
                gsmDisjunctColumn: if (gfcsDisjunctColumn in Options.Mouse.FixedCellSelection) then fxd := False;
              end;

              if not fxd and not FDragFixed and not FTouchScrolling then
              begin
                SelectCell(res, AMouseInfo.Shift, FMouseDragging);

                if not FMouseDragging and Options.Mouse.DirectEdit then
                begin
                  Allow := True;
                  DoCanEditCell(FocusedCell.Column, FocusedCell.Row, Allow);
                  if Allow then
                    EditCell(FocusedCell);
                end;
              end;

              DoCellClick(res, AMouseInfo);
            end
            else
              DoCellAnchorClick(FCellAnchor.Column, FCellAnchor.Row, FCellAnchorString);
          end
          else
          begin
            if FDraggingMode = gdmColumnDragging then
            begin
              b := True;
              DoCanDropColumn(FDragCell.Column, res.Column, b);
              if b then
              begin
                BeginUpdate;
                MoveColumn(FDragCell.Column, res.Column);
                EndUpdate;
                DoColumnDropped(FDragCell.Column, res.Column);
              end;
            end
            else
            if FDraggingMode = gdmRowDragging then
            begin
              b := True;
              DoCanDropRow(FDragCell.Row, res.Row, b);
              if b then
              begin
                BeginUpdate;
                MoveRow(FDragCell.Row, res.Row);
                EndUpdate;
                DoRowDropped(FDragCell.Row, res.Row);
              end;
            end;          
          end;
        end;
      end;

      FTouchScrolling := False;
      FDraggingMode := gdmNone;
      FDragCell := MakeCell(-1, -1);
      FDragCellTo := MakeCell(-1, -1);
      FDragCellToObj := nil;
      FDragFixed := False;
      FFirstCell.Row := -1;
      FFirstCell.Column := -1;
      FMouseDown := False;
      FMouseUp := False;
      FMouseDragging := False;
      FMouseDblClick := FMouseSizing;
      FMouseSizing := False;
      FSizing := False;
    end;
    gmkDouble:
    begin
      FDblClick := True;
      cell := XYToCell(AMouseInfo.X, AMouseInfo.Y);
      if (cell.Column <> -1) and (cell.Row <> -1) then
      begin
        if FMouseDblClick then
        begin
          FMouseDblClick := False;
          UpdateCursor(crHourGlass);
          try
            case FSizeMode of
              gsmPreviousColumnSizing, gsmColumnSizing: if Options.Mouse.ColumnAutoSizeOnDblClick then AutoSizeColumn(cell.Column);
              gsmPreviousRowSizing, gsmRowSizing: if Options.Mouse.RowAutoSizeOnDblClick then AutoSizeRow(cell.Row);
            end;
          finally
            UpdateCursor(FOldCursor);
          end;
        end;

        DoCellDblClick(cell.Column, cell.Row);
        FClearMouseDown := True;
      end;
    end;
    gmkWheel:
    begin
      if AMouseInfo.Handled then
        Exit;

      if ((ssCtrl in AMouseInfo.Shift) {$IFDEF FMXLIB}or (ssCommand in AMouseInfo.Shift){$ENDIF} or (Options.Mouse.WheelScrollKeepSelection)) and not Assigned(Adapter) then
      begin
        if AMouseInfo.WheelDelta > 0 then
          tr := TopRow - Options.Mouse.WheelScrollSize
        else
          tr := TopRow + Options.Mouse.WheelScrollSize;

        if tr < FixedRowCount then
          tr := FixedRowCount
        else if tr > RowCount - 1 - FixedBottomRowCount then
          tr := RowCount - 1 - FixedBottomRowCount;

        cl := MakeCell(0, tr);

        if not IsCellVisible(cl) then
        begin
          if AMouseInfo.WheelDelta > 0 then
            cl := PreviousVisibleRow(cl.Column, cl.Row)
          else
            cl := NextVisibleRow(cl.Column, cl.Row);
        end;

        Navigate(cl, True, True);
      end
      else
      begin
        sel := FocusedCell;
        if AMouseInfo.WheelDelta > 0 then
          sel.Row := Max(FixedRowCount, sel.Row - Options.Mouse.WheelScrollSize)
        else
          sel.Row := Min(RowCount - FixedBottomRowCount - 1, sel.Row + Options.Mouse.WheelScrollSize);

        cell := BaseCell(sel);
        sel := cell;

        if IsCellDisabled(sel) then
        begin
          if AMouseInfo.WheelDelta > 0 then
            sel := PreviousSelectableRow(sel.Column, sel.Row)
          else
            sel := NextSelectableRow(sel.Column, sel.Row);
        end;

        SelectCell(sel, AMouseInfo.Shift);
      end;

      AMouseInfo.Handled := True;
    end;
    gmkEnter:;
    gmkLeave:
    begin
      FHoverCell := MakeCell(-1, -1);
//      if Assigned(ToolBarPopup) and ToolBarPopup.Activated and not Editing then
//      begin
//        p := TTMSFNCUtils.GetMousePos;
//        if not ToolBarPopup.PointInPopup(p) and not ToolBarPopup.DropDownActive then
//          ToolBarPopup.Deactivate;
//      end;
    end;
  end;
end;

function TTMSFNCDataGridRenderer.HasCustomScrollBars: Boolean;
begin
  Result := ((CustomScrollBars and HasValidHost) or not HasValidHost) and not IsExporting
end;

function TTMSFNCDataGridRenderer.HasHint: Boolean;
var
  h: string;
begin
  Result := IsCellValid(FHoverCell);
  if Result then
  begin
    h := Hints[FHoverCell.Column, FHoverCell.Row];
    DoGetCellHint(FHoverCell, h);
    Result := (h <> '');
  end;
end;

function TTMSFNCDataGridRenderer.HasValidHost: Boolean;
begin
  Result := False;
  if Assigned(Host) then
    Result := Host is TTMSFNCDataGridCellParent;
end;

procedure TTMSFNCDataGridRenderer.SetStartCell(AColumn, ARow: Integer);
var
  stc: TTMSFNCDataGridCellCoord;
begin
  stc.Column := AColumn;
  stc.Row := ARow;

  stc.Column := Max(FixedColumnCount, Min(ColumnCount - 1 - FixedRightColumnCount, stc.Column));
  stc.Row := Max(FixedRowCount, Min(RowCount - 1 - FixedBottomRowCount, stc.Row));

  Selection := MakeCellRange(stc.Column, stc.Row, Selection.StopCell.Column, Selection.StopCell.Row);
end;

procedure TTMSFNCDataGridRenderer.SetStopCell(AColumn, ARow: Integer);
var
  stc: TTMSFNCDataGridCellCoord;
begin
  stc.Column := AColumn;
  stc.Row := ARow;

  stc.Column := Max(FixedColumnCount, Min(ColumnCount - 1 - FixedRightColumnCount, stc.Column));
  stc.Row := Max(FixedRowCount, Min(RowCount - 1 - FixedBottomRowCount, stc.Row));

  Selection := MakeCellRange(Selection.StartCell.Column, Selection.StartCell.Row, stc.Column, stc.Row);
end;

procedure TTMSFNCDataGridRenderer.SelectAllCells;
var
  i, j: Integer;
begin
  if (Options.Selection.Mode in [gsmCellRange, gsmRowRange, gsmColumnRange, gsmDisjunctCell, gsmDisjunctCellRange, gsmDisjunctColumn, gsmDisjunctRow]) then
  begin
    SetStartCell(FixedColumnCount, FixedRowCount);
    SetStopCell(ColumnCount - 1 - FixedRightColumnCount, RowCount - 1 - FixedBottomRowCount);
    SetFocusCell(FixedColumnCount, FixedRowCount);
  end;

  if (Options.Selection.Mode in [gsmDisjunctRow]) then
  begin
    IncUpdateCount;
    for i := FixedRowCount to RowCount - FixedBottomRowCount - 1 do
      SelectedRows[i] := True;
    DecUpdateCount;
  end;

  if (Options.Selection.Mode in [gsmDisjunctColumn]) then
  begin
    IncUpdateCount;
    for i := FixedColumnCount to ColumnCount - 1 - FixedRightColumnCount do
      SelectedColumns[i] := True;
    DecUpdateCount;
  end;

  if (Options.Selection.Mode in [gsmDisjunctCell, gsmDisjunctCellRange]) then
  begin
    IncUpdateCount;
    for i := FixedColumnCount to ColumnCount - 1 - FixedRightColumnCount do
      for j := FixedRowCount to RowCount - FixedBottomRowCount - 1 do
        SelectedCells[i, j] := True;
    DecUpdateCount;
  end;

  Navigate(FocusedCell);
  DoSelectCell(Selection.StartCell, Selection.StopCell);
end;

procedure TTMSFNCDataGridRenderer.SelectCell(ACell: TTMSFNCDataGridCellCoord; AShift: TShiftState = []; AMouseDragging: Boolean = False);
var
  sta, stc: TTMSFNCDataGridCellCoord;
  rs, cs, rsstart, csstart: Integer;
  bs, ff, fc: TTMSFNCDataGridCellCoord;
  cst, rst: Integer;
  ic, ir: Integer;
  I, J: Integer;
  doUpdate: Boolean;
  Allow: Boolean;
  cntr, cntc, cntcl: Integer;
  g: TTMSFNCDataGridRenderer;
begin
  inherited;
  if (ACell.Row < FixedRowCount) and (ACell.Column < FixedColumnCount) and (gfcsAll in Options.Mouse.FixedCellSelection) then
  begin
    SelectAllCells;
    Exit;
  end;

  ACell.Row := Max(FixedRowCount, Min(ACell.Row, RowCount - 1 - FixedBottomRowCount));
  ACell.Column := Max(FixedColumnCount, Min(ACell.Column, ColumnCount - 1 - FixedRightColumnCount));

  Allow := True;
  DoSelectingCell(ACell, ACell, Allow);

  if not Allow then
    Exit;

  FRealCell := ACell;
  ACell := BaseCell(ACell);
  SetFocusCell(ACell.Column, ACell.Row);

  case Options.Selection.Mode of
    gsmSingleCell:
    begin
      SetStartCell(FocusedCell.Column, FocusedCell.Row);
      SetStopCell(FocusedCell.Column, FocusedCell.Row);
    end;
    gsmSingleRow:
    begin
      SetStartCell(FixedColumnCount, FocusedCell.Row);
      SetStopCell(ColumnCount - 1 - FixedRightColumnCount, FocusedCell.Row);
    end;
    gsmSingleColumn:
    begin
      SetStartCell(FocusedCell.Column, FixedRowCount);
      SetStopCell(FocusedCell.Column, RowCount - 1 - FixedBottomRowCount);
    end;
    gsmCellRange, gsmRowRange, gsmColumnRange, gsmDisjunctRow, gsmDisjunctColumn, gsmDisjunctCell, gsmDisjunctCellRange:
    begin
      doUpdate := True;
      if (not AMouseDragging or (FFirstCell.Row = -1) or (FFirstCell.Column = -1)) and not (ssShift in AShift) and not (ssCtrl in AShift) {$IFDEF FMXLIB}and not (ssCommand in AShift){$ENDIF} then
      begin
        SetStartCell(FRealCell.Column, FocusedCell.Row);
        SetStopCell(FocusedCell.Column, FocusedCell.Row);
        FFirstCell := Selection.StartCell;
        FSaveCell := Selection.StartCell;
      end
      else if (ssShift in AShift) and ((FSaveCell.Row = -1) or (FSaveCell.Column = -1)) then
      begin
        SetStartCell(FocusedCell.Column, FocusedCell.Row);
        SetStopCell(FocusedCell.Column, FocusedCell.Row);
        FSaveCell := Selection.StartCell;
      end
      else if (ssCtrl in AShift) {$IFDEF FMXLIB}or (ssCommand in AShift){$ENDIF} and (Options.Selection.Mode in [gsmDisjunctRow, gsmDisjunctColumn, gsmDisjunctCell, gsmDisjunctCellRange]) then
      begin
        case Options.Selection.Mode of
          gsmDisjunctRow:
          begin
            if FocusedCell.Row < Selection.StartCell.Row then
            begin
              SetStartCell(FRealCell.Column, FocusedCell.Row);
              FFirstCell := Selection.StopCell;
              FSaveCell := Selection.StopCell;
            end
            else if FocusedCell.Row > Selection.StopCell.Row then
            begin
              SetStopCell(FocusedCell.Column, FocusedCell.Row);
              FFirstCell := Selection.StartCell;
              FSaveCell := Selection.StartCell;
            end
            else
              doUpdate := False;
          end;
          gsmDisjunctColumn:
          begin
            if FocusedCell.Column < Selection.StartCell.Column then
            begin
              SetStartCell(FocusedCell.Column, FRealCell.Row);
              FFirstCell := Selection.StopCell;
              FSaveCell := Selection.StopCell;
            end
            else if FocusedCell.Column > Selection.StopCell.Column then
            begin
              SetStopCell(FocusedCell.Column, FocusedCell.Row);
              FFirstCell := Selection.StartCell;
              FSaveCell := Selection.StartCell;
            end
            else
              doUpdate := False;
          end;
          gsmDisjunctCell, gsmDisjunctCellRange:
          begin
            doUpdate := False;
            if FocusedCell.Column < Selection.StartCell.Column then
            begin
              if FocusedCell.Row < Selection.StartCell.Row then
                SetStartCell(FocusedCell.Column, FocusedCell.Row)
              else if FocusedCell.Row > Selection.StopCell.Row then
                SetStopCell(FocusedCell.Column, FocusedCell.Row)
              else if (FocusedCell.Row >= Selection.StartCell.Row) or (FocusedCell.Row <= Selection.StopCell.Row) then
                SetStartCell(FocusedCell.Column, Selection.StartCell.Row);
            end
            else if FocusedCell.Column > Selection.StopCell.Column then
            begin
              if FocusedCell.Row < Selection.StartCell.Row then
                SetStartCell(FocusedCell.Column, FocusedCell.Row)
              else if FocusedCell.Row > Selection.StopCell.Row then
                SetStopCell(FocusedCell.Column, FocusedCell.Row)
              else if (FocusedCell.Row >= Selection.StartCell.Row) or (FocusedCell.Row >= Selection.StopCell.Row) then
                SetStopCell(FocusedCell.Column, Selection.StopCell.Row);
            end
            else if (FocusedCell.Column >= Selection.StartCell.Column) and (FocusedCell.Column <= Selection.StopCell.Column) then
            begin
              if FocusedCell.Row < Selection.StartCell.Row then
                SetStartCell(Selection.StartCell.Column, FocusedCell.Row)
              else if FocusedCell.Row > Selection.StopCell.Row then
                SetStopCell(Selection.StopCell.Column, FocusedCell.Row);
            end
          end;
        end;
      end;

      if (ssShift in AShift) or (ssCtrl in AShift) then
        FFirstCell := FSaveCell;

      if doUpdate then
      begin
        if (FocusedCell.Column < FFirstCell.Column) then
        begin
          SetStartCell(FocusedCell.Column, Selection.StartCell.Row);
          SetStopCell(FFirstCell.Column, Selection.StopCell.Row);
        end
        else
        begin
          SetStopCell(FocusedCell.Column, Selection.StopCell.Row);
          SetStartCell(FFirstCell.Column, Selection.StartCell.Row);
        end;

        if (FocusedCell.Row < FFirstCell.Row) then
        begin
          SetStartCell(Selection.StartCell.Column, FocusedCell.Row);
          SetStopCell(Selection.StopCell.Column, FFirstCell.Row);
        end
        else
        begin
          SetStopCell(Selection.StopCell.Column, FocusedCell.Row);
          SetStartCell(Selection.StartCell.Column, FFirstCell.Row);
        end;
      end;

      case Options.Selection.Mode of
        gsmDisjunctRow, gsmDisjunctColumn, gsmDisjunctCell, gsmDisjunctCellRange:
        begin
          if not (ssCtrl in AShift) {$IFDEF FMXLIB}and not (ssCommand in AShift){$ENDIF} and not (ssShift in AShift) then
          begin
            SetStartCell(FocusedCell.Column, FocusedCell.Row);
            SetStopCell(FocusedCell.Column, FocusedCell.Row);
            FSaveCell := FocusedCell;
          end;
        end;
      end;

      case Options.Selection.Mode of
        gsmRowRange, gsmDisjunctRow:
        begin
          SetStartCell(FixedColumnCount, Selection.StartCell.Row);
          SetStopCell(ColumnCount - 1 - FixedRightColumnCount, Selection.StopCell.Row);
        end;
        gsmColumnRange, gsmDisjunctColumn:
        begin
          SetStartCell(Selection.StartCell.Column, FixedRowCount);
          SetStopCell(Selection.StopCell.Column, RowCount - 1 - FixedBottomRowCount);
        end;
      end;
    end;
  end;

  stc := Selection.StopCell;
  sta := Selection.StartCell;

  if Selection.StopCell.Column < Selection.StartCell.Column then
  begin
    SetStopCell(sta.Column, Selection.StopCell.Row);
    SetStartCell(stc.Column, Selection.StartCell.Row);
  end;

  if Selection.StopCell.Row < Selection.StartCell.Row then
  begin
    SetStopCell(Selection.StopCell.Column, sta.Row);
    SetStartCell(Selection.StartCell.Column, stc.Row);
  end;

  cs := Selection.StopCell.Column;
  csstart := Selection.StartCell.Column;
  rs := Selection.StopCell.Row;
  rsstart := Selection.StartCell.Row;
  for ic := Max(StartColumn, csstart) to Min(StopColumn, cs) do
  begin
    for ir := Max(StartRow, rsstart) to Min(StopRow, rs) do
    begin
      bs := BaseCell(MakeCell(ic, ir));
      cst := ColumnSpan(bs);
      if bs.Column + cst - 1 > cs then
        cs := bs.Column + cst - 1;

      if bs.Column < csstart then
        csstart := bs.Column;

      rst := RowSpan(bs);
      if bs.Row + rst - 1 > rs then
        rs := bs.Row + rst - 1;

      if bs.Row < rsstart then
        rsstart := bs.Row;
    end;
  end;

  SetStopCell(cs,rs);
  SetStartCell(csstart, rsstart);

  case Options.Selection.Mode of
    gsmDisjunctRow:
    begin
      IncUpdateCount;

      if not (ssCtrl in AShift) {$IFDEF FMXLIB}and not (ssCommand in AShift){$ENDIF} then
        SelectedRowDictionary.Clear;

      if ssShift in AShift then
      begin
        for I := Selection.StartCell.Row to Selection.StopCell.Row do
          SelectedRows[I] := not SelectedRows[I];
      end
      else
      begin
        SelectedRows[FocusedCell.Row] := not SelectedRows[FocusedCell.Row];
        cntr := GetSelectedRowCount;
        if not SelectedRows[FocusedCell.Row] and (cntr = 0) then
          SelectedRows[FocusedCell.Row] := True;
      end;

      if not SelectedRows[FocusedCell.Row] then
        for I := Selection.StartCell.Row to Selection.StopCell.Row do
          if (I <> FocusedCell.Row) and (SelectedRows[I]) then
            SetFocusCell(FocusedCell.Column, I);

      DecUpdateCount;
    end;
    gsmDisjunctColumn:
    begin
      IncUpdateCount;

      if not (ssCtrl in AShift) {$IFDEF FMXLIB}and not (ssCommand in AShift){$ENDIF} then
        SelectedColumnDictionary.Clear;

      if ssShift in AShift then
      begin
        for I := Selection.StartCell.Column to Selection.StopCell.Column do
          SelectedColumns[I] := not SelectedColumns[I];
      end
      else
      begin
        SelectedColumns[FocusedCell.Column] := not SelectedColumns[FocusedCell.Column];
        cntc := GetSelectedColumnCount;
        if not SelectedColumns[FocusedCell.Column] and (cntc = 0) then
          SelectedColumns[FocusedCell.Column] := True;
      end;

      if not SelectedColumns[FocusedCell.Column] then
        for I := Selection.StartCell.Column to Selection.StopCell.Column do
          if (I <> FocusedCell.Column) and (SelectedColumns[I]) then
            SetFocusCell(I, FocusedCell.Row);

      DecUpdateCount;
    end;
    gsmDisjunctCell, gsmDisjunctCellRange:
    begin
      IncUpdateCount;

      if not (ssCtrl in AShift) {$IFDEF FMXLIB}and not (ssCommand in AShift){$ENDIF} then
      begin
        SelectedCellDictionary.Clear;
        SelectedColumnDictionary.Clear;
        SelectedRowDictionary.Clear;
      end;

      if ssShift in AShift then
      begin
        case Options.Selection.Mode of
          gsmDisjunctCell:
          begin
            for I := Selection.StartCell.Column to Selection.StopCell.Column do
              for J := Selection.StartCell.Row to Selection.StopCell.Row do
                SelectedCells[I, J] := not SelectedCells[I, J];
          end;
          gsmDisjunctCellRange:
          begin
            if FocusedCell.Row > FFirstCell.Row then
            begin
              ff := FFirstCell;
              fc := FocusedCell;
            end
            else
            begin
              fc := FFirstCell;
              ff := FocusedCell;
            end;

            for I := FixedColumnCount to ColumnCount - 1 - FixedRightColumnCount do
            begin
              for J := Selection.StartCell.Row to Selection.StopCell.Row do
              begin
                if (J = Selection.StartCell.Row) and (J = Selection.StopCell.Row) and (I >= Selection.StartCell.Column) and (I <= Selection.StopCell.Column) then
                  SelectedCells[I, J] := not SelectedCells[I, J]
                else if (J = Selection.StartCell.Row) and (J < Selection.StopCell.Row) and (I >= ff.Column) then
                  SelectedCells[I, J] := not SelectedCells[I, J]
                else if (J > Selection.StartCell.Row) and (J < Selection.StopCell.Row) then
                  SelectedCells[I, J] := not SelectedCells[I, J]
                else if (J = Selection.StopCell.Row) and (J > Selection.StartCell.Row) and (I <= fc.Column) then
                  SelectedCells[I, J] := not SelectedCells[I, J];
              end;
            end;
          end;
        end;
      end
      else
      begin
        SelectedCells[FocusedCell.Column, FocusedCell.Row] := not SelectedCells[FocusedCell.Column, FocusedCell.Row];
        cntcl := GetSelectedCellCount;
        if not SelectedCells[FocusedCell.Column, FocusedCell.Row] and (cntcl = 0) then
          SelectedCells[FocusedCell.Column, FocusedCell.Row] := True;
      end;

      if not SelectedCells[FocusedCell.Column, FocusedCell.Row] then
        for I := Selection.StartCell.Column to Selection.StopCell.Column do
          for J := Selection.StartCell.Row to Selection.StopCell.Row do
            if (I <> FocusedCell.Column) and (J <> FocusedCell.Row) and SelectedCells[I, J] then
              SetFocusCell(I, J);

      DecUpdateCount;
    end;
  end;

  Navigate(FocusedCell);

  if Allow and Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).SelectCell(FocusedCell);

  if HasGrid[FocusedCell.Column, FocusedCell.Row] then
  begin
    g := Grids[FocusedCell.Column, FocusedCell.Row];
    if Assigned(g) then
    begin
      g.Focus;
      g.FTabKey := FTabKey;
      g.HandleEnter;
    end;
  end;

  if Allow then
    DoSelectCell(Selection.StartCell, Selection.StopCell);
end;

procedure TTMSFNCDataGridRenderer.SelectColumns(AFromColumn,
  AToColumn: Integer);
var
  i: Integer;
begin
  if Options.Selection.Mode = gsmColumnRange then
    Selection := MakeCellRange(AFromColumn, 0, AToColumn, 0)
  else
  if Options.Selection.Mode = gsmSingleColumn then
    Selection := MakeCellRange(AFromColumn, 0, AFromColumn, 0)
  else
  if Options.Selection.Mode = gsmDisjunctColumn then
    for i := AFromColumn to AToColumn do
      SelectedColumns[i] := True;
end;

procedure TTMSFNCDataGridRenderer.SelectRows(AFromRow, AToRow: Integer);
var
  i: Integer;
begin
  if Options.Selection.Mode = gsmRowRange then
    Selection := MakeCellRange(0, AFromRow, 0, AToRow)
  else
  if Options.Selection.Mode = gsmSingleRow then
    Selection := MakeCellRange(0, AFromRow, 0, AFromRow)
  else
  if Options.Selection.Mode = gsmDisjunctRow then
    for i := AFromRow to AToRow do
      SelectedRows[i] := true;
end;

procedure TTMSFNCDataGridRenderer.Navigate(ACell: TTMSFNCDataGridCellCoord; AForceScroll: Boolean = False; AIgnoreColumn: Boolean = False; AIgnoreRow: Boolean = False);
var
  hs, vs, rhs, rvs, vss, hss: Double;
  cw, ch: Double;
  totalh, totalw: Double;
  cs, rs: Integer;
  toth, totw: Double;
  cl: TTMSFNCDataGridCellCoord;
  cr: TRectF;
  c, r: Integer;
begin
  cr := GetContentClipRect;
  cw := cr.Right -  cr.Left;
  ch := cr.Bottom - cr.Top;
  vs := GetVScrollValue;
  hs := GetHScrollValue;

  vss := vs;
  hss := hs;

  if ScrollMode = gsmCellScrolling then
  begin
    vss := GetVerticalScrollPosition;
    hss := GetHorizontalScrollPosition;
  end;

  totalw := 0;
  totalh := 0;
  ACell := BaseCell(ACell);
  cs := ColumnSpan(ACell);
  rs := RowSpan(ACell);
  if cs > 0 then
  begin
    for c := ACell.Column to ACell.Column + cs - 1 do
      totalw := totalw + ColumnWidths[c];
  end
  else
    totalw := ColumnWidths[ACell.Column];

  if rs > 0 then
  begin
    for r := ACell.Row to ACell.Row + rs - 1 do
      totalh := totalh + RowHeights[r];
  end
  else
    totalh := RowHeights[ACell.Row];

  rvs := RowPositions[ACell.Row];
  if AForceScroll then
  begin
    case ScrollMode of
      gsmPixelScrolling: vs := rvs - GetTotalFixedRowHeight;
      gsmCellScrolling: vs := ACell.Row - FixedRowCount - FreezeRowCount;
    end;
  end
  else
  begin
    if (rvs < vss + GetTotalFixedRowHeight) then
    begin
      case ScrollMode of
        gsmPixelScrolling: vs := rvs - GetTotalFixedRowHeight;
        gsmCellScrolling: vs := ACell.Row - FixedRowCount - FreezeRowCount;
      end;
    end
    else
    begin
      if totalh < ch - GetTotalFixedBottomRowHeight then
      begin
        case ScrollMode of
          gsmPixelScrolling:
          begin
            if (rvs + totalh > vss + ch - GetTotalFixedBottomRowHeight) then
              vs := rvs + totalh - ch - 1 + GetTotalFixedBottomRowHeight;
          end;
          gsmCellScrolling:
          begin
            while (rvs + totalh > vss + ch - GetTotalFixedBottomRowHeight) do
            begin
              vs := vs + 1;
              cl := BaseCell(MakeCell(ACell.Column, Round(vs)));
              rs := RowSpan(cl);
              if rs > 0 then
                toth := RowPositions[cl.Row + rs - 1] - RowPositions[cl.Row]
              else
              begin
                rs := 1;
                toth := RowHeights[cl.Row];
              end;

              vs := vs + rs - 1;
              vss := vss + toth;
            end;
          end;
        end;
      end;
    end;
  end;

  rhs := ColumnPositions[ACell.Column];
  if AForceScroll then
  begin
    case ScrollMode of
      gsmPixelScrolling: hs := rhs - GetTotalFixedColumnWidth;
      gsmCellScrolling: hs := ACell.Column - FixedColumnCount - FreezeColumnCount;
    end;
  end
  else
  begin
    if (rhs < hss + GetTotalFixedColumnWidth) then
    begin
      case ScrollMode of
        gsmPixelScrolling: hs := rhs - GetTotalFixedColumnWidth;
        gsmCellScrolling: hs := ACell.Column - FixedColumnCount - FreezeColumnCount;
      end;
    end
    else
    begin
      if (totalw < cw - GetTotalFixedRightColumnWidth) then
      begin
        case ScrollMode of
          gsmPixelScrolling:
          begin
            if (rhs + totalw > hss + cw - GetTotalFixedRightColumnWidth) then
              hs := rhs + totalw - cw - 1 + GetTotalFixedRightColumnWidth;
          end;
          gsmCellScrolling:
          begin
            while (rhs + totalw > hss + cw - GetTotalFixedRightColumnWidth) do
            begin
              hs := hs + 1;
              cl := BaseCell(MakeCell(Round(hs), ACell.Row));
              cs := ColumnSpan(cl);
              if cs > 0 then
                totw := ColumnPositions[cl.Column] - ColumnPositions[cl.Column + cs - 1]
              else
              begin
                cs := 1;
                totw := ColumnWidths[cl.Column];
              end;

              hs := hs + cs - 1;
              hss := hss + totw;
            end;
          end;
        end;
      end;
    end;
  end;

  if AIgnoreColumn then
    Scroll(GetHorizontalScrollPosition, vs)
  else if AIgnoreRow then
    Scroll(hs, GetVerticalScrollPosition)
  else
    Scroll(hs, vs);
end;

procedure TTMSFNCDataGridRenderer.Scroll(AHorizontalPos, AVerticalPos: Double);
var
  h, v: Single;
  hv: Boolean;
begin
  FBlockScrollingUpdate := True;
  h := GetHScrollValue;
  SetHScrollValue(AHorizontalPos);
  v := GetVScrollValue;
  SetVScrollValue(AVerticalPos);

  hv := v <> GetVScrollValue;
  if hv then
    DoVScroll(GetVScrollValue);

  if h <> GetHScrollValue then
    DoHScroll(GetHScrollValue);

  FBlockScrollingUpdate := False;
  Update;

  if hv and Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    (Adapter as TTMSFNCDataGridRendererDataAdapter).Scroll;
end;

procedure TTMSFNCDataGridRenderer.SetCellAppearance(
  const Value: TTMSFNCDataGridCellAppearance);
begin
  if FCellAppearance <> Value then
    FCellAppearance.Assign(Value);
end;

procedure TTMSFNCDataGridRenderer.SetDataCheckBox(AColumn, ARow: Integer);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
      cd.&Type := gcitDataCheckBox;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetCheckBox(AColumn, ARow: Integer; AValue: Boolean = False);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
    begin
      cd.&Type := gcitCheckBox;
      cd.BooleanValue := AValue;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetCheckBox(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False);
begin
  SetCheckBox(ACell.Column, ACell.Row, AValue);
end;

procedure TTMSFNCDataGridRenderer.SetCheckBoxColumn(AColumn: Integer; AValue: Boolean = False);
var
  I: Integer;
begin
  BeginUpdate;
  try
    for I := FixedRowCount to RowCount - 1 - FixedBottomRowCount do
      SetCheckBox(AColumn, I, AValue);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetDataCheckBox(ACell: TTMSFNCDataGridCellCoord);
begin
  SetDataCheckBox(ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.SetDataCheckBoxColumn(AColumn: Integer);
var
  I: Integer;
begin
  BeginUpdate;
  try
    for I := FixedRowCount to RowCount - 1 - FixedBottomRowCount do
      SetDataCheckBox(AColumn, I);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetRadioGroupIndex(AColumn, ARow: Integer;
  const Value: Integer);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
      cd.RadioGroupIndex := Value;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetClipRect(ARect: TRectF);
begin
  FClipRect := ARect;
  FUseClipRect := True;
end;

procedure TTMSFNCDataGridRenderer.SetColors(AColumn, ARow: Integer;
  const Value: TTMSFNCGraphicsColor);
begin
  Layouts[AColumn, ARow].Fill.Color := Value;
end;

procedure TTMSFNCDataGridRenderer.SetColumns(
  const Value: TTMSFNCDataGridColumns);
begin
  FColumns.Assign(Value);
end;

procedure TTMSFNCDataGridRenderer.SetComments(AColumn, ARow: Integer;
  const Value: string);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
      cd.Comment := Value;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetHints(AColumn, ARow: Integer;
  const Value: string);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
      cd.Hint := Value;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetControlAligns(AColumn, ARow: Integer;
  const Value: TTMSFNCDataGridCellControlAlign);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
      cd.ControlAlign := Value;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetControlPositions(AColumn, ARow: Integer;
  const Value: TTMSFNCDataGridCellControlPosition);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
      cd.ControlPosition := Value;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetControlHeights(AColumn, ARow: Integer;
  const Value: Integer);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
      cd.ControlHeight := Value;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetControlTexts(AColumn, ARow: Integer;
  const Value: string);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
      cd.ControlText := Value;
  finally
    EndUpdate;
  end;
end;

procedure SetEditorValue(AControl: TTMSFNCDataGridCellControl; AValue: TTMSFNCDataGridCellValue);
var
  AContext: TRttiContext;
  rt: TRttiType;
  prop, prop2: TRttiProperty;
  propv2: TValue;
  sl: TStrings;
  idx: Integer;
  val: TValue;
  i: Integer;
  d: Double;
  b: Boolean;
  cl: Int64;
  v: string;
  dt: TDateTime;
  fmt: TFormatSettings;
begin
  fmt := FormatSettings;

  AContext := TRttiContext.Create;
  try
    rt := AContext.GetType(AControl.ClassInfo);

    prop := rt.GetProperty('ItemIndex');
    prop2 := rt.GetProperty('Items');
    if Assigned(prop) and prop.IsWritable then
    begin
      if Assigned(prop2) and prop2.IsWritable then
      begin
        propv2 := prop2.GetValue(AControl);
        if propv2.IsType<TStrings> then
        begin
          sl := propv2.AsType<TStrings>;
          idx := sl.IndexOf(TTMSFNCDataGridData.ValueToString(AValue));
          val := CellValueToTValue(idx);
          prop.SetValue(AControl, val);
        end;
      end
      else
      begin
        val := CellValuetoTValue(AValue);
        prop.SetValue(AControl, val);
      end;

      Exit;
    end;

    prop := rt.GetProperty('Value');
    if not Assigned(prop) then
      prop := rt.GetProperty('Position');

    if Assigned(Prop) and Prop.IsWritable then
    begin
      if prop.GetValue(AControl).IsType<Integer> then
      begin
        i := TTMSFNCDataGridData.ValueToInteger(AValue, fmt);
        val := CellValueToTValue(i);
        prop.SetValue(AControl, val);
        Exit;
      end
      else if prop.GetValue(AControl).IsType<Double> then
      begin
        d := TTMSFNCDataGridData.ValueToFloat(AValue, fmt);
        val := CellValueToTValue(d);
        prop.SetValue(AControl, val);
        Exit;
      end
    end;

    prop := rt.GetProperty('Date');
    if Assigned(prop) and prop.IsWritable and prop.GetValue(AControl).IsType<TDateTime> then
    begin
      dt := TTMSFNCDataGridData.ValueToDateTime(AValue, fmt);
      val := CellValueToTValue(dt);
      prop.SetValue(AControl, val);
      Exit;
    end;

    prop := rt.GetProperty('SelectedColor');
    if Assigned(prop) and prop.IsWritable and prop.GetValue(AControl).IsType<TTMSFNCGraphicsColor> then
    begin
      cl := TTMSFNCDataGridData.ValueToInt64(AValue, fmt);
      val := CellValueToTValue(cl);
      prop.SetValue(AControl, val);
      Exit;
    end;

    prop := rt.GetProperty({$IFDEF FMXLIB}'IsChecked'{$ELSE}'Checked'{$ENDIF});
    if Assigned(prop) and prop.IsWritable then
    begin
      b := TTMSFNCDataGridData.ValueToBoolean(AValue, fmt);
      val := CellValueToTValue(b);
      prop.SetValue(AControl, val);
      Exit;
    end;

    prop := rt.GetProperty('Text');
    if Assigned(Prop) and Prop.IsWritable then
    begin
      v := TTMSFNCDataGridData.ValueToString(AValue);
      val := CellValueToTValue(v);
      prop.SetValue(AControl, val);
      Exit;
    end;

  finally
    AContext.Free;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetControlWidths(AColumn, ARow: Integer;
  const Value: Integer);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
      cd.ControlWidth := Value;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetPadding(const Value: TTMSFNCMargins);
begin
  if FPadding <> Value then
    FPadding.Assign(Value);
end;

procedure TTMSFNCDataGridRenderer.SetPageIndex(const Value: Integer);
begin
  if Value <> GetPageIndex then
    GoToPage(Value);
end;

procedure TTMSFNCDataGridRenderer.SetDataProgressBar(AColumn, ARow: Integer);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
      cd.&Type := gcitDataProgressBar;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetDataProgressBar(ACell: TTMSFNCDataGridCellCoord);
begin
  SetDataProgressBar(ACell.Column, ACell.Row);
end;

procedure TTMSFNCDataGridRenderer.SetDataProgressBarColumn(AColumn: Integer);
var
  I: Integer;
begin
  BeginUpdate;
  try
    for I := FixedRowCount to RowCount - 1 - FixedBottomRowCount do
      SetDataProgressBar(AColumn, I);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetProgressBar(AColumn, ARow: Integer; AValue: Integer = 0);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
    begin
      cd.&Type := gcitProgressBar;
      cd.IntegerValue := AValue;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetProgressBar(ACell: TTMSFNCDataGridCellCoord; AValue: Integer = 0);
begin
  SetProgressBar(ACell.Column, ACell.Row, AValue);
end;

procedure TTMSFNCDataGridRenderer.SetProgressBarColumn(AColumn: Integer; AValue: Integer = 0);
var
  I: Integer;
begin
  BeginUpdate;
  try
    for I := FixedRowCount to RowCount - 1 - FixedBottomRowCount do
      SetProgressBar(AColumn, I, AValue);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
    begin
      cd.&Type := gcitRadioButton;
      cd.BooleanValue := AValue;
      cd.RadioGroupIndex := AGroupIndex;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  SetRadioButton(ACell.Column, ACell.Row, AValue, AGroupIndex);
end;

procedure TTMSFNCDataGridRenderer.SetRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
var
  I: Integer;
begin
  BeginUpdate;
  try
    for I := FixedRowCount to RowCount - 1 - FixedBottomRowCount do
      SetRadioButton(AColumn, I, AValue, AGroupIndex);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetDataRadioButton(AColumn, ARow: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
    begin
      cd.&Type := gcitDataRadioButton;
      cd.RadioGroupIndex := AGroupIndex;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetDataRadioButton(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean = False; AGroupIndex: Integer = 0);
begin
  SetDataRadioButton(ACell.Column, ACell.Row, AValue, AGroupIndex);
end;

procedure TTMSFNCDataGridRenderer.SetDataRadioButtonColumn(AColumn: Integer; AValue: Boolean = False; AGroupIndex: Integer = 0);
var
  I: Integer;
begin
  BeginUpdate;
  try
    for I := FixedRowCount to RowCount - 1 - FixedBottomRowCount do
      SetDataRadioButton(AColumn, I, AValue, AGroupIndex);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetDragAppearance(
  const Value: TTMSFNCDataGridDragAppearance);
begin
  FDragAppearance.Assign(Value);
end;

procedure TTMSFNCDataGridRenderer.SetButton(AColumn, ARow: Integer; AText: string = '');
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
    begin
      cd.&Type := gcitButton;
      cd.ControlText := AText;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetAdaptToStyle(const Value: Boolean);
begin
  FAdaptToStyle := Value;
  if AdaptToStyle then
  begin
    FAdaptedToStyle := True;
    ApplyStyle;
  end
  else if FAdaptedToStyle then
  begin
    ResetToDefaultStyle;
    FAdaptedToStyle := False;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetBitmap(AColumn, ARow: Integer;
  AName: string);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
    begin
      cd.&Type := gcitBitmap;
      cd.BitmapName := AName;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetBitmap(ACell: TTMSFNCDataGridCellCoord;
  ABitmap: TTMSFNCBitmap);
begin
  SetBitmap(ACell.Column, ACell.Row, ABitmap);
end;

procedure TTMSFNCDataGridRenderer.SetBitmap(AColumn, ARow: Integer;
  ABitmap: TTMSFNCBitmap);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
    begin
      cd.&Type := gcitBitmap;
      cd.Bitmap.Assign(ABitmap);
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetBitmap(ACell: TTMSFNCDataGridCellCoord;
  AName: string);
begin
  SetBitmap(ACell.Column, ACell.Row, AName);
end;

procedure TTMSFNCDataGridRenderer.SetBitmapColumn(AColumn: Integer;
  ABitmap: TTMSFNCBitmap);
var
  I: Integer;
begin
  BeginUpdate;
  try
    for I := FixedRowCount to RowCount - 1 - FixedBottomRowCount do
      SetBitmap(AColumn, I, ABitmap);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetBitmapColumn(AColumn: Integer;
  AName: string);
var
  I: Integer;
begin
  BeginUpdate;
  try
    for I := FixedRowCount to RowCount - 1 - FixedBottomRowCount do
      SetBitmap(AColumn, I, AName);
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetBitmapContainer(
  const Value: TTMSFNCBitmapContainer);
begin
  FBitmapContainer := Value;
  Display;
end;

procedure TTMSFNCDataGridRenderer.SetBooleans(AColumn, ARow: Integer;
  const Value: Boolean);
var
  cl: TTMSFNCDataGridColumn;

  procedure UpdateCell(ACell: TTMSFNCDataGridCellCoord; AValue: Boolean);
  var
    cll: TTMSFNCDataGridCell;
    v: TTMSFNCDataGridCellValue;
  begin
    cll := GetCellObjectForCell(ACell);
    if Assigned(cll) then
    begin
      TTMSFNCDataGridCellOpen(cll).UpdateData(CellData[ACell.Column, ACell.Row]);
      if cll is TTMSFNCDataGridCheckedCell then
        (cll as TTMSFNCDataGridCheckedCell).Checked := AValue;
      TTMSFNCDataGridCellOpen(cll).CreateControlCache;

      if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
      begin
        v := AValue;
        (Adapter as TTMSFNCDataGridRendererDataAdapter).EditSetData(ACell, nil, v);
      end;
    end;
  end;

begin
  if HasDataCheckBox[AColumn, ARow] or HasDataRadioButton[AColumn, ARow] then
  begin
    cl := GetColumn(AColumn);
    if Assigned(cl) then
    begin
      if Value then
        Cells[AColumn, ARow] := cl.Binding.BooleanTrueValue
      else
        Cells[AColumn, ARow] := cl.Binding.BooleanFalseValue
    end;
  end
  else
    inherited SetBooleans(AColumn, ARow, Value);

  UpdateCell(MakeCell(AColumn, ARow), Value);
end;

procedure TTMSFNCDataGridRenderer.SetButton(ACell: TTMSFNCDataGridCellCoord; AText: string = '');
begin
  SetButton(ACell.Column, ACell.Row, AText);
end;

procedure TTMSFNCDataGridRenderer.SetButtonColumn(AColumn: Integer; AText: string = '');
var
  I: Integer;
begin
  BeginUpdate;
  try
    for I := FixedRowCount to RowCount - 1 - FixedBottomRowCount do
      SetButton(AColumn, I, AText);
  finally
    EndUpdate;
  end;
end;

function TTMSFNCDataGridRenderer.GetFilterClearButton: TTMSFNCToolBarButton;
begin
  if not Assigned(FFilterClearButton) then
  begin
    FFilterClearButton := TTMSFNCToolBarButton.Create(FilterContainer);
    FFilterClearButton.Parent := FilterContainer;
    FFilterClearButton.Appearance.FlatStyle := True;
    FFilterClearButton.Width := 25;
    FFilterClearButton.Left := 5;
    FFilterClearButton.Top := 10 + FilterTypeComboBox.Height;
    FFilterClearButton.Height := 22;
    FFilterClearButton.Text := '';
    FFilterClearButton.HorizontalTextAlign := gtaLeading;
    FFilterClearButton.Appearance.NormalFill.Color := gcWhite;
    FFilterClearButton.Appearance.NormalStroke.Color := gcWhite;
    FFilterClearButton.Hint := 'Clear Filter';
    FFilterClearButton.ShowHint := True;
    FFilterClearButton.OnClick := DoFilterClearClick;

    FilterClearButton.AdaptToStyle := AdaptToStyle;
  end;

  FFilterClearButton.Bitmaps.Clear;  
  FFilterClearButton.Bitmaps.AddBitmap(CellAppearance.FilterClearIcon);  
  Result := FFilterClearButton;
end;

function TTMSFNCDataGridRenderer.GetFilterContainer: TTMSFNCCustomControl;
begin
  if not Assigned(FFilterContainer) then
    FFilterContainer := TTMSFNCCustomControl.Create(nil);

  Result := FFilterContainer;
end;

function TTMSFNCDataGridRenderer.GetFilterListBox: TTMSFNCCheckedListBox;
begin
  if not Assigned(FFilterListBox) then
  begin
    FFilterListBox := TTMSFNCCheckedListBox.Create(FilterContainer);
    FFilterListBox.Parent := FilterContainer;
    FFilterListBox.ControlAlignment := caClient;
    {$IFNDEF FMXLIB}
    FFilterListBox.AlignWithMargins := True;
    {$ENDIF}
    FFilterListBox.Margins.Left := 5;
    FFilterListBox.Margins.Top := 5;
    FFilterListBox.Margins.Right := 5;
    FFilterListBox.Margins.Bottom := 0;
    FFilterListBox.OnItemCheckChanged := DoFilterListBoxItemCheckChanged;
    FFilterListBox.AdaptToStyle := AdaptToStyle;
  end;

  Result := FFilterListBox;
end;

function TTMSFNCDataGridRenderer.GetFilterPopup: TTMSFNCPopup;
var
  p: TTMSFNCDataGridCellParent;
begin
  if not Assigned(FFilterPopup) then
  begin
    p := GetParentHost;
    FFilterPopup := TTMSFNCPopup.Create(p);
    FFilterPopup.Placement := ppAbsolute;
    FFilterPopup.AdaptToStyle := AdaptToStyle;
  end;

  Result := FFilterPopup;
end;

function TTMSFNCDataGridRenderer.GetFilterEdit: TTMSFNCEdit;
begin
  if not Assigned(FFilterEdit) then
  begin
    FFilterEdit := TTMSFNCEdit.Create(FilterContainer);
    FFilterEdit.Parent := FilterContainer;
    FFilterEdit.OnEnter := @DoFilterEnter;
    {$IFDEF FMXLIB}
    FFilterEdit.TextPrompt := sTMSFNCDataGridFilterEditPrompt;
    FFilterEdit.Align := TAlignLayout.Top;
    FFilterEdit.OnChangeTracking := DoFilterEditChange;
    {$ELSE}
    FFilterEdit.TextHint := sTMSFNCDataGridFilterEditPrompt;
    FFilterEdit.Align := alTop;
    FFilterEdit.AlignWithMargins := True;
    FFilterEdit.OnChange := @DoFilterEditChange;
    {$ENDIF}
    FFilterEdit.Margins.Left := 35;
    FFilterEdit.Margins.Top := 5;
    FFilterEdit.Margins.Right := 5;
    FFilterEdit.Margins.Bottom := 0;
  end;

  Result := FFilterEdit;
end;

function TTMSFNCDataGridRenderer.GetFilterEditors(AColumn,
  ARow: Integer): TTMSFNCDataGridCellControl;
var
  cd: TTMSFNCDataGridCellItem;
begin
  Result := nil;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
  begin
    if not Assigned(TTMSFNCDataGridCellItemExtendedOpen(cd).FilterEditor) then
      TTMSFNCDataGridCellItemExtendedOpen(cd).FilterEditor := CreateFilterEditor(MakeCell(AColumn, ARow));

    Result := TTMSFNCDataGridCellItemExtendedOpen(cd).FilterEditor;
  end;
end;

function TTMSFNCDataGridRenderer.GetFilterExitTimer: TTimer;
begin
  if not Assigned(FFilterExitTimer) then
  begin
    FFilterExitTimer := TTimer.Create(nil);
    FFilterExitTimer.OnTimer := DoFilterExitTimer;
    FFilterExitTimer.Interval := 100;
    FFilterExitTimer.Enabled := False;
  end;

  Result := FFilterExitTimer;
end;

function TTMSFNCDataGridRenderer.GetFilterSortAscendingButton: TTMSFNCToolBarButton;
begin
  if not Assigned(FFilterSortAscendingButton) then
  begin
    FFilterSortAscendingButton := TTMSFNCToolBarButton.Create(FilterContainer);
    FFilterSortAscendingButton.Parent := FilterContainer;
    {$IFDEF FMXLIB}
    FFilterSortAscendingButton.Align := TAlignLayout.Bottom;
    {$ELSE}
    FFilterSortAscendingButton.Align := alBottom;
    FFilterSortAscendingButton.AlignWithMargins := True;
    {$ENDIF}
    FFilterSortAscendingButton.Appearance.FlatStyle := True;
    FFilterSortAscendingButton.Text := sTMSFNCDataGridFilterSortButtonAscending;
    FFilterSortAscendingButton.HorizontalTextAlign := gtaLeading;
    FFilterSortAscendingButton.Appearance.NormalFill.Color := gcWhite;
    FFilterSortAscendingButton.Appearance.NormalStroke.Color := gcWhite;
    FFilterSortAscendingButton.Hint := 'Sort Ascending';
    FFilterSortAscendingButton.ShowHint := True;
    FFilterSortAscendingButton.Margins.Left := 5;
    FFilterSortAscendingButton.Margins.Top := 5;
    FFilterSortAscendingButton.Margins.Right := 5;
    FFilterSortAscendingButton.Margins.Bottom := 5;
    FFilterSortAscendingButton.DataString := 'asc';
    FFilterSortAscendingButton.OnClick := DoFilterSortClick;

    FFilterSortAscendingButton.AdaptToStyle := AdaptToStyle;
  end;

  FFilterSortAscendingButton.Bitmaps.Clear;
  FFilterSortAscendingButton.Bitmaps.AddBitmap(CellAppearance.SortAscendingIcon);  
  Result := FFilterSortAscendingButton;
end;

function TTMSFNCDataGridRenderer.GetFilterSortDescendingButton: TTMSFNCToolBarButton;
begin
  if not Assigned(FFilterSortDescendingButton) then
  begin
    FFilterSortDescendingButton := TTMSFNCToolBarButton.Create(FilterContainer);
    FFilterSortDescendingButton.Parent := FilterContainer;
    {$IFDEF FMXLIB}
    FFilterSortDescendingButton.Align := TAlignLayout.Bottom;
    {$ELSE}
    FFilterSortDescendingButton.Align := alBottom;
    FFilterSortDescendingButton.AlignWithMargins := True;
    {$ENDIF}
    FFilterSortDescendingButton.Appearance.FlatStyle := True;
    FFilterSortDescendingButton.Text := sTMSFNCDataGridFilterSortButtonDescending;
    FFilterSortDescendingButton.HorizontalTextAlign := gtaLeading;
    FFilterSortDescendingButton.Appearance.NormalFill.Color := gcWhite;
    FFilterSortDescendingButton.Appearance.NormalStroke.Color := gcWhite;
    FFilterSortDescendingButton.Hint := 'Sort Descending';
    FFilterSortDescendingButton.ShowHint := True;
    FFilterSortDescendingButton.Margins.Left := 5;
    FFilterSortDescendingButton.Margins.Top := 5;
    FFilterSortDescendingButton.Margins.Right := 5;
    FFilterSortDescendingButton.Margins.Bottom := 0;
    FFilterSortDescendingButton.DataString := 'desc';
    FFilterSortDescendingButton.OnClick := DoFilterSortClick;

    FFilterSortDescendingButton.AdaptToStyle := AdaptToStyle;
  end;

  FFilterSortDescendingButton.Bitmaps.Clear;
  FFilterSortDescendingButton.Bitmaps.AddBitmap(CellAppearance.SortDescendingIcon);  
  Result := FFilterSortDescendingButton;
end;

function TTMSFNCDataGridRenderer.GetFilterTimer: TTimer;
begin
  if not Assigned(FFilterTimer) then
  begin
    FFilterTimer := TTimer.Create(nil);
    FFilterTimer.OnTimer := DoFilterTimer;
    FFilterTimer.Interval := 1;
    FFilterTimer.Enabled := False;
  end;

  Result := FFilterTimer;
end;

function TTMSFNCDataGridRenderer.GetFilterTypeComboBox: TComboBoxSafe;
var
  fdt, I: TTMSFNCDataGridDataFilterType;
  a: TArray<String>;
begin
  if not Assigned(FFilterTypeComboBox) then
  begin
    FFilterTypeComboBox := TComboBoxSafe.Create(FilterContainer);
    FFilterTypeComboBox.Parent := FilterContainer;
    {$IFDEF FMXLIB}
    FFilterTypeComboBox.Align := TAlignLayout.Top;
    {$ELSE}
    FFilterTypeComboBox.Align := alTop;
    FFilterTypeComboBox.AlignWithMargins := True;
    {$ENDIF}
    {$IFDEF VCLLIB}
    FFilterTypeComboBox.Style := csDropDownList;
    {$ENDIF}
    FFilterTypeComboBox.Margins.Left := 5;
    FFilterTypeComboBox.Margins.Top := 5;
    FFilterTypeComboBox.Margins.Right := 5;
    FFilterTypeComboBox.Margins.Bottom := 0;

    SetLength(a, 0);
    for I := Low(TTMSFNCDataGridDataFilterType) to High(TTMSFNCDataGridDataFilterType) do
    begin
      SetLength(a, Length(a) + 1);
      a[Length(a) - 1] := GetFilterTypeName(I);
    end;

    FFilterTypeComboBox.SafeItems := a;
    fdt := gftEqual;
    FFilterTypeComboBox.SafeItemIndex := Integer(fdt);
    FFilterTypeComboBox.OnChange := @DoFilterTypeChange;
  end;

  Result := FFilterTypeComboBox;
end;

function TTMSFNCDataGridRenderer.GetFindDialog: TTMSFNCFindDialog;
begin
  if not Assigned(FFindDialog) then
  begin
    FFindDialog := TTMSFNCFindDialog.Create(nil);
    FFindDialog.Options := [fdoCaseSensitive, fdoNextEnabled, fdoFindEnabled,
      fdoCloseEnabled, fdoDown];
    FFindDialog.VisibleOptions := [fdovCaseSensitive, fdovExpression, fdovCloseIfFound,
    fdovWholeWordOnly, fdovNext, fdovFind,
       fdovClose, fdovFindCombo, fdovFindMemo, fdovDirection];
    FFindDialog.OnFind := DoFind;
    FFindDialog.OnFindNext := DoFindNext;
  end;

  Result := FFindDialog;
end;

function TTMSFNCDataGridRenderer.GetFocusedCell: TTMSFNCDataGridCellCoord;
begin
  Result := FFocusedCell;
end;

function TTMSFNCDataGridRenderer.GetFontColors(AColumn, ARow: Integer): TTMSFNCGraphicsColor;
var
  l: TTMSFNCDataGridCellLayout;
begin
  Result := gcNull;
  l := GetCellAppearance(GetCellState(MakeCell(AColumn, ARow)), MakeCell(AColumn, ARow));
  if Assigned(l) then
    Result := l.Font.Color;
end;

function TTMSFNCDataGridRenderer.GetFontNames(AColumn, ARow: Integer): string;
var
  l: TTMSFNCDataGridCellLayout;
begin
  Result := '';
  l := GetCellAppearance(GetCellState(MakeCell(AColumn, ARow)), MakeCell(AColumn, ARow));
  if Assigned(l) then
    Result := l.Font.Name;
end;

function TTMSFNCDataGridRenderer.GetFontSizes(AColumn, ARow: Integer): Single;
var
  l: TTMSFNCDataGridCellLayout;
begin
  Result := 0;
  l := GetCellAppearance(GetCellState(MakeCell(AColumn, ARow)), MakeCell(AColumn, ARow));
  if Assigned(l) then
    Result := l.Font.Size;
end;

function TTMSFNCDataGridRenderer.GetFontStyles(AColumn, ARow: Integer): TFontStyles;
var
  l: TTMSFNCDataGridCellLayout;
begin
  Result := [];
  l := GetCellAppearance(GetCellState(MakeCell(AColumn, ARow)), MakeCell(AColumn, ARow));
  if Assigned(l) then
    Result := l.Font.Style;
end;

function TTMSFNCDataGridRenderer.GetFullRect: TRectF;
begin
  Result := inherited GetContentRect;

  if not IsExporting then
  begin
    if HasCustomScrollBars then
    begin
      if Assigned(CustomHorizontalScrollBar) and CustomHorizontalScrollBar.Visible then
        Result.Bottom := CustomHorizontalScrollBar.Top + CustomHorizontalScrollBar.Height;

      if Assigned(CustomVerticalScrollBar) and CustomVerticalScrollBar.Visible then
        Result.Right := CustomVerticalScrollBar.Left + CustomVerticalScrollBar.Width;
    end
    else
    begin
      if Assigned(HorizontalScrollBar) and HorizontalScrollBar.Visible then
      begin
        {$IFDEF FMXLIB}
        Result.Bottom := HorizontalScrollBar.Position.Y;
        {$ELSE}
        Result.Bottom := HorizontalScrollBar.Top;
        {$ENDIF}

        Result.Bottom := Result.Bottom + HorizontalScrollBar.Height;
      end;

      if Assigned(VerticalScrollBar) and VerticalScrollBar.Visible then
      begin
        {$IFDEF FMXLIB}
        Result.Right := VerticalScrollBar.Position.X;
        {$ELSE}
        Result.Right := VerticalScrollBar.Left;
        {$ENDIF}

        Result.Right := Result.Right + VerticalScrollBar.Width;
      end;
    end;
  end;
end;

function TTMSFNCDataGridRenderer.GetGrids(AColumn,
  ARow: Integer): TTMSFNCDataGridRenderer;
var
  cd: TTMSFNCDataGridCellItem;
  o: TObject;
begin
  Result := nil;
  cd := CellData[AColumn, ARow];
  if Assigned(cd) and IsExtendedCell(cd) then
  begin
    o := TTMSFNCDataGridCellItemExtendedOpen(AsExtendedCell(cd)).Renderer;
    if Assigned(o) and (o is TTMSFNCDataGridRenderer) then
      Result := (o as TTMSFNCDataGridRenderer);
  end;

  if not Assigned(Result) then
    raise Exception.Create('Grid not available, please call SetGrid/AddGrid first.');
end;

procedure TTMSFNCDataGridRenderer.ConfigureLayout(ACell: TTMSFNCDataGridCellCoord; ALayout: TTMSFNCDataGridCellLayout);
begin
  ALayout.Assign(GetCellAppearance(GetCellState(ACell), ACell))
end;

function TTMSFNCDataGridRenderer.ConfigureScrollBarsRect(ARect: TRectF): TRectF;
begin
  Result := ARect;

  if not IsExporting then
  begin
    if HasCustomScrollBars then
    begin
      if Assigned(CustomHorizontalScrollBar) and CustomHorizontalScrollBar.Visible then
        Result.Bottom := CustomHorizontalScrollBar.Top;

      if Assigned(CustomVerticalScrollBar) and CustomVerticalScrollBar.Visible then
        Result.Right := CustomVerticalScrollBar.Left;
    end
    else
    begin
      if Assigned(HorizontalScrollBar) and HorizontalScrollBar.Visible then
      begin
        {$IFDEF FMXLIB}
        Result.Bottom := HorizontalScrollBar.Position.Y;
        {$ELSE}
        Result.Bottom := HorizontalScrollBar.Top;
        {$ENDIF}
      end;

      if Assigned(VerticalScrollBar) and VerticalScrollBar.Visible then
      begin
        {$IFDEF FMXLIB}
        Result.Right := VerticalScrollBar.Position.X;
        {$ELSE}
        Result.Right := VerticalScrollBar.Left;
        {$ENDIF}
      end;
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetLayout(ACell: TTMSFNCDataGridCell);
begin
  if Assigned(ACell) then
    ConfigureLayout(ACell.Coordinate, ACell.Layout);
end;

procedure TTMSFNCDataGridRenderer.SetOptions(
  const Value: TTMSFNCDataGridOptions);
begin
  Options.Assign(Value);
end;

procedure TTMSFNCDataGridRenderer.SetCustomScrollBars(const Value: Boolean);
begin
  if FCustomScrollBars <> Value then
  begin
    FCustomScrollBars := Value;
    Update;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetTextAligns(AColumn, ARow: Integer;
  const Value: TTMSFNCGraphicsTextAlign);
begin
  Layouts[AColumn, ARow].TextAlign := Value;
end;

procedure TTMSFNCDataGridRenderer.SetHorizontalScrollBarVisible(
  const Value: Boolean);
begin
  if FHorizontalScrollBarVisible <> Value then
  begin
    FHorizontalScrollBarVisible := Value;
    Update;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetHScrollValue(AValue: Single);
begin
  FLocalHScrollValue := AValue;

  if HasCustomScrollBars and Assigned(CustomHorizontalScrollBar) then
  begin
    if CustomHorizontalScrollBar.PageSize > 0 then
      CustomHorizontalScrollBar.Value := Min(CustomHorizontalScrollBar.Max - CustomHorizontalScrollBar.PageSize, Max(0, AValue));
  end
  else if Assigned(HorizontalScrollBar) then
  begin
    {$IFDEF FMXLIB}
    if HorizontalScrollBar.ViewportSize > 0 then
      HorizontalScrollBar.Value := Min(HorizontalScrollBar.Max - HorizontalScrollBar.ViewportSize, Max(0, AValue));
    {$ENDIF}
    {$IFDEF VCLLIB}
    if HorizontalScrollBar.PageSize > 0 then
      HorizontalScrollBar.Position := Min(HorizontalScrollBar.Max - HorizontalScrollBar.PageSize, Max(0, Round(AValue)));
    {$ENDIF}
    {$IFDEF WEBLIB}
    if HorizontalScrollBar.PageSize > 0 then
      HorizontalScrollBar.Position := Round(Min(HorizontalScrollBar.Max - HorizontalScrollBar.PageSize, Max(0, AValue)));
    {$ENDIF}
  end;
end;

procedure TTMSFNCDataGridRenderer.SetScrollMode(
  const Value: TTMSFNCDataGridScrollMode);
begin
  if FScrollMode <> Value then
  begin
    FScrollMode := Value;
    if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
      (Adapter as TTMSFNCDataGridRendererDataAdapter).UpdateScrollMode;
    Update;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetScrollUpdate(
  const Value: TTMSFNCDataGridScrollUpdate);
begin
  if FScrollUpdate <> Value then
  begin
    FScrollUpdate := Value;
    if Assigned(FCustomVerticalScrollBar) then
      FCustomVerticalScrollBar.Tracking := ScrollUpdate = gsuContinuous;

    if Assigned(FCustomHorizontalScrollBar) then
      FCustomHorizontalScrollBar.Tracking := ScrollUpdate = gsuContinuous;

    Update;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetStretchScrollBars(const Value: Boolean);
begin
  if FStretchScrollBars <> Value then
  begin
    FStretchScrollBars := Value;
    Update;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetVerticalTextAligns(AColumn, ARow: Integer;
  const Value: TTMSFNCGraphicsTextAlign);
begin
  Layouts[AColumn, ARow].VerticalTextAlign := Value;
end;

procedure TTMSFNCDataGridRenderer.SetVerticalScrollBarVisible(
  const Value: Boolean);
begin
  if FVerticalScrollBarVisible <> Value then
  begin
    FVerticalScrollBarVisible := Value;
    Update;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetReadOnlys(AColumn, ARow: Integer;
  const Value: Boolean);
var
  cd: TTMSFNCDataGridCellItemExtended;
begin
  if not IsCellValid(AColumn, ARow) then
    Exit;

  cd := CellDataExtended[AColumn, ARow];
  if Assigned(cd) then
  begin
    cd.ReadOnly := Value;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetVScrollValue(AValue: Single);
begin
  FLocalVScrollValue := AValue;

  if HasCustomScrollBars and Assigned(CustomVerticalScrollBar) then
  begin
    if CustomVerticalScrollBar.PageSize > 0 then
      CustomVerticalScrollBar.Value := Min(CustomVerticalScrollBar.Max - CustomVerticalScrollBar.PageSize, Max(0, AValue));
  end
  else if Assigned(VerticalScrollBar) then
  begin
    {$IFDEF FMXLIB}
    if VerticalScrollBar.ViewportSize > 0 then
      VerticalScrollBar.Value := Min(VerticalScrollBar.Max - VerticalScrollBar.ViewportSize, Max(0, AValue));
    {$ENDIF}
    {$IFDEF VCLLIB}
    if VerticalScrollBar.PageSize > 0 then
      VerticalScrollBar.Position := Min(VerticalScrollBar.Max - VerticalScrollBar.PageSize, Max(0, Round(AValue)));
    {$ENDIF}
    {$IFDEF WEBLIB}
    if VerticalScrollBar.PageSize > 0 then
      VerticalScrollBar.Position := Round(Min(VerticalScrollBar.Max - VerticalScrollBar.PageSize, Max(0, AValue)));
    {$ENDIF}
  end;
end;

procedure TTMSFNCDataGridRenderer.ShowFindDialog;
var
  a: Boolean;
  f: TTMSFNCFindDialog;
begin
  a := True;
  f := FindDialog;
  if Assigned(f) then
  begin
    DoBeforeShowFindDialog(f, a);
    if a then
    begin
      f.Execute;
      DoAfterShowFindDialog(f);
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.ShowReplaceDialog;
var
  a: Boolean;
  r: TTMSFNCReplaceDialog;
begin
  a := True;
  r := ReplaceDialog;
  if Assigned(r) then
  begin
    DoBeforeShowReplaceDialog(r, a);
    if a then
    begin
      r.Execute;
      DoAfterShowReplaceDialog(r);
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.StopAnimationTimer;
begin
  AnimateTimer.Enabled := False;
  FAnimating := False;
end;

function TTMSFNCDataGridRenderer.StopEditing: Boolean;
begin
  Result := True;
  if FInplaceEditorActive then
    Result := CloseInplaceEditor(False);
end;

procedure TTMSFNCDataGridRenderer.SwapColumnData(AFromColumn,
  AToColumn: Integer);
var
  sc, sct: Boolean;
  R: Integer;
  cFrom, cTo: TTMSFNCDataGridColumn;
  hFrom, hTo: string;
begin
  inherited;
  sc := SelectedColumns[AFromColumn];
  sct := SelectedColumns[AToColumn];

  SelectedColumns[AFromColumn] := sct;
  SelectedColumns[AToColumn] := sc;

  if SelectedCellDictionary.Count > 0 then
  begin
    for R := 0 to RowCount - 1 do
    begin
      sc := SelectedCells[AFromColumn, R];
      sct := SelectedCells[AToColumn, R];
      SelectedCells[AFromColumn, R] := sct;
      SelectedCells[AToColumn, R] := sc;
    end;
  end;

  SynchronizeColumns;
  if (AFromColumn >= 0) and (AFromColumn <= Columns.Count - 1) and
    (AToColumn >= 0) and (AToColumn <= Columns.Count - 1) then
  begin
    cFrom := TTMSFNCDataGridColumn.Create(nil);
    cTo := TTMSFNCDataGridColumn.Create(nil);
    try
      hFrom := Columns[AFromColumn].Header;
      hTo := Columns[AToColumn].Header;

      cFrom.Assign(Columns[AFromColumn]);
      cFrom.Header := hTo;
      cTo.Assign(Columns[AToColumn]);
      cTo.Header := hFrom;

      Columns[AFromColumn].Assign(cTo);
      Columns[AToColumn].Assign(cFrom);
    finally
      cTo.Free;
      cFrom.Free;
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.SwapRowData(AFromRow, AToRow: Integer);
var
  sr, srt: Boolean;
  C: Integer;
begin
  inherited;
  sr := SelectedRows[AFromRow];
  srt := SelectedRows[AToRow];

  SelectedRows[AFromRow] := srt;
  SelectedRows[AToRow] := sr;

  if SelectedCellDictionary.Count > 0 then
  begin
    for C := 0 to ColumnCount - 1 do
    begin
      sr := SelectedCells[C, AFromRow];
      srt := SelectedCells[C, AToRow];
      SelectedCells[C, AFromRow] := srt;
      SelectedCells[C, AToRow] := sr;
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.SynchronizeColumns(ACollectionUpdate: Boolean = False);
begin
  if GetIsDestroying or FBlockSynchronizeColumns then
    Exit;

  FBlockSynchronizeColumns := True;
  IncUpdateCount;
  if ACollectionUpdate then
    ColumnCount := Columns.Count
  else
  begin
    while Columns.Count > ColumnCount do
      Columns.Delete(Columns.Count - 1);

    while Columns.Count < ColumnCount do
      Columns.Add;
  end;
  DecUpdateCount;
  FBlockSynchronizeColumns := False;
end;

procedure TTMSFNCDataGridRenderer.SetProperties(ACell: TTMSFNCDataGridCell);
var
  sd: TTMSFNCDataGridSortDirection;
  c: string;
  w, h: Integer;
  t: string;
  ctx: TRttiContext;
  rt: TRttiType;
  prop: TRttiProperty;
  val: TValue;
  fe: Boolean;
  rs: TTMSFNCDataGridRowNodestate;
  cl: TTMSFNCDataGridColumn;
  ci: TTMSFNCDataGridCellItem;
  eci: TTMSFNCDataGridCellItemExtended;
  ctl: TTMSFNCDataGridCellControl;
  ctla: TTMSFNCDataGridCellControlAlign;
  ctlp: TTMSFNCDataGridCellControlPosition;
  ctlm: TTMSFNCMargins;
  ctlw, ctlh: Integer;
  ctltxt: string;
begin
  if not Assigned(ACell) then
    Exit;

  ci := ACell.Data;
  cl := GetColumn(ACell.Column);

  eci := nil;
  ctl := nil;

  if IsExtendedCell(ci) then
  begin
    eci := AsExtendedCell(ci);
    ctl := eci.Control;
  end;

  ctla := gcaNone;
  if Assigned(cl) and (gcsControlAlign in cl.Settings) then
    ctla := cl.ControlAlign
  else if Assigned(eci) then
    ctla := eci.ControlAlign;

  ctlp := gcpCenterRight;
  if Assigned(cl) and (gcsControlPosition in cl.Settings) then
    ctlp := cl.ControlPosition
  else if Assigned(eci) then
    ctlp := eci.ControlPosition;

  ctlm := nil;
  if Assigned(cl) and (gcsControlMargins in cl.Settings) then
    ctlm := cl.ControlMargins
  else if Assigned(eci) then
    ctlm := eci.ControlMargins;

  ctlw := -1;
  if Assigned(cl) and (gcsControlWidth in cl.Settings) then
    ctlw := cl.ControlWidth
  else if Assigned(eci) then
    ctlw := eci.ControlWidth;

  ctlh := -1;
  if Assigned(cl) and (gcsControlHeight in cl.Settings) then
    ctlh := cl.ControlHeight
  else if Assigned(eci) then
    ctlh := eci.ControlHeight;

  ctltxt := '';
  if Assigned(cl) and (gcsControlText in cl.Settings) then
    ctltxt := cl.ControlText
  else if Assigned(eci) then
    ctltxt := eci.ControlText;

  if Options.Filtering.Enabled and (ACell.Row = Options.Filtering.Row) and (ACell.Column >= FixedColumnCount)
    and (ACell.Column <= ColumnCount - FixedRightColumnCount - 1) then
  begin
    if Assigned(cl) and (gcsFilterButton in cl.Settings) then
      ACell.FilterButton := cl.FilterButton
    else
      ACell.FilterButton := gfcButton in Options.Filtering.Controls;

    if not Assigned(ctl) then
    begin
      if Assigned(cl) and (gcsFilterEditor in cl.Settings) then
        fe := cl.FilterEditor
      else
        fe := gfcEditor in Options.Filtering.Controls;

      if fe then
      begin
        ctl := FilterEditors[ACell.Column, ACell.Row];
        if Assigned(ctl) then
          ctla := gcaClient;
      end;
    end;
  end;

  ACell.Control := ctl;
  ACell.ControlAlign := ctla;
  ACell.ControlPosition := ctlp;
  ACell.ControlMargins.Assign(ctlm);

  ACell.CommentIndicator := c <> '';
  ACell.Comment := c;

  if Assigned(ACell.Control) then
  begin
    w := ctlw;
    h := ctlh;
    t := ctltxt;

    if w <> -1 then
      ACell.Control.Width := w;

    if h <> -1 then
      ACell.Control.Height := h;

    if t <> '' then
    begin
      ctx := TRttiContext.Create;
      try
        rt := ctx.GetType(ACell.Control.ClassInfo);
        {$IFDEF FMXLIB}
        prop := rt.GetProperty('Text');
        {$ELSE}
        prop := rt.GetProperty('Caption');
        {$ENDIf}
        if Assigned(Prop) and Prop.IsWritable then
        begin
          val := CellValuetoTValue(t);
          prop.SetValue(ACell.Control, val);
        end;
      finally
        ctx.Free;
      end;
    end;
  end;

  ACell.BitmapContainer := BitmapContainer;

  if Filter.HasFilter(ACell.Column) then
    TTMSFNCDataGridCellOpen(ACell).FilterIcon := CellAppearance.FilterActiveIcon
  else
    TTMSFNCDataGridCellOpen(ACell).FilterIcon := CellAppearance.FilterIcon;

  TTMSFNCDataGridCellOpen(ACell).SortDescendingIcon := CellAppearance.SortDescendingIcon;
  TTMSFNCDataGridCellOpen(ACell).SortAscendingIcon := CellAppearance.SortAscendingIcon;

  TTMSFNCDataGridCellOpen(ACell).OnFilterButtonClick := DoCellFilterButtonClick;
  TTMSFNCDataGridCellOpen(ACell).OnCommentIndicatorClick := DoCellCommentIndicatorClick;
  TTMSFNCDataGridCellOpen(ACell).OnBeforeDraw := DoCellBeforeDraw;
  TTMSFNCDataGridCellOpen(ACell).OnAfterDraw := DoCellAfterDraw;

  if Options.Sorting.Enabled and (SortIndexDictionary.Count > 0) and (ACell.Row = Options.Sorting.Row) then
  begin
    sd := gsdAscending;
    if SortIndexDictionary.TryGetValue(ACell.Column, sd) then
    begin
      case sd of
        gsdAscending: ACell.SortIndicator := gsiAscending;
        gsdDescending: ACell.SortIndicator := gsiDescending;
      end;
    end;
  end;

  if ACell is TTMSFNCDataGridNodeCell then
  begin
    rs := RowNodeState[ACell.Row];
    (ACell as TTMSFNCDataGridNodeCell).State := rs;

    TTMSFNCDataGridNodeCellOpen(ACell as TTMSFNCDataGridNodeCell).CollapseIcon := CellAppearance.CollapseIcon;
    TTMSFNCDataGridNodeCellOpen(ACell as TTMSFNCDataGridNodeCell).ExpandIcon := CellAppearance.ExpandIcon;

    (ACell as TTMSFNCDataGridNodeCell).OnCollapse := DoCellNodeCollapse;
    (ACell as TTMSFNCDataGridNodeCell).OnExpand := DoCellNodeExpand;
  end
  else if ACell is TTMSFNCDataGridBitmapCell then
  begin
    (ACell as TTMSFNCDataGridBitmapCell).Bitmap.Assign(Bitmaps[ACell.Column, ACell.Row]);
    (ACell as TTMSFNCDataGridBitmapCell).BitmapName := BitmapNames[ACell.Column, ACell.Row];
  end
  else if ACell is TTMSFNCDataGridCheckedCell then
  begin
    (ACell as TTMSFNCDataGridCheckedCell).Checked := Booleans[ACell.Column, ACell.Row];
    (ACell as TTMSFNCDataGridCheckedCell).OnChange := @DoCellCheckChange;
  end
  else if ACell is TTMSFNCDataGridProgressBarCell then
    (ACell as TTMSFNCDataGridProgressBarCell).Value := ProgressValue[ACell.Column, ACell.Row]
  else if ACell is TTMSFNCDataGridButtonCell then
    (ACell as TTMSFNCDataGridButtonCell).OnClick := DoCellButtonClick;
end;

procedure TTMSFNCDataGridRenderer.UnhilightCells(
  AOptions: TTMSFNCDataGridHilightOptions);
begin
  case AOptions.Mode of
    ghmCell: InternalUnmarkCells('hi', AOptions.Column, AOptions.Row, AOptions.Column, AOptions.Row);
    ghmColumn:
    begin
      if AOptions.IncludeFixed then
        InternalUnmarkCells('hi', AOptions.Column, 0, AOptions.Column, RowCount - 1)
      else
        InternalUnmarkCells('hi', AOptions.Column, FixedRowCount, AOptions.Column, RowCount - 1 - FixedBottomRowCount);
    end;
    ghmRow:
    begin
      if AOptions.IncludeFixed then
        InternalUnmarkCells('hi', 0, AOptions.Row, ColumnCount - 1, AOptions.Row)
      else
        InternalUnmarkCells('hi', FixedColumnCount, AOptions.Row, ColumnCount - 1 - FixedRightColumnCount, AOptions.Row);
    end;
    ghmGrid:
    begin
      if AOptions.IncludeFixed then
        InternalUnmarkCells('hi', 0, 0, ColumnCount - 1, RowCount - 1)
      else
        InternalUnmarkCells('hi', FixedColumnCount, FixedRowCount, ColumnCount - 1 - FixedRightColumnCount, RowCount - 1 - FixedBottomRowCount);
    end;
  end;
end;

function TTMSFNCDataGridRenderer.UnhilightText(ACaseSensitive: Boolean;
  AValue: string): string;
begin
  Result := Unhilight(AValue, 'hi');
end;

procedure TTMSFNCDataGridRenderer.UnmarkCells(
  AOptions: TTMSFNCDataGridMarkOptions);
begin
  case AOptions.Mode of
    ghmCell: InternalUnmarkCells('e', AOptions.Column, AOptions.Row, AOptions.Column, AOptions.Row);
    ghmColumn:
    begin
      if AOptions.IncludeFixed then
        InternalUnmarkCells('e', AOptions.Column, 0, AOptions.Column, RowCount - 1)
      else
        InternalUnmarkCells('e', AOptions.Column, FixedRowCount, AOptions.Column, RowCount - 1 - FixedBottomRowCount);
    end;
    ghmRow:
    begin
      if AOptions.IncludeFixed then
        InternalUnmarkCells('e', 0, AOptions.Row, ColumnCount - 1, AOptions.Row)
      else
        InternalUnmarkCells('e', FixedColumnCount, AOptions.Row, ColumnCount - 1 - FixedRightColumnCount, AOptions.Row);
    end;
    ghmGrid:
    begin
      if AOptions.IncludeFixed then
        InternalUnmarkCells('e', 0, 0, ColumnCount - 1, RowCount - 1)
      else
        InternalUnmarkCells('e', FixedColumnCount, FixedRowCount, ColumnCount - 1 - FixedRightColumnCount, RowCount - 1 - FixedBottomRowCount);
    end;
  end;
end;

function TTMSFNCDataGridRenderer.UnmarkText(AValue: string): string;
begin
  Result := Unhilight(AValue, 'e');
end;

procedure TTMSFNCDataGridRenderer.Update;
begin
  UpdateScrollBars;
  inherited;
  UpdateVisualRange;
  UpdateBounds;
  Calculate;
end;

procedure TTMSFNCDataGridRenderer.UpdateAutoSizing;
begin
  if Assigned(FHost) and (FHost is TComponent) then
  begin
    if (csReading in (FHost as TComponent).ComponentState) then
      Exit;
  end;

  if Options.Column.Stretching.Enabled then
  begin
    FColumnStretchingActive := StretchColumns(Options.Column.Stretching.Mode, Options.Column.Stretching.Range, Options.Column.Stretching.&Index);
    FColumnStretchingApplied := True;
  end
  else if FColumnStretchingApplied then
  begin
    FColumnStretchingActive := False;
    FColumnStretchingApplied := False;
    ResetColumnWidths;
  end;

  if Options.Row.Stretching.Enabled then
  begin
    FRowStretchingActive := StretchRows(Options.Row.Stretching.Mode, Options.Row.Stretching.Range, Options.Row.Stretching.&Index);
    FRowStretchingApplied := True;
  end
  else if FRowStretchingApplied then
  begin
    FRowStretchingActive := False;
    FRowStretchingApplied := False;
    ResetRowHeights;
  end;
end;

procedure TTMSFNCDataGridRenderer.UpdateBounds;
begin
  if (UpdateCount > 0) or GetIsDestroying then
    Exit;

  if FLocalRectChanged and not IsBlocked then
  begin
    if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
      (Adapter as TTMSFNCDataGridRendererDataAdapter).UpdateBounds;

    FLocalRectChanged := False;
  end;
end;

procedure TTMSFNCDataGridRenderer.UpdateCursor(ACursor: TCursor);
begin
  if Assigned(OnUpdateCursor) then
    OnUpdateCursor(Self, ACursor);
end;

procedure TTMSFNCDataGridRenderer.UpdateFilterList(AColumn: Integer);
var
  sl, fsl: TStringList;
  s, sc, fs, sf: string;
  k,i: Integer;
  it: TTMSFNCListBoxItem;
  f: TTMSFNCDataGridDataFilterData;
  fdt: TTMSFNCDataGridDataFilterType;
  c: Integer;
  b, a: Boolean;
  obj: TObject;
  e: TTMSFNCDataGridCellControl;
begin
  sl := TStringList.Create;

  try
    sl.Duplicates := dupIgnore;
    sl.Sorted := True;
    sl.Add(sTMSFNCDataGridFilterAll);

    c := AColumn;
    if Filter.Count > 0 then
      c := Filter[0].Column;

    a := True;
    if Assigned(Adapter) and (Adapter is TTMSFNCDataGridRendererDataAdapter) then
    begin
      case (Adapter as TTMSFNCDataGridRendererDataAdapter).FilterMode of
        afmNone: Exit;
        afmAdapter:
        begin
          (Adapter as TTMSFNCDataGridRendererDataAdapter).GetFilterData(AColumn, sl);
          a := False;
        end;
      end;
    end;

    if a then
    begin
      for i := FixedRowCount to RowCount - FixedBottomRowCount - 1 do
      begin
        if IsRowNormal(i) and ((Options.Filtering.MultiColumn and ((not IsRowFiltered(i) and (c <> AColumn)) or (c = AColumn))) or not Options.Filtering.MultiColumn) then
        begin
          sf := Trim(Strings[AColumn, i]);
          if sf <> '' then
            sl.AddObject(sf, Filter.CommonDoubleQuotedItem);
        end;
      end;
    end;

    DoNeedFilterDropDownData(AColumn, sl);

    FLoadFilter := True;

    FilterListBox.BeginUpdate;
    try
      {$IFDEF FMXLIB}
      if not FStyleApplied then
      {$ENDIF}
      begin
        FilterListBox.Fill.Assign(FilterAppearance.Fill);
        FilterListBox.Stroke.Assign(FilterAppearance.Stroke);
        FilterListBox.DefaultItem.TextColor := FilterAppearance.Font.Color;
        FilterListBox.ItemsAppearance.Font.Assign(FilterAppearance.Font);
      end;

      FilterListBox.Items.Clear;
      for I := 0 to sl.Count - 1 do
      begin
        it := FilterListBox.Items.Add;
        it.Text := sl[I];
        it.DataObject := sl.Objects[I];
      end;

      FilterListBox.ItemIndex := -1;
      FilterListBox.Tag := AColumn;
      FilterListBox.CheckAll;
      fdt := gftEqual;
      {$IFDEF FMXLIB}
      FilterEdit.Text := '';
      FilterTypeComboBox.SafeItemIndex := Integer(fdt);
      {$ELSE}
      FilterTypeComboBox.SafeItemIndex := Integer(fdt);
      FilterEdit.Text := '';
      {$ENDIF}

      e := nil;
      if HasFilterEditor[AColumn, Options.Filtering.Row] then
      begin
        e := FilterEditors[AColumn, Options.Filtering.Row];
        if Assigned(e) and (e is TTMSFNCDataGridFilterPanel) then
          (e as TTMSFNCDataGridFilterPanel).Reset;
      end;

      {$IFDEF FMXLIB}
      if not FStyleApplied then
      {$ENDIF}
      begin
        {$IFDEF FMXLIB}
        FilterEdit.StyledSettings := FilterEdit.StyledSettings - [TStyledSetting.FontColor];
        FilterEdit.FontColor := FilterAppearance.Font.Color;
        FilterEdit.OnApplyStyleLookup := DoFilterEditApplyStyleLookup;
        {$ENDIF}
        {$IFNDEF FMXLIB}
        FilterEdit.Color := FilterAppearance.Fill.Color;
        FilterEdit.Font.Color := FilterAppearance.Font.Color;
        {$ENDIF}
      end;

      if Filter.HasFilter(AColumn) then
      begin
        f := Filter.ColumnFilter[AColumn];

        FilterEdit.Text := f.DataString;
        FilterTypeComboBox.SafeItemIndex := Integer(f.&Type);

        if Assigned(e) and (e is TTMSFNCDataGridFilterPanel) then
          (e as TTMSFNCDataGridFilterPanel).SetFilter(f);

        if f.Condition <> '' then
        begin
          fsl := TStringList.Create;
          try
            fsl.QuoteChar := '''';
            TTMSFNCUtils.Split('|', f.Condition, fsl);
            b := False;

            for I := 0 to FilterListBox.Items.Count - 1 do
            begin
              s := FilterListBox.Items[I].Text;
              if s <> sTMSFNCDataGridFilterAll then
              begin
                for K := 0 to fsl.Count - 1 do
                begin
                  fs := Trim(fsl[K]);
                  obj := FilterListBox.Items[I].DataObject;

                  if Assigned(obj) and (obj is TTMSFNCDataGridDataFilterItem) and (obj as TTMSFNCDataGridDataFilterItem).DoubleQuotes then
                    sc := '"' + s + '"'
                  else
                    sc := s;

                  FilterListBox.Checked[I] := fs = sc;
                  if FilterListBox.Checked[I] then
                    Break
                  else
                    b := True;
                end;
              end;
            end;

            for I := 0 to FilterListBox.Items.Count - 1 do
            begin
              s := FilterListBox.Items[I].Text;
              if s = sTMSFNCDataGridFilterAll then
              begin
                if b then
                  FilterListBox.Checked[I] := False;

                Break;
              end;
            end;
          finally
            fsl.Free;
          end;
        end;
      end;
    finally
      FilterListBox.EndUpdate;
    end;
  finally
    sl.Free;
  end;

  FLoadFilter := False;
end;

procedure TTMSFNCDataGridRenderer.DoFilterListBoxItemCheckChanged(
  Sender: TObject; AItem: TTMSFNCCheckedListBoxItem);
var
  I: Integer;
  b: Boolean;
  it: TTMSFNCCheckedListBoxItem;
  cl: TTMSFNCCheckedListBox;
  obj: TObject;
begin
  if (Sender is TTMSFNCCheckedListBox) then
  begin
    cl := (Sender as TTMSFNCCheckedListBox);
    
    if AItem.Text = sTMSFNCDataGridFilterAll then
    begin
      if AItem.Checked then
        cl.CheckAll
      else
        cl.UncheckAll;
    end
    else
    begin    
      b := True;
      it := nil;
      for I := 0 to cl.Items.Count - 1 do
      begin
        if (cl.Items[I].Text <> sTMSFNCDataGridFilterAll) then
        begin
          b := b and cl.Checked[I];
          obj := cl.Items[I].DataObject;
          if Assigned(obj) and (obj is TTMSFNCDataGridDataFilterItem) then
            b := b and (obj as TTMSFNCDataGridDataFilterItem).PartOfCheckAll;
        end
        else
        begin
          it := cl.Items[I] as TTMSFNCCheckedListBoxItem;
        end;
      end;

      if Assigned(it) then
        it.Checked := b; 
    end;

    FFilterTime := 0;
    FilterTimer.Enabled := True;
  end;
end;

{$IFDEF FMXLIB}
procedure TTMSFNCDataGridRenderer.DoFilterEditApplyStyleLookup(Sender: TObject);
var
  r: TRectangle;
  b: TFmxObject;
begin
  if FStyleApplied then
    Exit;
  b := (Sender as TFmxObject).FindStyleResource('background');
  if Assigned(b) then
  begin
    r := TRectangle.Create(b);
    r.Align := TAlignLayout.Client;
    r.HitTest := False;
    r.Parent := b;


    case FilterAppearance.Fill.Kind of
      gfkSolid:
      begin
        r.Fill.Kind := TBrushKind.Solid;
        r.Fill.Color := FilterAppearance.Fill.Color;
      end;
      gfkGradient:
      begin
        r.Fill.Kind := TBrushKind.Gradient;
        r.Fill.Gradient.Color := FilterAppearance.Fill.Color;
        r.Fill.Gradient.Color1 := FilterAppearance.Fill.ColorTo;
      end
      else
        r.Fill.Kind := TBrushKind.None;
    end;

    r.Stroke.Color := FilterAppearance.Stroke.Color;

    if FilterAppearance.Stroke.Kind <> gskSolid then
      r.Stroke.Kind := TBrushKind.None
    else
      r.Stroke.Kind := TBrushKind.Solid;
  end;
end;
{$ENDIF}

procedure TTMSFNCDataGridRenderer.DoFilterClearClick(Sender: TObject);
var
  btn: TTMSFNCToolBarButton;
  c: Integer;
begin
  if (Sender is TTMSFNCToolBarButton) then
  begin
    btn := (Sender as TTMSFNCToolBarButton);
    c := btn.DataInteger;
    if Filter.HasFilter(c) then
      Filter.RemoveColumnFilter(c);

    if Filter.Count = 0 then
      ClearFilter
    else
    begin
      IncUpdateCount;
      RemoveFilter;
      DecUpdateCount;
      ApplyFilter;
    end;

    UpdateFilterList(c);
  end;
end;

procedure TTMSFNCDataGridRenderer.DoFilterEditChange(Sender: TObject);
begin
  if FLoadFilter then
    Exit;

  FFilterTime := 0;
  FilterTimer.Enabled := True;
end;

procedure TTMSFNCDataGridRenderer.DoFilterEnter(Sender: TObject);
begin
  FilterEdit.SelectAll;
end;

procedure TTMSFNCDataGridRenderer.DoFilterExitTimer(Sender: TObject);
{$IFDEF FMXMOBILE}
var
  f: Boolean;
{$ENDIF}
begin
  {$IFDEF FMXMOBILE}
  f := FilterListBox.IsFocused or FilterListBox.TreeView.IsFocused or FilterEdit.IsFocused or FilterClearButton.IsFocused or FilterSortAscendingButton.IsFocused or
    FilterSortDescendingButton.IsFocused or FilterContainer.IsFocused;

  if not f then
  begin
    FilterExitTimer.Enabled := False;
    FilterPopup.IsOpen := False;
  end;
  {$ELSE}
  FilterExitTimer.Enabled := False;
  {$ENDIF}
end;

procedure TTMSFNCDataGridRenderer.DoFilterSortClick(Sender: TObject);
var
  btn: TTMSFNCToolBarButton;
  c: Integer;
begin
  if (Sender is TTMSFNCToolBarButton) then
  begin
    btn := (Sender as TTMSFNCToolBarButton);
    c := btn.DataInteger;
    if btn.DataString = 'asc' then
    begin
      if IsGrouped then
        GroupSort(c, gsdAscending)
      else
        Sort(c, gsdAscending)
    end
    else if btn.DataString = 'desc' then
    begin
      if IsGrouped then
        GroupSort(c, gsdDescending)
      else
        Sort(c, gsdDescending);
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.DoFilterTimer(Sender: TObject);
var
  fd: TTMSFNCDataGridDataFilterData;
  cnd, cs: string;
  b: Boolean;
  obj: TObject;
  i, filtercol: integer;
  c: Boolean;
  fdt: TTMSFNCDataGridDataFilterType;
begin
  Inc(FFilterTime);
  if FFilterTime >= FILTERDELAY then
  begin
    FilterTimer.Enabled := False;

    if Assigned(FilterListBox) then
    begin
      filtercol := FilterListBox.Tag;

      if Options.Filtering.MultiColumn then
      begin
        for i := Filter.Count - 1 downto 0 do
        begin
          if Filter[i].Column = filtercol then
            Filter.Delete(i);
        end;
      end
      else
        Filter.Clear;

      fd := Filter.Add;
      fd.Column := filtercol;

      fdt := gftEqual;
      if FilterTypeComboBox.Visible and (FilterTypeComboBox.ItemIndex <> -1) then
        fdt := TTMSFNCDataGridDataFilterType(FilterTypeComboBox.ItemIndex);

      if Assigned(FilterEdit) and FilterEdit.Visible and ((FilterEdit.Text <> '') or (fdt in [gftEmpty, gftNotEmpty])) then
      begin
        fd.DataString := FilterEdit.Text;
        fd.&Type := fdt;
        fd.BuildCondition(FilterEdit.Text);
        cnd := fd.Condition;
      end
      else if FilterListBox.Visible then
      begin
        cnd := '';
        fd.DataString := '';
        fd.&Type := gftEqual;

        for I := 0 to FilterListBox.Items.Count - 1 do
        begin
          if FilterListBox.Items[I].Text = sTMSFNCDataGridFilterAll then
          begin
            if FilterListBox.Checked[I] then
            begin
              cnd := sTMSFNCDataGridFilterAll;
              Break;
            end;
          end
          else if FilterListBox.Checked[I] then
          begin
            if cnd <> '' then
              cnd := cnd + ' | ';

            b := False;
            obj := FilterListBox.Items[I].DataObject;
            if Assigned(obj) and (obj is TTMSFNCDataGridDataFilterItem) then
              b := (obj as TTMSFNCDataGridDataFilterItem).DoubleQuotes;

            if b then
              cnd := cnd + '"' + FilterListBox.Items[I].Text + '"'
            else
              cnd := cnd + FilterListBox.Items[I].Text;
          end;
        end;

        fd.Condition := cnd;
        cnd := fd.Condition;
      end;

      c := True;
      cs := cnd;
      if (cnd <> '') or (fd.&Type in [gftEmpty, gftNotEmpty]) then
      begin
        DoBeforeApplyFilter(fd.Column, cnd, c);
        if c then
        begin
          if cnd <> sTMSFNCDataGridFilterAll then
          begin
            fd.Condition := cnd;
            IncUpdateCount;
            RemoveFilter;
            DecUpdateCount;
            ApplyFilter;
          end
          else
            ClearFilter;

          DoAfterApplyFilter(fd.Column, cnd);
        end;
      end;
    end;
  end;
end;

procedure TTMSFNCDataGridRenderer.DoFilterTypeChange(Sender: TObject);
begin
  if FLoadFilter then
    Exit;

  FFilterTime := 0;
  FilterTimer.Enabled := True;
end;

function FindOptionsToParameters(AOptions: TTMSFNCFindDialogOptions): TTMSFNCDataGridDataFindParameters;
var
  p: TTMSFNCDataGridDataFindParameters;
begin
  p := [gfnIgnoreHTMLTags, gfnAutoGoTo, gfnDirectionLeftRight];
  if fdoCaseSensitive in AOptions then
    p := p + [gfnMatchCase];

  if fdoWholeWordOnly in AOptions then
    p := p + [gfnMatchFull];

  if fdoExpression in AOptions then
    p := p + [gfnMatchRegular];

  Result := p;
end;

function ReplaceOptionsToParameters(AOptions: TTMSFNCReplaceDialogOptions): TTMSFNCDataGridDataFindParameters;
var
  p: TTMSFNCDataGridDataFindParameters;
begin
  p := [gfnIgnoreHTMLTags, gfnAutoGoTo, gfnDirectionLeftRight];
  if rdoCaseSensitive in AOptions then
    p := p + [gfnMatchCase];

  if rdoWholeWordOnly in AOptions then
    p := p + [gfnMatchFull];

  if rdoExpression in AOptions then
    p := p + [gfnMatchRegular];

  Result := p;
end;

procedure TTMSFNCDataGridRenderer.DoFind(Sender: TObject; AFindText: string;
  var AFound: Boolean);
var
  p: TTMSFNCDataGridDataFindParameters;
  f: TTMSFNCFindDialog;
begin
  if (Sender is TTMSFNCFindDialog) then
  begin
    f := (Sender as TTMSFNCFindDialog);
    p := FindOptionsToParameters(f.Options);
    AFound := IsCellValid(Find(AFindText, p));
  end;
end;

procedure TTMSFNCDataGridRenderer.DoFindNext(Sender: TObject; AFindText: string;
  var AFound: Boolean);
var
  p: TTMSFNCDataGridDataFindParameters;
  f: TTMSFNCFindDialog;
begin
  if (Sender is TTMSFNCFindDialog) then
  begin
    f := (Sender as TTMSFNCFindDialog);
    p := FindOptionsToParameters(f.Options);

    if not (fdoDown in f.Options) then
      p := p + [gfnBackward];

    AFound := IsCellValid(Find(AFindText, p, FocusedCell));
  end;
end;

procedure TTMSFNCDataGridRenderer.DoReplace(Sender: TObject; AFindText,
  AReplaceText: string);
var
  c: TTMSFNCDataGridCellCoord;
  ov: string;
  nv: string;
begin
  c := FocusedCell;
  if (c.Column <> -1) and (c.Row <> -1) then
  begin
    ov := Strings[c.Column, c.Row];
    nv := StringReplace(ov, AFindText, AReplaceText, [rfReplaceAll, rfIgnoreCase]);
    Cells[c.Column, c.Row] := nv;
  end;
end;

procedure TTMSFNCDataGridRenderer.DoReplaceAll(Sender: TObject; AFindText,
  AReplaceText: string);
var
  p: TTMSFNCDataGridDataFindParameters;
  r: TTMSFNCReplaceDialog;
begin
  if (Sender is TTMSFNCReplaceDialog) then
  begin
    r := (Sender as TTMSFNCReplaceDialog);
    p := ReplaceOptionsToParameters(r.Options);
    Replace(AFindText, AReplaceText, p);
  end;
end;

procedure TTMSFNCDataGridRenderer.DoReplaceFind(Sender: TObject; AReplaceText: string);
var
  p: TTMSFNCDataGridDataFindParameters;
  r: TTMSFNCReplaceDialog;
begin
  if (Sender is TTMSFNCReplaceDialog) then
  begin
    r := (Sender as TTMSFNCReplaceDialog);
    p := ReplaceOptionsToParameters(r.Options);
    Find(AReplaceText, p);
  end;
end;

procedure TTMSFNCDataGridRenderer.DoReplaceFindNext(Sender: TObject;
  AFindText: string);
var
  p: TTMSFNCDataGridDataFindParameters;
  f: TTMSFNCReplaceDialog;
begin
  if (Sender is TTMSFNCReplaceDialog) then
  begin
    f := (Sender as TTMSFNCReplaceDialog);
    p := ReplaceOptionsToParameters(f.Options);

    if not (rdoDown in f.Options) then
      p := p + [gfnBackward];

    Find(AFindText, p, FocusedCell);
  end;
end;

procedure TTMSFNCDataGridRenderer.FirstPage;
begin
  PageIndex := 0;
end;

procedure TTMSFNCDataGridRenderer.Focus;
begin
  if Assigned(OnFocus) then
    OnFocus(Self);
end;

procedure TTMSFNCDataGridRenderer.UpdateScrollBars(AUpdate: Boolean = True; ACalculate: Boolean = True);
var
  vs, hs: TScrollBar;
  vsc, hsc: TTMSFNCScrollBar;
  w, h, ww, hh: Double;
  cw, ch: Double;
  cr: TRectF;
  vmgr: TTMSFNCMargins;
  hmgr: TTMSFNCMargins;
  lr: TRectF;
  {$IFDEF CMNWEBLIB}
  p: TTMSFNCDataGridCellParent;
  {$ENDIF}
begin
  if (FBlockScrollingUpdate) or (UpdateCount > 0) or GetIsDestroying then
    Exit;

  FBlockScrollingUpdate := True;

  vs := VerticalScrollBar;
  hs := HorizontalScrollBar;
  vsc := CustomVerticalScrollBar;
  hsc := CustomHorizontalScrollBar;

  if ACalculate then
  begin
    UpdateAutoSizing;
    UpdateColumnRowCalculations;
  end;

  if AUpdate then
    cr := GetCalculationRect
  else
    cr := GetContentRect;

  cw := cr.Right - cr.Left;
  ch := cr.Bottom - cr.Top;
  w := TotalColumnWidth;
  h := TotalRowHeight;

  lr := LocalRect;

  ww := lr.Right;
  hh := lr.Bottom;

  if Assigned(vs) then
    vs.Visible := (h > 0) and (CompareValueEx(h, ch, 1) = 1) and VerticalScrollBarVisible and not RowStretchingActive and not HasCustomScrollBars and not IsExporting;

  if Assigned(hs) then
    hs.Visible := (w > 0) and (CompareValueEx(w, cw, 1) = 1) and HorizontalScrollBarVisible and not ColumnStretchingActive and not HasCustomScrollBars and not IsExporting;

  if Assigned(vsc) then
    vsc.Visible := (h > 0) and (CompareValueEx(h, ch, 1) = 1) and VerticalScrollBarVisible and not RowStretchingActive and HasCustomScrollBars and not IsExporting;

  if Assigned(hsc) then
  begin
    if Assigned(vsc) and vsc.Visible and ((Assigned(Host) and (Host is TTMSFNCDataGridRenderer)) or not Assigned(Host)) then
      hsc.Visible := (w > 0) and (CompareValueEx(w, cw - vsc.Width, 1) = 1) and HorizontalScrollBarVisible and not ColumnStretchingActive and HasCustomScrollBars and not IsExporting
    else
      hsc.Visible := (w > 0) and (CompareValueEx(w, cw, 1) = 1) and HorizontalScrollBarVisible and not ColumnStretchingActive and HasCustomScrollBars and not IsExporting
  end;

  {$IFDEF CMNWEBLIB}
  if HasValidHost then
  begin
    p := Host as TTMSFNCDataGridCellParent;

    if Assigned(vsc) then
    begin
      if vsc.Visible and HasCustomScrollBars then
        vsc.Parent := p
      else
        vsc.Parent := nil;
    end;

    if Assigned(hsc) then
    begin
      if hsc.Visible and HasCustomScrollBars then
        hsc.Parent := p
      else
        hsc.Parent := nil;
    end;

    if Assigned(vs) then
    begin
      if vs.Visible and not HasCustomScrollBars then
        vs.Parent := p
      else
        vs.Parent := nil;
    end;

    if Assigned(hs) then
    begin
      if hs.Visible and not HasCustomScrollBars then
        hs.Parent := p
      else
        hs.Parent := nil;
    end;
  end;
  {$ENDIF}

  vmgr := TTMSFNCMargins.Create;
  hmgr := TTMSFNCMargins.Create;

  if HasCustomScrollBars then
  begin
    if Assigned(vsc) and vsc.Visible then
      hmgr.Right := vsc.Width + 1
  end
  else
  begin
    if Assigned(vs) and vs.Visible then
      hmgr.Right := vs.Width + 1{$IFDEF CMNWEBLIB} + 1{$ENDIF}
    else
      hmgr.Right := 1{$IFDEF CMNWEBLIB} + 1{$ENDIF};
  end;

  vmgr.Top := 1;
  if HasCustomScrollBars then
  begin
    vmgr.Right := 1;
    hmgr.Bottom := 1;
  end
  else
  begin
    vmgr.Right := 1{$IFDEF CMNWEBLIB} + 1{$ENDIF};
    hmgr.Bottom := 1{$IFDEF CMNWEBLIB} + 1{$ENDIF};
  end;
  hmgr.Left := 1;

  if HasCustomScrollBars then
  begin
    if Assigned(hsc) and hsc.Visible then
      vmgr.Bottom := hsc.Height + 1
  end
  else
  begin
    if Assigned(hs) and hs.Visible then
      vmgr.Bottom := hs.Height + 1{$IFDEF CMNWEBLIB} + 1{$ENDIF}
    else
      vmgr.Bottom := 1{$IFDEF CMNWEBLIB} + 1{$ENDIF};
  end;

  vmgr.Top := vmgr.Top + LocalRect.Top;
  hmgr.Left := hmgr.Left + LocalRect.Left;

  if not StretchScrollBars then
  begin
    hmgr.Left := hmgr.Left + TotalFixedColumnWidth;
    hmgr.Right := hmgr.Right + TotalFixedRightColumnWidth;
    vmgr.Top := vmgr.Top + TotalFixedRowHeight;
    vmgr.Bottom := vmgr.Bottom + TotalFixedBottomRowHeight;
  end;

  {$IFDEF FMXLIB}
  if Assigned(hs) and Assigned(vs) then
  begin
    hs.Position.X := hmgr.Left;
    hs.Position.Y := hh - hmgr.Bottom - hs.Height;
    vs.Position.X := ww - vmgr.Right - vs.Width;
    vs.Position.Y := vmgr.Top;
    hs.Width := ww - hmgr.Right - hmgr.Left;
    vs.Height := hh - vmgr.Bottom - vmgr.Top;
  end;

  if Assigned(hsc) and Assigned(vsc) then
  begin
    hsc.Position.X := hmgr.Left;
    hsc.Position.Y := hh - hmgr.Bottom - hsc.Height;
    vsc.Position.X := ww - vmgr.Right - vsc.Width;
    vsc.Position.Y := vmgr.Top;
    hsc.Width := ww - hmgr.Right - hmgr.Left;
    vsc.Height := hh - vmgr.Bottom - vmgr.Top;
  end;
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  if Assigned(hs) and Assigned(vs) then
  begin
    hs.Left := Round(hmgr.Left);
    hs.Top := Round(hh - hmgr.Bottom - hs.Height);
    vs.Left := Round(ww - vmgr.Right - vs.Width);
    vs.Top := Round(vmgr.Top);
    hs.Width := Round(Max(0, ww - hmgr.Right - hmgr.Left));
    {$IFDEF MSWINDOWS}
    vs.Width := GetSystemMetrics(SM_CYVSCROLL);
    hs.Height := GetSystemMetrics(SM_CYHSCROLL);
    {$ENDIF}
    vs.Height := Round(Max(0, hh - vmgr.Bottom - vmgr.Top));
  end;

  if Assigned(hsc) and Assigned(vsc) then
  begin
    hsc.Left := Round(hmgr.Left);
    hsc.Top := Round(hh - hmgr.Bottom - hsc.Height);
    vsc.Left := Round(ww - vmgr.Right - vsc.Width);
    vsc.Top := Round(vmgr.Top);
    hsc.Width := Round(Max(0, ww - hmgr.Right - hmgr.Left));
    vsc.Height := Round(Max(0, hh - vmgr.Bottom - vmgr.Top));
  end;
  {$ENDIF}

  hmgr.Free;
  vmgr.Free;

  cr := GetContentRect;
  cw := cr.Right - cr.Left;
  ch := cr.Bottom - cr.Top;

  {$IFDEF FMXLIB}
  if Assigned(vs) then
  begin
    if ScrollMode = gsmCellScrolling then
    begin
      vs.ViewportSize := GetRowViewPortSize;
      vs.Max := RowCount;
      vs.SmallChange := 1;
    end
    else
    begin
      vs.ViewPortSize := Min(h, ch);
      vs.Max := h;
      vs.SmallChange := Round(DefaultRowHeight);
    end;

    vs.Value := Min(vs.Value, vs.Max - vs.ViewportSize);
  end;

  if Assigned(hs) then
  begin
    if ScrollMode = gsmCellScrolling then
    begin
      hs.ViewportSize := GetColumnViewPortSize;
      hs.Max := ColumnCount;
      hs.SmallChange := 1;
    end
    else
    begin
      hs.ViewPortSize := Min(w, cw);
      hs.Max := w;
      hs.SmallChange := Round(DefaultColumnWidth);
    end;

    hs.Value := Min(hs.Value, hs.Max - hs.ViewportSize);
  end;

  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  if Assigned(vs) then
  begin
    if ScrollMode = gsmCellScrolling then
    begin
      vs.PageSize := Round(Max(0, GetRowViewPortSize));
      vs.Max := Max(vs.PageSize, RowCount);
      vs.SmallChange := 1;
    end
    else
    begin
      vs.PageSize := Round(Max(0, Min(h, ch)));
      vs.Max := Round(Max(0, h));
      vs.SmallChange := Round(DefaultRowHeight);
    end;

    vs.LargeChange := Min(High(TScrollBarInc), Max(1, vs.PageSize));
    vs.Position := Min(vs.Position, vs.Max);
  end;

  if Assigned(hs) then
  begin
    if ScrollMode = gsmCellScrolling then
    begin
      hs.PageSize := Round(Max(0, GetColumnViewPortSize));
      hs.Max := Max(hs.PageSize, ColumnCount);
      hs.SmallChange := 1;
    end
    else
    begin
      hs.PageSize := Round(Max(0, Min(w, cw)));
      hs.Max := Round(Max(0, w));
      hs.SmallChange := Round(DefaultColumnWidth);
    end;

    hs.LargeChange := Min(High(TScrollBarInc), Max(1, hs.PageSize));
    hs.Position := Min(hs.Position, hs.Max);
  end;
  {$ENDIF}

  if Assigned(vsc) then
  begin
    if ScrollMode = gsmCellScrolling then
    begin
      vsc.PageSize := GetRowViewPortSize;
      vsc.Max := RowCount;
      vsc.SmallChange := 1;
    end
    else
    begin
      vsc.PageSize := Min(h, ch);
      vsc.Max := h;
      vsc.SmallChange := Round(DefaultRowHeight);
    end;

    vsc.LargeChange := Max(1, vsc.PageSize);
    vsc.Value := Min(vsc.Value, vsc.Max - vsc.PageSize);
  end;

  if Assigned(hsc) then
  begin
    if ScrollMode = gsmCellScrolling then
    begin
      hsc.PageSize := GetColumnViewPortSize;
      hsc.Max := ColumnCount;
      hsc.SmallChange := 1;
    end
    else
    begin
      hsc.PageSize := Min(w, cw);
      hsc.Max := w;
      hsc.SmallChange := Round(DefaultColumnWidth);
    end;

    hsc.LargeChange := Max(1, hsc.PageSize);
    hsc.Value := Min(hsc.Value, hsc.Max - hsc.PageSize);
  end;

  FBlockScrollingUpdate := False;

  if AUpdate then
    UpdateScrollBars(False);
end;

procedure TTMSFNCDataGridRenderer.SetFilterAppearance(const Value: TTMSFNCDataGridFilterAppearance);
begin
  FFilterAppearance.Assign(Value);
end;

procedure TTMSFNCDataGridRenderer.SetFocusCell(ACol, ARow: Integer);
begin
  FFocusedCell.Column := ACol;
  FFocusedCell.Row := ARow;
  FFocusedCell.Column := Max(FixedColumnCount, Min(ColumnCount - 1 - FixedRightColumnCount, FFocusedCell.Column));
  FFocusedCell.Row := Max(FixedRowCount, Min(RowCount - 1 - FixedBottomRowCount, FFocusedCell.Row));
end;

procedure TTMSFNCDataGridRenderer.SetFocusedCell(
  const Value: TTMSFNCDataGridCellCoord);
var
  val: TTMSFNCDataGridCellCoord;
begin
  SetFocusCell(Value.Column, Value.Row);
  if IsCellDisabled(Value) then
  begin
    val := NextSelectableColumn(FocusedCell.Column, FocusedCell.Row);
    SetFocusCell(val.Column, val.Row);
  end;
  SelectCell(FFocusedCell, []);
end;

procedure TTMSFNCDataGridRenderer.SetFontColors(AColumn, ARow: Integer;
  const Value: TTMSFNCGraphicsColor);
begin
  Layouts[AColumn, ARow].Font.Color := Value;
end;

procedure TTMSFNCDataGridRenderer.SetFontNames(AColumn, ARow: Integer;
  const Value: string);
begin
  Layouts[AColumn, ARow].Font.Name := Value;
end;

procedure TTMSFNCDataGridRenderer.SetFonts(ASetType: TTMSFNCAppearanceGlobalFontType);
var
  I: Integer;
  c: TTMSFNCDataGridColumn;
begin
  BeginUpdate;
  try
    GlobalFont.ApplyChange(CellAppearance.FixedLayout.Font, ASetType);
    GlobalFont.ApplyChange(CellAppearance.FocusedLayout.Font, ASetType);
    GlobalFont.ApplyChange(CellAppearance.NormalLayout.Font, ASetType);
    GlobalFont.ApplyChange(CellAppearance.GroupLayout.Font, ASetType);
    GlobalFont.ApplyChange(CellAppearance.SummaryLayout.Font, ASetType);
    GlobalFont.ApplyChange(CellAppearance.SelectedLayout.Font, ASetType);
    GlobalFont.ApplyChange(CellAppearance.FixedSelectedLayout.Font, ASetType);
    GlobalFont.ApplyChange(CellAppearance.BandLayout.Font, ASetType);

    for I := 0 to Columns.Count - 1 do
    begin
      c := Columns[I];
      GlobalFont.ApplyChange(c.Appearance.FixedLayout.Font, ASetType);
      GlobalFont.ApplyChange(c.Appearance.FocusedLayout.Font, ASetType);
      GlobalFont.ApplyChange(c.Appearance.NormalLayout.Font, ASetType);
      GlobalFont.ApplyChange(c.Appearance.GroupLayout.Font, ASetType);
      GlobalFont.ApplyChange(c.Appearance.SummaryLayout.Font, ASetType);
      GlobalFont.ApplyChange(c.Appearance.SelectedLayout.Font, ASetType);
      GlobalFont.ApplyChange(c.Appearance.FixedSelectedLayout.Font, ASetType);
      GlobalFont.ApplyChange(c.Appearance.BandLayout.Font, ASetType);
    end;

  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.SetFontSizes(AColumn, ARow: Integer;
  const Value: Single);
begin
  Layouts[AColumn, ARow].Font.Size := {$IFNDEF FMXLIB}Round{$ENDIF}(Value);
end;

procedure TTMSFNCDataGridRenderer.SetFontStyles(AColumn, ARow: Integer;
  const Value: TFontStyles);
begin
  Layouts[AColumn, ARow].Font.Style := Value;
end;

procedure TTMSFNCDataGridRenderer.SetGlobalFont(const Value: TTMSFNCAppearanceGlobalFont);
begin
  FGlobalFont.Assign(Value);
end;

function TTMSFNCDataGridRenderer.SetGrid(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridRenderer;
begin
  Result := SetGrid(ACell.Column, ACell.Row);
end;

function TTMSFNCDataGridRenderer.SetGrid(AColumn, ARow: Integer): TTMSFNCDataGridRenderer;
var
  cd: TTMSFNCDataGridCellItemExtended;
  o: TObject;
  r: TTMSFNCDataGridRenderer;
begin
  Result := nil;
  if not IsCellValid(AColumn, ARow) then
    Exit;

  BeginUpdate;
  try
    cd := CellDataExtended[AColumn, ARow];
    if Assigned(cd) then
    begin
      o := TTMSFNCDataGridCellItemExtendedOpen(cd).Renderer;
      if not Assigned(o) then
      begin
        r := TTMSFNCDataGridRenderer.Create(Self);
        TTMSFNCDataGridCellItemExtendedOpen(cd).Renderer := r;
        Result := r;
      end
      else if o is TTMSFNCDataGridRenderer then
        Result := o as TTMSFNCDataGridRenderer;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridRenderer.Calculate;
var
  c, r: Integer;
  sdx, sdy, dx, dy: Double;
  offsetx, offsety: Double;
  ac, ar: Integer;
  rc, rclip: TRectF;
  fcc, rcc, rcnt, ccnt: Integer;
  k: string;

  procedure SetCellRectLeftRight;
  begin
    sdx := dx;
    dx := dx + ColumnWidths[c];
  end;

  procedure SetCellRectTopBottom;
  begin
    sdy := dy;
    dy := dy + RowHeights[r];
  end;

  procedure AddCellWithKind(AKind: TTMSFNCDataGridCellKind);
  var
    d: TTMSFNCDataGridCellItem;
    sz: TSizeF;
    bc, cl: TTMSFNCDataGridCellCoord;
    cx, cy: Single;
    cc: Integer;
    rr, off: Integer;
    cell: TTMSFNCDataGridCell;
    cls: TTMSFNCDataGridCellClass;
  begin
    cl := MakeCell(c, r);
    d := CellData[cl.Column, cl.Row];
    BeginUsingCellData(cl, d);
    try
      if IsCellMerged(cl) then
      begin
        bc := BaseCell(cl);
        if FMergedCells.Contains(bc) then
          Exit
        else
          FMergedCells.Add(bc);

        cl := bc;
        EndUsingCellData;
        d := CellData[cl.Column, cl.Row];
        BeginUsingCellData(cl, d);
      end;

      sz := GetCellSize(cl);

      cx := sdx;
      cy := sdy;

      if AKind = gckCell then
      begin
        for cc := cl.Column to StartColumn - 1 do
          cx := cx - ColumnWidths[cc];

        for rr := cl.Row to StartRow - 1 do
          cy := cy - RowHeights[rr];

        if (cl.Column = Options.Column.NodeIndex) then
        begin
          if RowTypes[cl.Row] = grtDefault then
            off := (20 * Max(0, (RowNodeLevel[cl.Row] - 1)))
          else
            off := (20 * Max(0, RowNodeLevel[cl.Row]));

          cx := cx + off;
          sz.cx := sz.cx - off;
        end;
      end;

      rc.Left := {$IFDEF FMXWEBLIB}Int{$ENDIF}(cx){$IFDEF FMXWEBLIB} + 0.5{$ENDIF};
      rc.Right := {$IFDEF FMXWEBLIB}Int{$ENDIF}(cx + sz.cx){$IFDEF FMXWEBLIB} + 0.5{$ELSE} + 1{$ENDIF};
      rc.Top := {$IFDEF FMXWEBLIB}Int{$ENDIF}(cy){$IFDEF FMXWEBLIB} + 0.5{$ENDIF};
      rc.Bottom := {$IFDEF FMXWEBLIB}Int{$ENDIF}(cy + sz.cy){$IFDEF FMXWEBLIB} + 0.5{$ELSE} + 1{$ENDIF};

      cls := GetBaseCellClass(cl);
      DoGetCellClass(cl.Column, cl.Row, cls);
      if Assigned(cls) then
      begin
        cell := cls.Create(rc, cl.Column, cl.Row, d, AKind, Self);

        ConfigureCell(cell, gccmProperties);

        case AKind of
          gckCell:
          begin
            FNormalCellList.Add(cell);
            AddCellToDisplay(cell, FNormalCellDisplay);
          end;
          gckFreezeFixedRowCell, gckFreezeFixedColumnCell, gckFreezeRootCell, gckFixedRootCell:
          begin
            FFixedRootCellList.Add(cell);
            AddCellToDisplay(cell, FFixedRootCellDisplay);
          end;
          gckFreezeRowCell, gckFixedRowCell:
          begin
            FFixedRowCellList.Add(cell);
            AddCellToDisplay(cell, FFixedRowCellDisplay);
          end;
          gckFreezeColumnCell, gckFixedColumnCell:
          begin
            FFixedColumnCellList.Add(cell);
            AddCellToDisplay(cell, FFixedColumnCellDisplay);
          end;
        end;
      end;
    finally
      EndUsingCellData;
    end;
  end;

  procedure AddFreezeRootCell;
  begin
    AddCellWithKind(gckFreezeRootCell);
  end;

  procedure AddFixedRootCell;
  begin
    AddCellWithKind(gckFixedRootCell);
  end;

  procedure AddFixedRowCell;
  begin
    AddCellWithKind(gckFixedRowCell);
  end;

  procedure AddCell;
  begin
    AddCellWithKind(gckCell);
  end;

  procedure AddFreezeRowCell;
  begin
    AddCellWithKind(gckFreezeRowCell);
  end;

  procedure AddFreezeColumnCell;
  begin
    AddCellWithKind(gckFreezeColumnCell);
  end;

  procedure AddFixedColumnCell;
  begin
    AddCellWithKind(gckFixedColumnCell);
  end;

  procedure AddFreezeFixedColumnCell;
  begin
    AddCellWithKind(gckFreezeFixedColumnCell);
  end;

  procedure AddFreezeFixedRowCell;
  begin
    AddCellWithKind(gckFreezeFixedRowCell);
  end;

begin
  if (UpdateCount > 0) or GetIsDestroying then
    Exit;

  if Assigned(ControlWrapperList) then
  begin
    for k in ControlWrapperList.Keys do
    begin
      if not IsCellDisplayed(TTMSFNCDataGridCellCoord.FromString(k)) then
        ControlWrapperList.Remove(k);
    end;
  end;

  FMergedCells.Clear;

  FNormalCellDisplay.Clear;
  FFixedColumnCellDisplay.Clear;
  FFixedRowCellDisplay.Clear;
  FFixedRootCellDisplay.Clear;

  FNormalCellList.Clear;
  FFixedRowCellList.Clear;
  FFixedColumnCellList.Clear;
  FFixedRootCellList.Clear;

  if (ColumnCount > 0) and (RowCount > 0) then
  begin
    offsetx := GetTotalColumnOffset;
    offsety := GetTotalRowOffset;
    ac := StartColumn;
    ar := StartRow;
    rcnt := Min(StopRow, RowCount - 1 - FixedBottomRowCount);
    ccnt := Min(StopColumn, ColumnCount - 1 - FixedRightColumnCount);
    fcc := FixedColumnCount + FreezeColumnCount;
    rcc := FixedRowCount + FreezeRowCount;

    rclip := GetContentClipRect;

    dx := rclip.Right - rclip.Left;
    for c := ColumnCount - FixedRightColumnCount to ColumnCount - 1 do
      dx := dx - ColumnWidths[c];

    r := -1;
    for c := ColumnCount - FixedRightColumnCount to ColumnCount - 1 do
    begin
      SetCellRectLeftRight;

      dy := rclip.Bottom - rclip.Top;
      for r := RowCount - FixedBottomRowCount to RowCount - 1 do
        dy := dy - RowHeights[r];

      for r := RowCount - FixedBottomRowCount to RowCount - 1 do
      begin
        SetCellRectTopBottom;
        if IsCellFixedRoot(MakeCell(c, r)) then
          AddFixedRootCell;
      end;

      dy := offsety;
      r := ar;
      while r <= rcnt do
      begin
        SetCellRectTopBottom;
        if not IsCellFreezeFixed(MakeCell(c, r)) and not IsCellFixedRoot(MakeCell(c, r)) and (r >= FixedRowCount) then
        begin
          if IsCellFreeze(MakeCell(c, r)) then
            AddFreezeColumnCell
          else if not IsCellFreezeFixed(MakeCell(c, r)) then
            AddFixedColumnCell;
        end;

        if IsRowNode(r) and (RowNodeState[r] = grnsCollapsed) then
          r := r + RowNodeChildCount[r];

        Inc(r);
      end;

      dy := 0;
      for r := 0 to rcc - 1 do
      begin
        SetCellRectTopBottom;
        if IsCellFreezeFixed(MakeCell(c, r)) and (r >= FixedRowCount) then
          AddFreezeFixedColumnCell
        else if IsCellFixedRoot(MakeCell(c, r)) then
          AddFixedRootCell;
      end;
    end;

    dx := 0;
    r := -1;
    for c := 0 to fcc - 1 do
    begin
      SetCellRectLeftRight;

      dy := 0;
      for r := 0 to rcc - 1 do
      begin
        SetCellRectTopBottom;
        if IsCellFreezeRoot(MakeCell(c, r)) then
          AddFreezeRootCell
        else if IsCellFreezeFixed(MakeCell(c, r)) and (r >= FixedRowCount) and (c < FixedColumnCount) then
          AddFreezeFixedColumnCell
        else if IsCellFreezeFixed(MakeCell(c, r)) and (c >= FixedColumnCount) and (r < FixedRowCount) then
          AddFreezeFixedRowCell
        else if IsCellFixedRoot(MakeCell(c, r)) then
          AddFixedRootCell;
      end;

      dy := offsety;
      r := ar;
      while r <= rcnt do
      begin
        SetCellRectTopBottom;
        if not IsCellFreezeFixed(MakeCell(c, r)) and not IsCellFixedRoot(MakeCell(c, r)) and (r >= FixedRowCount) then
        begin
          if IsCellFreeze(MakeCell(c, r)) then
            AddFreezeColumnCell
          else if not IsCellFreezeFixed(MakeCell(c, r)) then
            AddFixedColumnCell
        end;

        if IsRowNode(r) and (RowNodeState[r] = grnsCollapsed) then
          r := r + RowNodeChildCount[r];

        Inc(r);
      end;

      dy := rclip.Bottom - rclip.Top;
      for r := RowCount - FixedBottomRowCount to RowCount - 1 do
        dy := dy - RowHeights[r];

      for r := RowCount - FixedBottomRowCount to RowCount - 1 do
      begin
        SetCellRectTopBottom;
        if IsCellFreezeFixed(MakeCell(c, r)) and (c >= FixedColumnCount) then
          AddFreezeFixedRowCell
        else if IsCellFixedRoot(MakeCell(c, r)) then
          AddFixedRootCell;
      end;
    end;

    dx := offsetx;
    r := -1;
    for c := ac to ccnt do
    begin
      SetCellRectLeftRight;

      dy := 0;
      for r := 0 to rcc - 1 do
      begin
        SetCellRectTopBottom;
        if not IsCellFreezeFixed(MakeCell(c, r)) and not IsCellFixedRoot(MakeCell(c, r)) and (c >= FixedColumnCount) then
        begin
          if IsCellFreeze(MakeCell(c, r)) then
            AddFreezeRowCell
          else if not IsCellFreezeFixed(MakeCell(c, r)) then
            AddFixedRowCell
        end;
      end;

      dy := offsety;
      r := ar;
      while r <= rcnt do
      begin
        while IsRowFiltered(r) do
          Inc(r);

        SetCellRectTopBottom;
        if not IsCellFixed(MakeCell(c, r)) and not IsCellFreeze(MakeCell(c, r)) and not IsCellFreezeFixed(MakeCell(c, r)) then
          AddCell;

        if IsRowNode(r) and (RowNodeState[r] = grnsCollapsed) then
          r := r + RowNodeChildCount[r];

        Inc(r);
      end;

      dy := rclip.Bottom - rclip.top;
      for r := RowCount - FixedBottomRowCount to RowCount - 1 do
        dy := dy - RowHeights[r];

      for r := RowCount - FixedBottomRowCount to RowCount - 1 do
      begin
        SetCellRectTopBottom;
        if not IsCellFreezeFixed(MakeCell(c, r)) and not IsCellFixedRoot(MakeCell(c, r)) then
          AddFixedRowCell;
      end;
    end;
  end;

  UpdateInplaceEditorPosition;
  DoDisplay;
end;

function TTMSFNCDataGridRenderer.CancelEditing: Boolean;
begin
  Result := True;
  if FInplaceEditorActive then
    Result := CloseInplaceEditor(True);
end;

function TTMSFNCDataGridRenderer.CanRegisterRuntimeClasses: Boolean;
begin
  Result := True;
  if Assigned(FHost) and (FHost is TComponent) then
    Result := not (csDesigning in (FHost as TComponent).ComponentState);
end;

{ TTMSFNCDataGridCellAppearance }

procedure TTMSFNCDataGridCellAppearance.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCDataGridCellAppearance) then
  begin
    FBandLayout.Assign((Source as TTMSFNCDataGridCellAppearance).BandLayout);
    FFixedSelectedLayout.Assign((Source as TTMSFNCDataGridCellAppearance).FixedSelectedLayout);
    FFixedLayout.Assign((Source as TTMSFNCDataGridCellAppearance).FixedLayout);
    FNormalLayout.Assign((Source as TTMSFNCDataGridCellAppearance).NormalLayout);
    FGroupLayout.Assign((Source as TTMSFNCDataGridCellAppearance).GroupLayout);
    FSummaryLayout.Assign((Source as TTMSFNCDataGridCellAppearance).SummaryLayout);
    FSelectedLayout.Assign((Source as TTMSFNCDataGridCellAppearance).SelectedLayout);
    FFocusedLayout.Assign((Source as TTMSFNCDataGridCellAppearance).FocusedLayout);
    FShowFocus := (Source as TTMSFNCDataGridCellAppearance).ShowFocus;
    FExpandIcon.Assign((Source as TTMSFNCDataGridCellAppearance).ExpandIcon);
    FCollapseIcon.Assign((Source as TTMSFNCDataGridCellAppearance).CollapseIcon);
    FFilterIcon.Assign((Source as TTMSFNCDataGridCellAppearance).FilterIcon);
    FFilterClearIcon.Assign((Source as TTMSFNCDataGridCellAppearance).FilterClearIcon);
    FFilterTypeIcon.Assign((Source as TTMSFNCDataGridCellAppearance).FilterTypeIcon);
    FFilterActiveIcon.Assign((Source as TTMSFNCDataGridCellAppearance).FilterActiveIcon);
    FSortAscendingIcon.Assign((Source as TTMSFNCDataGridCellAppearance).SortAscendingIcon);
    FSortDescendingIcon.Assign((Source as TTMSFNCDataGridCellAppearance).SortDescendingIcon);
  end;
end;

constructor TTMSFNCDataGridCellAppearance.Create;
begin
  FExpandIcon := TTMSFNCBitmap(TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDEXPAND, HInstance));
  FCollapseIcon := TTMSFNCBitmap(TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDCOLLAPSE, HInstance));
  FFilterIcon := TTMSFNCBitmap(TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDFILTER, HInstance));
  FFilterClearIcon := TTMSFNCBitmap(TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDFILTERCLEAR, HInstance));
  FFilterTypeIcon := TTMSFNCBitmap(TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDFILTERTYPE, HInstance));
  FFilterActiveIcon := TTMSFNCBitmap(TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDFILTERACTIVE, HInstance));
  FSortAscendingIcon := TTMSFNCBitmap(TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDSORTASC, HInstance));
  FSortDescendingIcon := TTMSFNCBitmap(TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDSORTDESC, HInstance));

  FBandLayout := TTMSFNCDataGridCellLayout.Create(gcLightyellow);
  FFixedLayout := TTMSFNCDataGridCellLayout.Create(Lighter(MakeGraphicsColor(238, 238, 238), 10));
  FNormalLayout := TTMSFNCDataGridCellLayout.Create;
  FGroupLayout := TTMSFNCDataGridCellLayout.Create(Lighter(MakeGraphicsColor(238, 238, 238), 10));
  FSummaryLayout := TTMSFNCDataGridCellLayout.Create(Lighter(MakeGraphicsColor(245, 245, 230), 10));
  FSelectedLayout := TTMSFNCDataGridCellLayout.Create(Lighter(gcLightsteelblue, 80));
  FFocusedLayout := TTMSFNCDataGridCellLayout.Create(Lighter(gcLightsteelblue, 40));
  FFixedSelectedLayout := TTMSFNCDataGridCellLayout.Create(Lighter(gcLightsteelblue, 80));

  FShowFocus := True;

  FBandLayout.OnChange := @LayoutChanged;
  FFixedLayout.OnChange := @LayoutChanged;
  FNormalLayout.OnChange := @LayoutChanged;
  FGroupLayout.OnChange := @LayoutChanged;
  FSummaryLayout.OnChange := @LayoutChanged;
  FSelectedLayout.OnChange := @LayoutChanged;
  FFocusedLayout.OnChange := @LayoutChanged;
  FFixedSelectedLayout.OnChange := @LayoutChanged;

  FExpandIcon.OnChange := @LayoutChanged;
  FCollapseIcon.OnChange := @LayoutChanged;
  FFilterIcon.OnChange := @LayoutChanged;
  FFilterClearIcon.OnChange := @LayoutChanged;
  FFilterTypeIcon.OnChange := @LayoutChanged;
  FFilterActiveIcon.OnChange := @LayoutChanged;
  FSortAscendingIcon.OnChange := @LayoutChanged;
  FSortDescendingIcon.OnChange := @LayoutChanged;
end;

destructor TTMSFNCDataGridCellAppearance.Destroy;
begin
  FExpandIcon.Free;
  FCollapseIcon.Free;
  FFilterIcon.Free;
  FFilterClearIcon.Free;
  FFilterTypeIcon.Free;
  FFilterActiveIcon.Free;
  FSortDescendingIcon.Free;
  FSortAscendingIcon.Free;
  FBandLayout.Free;
  FFixedLayout.Free;
  FNormalLayout.Free;
  FGroupLayout.Free;
  FSummaryLayout.Free;
  FSelectedLayout.Free;
  FFocusedLayout.Free;
  FFixedSelectedLayout.Free;
  inherited;
end;

procedure TTMSFNCDataGridCellAppearance.LayoutChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TTMSFNCDataGridCellAppearance.SetBandLayout(
  const Value: TTMSFNCDataGridCellLayout);
begin
  FBandLayout.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetFilterIcon(
  const Value: TTMSFNCBitmap);
begin
  if FFilterIcon <> Value then
    FFilterIcon.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetFilterActiveIcon(
  const Value: TTMSFNCBitmap);
begin
  if FFilterActiveIcon <> Value then
    FFilterActiveIcon.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetFilterClearIcon(
  const Value: TTMSFNCBitmap);
begin
  if FFilterClearIcon <> Value then
    FFilterClearIcon.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetFilterTypeIcon(
  const Value: TTMSFNCBitmap);
begin
  if FFilterTypeIcon <> Value then
    FFilterTypeIcon.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetFixedLayout(
  const Value: TTMSFNCDataGridCellLayout);
begin
  FFixedLayout.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetFixedSelectedLayout(
  const Value: TTMSFNCDataGridCellLayout);
begin
  FFixedSelectedLayout.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetFocusedLayout(
  const Value: TTMSFNCDataGridCellLayout);
begin
  FFocusedLayout.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetGroupLayout(
  const Value: TTMSFNCDataGridCellLayout);
begin
  FGroupLayout.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetCollapseIcon(
  const Value: TTMSFNCBitmap);
begin
  if FCollapseIcon <> Value then
    FCollapseIcon.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetExpandIcon(
  const Value: TTMSFNCBitmap);
begin
  if FExpandIcon <> Value then
    FExpandIcon.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetNormalLayout(
  const Value: TTMSFNCDataGridCellLayout);
begin
  FNormalLayout.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetSelectedLayout(
  const Value: TTMSFNCDataGridCellLayout);
begin
  FSelectedLayout.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetShowFocus(const Value: Boolean);
begin
  if FShowFocus <> Value then
  begin
    FShowFocus := Value;
    LayoutChanged(Self);
  end;
end;

procedure TTMSFNCDataGridCellAppearance.SetSortAscendingIcon(
  const Value: TTMSFNCBitmap);
begin
  if FSortAscendingIcon <> Value then
    FSortAscendingIcon.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetSortDescendingIcon(
  const Value: TTMSFNCBitmap);
begin
  if FSortDescendingIcon <> Value then
    FSortDescendingIcon.Assign(Value);
end;

procedure TTMSFNCDataGridCellAppearance.SetSummaryLayout(
  const Value: TTMSFNCDataGridCellLayout);
begin
  FSummaryLayout.Assign(Value);
end;

{ TTMSFNCDataGridCellList }

function TTMSFNCDataGridCellList.GetItemForCell(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCell;
var
  it: TTMSFNCDataGridCell;
  I: Integer;
begin
  Result := nil;
  for I := 0 to Count - 1 do
  begin
    it := Items[I];
    if (it.Column = ACell.Column) and (it.Row = ACell.Row) then
    begin
      Result := it;
      Break;
    end;
  end;
end;

{ TTMSFNCDataGridDisplayList }

function TTMSFNCDataGridDisplayList.GetItemForCell(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCell;
var
  it: TTMSFNCDataGridCell;
  I: Integer;
begin
  Result := nil;
  for I := 0 to Count - 1 do
  begin
    it := Items[I];
    if (it.Column = ACell.Column) and (it.Row = ACell.Row) then
    begin
      Result := it;
      Break;
    end;
  end;
end;

{ TTMSFNCDataGridColumnRowOptions }

procedure TTMSFNCDataGridColumnRowOptions.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridColumnRowOptions then
  begin
    FStretching.Assign((Source as TTMSFNCDataGridColumnRowOptions).Stretching);
  end;
end;

constructor TTMSFNCDataGridColumnRowOptions.Create;
begin
  FStretching := TTMSFNCDataGridStretchingOptions.Create;
  FStretching.OnChangeRebuild := DoStretchingChanged;
end;

destructor TTMSFNCDataGridColumnRowOptions.Destroy;
begin
  FreeAndNil(FStretching);
  inherited;
end;

procedure TTMSFNCDataGridColumnRowOptions.DoStretchingChanged(Sender: TObject);
begin
  if Assigned(OnChangeRebuild) then
    OnChangeRebuild(Self);
end;

procedure TTMSFNCDataGridColumnRowOptions.SetStretching(
  const Value: TTMSFNCDataGridStretchingOptions);
begin
  if FStretching <> Value then
    FStretching.Assign(Value);
end;

{ TTMSFNCDataGridOptions }

procedure TTMSFNCDataGridOptions.Assign(Source: TPersistent);
begin
  inherited;
  if Source is TTMSFNCDataGridOptions then
  begin
    FColumn.Assign((Source as TTMSFNCDataGridOptions).Column);
    FRow.Assign((Source as TTMSFNCDataGridOptions).Row);
    FBanding.Assign((Source as TTMSFNCDataGridOptions).Banding);
    FClipboard.Assign((Source as TTMSFNCDataGridOptions).Clipboard);
    FKeyboard.Assign((Source as TTMSFNCDataGridOptions).Keyboard);
    FMouse.Assign((Source as TTMSFNCDataGridOptions).Mouse);
    FEditing.Assign((Source as TTMSFNCDataGridOptions).Editing);
    FLookup.Assign((Source as TTMSFNCDataGridOptions).Lookup);
  end;
end;

constructor TTMSFNCDataGridOptions.Create;
begin
  inherited;
  FColumn := TTMSFNCDataGridColumnOptions.Create;
  FColumn.OnChangeRebuild := DoColumnChangedRebuild;
  FRow := TTMSFNCDataGridRowOptions.Create;
  FRow.OnChangeRebuild := DoRowChangedRebuild;
  FSelection :=  TTMSFNCDataGridSelectionOptions.Create;
  FSelection.OnChangeRebuild := DoSelectionChangedRebuild;
  FSelection.OnChange := @DoSelectionChanged;
  FBanding := TTMSFNCDataGridBandingOptions.Create;
  FBanding.OnChange := @DoBandingChanged;
  FClipboard := TTMSFNCDataGridClipboardOptions.Create;
  FKeyboard := TTMSFNCDataGridKeyboardOptions.Create;
  FMouse := TTMSFNCDataGridMouseOptions.Create;
  FEditing := TTMSFNCDataGridEditingOptions.Create;
  FLookup := TTMSFNCDataGridLookupOptions.Create;
end;

destructor TTMSFNCDataGridOptions.Destroy;
begin
  FLookup.Free;
  FEditing.Free;
  FMouse.Free;
  FKeyboard.Free;
  FClipboard.Free;
  FBanding.Free;
  FSelection.Free;
  FColumn.Free;
  FRow.Free;
  inherited;
end;

procedure TTMSFNCDataGridOptions.DoCellsChangedRebuild(Sender: TObject);
begin
  if Assigned(OnChangeRebuild) then
    OnChangeRebuild(Self);
end;

procedure TTMSFNCDataGridOptions.DoColumnChangedRebuild(Sender: TObject);
begin
  if Assigned(OnChangeRebuild) then
    OnChangeRebuild(Self);
end;

procedure TTMSFNCDataGridOptions.SetBanding(
  const Value: TTMSFNCDataGridBandingOptions);
begin
  if FBanding <> Value then
    FBanding.Assign(Value);
end;

procedure TTMSFNCDataGridOptions.SetClipboard(
  const Value: TTMSFNCDataGridClipboardOptions);
begin
  if FClipboard <> Value then
    FClipboard.Assign(Value);
end;

procedure TTMSFNCDataGridOptions.SetColumn(
  const Value: TTMSFNCDataGridColumnOptions);
begin
  if FColumn <> Value then
    FColumn.Assign(Value);
end;

procedure TTMSFNCDataGridOptions.SetEditing(
  const Value: TTMSFNCDataGridEditingOptions);
begin
  if FEditing <> Value then
    FEditing.Assign(Value);
end;

procedure TTMSFNCDataGridOptions.SetIO(const Value: TTMSFNCDataGridIOOptions);
begin
  inherited IO := Value;
end;

procedure TTMSFNCDataGridOptions.SetKeyboard(
  const Value: TTMSFNCDataGridKeyboardOptions);
begin
  if FKeyboard <> Value then
    FKeyboard.Assign(Value);
end;

procedure TTMSFNCDataGridOptions.SetLookup(
  const Value: TTMSFNCDataGridLookupOptions);
begin
  if FLookup <> Value then
    FLookup := Value;
end;

procedure TTMSFNCDataGridOptions.SetMouse(
  const Value: TTMSFNCDataGridMouseOptions);
begin
  if FMouse <> Value then
    FMouse.Assign(Value);
end;

procedure TTMSFNCDataGridOptions.DoRowChangedRebuild(Sender: TObject);
begin
  if Assigned(OnChangeRebuild) then
    OnChangeRebuild(Self);
end;

procedure TTMSFNCDataGridOptions.DoBandingChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TTMSFNCDataGridOptions.DoSelectionChanged(Sender: TObject);
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TTMSFNCDataGridOptions.DoSelectionChangedRebuild(Sender: TObject);
begin
  if Assigned(OnChangeRebuild) then
    OnChangeRebuild(Self);
end;

function TTMSFNCDataGridOptions.GetIO: TTMSFNCDataGridIOOptions;
begin
  Result := TTMSFNCDataGridIOOptions(inherited IO);
end;

function TTMSFNCDataGridOptions.GetIOOptionsClass: TTMSFNCDataGridDataIOOptionsClass;
begin
  Result := TTMSFNCDataGridIOOptions;
end;

procedure TTMSFNCDataGridOptions.SetRow(
  const Value: TTMSFNCDataGridRowOptions);
begin
  if FRow <> Value then
    FRow.Assign(Value);
end;

procedure TTMSFNCDataGridOptions.SetSelection(
  const Value: TTMSFNCDataGridSelectionOptions);
begin
  if FSelection <> Value then
    FSelection.Assign(Value);
end;

{ TTMSFNCDataGridSelectionOptions }

procedure TTMSFNCDataGridSelectionOptions.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCDataGridSelectionOptions) then
  begin
    FMode := (Source as TTMSFNCDataGridSelectionOptions).Mode;
    FShowSelectionInFixedCells := (Source as TTMSFNCDataGridSelectionOptions).ShowSelectionInFixedCells;
  end;
end;

procedure TTMSFNCDataGridSelectionOptions.Changed;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

procedure TTMSFNCDataGridSelectionOptions.ChangedRebuild;
begin
  if Assigned(FOnChangeRebuild) then
    FOnChangeRebuild(Self);
end;

constructor TTMSFNCDataGridSelectionOptions.Create;
begin
  FMode := gsmSingleCell;
end;

procedure TTMSFNCDataGridSelectionOptions.SetMode(const Value: TTMSFNCDataGridSelectionMode);
begin
  if FMode <> Value then
  begin
    FMode := Value;
    ChangedRebuild;
  end;
end;

procedure TTMSFNCDataGridSelectionOptions.SetShowSelectionInFixedCells(
  const Value: Boolean);
begin
  if FShowSelectionInFixedCells <> Value then
  begin
    FShowSelectionInFixedCells := Value;
    Changed;
  end;
end;

{ TTMSFNCDataGridBandingOptions }

procedure TTMSFNCDataGridBandingOptions.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCDataGridBandingOptions) then
  begin
    FEnabled := (Source as TTMSFNCDataGridBandingOptions).Enabled;
  end;
end;

procedure TTMSFNCDataGridBandingOptions.Changed;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

constructor TTMSFNCDataGridBandingOptions.Create;
begin
  inherited Create;
  FEnabled := False;
  FNormalRowCount := 1;
  FBandRowCount := 1;
end;

procedure TTMSFNCDataGridBandingOptions.SetBandRowCount(const Value: integer);
begin
  if (Value > 0) and (FBandRowCount <> Value) then
  begin
    FBandRowCount := Value;
    FTotalRowCount := FNormalRowCount + FBandRowCount;
    Changed;
  end;
end;

procedure TTMSFNCDataGridBandingOptions.SetEnabled(const Value: Boolean);
begin
  if (FEnabled <> Value) then
  begin
    FEnabled := Value;
    Changed;
  end;
end;

procedure TTMSFNCDataGridBandingOptions.SetNormalRowCount(const Value: integer);
begin
  if (Value > 0) and (FNormalRowCount <> Value) then
  begin
    FNormalRowCount := Value;
    FTotalRowCount := FNormalRowCount + FBandRowCount;
    Changed;
  end;
end;

{ TTMSFNCDataGridClipboardOptions }

procedure TTMSFNCDataGridClipboardOptions.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridClipboardOptions then
  begin
    FPasteAction := (Source as TTMSFNCDataGridClipboardOptions).PasteAction;
    FIgnoreReadOnly := (Source as TTMSFNCDataGridClipboardOptions).IgnoreReadOnly;
    FEnabled := (Source as TTMSFNCDataGridClipboardOptions).Enabled;
    FAllowRowGrow := (Source as TTMSFNCDataGridClipboardOptions).AllowRowGrow;
    FAllowColumnGrow := (Source as TTMSFNCDataGridClipboardOptions).AllowColumnGrow;
  end;
end;

constructor TTMSFNCDataGridClipboardOptions.Create;
begin
  FPasteAction := gpaReplace;
  FIgnoreReadOnly := False;
  FEnabled := True;
  FAllowRowGrow := False;
  FAllowColumnGrow := False;
end;

{ TTMSFNCDataGridMouseOptions }

procedure TTMSFNCDataGridMouseOptions.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCDataGridMouseOptions) then
  begin
    FDirectEdit := (Source as TTMSFNCDataGridMouseOptions).DirectEdit;
    FWheelScrollSize := (Source as TTMSFNCDataGridMouseOptions).WheelScrollSize;
    FColumnSizing := (Source as TTMSFNCDataGridMouseOptions).ColumnSizing;
    FRowSizing := (Source as TTMSFNCDataGridMouseOptions).RowSizing;
    FFixedColumnSizing := (Source as TTMSFNCDataGridMouseOptions).FixedColumnSizing;
    FFixedRowSizing := (Source as TTMSFNCDataGridMouseOptions).FixedRowSizing;
    FColumnDragging := (Source as TTMSFNCDataGridMouseOptions).ColumnDragging;
    FRowDragging := (Source as TTMSFNCDataGridMouseOptions).RowDragging;
    FAutoScrolling := (Source as TTMSFNCDataGridMouseOptions).AutoScrolling;
    FAutoDragging := (Source as TTMSFNCDataGridMouseOptions).AutoDragging;
    FAutoScrollingSpeed := (Source as TTMSFNCDataGridMouseOptions).AutoScrollingSpeed;
    FAutoScrollingInterval := (Source as TTMSFNCDataGridMouseOptions).AutoScrollingInterval;
    FTouchScrolling := (Source as TTMSFNCDataGridMouseOptions).TouchScrolling;
    FTouchScrollingSensitivity := (Source as TTMSFNCDataGridMouseOptions).TouchScrollingSensitivity;
    FFixedCellSelection := (Source as TTMSFNCDataGridMouseOptions).FixedCellSelection;
    FRowAutoSizeOnDblClick := (Source as TTMSFNCDataGridMouseOptions).RowAutoSizeOnDblClick;
    FColumnAutoSizeOnDblClick := (Source as TTMSFNCDataGridMouseOptions).ColumnAutoSizeOnDblClick;
    FWheelScrollKeepSelection := (Source as TTMSFNCDataGridMouseOptions).WheelScrollKeepSelection;
    FColumnSizeMargin := (Source as TTMSFNCDataGridMouseOptions).ColumnSizeMargin;
    FClickMargin := (Source as TTMSFNCDataGridMouseOptions).ClickMargin;
    FRowSizeMargin := (Source as TTMSFNCDataGridMouseOptions).RowSizeMargin;
    FAutoScrollingSpeedMode  := (Source as TTMSFNCDataGridMouseOptions).AutoScrollingSpeedMode;
  end;
end;

constructor TTMSFNCDataGridMouseOptions.Create;
begin
  FTouchScrollingSensitivity := 1;
  FTouchScrolling := False;
  FDirectEdit := False;
  FWheelScrollSize := 4;
  FColumnSizing := False;
  FRowSizing := False;
  FFixedColumnSizing := False;
  FFixedRowSizing := False;
  FColumnDragging := False;
  FRowDragging := False;
  FAutoScrolling := True;
  FAutoDragging := True;
  FAutoScrollingSpeed := 1;
  FAutoScrollingInterval := 100;
  FFixedCellSelection := [];
  FRowAutoSizeOnDblClick := True;
  FColumnAutoSizeOnDblClick := True;
  FWheelScrollKeepSelection := True;
  FColumnSizeMargin := 6;
  FRowSizeMargin := 6;
  FClickMargin := 2;
  FAutoScrollingSpeedMode := gasmPixels;
end;

function TTMSFNCDataGridMouseOptions.IsTouchScrollingSensitivityStored: Boolean;
begin
  Result := TouchScrollingSensitivity <> 1;
end;

procedure TTMSFNCDataGridMouseOptions.SetAutoScrollingInterval(
  const Value: Integer);
begin
  FAutoScrollingInterval := Value;
end;

procedure TTMSFNCDataGridMouseOptions.SetClickMargin(const Value: Integer);
begin
  FClickMargin := Value;
end;

procedure TTMSFNCDataGridMouseOptions.SetColumnSizeMargin(const Value: Integer);
begin
  FColumnSizeMargin := Value;
end;

procedure TTMSFNCDataGridMouseOptions.SetRowSizeMargin(const Value: Integer);
begin
  FRowSizeMargin := Value;
end;

procedure TTMSFNCDataGridMouseOptions.SetTouchScrolling(const Value: Boolean);
begin
  FTouchScrolling := Value;
end;

procedure TTMSFNCDataGridMouseOptions.SetWheelScrollKeepSelection(
  const Value: Boolean);
begin
  FWheelScrollKeepSelection := Value;
end;

procedure TTMSFNCDataGridMouseOptions.SetWheelScrollSize(const Value: Integer);
begin
  FWheelScrollSize := Value;
end;

{ TTMSFNCDataGridKeyboardOptions }

procedure TTMSFNCDataGridKeyboardOptions.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCDataGridKeyboardOptions) then
  begin
    FTabKeyDirection := (Source as TTMSFNCDataGridKeyboardOptions).TabKeyDirection;
    FTabKeyHandling := (Source as TTMSFNCDataGridKeyboardOptions).TabKeyHandling;
    FEnterKeyHandling := (Source as TTMSFNCDataGridKeyboardOptions).EnterKeyHandling;
    FDeleteKeyHandling := (Source as TTMSFNCDataGridKeyboardOptions).DeleteKeyHandling;
    FInsertKeyHandling := (Source as TTMSFNCDataGridKeyboardOptions).InsertKeyHandling;
    FEnterKeyDirectEdit := (Source as TTMSFNCDataGridKeyboardOptions).EnterKeyDirectEdit;
    FTabKeyDirectEdit := (Source as TTMSFNCDataGridKeyboardOptions).TabKeyDirectEdit;
    FArrowKeyDirectEdit := (Source as TTMSFNCDataGridKeyboardOptions).ArrowKeyDirectEdit;
    FCellMergeShortcut := (Source as TTMSFNCDataGridKeyboardOptions).CellMergeShortcut;
    FFindShortcut := (Source as TTMSFNCDataGridKeyboardOptions).FindShortcut;
    FReplaceShortcut := (Source as TTMSFNCDataGridKeyboardOptions).ReplaceShortcut;
    FAppendRowOnArrowKeyDown := (Source as TTMSFNCDataGridKeyboardOptions).AppendRowOnArrowKeyDown;
    FAppendColumnOnArrowKeyRight := (Source as TTMSFNCDataGridKeyboardOptions).AppendColumnOnArrowKeyRight;
    FHomeKeyHandling := (Source as TTMSFNCDataGridKeyboardOptions).HomeKeyHandling;
    FEndKeyHandling := (Source as TTMSFNCDataGridKeyboardOptions).EndKeyHandling;
  end;
end;

constructor TTMSFNCDataGridKeyboardOptions.Create;
begin
  FAppendRowOnArrowKeyDown := False;
  FAppendColumnOnArrowKeyRight := False;
  FFindShortcut := True;
  FReplaceShortcut := True;
  FTabKeyDirection := gtkdNextColumnCell;
  FTabKeyHandling := gtkhMixed;
  FPageScrollSize := 0;
  FEnterKeyHandling := gekhNone;
  FDeleteKeyHandling := gdkhNone;
  FInsertKeyHandling := gikhNone;
  FEnterKeyDirectEdit := False;
  FArrowKeyDirectEdit := False;
  FTabKeyDirectEdit := False;
  FCellMergeShortcut := False;
  FHomeKeyHandling := ghkhFirstColumn;
  FEndKeyHandling := genkhLastColumn;
end;

procedure TTMSFNCDataGridKeyboardOptions.SetPageScrollSize(const Value: Integer);
begin
  FPageScrollSize := Value;
end;

{ TTMSFNCDataGridRendererCell }

procedure TTMSFNCDataGridRendererCell.AfterExport;
begin
  inherited;
  FreeAndNil(FExportRenderer);
end;

procedure TTMSFNCDataGridRendererCell.ApplyScale(AScale: Single);
begin
  inherited;
  if Assigned(FExportRenderer) then
    FExportRenderer.ApplyExportScale(AScale);
end;

procedure TTMSFNCDataGridRendererCell.BeforeExport;
var
  r: TTMSFNCDataGridRenderer;
begin
  inherited;
  FreeAndNil(FExportRenderer);
  r := Renderer;
  if Assigned(r) then
  begin
    FExportRenderer := TTMSFNCDataGridRenderer.Create(r.Host);
    FExportRenderer.Assign(r);
  end;
end;

constructor TTMSFNCDataGridRendererCell.Create(ARect: TRectF; AColumn,
  ARow: Integer; AData: TTMSFNCDataGridCellItem;
  AKind: TTMSFNCDataGridCellKind; ARoot: TObject = nil);
begin
  inherited;
  FRenderRect := EmptyRect;
end;

destructor TTMSFNCDataGridRendererCell.Destroy;
var
  r: TTMSFNCDataGridRenderer;
  d: TNotifyEvent;
begin
  FreeAndNil(FExportRenderer);

  r := Renderer;
  if Assigned(r) then
  begin
    if Assigned(r.Host) then
    begin
      if Assigned(r.VerticalScrollBar) then
        r.VerticalScrollBar.Visible := False;
      if Assigned(r.HorizontalScrollBar) then
        r.HorizontalScrollBar.Visible := False;

      if Assigned(r.CustomVerticalScrollBar) then
        r.CustomVerticalScrollBar.Visible := False;
      if Assigned(r.CustomHorizontalScrollBar) then
        r.CustomHorizontalScrollBar.Visible := False;
    end;

    d := r.OnDisplay;
    r.OnDisplay := nil;
    r.Update;
    r.OnDisplay := d;
  end;

  inherited;
end;

procedure TTMSFNCDataGridRendererCell.DrawContent(AGraphics: TTMSFNCGraphics);
var
  r: TTMSFNCDataGridRenderer;
  d: TNotifyEvent;
  rd: TRectF;

  procedure FixRect;
  begin
    {$IFDEF FMXWEBLIB}
    rd := RectF(rd.Left - 0.5, rd.Top - 0.5, rd.Right + 0.5, rd.Bottom + 0.5);
    {$ENDIF}
  end;
begin
  inherited;

  if IsExporting then
    r := FExportRenderer
  else
    r := Renderer;

  if Assigned(r) then
  begin
    d := r.OnDisplay;
    r.OnDisplay := nil;

    if IsExporting then
      r.BeforeExport;

    try
      rd := GetRendererRect;

      if RectIsEmpty(FRenderRect) or ((rd.Left <> FRenderRect.Left) or (rd.Top <> FRenderRect.Top)
        or (rd.Bottom <> FRenderRect.Bottom) or (rd.Right <> FRenderRect.Right)) then
      begin
        r.BeginUpdate;
        FRenderRect := rd;

        FixRect;

        r.SetLocalRect(rd);
        r.SetClipRect(ClipRect);
        r.EndUpdate;
      end
      else
        FixRect;

      r.Draw(AGraphics);
    finally
      r.OnDisplay := d;
      if IsExporting then
        r.AfterExport;
    end;
  end;
end;

function TTMSFNCDataGridRendererCell.GetDrawElements(
  AIsEditing: Boolean): TTMSFNCDataGridCellDrawElements;
begin
  Result := [gcdControl];
end;

function TTMSFNCDataGridRendererCell.GetRenderer: TTMSFNCDataGridRenderer;
begin
  Result := nil;
  if Assigned(Data) and IsExtendedCell(Data) and Assigned(TTMSFNCDataGridCellItemExtendedOpen(AsExtendedCell(Data)).Renderer) then
    Result := TTMSFNCDataGridCellItemExtendedOpen(AsExtendedCell(Data)).Renderer as TTMSFNCDataGridRenderer;
end;

function TTMSFNCDataGridRendererCell.GetRendererRect: TRectF;
var
  r, rct, res: TRectF;
begin
  r := Rect;
  res := r;

  if IsControlAssigned then
  begin
    rct := GetControlRect(True);

    case ControlAlign of
      gcaTop: res := RectF(r.Left, rct.Bottom, r.Right, r.Bottom);
      gcaBottom: res := RectF(r.Left, r.Top, r.Right, rct.Top);
      gcaLeft: res := RectF(rct.Right, r.Top, r.Right, r.Bottom);
      gcaRight: res := RectF(r.Left, r.Top, rct.Left, r.Bottom);
      gcaNone:
      begin
        case ControlPosition of
          gcpTopLeft, gcpTopCenter, gcpTopRight: res := RectF(r.Left, rct.Bottom, r.Right, r.Bottom);
          gcpCenterLeft: res := RectF(rct.Right, r.Top, r.Right, r.Bottom);
          gcpCenterRight: res := RectF(r.Left, r.Top, rct.Left, r.Bottom);
          gcpBottomLeft, gcpBottomCenter, gcpBottomRight: res := RectF(r.Left, r.Top, r.Right, rct.Top);
        end;
      end;
    end;
  end;

  Result := res;
end;

function TTMSFNCDataGridRendererCell.GetRequiredSize(AGraphics: TTMSFNCGraphics = nil): TSizeF;
var
  r: TRectF;
begin
  if Assigned(Renderer) then
  begin
    r := Renderer.LocalRect;
    Result.cx := r.Right - r.Left;
    Result.cy := r.Bottom - r.Top;
  end
  else
  begin
    Result.cx := 0;
    Result.cy := 0;
  end;
end;

{ TTMSFNCDataGridScrollBar }

procedure TTMSFNCDataGridScrollBar.DrawEx(AGraphics: TTMSFNCGraphics);
var
  r: TRectF;
begin
  r := ConvertToRectF(BoundsRect);
  Self.DrawBackground(AGraphics, r);
  Self.Export(AGraphics, r);
end;

{ TTMSFNCDataGridEdit }

constructor TTMSFNCDataGridEdit.Create(AOwner: TComponent);
begin
  inherited;
  AutoClose := False;
  {$IFDEF FMXLIB}
  TextAlign := TTextAlign.Leading;
  DisableFocusEffect := True;
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  BorderStyle := bsNone;
  {$IFDEF VCLLIB}
  ParentCtl3D := False;
  Ctl3D := False;
  {$ENDIF}
  {$IFDEF WEBLIB}
  ShowFocus := False;
  {$ENDIF}
  TabStop := False;
  DoubleBuffered := False;
  {$ENDIF}
end;

{$IFDEF FMXLIB}
function TTMSFNCDataGridEdit.GetDefaultStyleLookupName: string;
begin
  Result := 'textcellstyle';
end;
{$ENDIF}

{$IFDEF VCLLIB}
procedure TTMSFNCDataGridEdit.WMGetDlgCode(var Message: TWMGetDlgCode);
begin
  inherited;
  Message.Result := Message.Result or DLGC_WANTTAB;
end;
{$ENDIF}

{ TTMSFNCDataGridRendererDataAdapter }

function TTMSFNCDataGridRendererDataAdapter.GetRenderer: TTMSFNCDataGridRenderer;
begin
  if Assigned(DataInstance) and (DataInstance is TTMSFNCDataGridRenderer) then
    Result := DataInstance as TTMSFNCDataGridRenderer
  else
    Result := nil;
end;

procedure TTMSFNCDataGridRendererDataAdapter.SetRenderer(
  const Value: TTMSFNCDataGridRenderer);
begin
  DataInstance := Value;
end;

{ TTMSFNCDataGridEditingOptions }

procedure TTMSFNCDataGridEditingOptions.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCDataGridEditingOptions) then
  begin
    FEnabled := (Source as TTMSFNCDataGridEditingOptions).Enabled;
    FDirectDropDown := (Source as TTMSFNCDataGridEditingOptions).DirectDropDown;
    FAutoComplete.Assign((Source as TTMSFNCDataGridEditingOptions).AutoComplete);
  end;
end;

constructor TTMSFNCDataGridEditingOptions.Create;
begin
  FEnabled := True;
  FDirectDropDown := False;
  FAutoComplete := TTMSFNCDataGridAutoCompleteOptions.Create;
end;

destructor TTMSFNCDataGridEditingOptions.Destroy;
begin
  FreeAndNil(FAutoComplete);
  inherited;
end;

procedure TTMSFNCDataGridEditingOptions.SetAutoComplete(
  const Value: TTMSFNCDataGridAutoCompleteOptions);
begin
  if FAutoComplete <> Value then
    FAutoComplete.Assign(Value);
end;

{ TTMSFNCDataGridLookupOptions }

procedure TTMSFNCDataGridLookupOptions.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCDataGridLookupOptions) then
  begin
    FCaseSensitive := (Source as TTMSFNCDataGridLookupOptions).CaseSensitive;
    FIncremental := (Source as TTMSFNCDataGridLookupOptions).Incremental;
    FEnabled := (Source as TTMSFNCDataGridLookupOptions).Enabled;
    FResetInterval := (Source as TTMSFNCDataGridLookupOptions).ResetInterval;
  end;
end;

constructor TTMSFNCDataGridLookupOptions.Create;
begin
  inherited;
  FCaseSensitive := False;
  FIncremental := True;
  FEnabled := False;
  FResetInterval := 4000;
end;

{$IFDEF FMXLIB}
procedure TTMSFNCDataGridDatePicker.KeyDown(var Key: Word; var KeyChar: Char; Shift: TShiftState);
var
  IsReturnKey: Boolean;
begin
  IsReturnKey := Key = vkReturn;
  inherited;
  if IsReturnKey and Assigned(OnKeyDown) then
  begin
    Key := vkReturn;
    OnKeyDown(Self, Key, KeyChar, Shift);
  end;
end;
{$ENDIF}

{ TTMSFNCDataGridAutoCompleteOptions }

procedure TTMSFNCDataGridAutoCompleteOptions.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridAutoCompleteOptions then
  begin
    FType := (Source as TTMSFNCDataGridAutoCompleteOptions).&Type;
    FItems.Assign((Source as TTMSFNCDataGridAutoCompleteOptions).Items);
    FCaseSensitive := (Source as TTMSFNCDataGridAutoCompleteOptions).CaseSensitive;
  end;
end;

constructor TTMSFNCDataGridAutoCompleteOptions.Create;
begin
  FType := gactDisabled;
  FCaseSensitive := False;
  FItems := TStringList.Create;
end;

destructor TTMSFNCDataGridAutoCompleteOptions.Destroy;
begin
  FreeAndNil(FItems);
  inherited;
end;

procedure TTMSFNCDataGridAutoCompleteOptions.SetItems(const Value: TStringList);
begin
  if FItems <> Value then
  begin
    FItems.Assign(Value);
  end;
end;

{ TTMSFNCDataGridColumns }

function TTMSFNCDataGridColumns.Add: TTMSFNCDataGridColumn;
begin
  Result := TTMSFNCDataGridColumn(inherited Add);
end;

procedure TTMSFNCDataGridColumns.ClearColumn(AIndex: Integer);
begin
  if Assigned(FRenderer) then
  begin
    FRenderer.ClearDataForColumn(AIndex);
    FRenderer.ClearColumnInfo(AIndex);
  end;
end;

function TTMSFNCDataGridColumns.ColumnByHeader(
  AHeader: string): TTMSFNCDataGridColumn;
var
  i: integer;
begin
  Result := nil;
  for i := 0 to Count - 1 do
  begin
    if (Items[i].Header = AHeader) then
    begin
      Result := Items[i];
      Break;
    end;
  end;
end;

function TTMSFNCDataGridColumns.ColumnByName(AName: string): TTMSFNCDataGridColumn;
var
  i: integer;
begin
  Result := nil;
  for i := 0 to Count - 1 do
  begin
    if (Items[i].Name = AName) then
    begin
      Result := Items[i];
      Break;
    end;
  end;
end;

constructor TTMSFNCDataGridColumns.Create(AHost: TPersistent; ARenderer: TTMSFNCDataGridRenderer);
begin
  inherited Create(AHost, GetColumnClass);
  FRenderer := ARenderer;
end;

function TTMSFNCDataGridColumns.GetColumnClass: TTMSFNCDataGridColumnClass;
begin
  Result := TTMSFNCDataGridColumn;
end;

function TTMSFNCDataGridColumns.GetItem(Index: integer): TTMSFNCDataGridColumn;
begin
  Result := TTMSFNCDataGridColumn(inherited Items[Index]);
end;

function TTMSFNCDataGridColumns.Insert(Index: integer): TTMSFNCDataGridColumn;
begin
  Result := TTMSFNCDataGridColumn(inherited Insert(Index));
end;

procedure TTMSFNCDataGridColumns.SetItem(Index: integer;
  const Value: TTMSFNCDataGridColumn);
begin
  inherited Items[Index] := Value;
end;

procedure TTMSFNCDataGridColumns.UpdateColumns;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

{ TTMSFNCDataGridColumn }

procedure TTMSFNCDataGridColumn.AddSetting(
  ASetting: TTMSFNCDataGridColumnSetting);
begin
  Settings := Settings + [ASetting];
end;

procedure TTMSFNCDataGridColumn.AppearanceChanged(Sender: TObject);
begin
  UpdateSettings(gcsAppearance);
  UpdateColumn;
end;

procedure TTMSFNCDataGridColumn.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridColumn then
  begin
    FAppearance.Assign((Source as TTMSFNCDataGridColumn).Appearance);
    FSettings := (Source as TTMSFNCDataGridColumn).Settings;
    FType := (Source as TTMSFNCDataGridColumn).&Type;
    FEditor := (Source as TTMSFNCDataGridColumn).Editor;
    FEditorTarget := (Source as TTMSFNCDataGridColumn).EditorTarget;
    FFormatting.Assign((Source as TTMSFNCDataGridColumn).Formatting);
    FBinding.Assign((Source as TTMSFNCDataGridColumn).Binding);
    FEditorItems.Assign((Source as TTMSFNCDataGridColumn).EditorItems);
    FReadOnly := (Source as TTMSFNCDataGridColumn).ReadOnly;
    FTypeRange := (Source as TTMSFNCDataGridColumn).TypeRange;
    FControlText := (Source as TTMSFNCDataGridColumn).ControlText;
    FControlWidth := (Source as TTMSFNCDataGridColumn).ControlWidth;
    FControlHeight := (Source as TTMSFNCDataGridColumn).ControlHeight;
    FControlAlign := (Source as TTMSFNCDataGridColumn).ControlAlign;
    FControlPosition := (Source as TTMSFNCDataGridColumn).ControlPosition;
    FFilterButton := (Source as TTMSFNCDataGridColumn).FilterButton;
    FFilterEditor := (Source as TTMSFNCDataGridColumn).FilterEditor;
    FFilterEditorType := (Source as TTMSFNCDataGridColumn).FilterEditorType;

    Width := (Source as TTMSFNCDataGridColumn).Width;
    Header := (Source as TTMSFNCDataGridColumn).Header;
  end;
end;

procedure TTMSFNCDataGridColumn.BindingChanged(Sender: TObject);
begin
  UpdateColumn;
end;

procedure TTMSFNCDataGridColumn.UpdateColumn;
begin
  if Assigned(FColumns) then
    FColumns.UpdateColumns;
end;

procedure TTMSFNCDataGridColumn.UpdateSettings(ASetting: TTMSFNCDataGridColumnSetting);
begin
  if Assigned(FColumns) and Assigned(FColumns.Renderer) and FColumns.Renderer.IsDesigntime then
    FSettings := FSettings + [ASetting];
end;

procedure TTMSFNCDataGridColumn.ClearColumn;
begin
  if Assigned(FColumns) then
    FColumns.ClearColumn(Index);
end;

constructor TTMSFNCDataGridColumn.Create(ACollection: TCollection);
begin
  inherited;
  FColumns := nil;
  if Collection is TTMSFNCDataGridColumns then
    FColumns := Collection as TTMSFNCDataGridColumns;

  FTypeRange := gcirAll;
  FVisible := True;
  FFilterButton := True;
  FFilterEditor := False;
  FFilterEditorType := gfetAutomatic;
  FFormatting := TTMSFNCDataGridColumnFormatting.Create;
  FFormatting.OnChange := @FormattingChanged;
  FBinding := TTMSFNCDataGridColumnBinding.Create;
  FBinding.OnChange := @BindingChanged;
  FAppearance := TTMSFNCDataGridCellAppearance.Create;
  FAppearance.OnChange := @AppearanceChanged;
  FSettings := [];
  FType := gcitNormal;
  FEditor := getEdit;
  FEditorTarget := getCellValue;
  FEditorItems := TStringList.Create;
  FEditorItems.OnChange := @EditorItemsChanged;
  FControlText := '';
  FControlWidth := -1;
  FControlHeight := -1;
  FControlMargins := TTMSFNCMargins.Create;
  FControlMargins.OnChange := @MarginsChanged;
  if Assigned(Renderer) then
    FWidth := Renderer.ColumnWidths[Index]
  else
    FWidth := 75;
  FHeader := '';
  FControlAlign := gcaNone;
  FControlPosition := gcpCenterRight;
  UpdateColumn;
end;

destructor TTMSFNCDataGridColumn.Destroy;
begin
  FBinding.Free;
  FFormatting.Free;
  FAppearance.Free;
  FEditorItems.Free;
  FControlMargins.Free;
  ClearColumn;
  inherited;
  UpdateColumn;
  FColumns := nil;
end;

procedure TTMSFNCDataGridColumn.EditorItemsChanged(Sender: TObject);
begin
  UpdateSettings(gcsEditorItems);
end;

procedure TTMSFNCDataGridColumn.FormattingChanged(Sender: TObject);
begin
  UpdateSettings(gcsFormatting);
  UpdateColumn;
end;

function TTMSFNCDataGridColumn.GetDisplayName: string;
begin
  if Header = '' then
  begin
    if Name = '' then
      Result := 'Column ' + Inttostr(Index)
    else
      Result := Name;
  end
  else
    Result := Header;
end;

function TTMSFNCDataGridColumn.GetHeader: string;
begin
  Result := FHeader;
  if Assigned(Renderer) then
    Result := Renderer.Strings[Index, 0];
end;

function TTMSFNCDataGridColumn.GetRenderer: TTMSFNCDataGridRenderer;
begin
  Result := nil;
  if Assigned(Collection) and (Collection is TTMSFNCDataGridColumns) then
    Result := (Collection as TTMSFNCDataGridColumns).Renderer;
end;

function TTMSFNCDataGridColumn.GetValueType: TTypeKind;
var
  v: TTMSFNCDataGridCellValue;
begin
  Result := TTypeKind.tkUnknown;
  if Assigned(FColumns) and Assigned(FColumns.FRenderer) then
  begin
    v := FColumns.FRenderer.Cells[Index, FColumns.FRenderer.FixedRowCount];
    Result := TTMSFNCDataGridData.TypeOfValue(v);
  end;
end;

function TTMSFNCDataGridColumn.GetVisible: Boolean;
begin
  Result := FVisible;
end;

function TTMSFNCDataGridColumn.GetWidth: Single;
begin
  Result := FWidth;
end;

function TTMSFNCDataGridColumn.IsControlHeightStored: Boolean;
begin
  Result := ControlHeight <> -1;
end;

function TTMSFNCDataGridColumn.IsControlTextStored: Boolean;
begin
  Result := ControlText <> '';
end;

function TTMSFNCDataGridColumn.IsControlWidthStored: Boolean;
begin
  Result := ControlWidth <> -1;
end;

function TTMSFNCDataGridColumn.IsHeaderStored: Boolean;
begin
  Result := Header <> '';
end;

function TTMSFNCDataGridColumn.IsWidthStored: Boolean;
begin
  Result := Width <> 75;
end;

procedure TTMSFNCDataGridColumn.MarginsChanged(Sender: TObject);
begin
  UpdateSettings(gcsControlMargins);
  UpdateColumn;
end;

procedure TTMSFNCDataGridColumn.RemoveSetting(
  ASetting: TTMSFNCDataGridColumnSetting);
begin
  Settings := Settings - [ASetting];
end;

procedure TTMSFNCDataGridColumn.SetAppearance(
  const Value: TTMSFNCDataGridCellAppearance);
begin
  FAppearance.Assign(Value);
end;

procedure TTMSFNCDataGridColumn.SetBinding(
  const Value: TTMSFNCDataGridColumnBinding);
begin
  if FBinding <> Value then
    FBinding.Assign(Value);
end;

procedure TTMSFNCDataGridColumn.SetControlAlign(
  const Value: TTMSFNCDataGridCellControlAlign);
begin
  if FControlAlign <> Value then
  begin
    FControlAlign := Value;
    UpdateSettings(gcsControlAlign);
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetControlHeight(const Value: Integer);
begin
  if FControlHeight <> Value then
  begin
    FControlHeight := Value;
    UpdateSettings(gcsControlHeight);
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetControlMargins(const Value: TTMSFNCMargins);
begin
  if FControlMargins <> Value then
  begin
    FControlMargins.Assign(Value);
    UpdateSettings(gcsControlMargins);
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetControlPosition(
  const Value: TTMSFNCDataGridCellControlPosition);
begin
  if FControlPosition <> Value then
  begin
    FControlPosition := Value;
    UpdateSettings(gcsControlPosition);
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetControlText(const Value: string);
begin
  if FControlText <> Value then
  begin
    FControlText := Value;
    UpdateSettings(gcsControlText);
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetControlWidth(const Value: Integer);
begin
  if FControlWidth <> Value then
  begin
    FControlWidth := Value;
    UpdateSettings(gcsControlWidth);
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetHeader(const Value: string);
begin
  if FHeader <> Value then
  begin
    FHeader := Value;
    if Assigned(Renderer) then
      Renderer.Cells[Index, 0] := Value;
  end;
end;

procedure TTMSFNCDataGridColumn.SetTypeRange(
  const Value: TTMSFNCDataGridCellItemTypeRange);
begin
  if FTypeRange <> Value then
  begin
    FTypeRange := Value;
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetVisible(const Value: Boolean);
begin
  if FVisible <> Value then
  begin
    FVisible := Value;
    if Assigned(Renderer) then
    begin
      if FVisible then
        Renderer.UnhideColumn(Index)
      else
        Renderer.HideColumn(Index);
    end
  end;
end;

procedure TTMSFNCDataGridColumn.SetReadOnly(const Value: Boolean);
begin
  if FReadOnly <> Value then
  begin
    FReadOnly := Value;
    UpdateSettings(gcsReadOnly);
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetSettings(
  const Value: TTMSFNCDataGridColumnSettings);
begin
  if FSettings <> Value then
  begin
    FSettings := Value;
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetType(const Value: TTMSFNCDataGridCellItemType);
begin
  if FType <> Value then
  begin
    FType := Value;
    UpdateSettings(gcsType);
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetEditor(
  const Value: TTMSFNCDataGridInplaceEditorType);
begin
  if FEditor <> Value then
  begin
    FEditor := Value;
    if FEditor = getColorPicker then
    begin
      if Assigned(FColumns) and Assigned(FColumns.Renderer) and FColumns.Renderer.IsDesigntime then
        FEditorTarget := getFillColor;

      UpdateSettings(gcsEditorTarget);
    end;
    UpdateSettings(gcsEditor);
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetEditorItems(const Value: TStringList);
begin
  FEditorItems.Assign(Value);
end;

procedure TTMSFNCDataGridColumn.SetEditorTarget(
  const Value: TTMSFNCDataGridInplaceEditorTarget);
begin
  if FEditorTarget <> Value then
  begin
    FEditorTarget := Value;
    UpdateSettings(gcsEditorTarget);
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetFilterEditor(const Value: Boolean);
begin
  if FFilterEditor <> Value then
  begin
    FFilterEditor := Value;
    UpdateSettings(gcsFilterEditor);
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetFilterEditorType(
  const Value: TTMSFNCDataGridFilterEditorType);
begin
  if FFilterEditorType <> Value then
  begin
    FFilterEditorType := Value;
    UpdateSettings(gcsFilterEditorType);
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetFilterButton(const Value: Boolean);
begin
  if FFilterButton <> Value then
  begin
    FFilterButton := Value;
    UpdateSettings(gcsFilterButton);
    UpdateColumn;
  end;
end;

procedure TTMSFNCDataGridColumn.SetFormatting(
  const Value: TTMSFNCDataGridColumnFormatting);
begin
  FFormatting.Assign(Value);
end;

procedure TTMSFNCDataGridColumn.SetWidth(const Value: Single);
begin
  if FWidth <> Value then
  begin
    FWidth := Value;
    if Assigned(Renderer) then
      Renderer.ColumnWidths[Index] := Value;
  end;
end;

{ TTMSFNCDataGridColumnFormatting }

procedure TTMSFNCDataGridColumnFormatting.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridColumnFormatting then
  begin
    FFormat := (Source as TTMSFNCDataGridColumnFormatting).Format;
    FType := (Source as TTMSFNCDataGridColumnFormatting).&Type;
    FBooleanFalseText := (Source as TTMSFNCDataGridColumnFormatting).BooleanFalseText;
    FBooleanTrueText := (Source as TTMSFNCDataGridColumnFormatting).BooleanTrueText;
  end;
end;

constructor TTMSFNCDataGridColumnFormatting.Create;
begin
  FType := gdftDefault;
  FFormat := '';
  FConvertSettings := FormatSettings;
  FBooleanFalseText := '';
  FBooleanTrueText := '';
end;

function TTMSFNCDataGridColumnFormatting.IsBooleanFalseTextStored: Boolean;
begin
  Result := BooleanFalseText <> '';
end;

function TTMSFNCDataGridColumnFormatting.IsBooleanTrueTextStored: Boolean;
begin
  Result := BooleanTrueText <> '';
end;

function TTMSFNCDataGridColumnFormatting.IsFormatStored: Boolean;
begin
  Result := Format <> '';
end;

procedure TTMSFNCDataGridColumnFormatting.SetFormat(const Value: string);
begin
  if FFormat <> Value then
  begin
    FFormat := Value;
    UpdateColumnFormatting;
  end;
end;

procedure TTMSFNCDataGridColumnFormatting.SetType(
  const Value: TTMSFNCDataGridDataFormattingType);
begin
  if FType <> Value then
  begin
    FType := Value;
    UpdateColumnFormatting;
  end;
end;

procedure TTMSFNCDataGridColumnFormatting.UpdateColumnFormatting;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

{ TTMSFNCDataGridIOHTMLOptions }

procedure TTMSFNCDataGridIOHTMLOptions.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridIOHTMLOptions then
  begin
    FExportColors := (Source as TTMSFNCDataGridIOHTMLOptions).ExportColors;
    FExportImages := (Source as TTMSFNCDataGridIOHTMLOptions).ExportImages;
    FImageBaseName := (Source as TTMSFNCDataGridIOHTMLOptions).ImageBaseName;
    FImageFolder := (Source as TTMSFNCDataGridIOHTMLOptions).ImageFolder;
    FTableBorderSize := (Source as TTMSFNCDataGridIOHTMLOptions).TableBorderSize;
    FTableCellSpacing := (Source as TTMSFNCDataGridIOHTMLOptions).TableCellSpacing;
    FTableCellPadding := (Source as TTMSFNCDataGridIOHTMLOptions).TableCellPadding;
    FConvertSpecialChars := (Source as TTMSFNCDataGridIOHTMLOptions).ConvertSpecialChars;
    FFooterFile := (Source as TTMSFNCDataGridIOHTMLOptions).FooterFile;
    FFooterText := (Source as TTMSFNCDataGridIOHTMLOptions).FooterText;
    FHeaderFile := (Source as TTMSFNCDataGridIOHTMLOptions).HeaderFile;
    FHeaderText := (Source as TTMSFNCDataGridIOHTMLOptions).HeaderText;
    FNonBreakingText := (Source as TTMSFNCDataGridIOHTMLOptions).NonBreakingText;
    FPrefixTag := (Source as TTMSFNCDataGridIOHTMLOptions).PrefixTag;
    FSuffixTag := (Source as TTMSFNCDataGridIOHTMLOptions).SuffixTag;
    FSummary := (Source as TTMSFNCDataGridIOHTMLOptions).Summary;
    FTableStyle := (Source as TTMSFNCDataGridIOHTMLOptions).TableStyle;
    FTableWidth := (Source as TTMSFNCDataGridIOHTMLOptions).TableWidth;
    FXHTML := (Source as TTMSFNCDataGridIOHTMLOptions).XHTML;
  end;
end;

constructor TTMSFNCDataGridIOHTMLOptions.Create;
begin
  FExportImages := True;
  FExportColors := True;
  FImageBaseName := 'img';
  FImageFolder := 'images';
  FTableBorderSize := 1;
  FTableCellSpacing := 0;
  FTableCellPadding := 0;
  FConvertSpecialChars := False;
  FFooterFile := '';
  FFooterText := '';
  FHeaderFile := '';
  FHeaderText := '';
  FNonBreakingText := False;
  FPrefixTag := '';
  FSuffixTag := '';
  FSummary := '';
  FTableStyle := '';
  FTableWidth := 100;
  FXHTML := False;
end;

function TTMSFNCDataGridIOHTMLOptions.IsImageBaseNameStored: Boolean;
begin
  Result := ImageBaseName <> 'img';
end;

function TTMSFNCDataGridIOHTMLOptions.IsImageFolderStored: Boolean;
begin
  Result := ImageFolder <> 'images';
end;

{ TTMSFNCDataGridIOOptions }

procedure TTMSFNCDataGridIOOptions.Assign(Source: TPersistent);
begin
  inherited;
  if Source is TTMSFNCDataGridIOOptions then
  begin
    FHTML.Assign((Source as TTMSFNCDataGridIOOptions).HTML);
  end;
end;

constructor TTMSFNCDataGridIOOptions.Create;
begin
  inherited;
  FHTML := TTMSFNCDataGridIOHTMLOptions.Create;
end;

destructor TTMSFNCDataGridIOOptions.Destroy;
begin
  FreeAndNil(FHTML);
  inherited;
end;

procedure TTMSFNCDataGridIOOptions.SetHTML(
  const Value: TTMSFNCDataGridIOHTMLOptions);
begin
  if FHTML <> Value then
    FHTML.Assign(Value);
end;

{ TTMSFNCDataGridFilterAppearance }

procedure TTMSFNCDataGridFilterAppearance.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCDataGridFilterAppearance) then
  begin
    FFill.Assign((Source as TTMSFNCDataGridFilterAppearance).Fill);
    FStroke.Assign((Source as TTMSFNCDataGridFilterAppearance).Stroke);
    FFont.Assign((Source as TTMSFNCDataGridFilterAppearance).Font);
  end
  else
    inherited;
end;

constructor TTMSFNCDataGridFilterAppearance.Create;
begin
  FFill := TTMSFNCGraphicsFill.Create(gfkSolid, gcWhite);
  FFill.OnChanged := @DoFillChanged;
  FStroke := TTMSFNCGraphicsStroke.Create(gskSolid, gcSilver);
  FStroke.OnChanged := @DoStrokeChanged;
  FFont := TTMSFNCGraphicsFont.Create;
  FFont.OnChanged := @DoFontChanged;
end;

destructor TTMSFNCDataGridFilterAppearance.Destroy;
begin
  FFont.Free;
  FStroke.Free;
  FFill.Free;
  inherited;
end;

procedure TTMSFNCDataGridFilterAppearance.DoChange(Sender: TObject);
begin

end;

procedure TTMSFNCDataGridFilterAppearance.DoFillChanged(Sender: TObject);
begin
  DoChange(Self);
end;

procedure TTMSFNCDataGridFilterAppearance.DoFontChanged(Sender: TObject);
begin
  DoChange(Self);
end;

procedure TTMSFNCDataGridFilterAppearance.DoStrokeChanged(Sender: TObject);
begin
  DoChange(Self);
end;

procedure TTMSFNCDataGridFilterAppearance.SetFill(const Value: TTMSFNCGraphicsFill);
begin
  FFill.Assign(Value);
  DoFillChanged(FFill);
end;

procedure TTMSFNCDataGridFilterAppearance.SetFont(const Value: TTMSFNCGraphicsFont);
begin
  FFont.Assign(Value);
  DoFontChanged(FFont);
end;

procedure TTMSFNCDataGridFilterAppearance.SetStroke(const Value: TTMSFNCGraphicsStroke);
begin
  FStroke.Assign(Value);
end;

{ TTMSFNCDataGridDragAppearance }

procedure TTMSFNCDataGridDragAppearance.Assign(Source: TPersistent);
begin
  if (Source is TTMSFNCDataGridDragAppearance) then
  begin
    FFill.Assign((Source as TTMSFNCDataGridDragAppearance).Fill);
    FStroke.Assign((Source as TTMSFNCDataGridDragAppearance).Stroke);
    FSize := (Source as TTMSFNCDataGridDragAppearance).Size;
  end
  else
    inherited;
end;

constructor TTMSFNCDataGridDragAppearance.Create;
begin
  FFill := TTMSFNCGraphicsFill.Create(gfkSolid, Lighter(gcSteelblue, 30));
  FFill.OnChanged := @DoFillChanged;
  FStroke := TTMSFNCGraphicsStroke.Create(gskSolid, Lighter(gcSteelblue, 30));
  FStroke.OnChanged := @DoStrokeChanged;
  FSize := 12;
end;

destructor TTMSFNCDataGridDragAppearance.Destroy;
begin
  FStroke.Free;
  FFill.Free;
  inherited;
end;

procedure TTMSFNCDataGridDragAppearance.DoChange(Sender: TObject);
begin

end;

procedure TTMSFNCDataGridDragAppearance.DoFillChanged(Sender: TObject);
begin
  DoChange(Self);
end;

procedure TTMSFNCDataGridDragAppearance.DoStrokeChanged(Sender: TObject);
begin
  DoChange(Self);
end;

function TTMSFNCDataGridDragAppearance.IsSizeStored: Boolean;
begin
  Result := Size <> 12;
end;

procedure TTMSFNCDataGridDragAppearance.SetFill(const Value: TTMSFNCGraphicsFill);
begin
  FFill.Assign(Value);
  DoFillChanged(FFill);
end;

procedure TTMSFNCDataGridDragAppearance.SetSize(const Value: Single);
begin
  if FSize <> Value then
  begin
    FSize := Value;
    DoChange(Self);
  end;
end;

procedure TTMSFNCDataGridDragAppearance.SetStroke(const Value: TTMSFNCGraphicsStroke);
begin
  FStroke.Assign(Value);
end;

{ TTMSFNCDataGridColumnBinding }

procedure TTMSFNCDataGridColumnBinding.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridColumnBinding then
  begin
    FBooleanFalseValue := (Source as TTMSFNCDataGridColumnBinding).BooleanFalseValue;
    FBooleanTrueValue := (Source as TTMSFNCDataGridColumnBinding).BooleanTrueValue;
  end;
end;

constructor TTMSFNCDataGridColumnBinding.Create;
begin
  FBooleanFalseValue := 'False';
  FBooleanTrueValue := 'True';
end;

function TTMSFNCDataGridColumnBinding.IsBooleanFalseValueStored: Boolean;
begin
  Result :=  BooleanFalseValue <> 'False';
end;

function TTMSFNCDataGridColumnBinding.IsBooleanTrueValueStored: Boolean;
begin
  Result :=  BooleanTrueValue <> 'True';
end;

procedure TTMSFNCDataGridColumnBinding.SetBooleanFalseValue(
  const Value: string);
begin
  if FBooleanFalseValue <> '' then
  begin
    FBooleanFalseValue := Value;
    UpdateColumnBinding;
  end;
end;

procedure TTMSFNCDataGridColumnBinding.SetBooleanTrueValue(const Value: string);
begin
  if FBooleanTrueValue <> '' then
  begin
    FBooleanTrueValue := Value;
    UpdateColumnBinding;
  end;
end;

procedure TTMSFNCDataGridColumnBinding.UpdateColumnBinding;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

{ TTMSFNCDataGridStretchingOptions }

procedure TTMSFNCDataGridStretchingOptions.Assign(Source: TPersistent);
begin
  if Source is TTMSFNCDataGridStretchingOptions then
  begin
    FEnabled := (Source as TTMSFNCDataGridStretchingOptions).Enabled;
    FIndex := (Source as TTMSFNCDataGridStretchingOptions).&Index;
    FMode := (Source as TTMSFNCDataGridStretchingOptions).Mode;
    FRange := (Source as TTMSFNCDataGridStretchingOptions).Range;
  end;
end;

constructor TTMSFNCDataGridStretchingOptions.Create;
begin
  FEnabled := False;
  FIndex := -1;
  FMode := gstmAll;
  FRange := gstrNormal;
end;

procedure TTMSFNCDataGridStretchingOptions.SetEnabled(const Value: Boolean);
begin
  if FEnabled <> Value then
  begin
    FEnabled := Value;
    if Assigned(FOnChangeRebuild) then
      FOnChangeRebuild(Self);
  end;
end;

procedure TTMSFNCDataGridStretchingOptions.SetIndex(const Value: Integer);
begin
  if FIndex <> Value then
  begin
    FIndex := Value;
    if Assigned(FOnChangeRebuild) then
      FOnChangeRebuild(Self);
  end;
end;

procedure TTMSFNCDataGridStretchingOptions.SetMode(
  const Value: TTMSFNCDataGridStretchingMode);
begin
  if FMode <> Value then
  begin
    FMode := Value;
    if Assigned(FOnChangeRebuild) then
      FOnChangeRebuild(Self);
  end;
end;

procedure TTMSFNCDataGridStretchingOptions.SetRange(
  const Value: TTMSFNCDataGridStretchingRange);
begin
  if FRange <> Value then
  begin
    FRange := Value;
    if Assigned(FOnChangeRebuild) then
      FOnChangeRebuild(Self);
  end;
end;

{ TTMSFNCDataGridColumnOptions }

procedure TTMSFNCDataGridColumnOptions.Assign(Source: TPersistent);
begin
  inherited;
  if Source is TTMSFNCDataGridColumnOptions then
  begin
    FFixedAutoCheckAll := (Source as TTMSFNCDataGridColumnOptions).FixedAutoCheckAll;
    FNodeIndex := (Source as TTMSFNCDataGridColumnOptions).NodeIndex;
  end;
end;

constructor TTMSFNCDataGridColumnOptions.Create;
begin
  inherited;
  FFixedAutoCheckAll := True;
  FNodeIndex := 0;
end;

procedure TTMSFNCDataGridColumnOptions.SetNodeIndex(const Value: Integer);
begin
  if FNodeIndex <> Value then
  begin
    FNodeIndex := Value;
    if Assigned(OnChangeRebuild) then
      OnChangeRebuild(Self);
  end;
end;

{ TTMSFNCDataGridFilterPanel }

constructor TTMSFNCDataGridFilterPanel.Create(AOwner: TComponent; ARenderer: TTMSFNCDataGridRenderer; ACell: TTMSFNCDataGridCellCoord; AType: TTMSFNCDataGridFilterEditorType);
var
  I: TTMSFNCDataGridDataFilterType;
  it: TTMSFNCListBoxItem;
begin
  inherited Create(AOwner);

  DisableBackground;

  FAutoHideControls := True;
  FCell := ACell;
  FType := AType;
  FRenderer := ARenderer;

  FTypeButton := TTMSFNCToolBarButton.Create(Self);
  FTypeButton.Stored := False;
  FTypeButton.Parent := Self;
  FTypeButton.Appearance.FlatStyle := True;
  FTypeButton.Width := 25;
  FTypeButton.Height := 22;
  FTypeButton.Text := '';
  FTypeButton.HorizontalTextAlign := gtaLeading;
  FTypeButton.Appearance.NormalFill.Color := gcWhite;
  FTypeButton.Appearance.NormalStroke.Color := gcWhite;
  FTypeButton.Hint := 'Filter Type';
  FTypeButton.ShowHint := True;
  if Assigned(FRenderer) then
    FTypeButton.Bitmaps.AddBitmap(FRenderer.CellAppearance.FilterTypeIcon);

  FTypeButton.AdaptToStyle := AdaptToStyle;
  FTypeButton.DataInteger := FCell.Column;  

  FTypeListBox := TTMSFNCListBox.Create(Self);
  FTypeListBox.OnItemSelected := DoTypeListBoxSelected;

  case AType of
    gfetString:
    begin
      for I := Low(TTMSFNCDataGridDataFilterTextType) to High(TTMSFNCDataGridDataFilterTextType) do
      begin
        it := FTypeListBox.Items.Add;
        it.DataInteger := Integer(I);
        it.Text := GetFilterTypeName(I);
      end;
    end;
    gfetNumber, gfetFloat, gfetDateTime, gfetBoolean:
    begin
      for I := Low(TTMSFNCDataGridDataFilterNumberType) to High(TTMSFNCDataGridDataFilterNumberType) do
      begin
        it := FTypeListBox.Items.Add;
        it.DataInteger := Integer(I);
        it.Text := GetFilterTypeName(I);
      end;
    end;
    gfetCustom:
    begin
      for I := Low(TTMSFNCDataGridDataFilterType) to High(TTMSFNCDataGridDataFilterType) do
      begin
        it := FTypeListBox.Items.Add;
        it.DataInteger := Integer(I);
        it.Text := GetFilterTypeName(I);
      end;
    end;
  end;

  FTypeListBox.ItemIndex := ItemIndexByType(GetDefaultFilterType(FType));
  FTypeListBox.Height := Round(FTypeListBox.Items.Count * FTypeListBox.ItemsAppearance.FixedHeight) + 4;
  FTypeListBox.Width := 150;
  FTypeButton.DropDownControl := FTypeListBox;
  FTypeButton.OnClick := DoTypeButtonClick;
  
  FTypeListBox.AdaptToStyle := AdaptToStyle;
  FTypeListBox.DataInteger := FCell.Column;     

  FClearButton := TTMSFNCToolBarButton.Create(Self);
  FClearButton.Stored := False;
  FClearButton.Parent := Self;
  FClearButton.Appearance.FlatStyle := True;
  FClearButton.Width := 25;
  FClearButton.Height := 22;
  FClearButton.Text := '';
  FClearButton.HorizontalTextAlign := gtaLeading;
  FClearButton.Appearance.NormalFill.Color := gcWhite;
  FClearButton.Appearance.NormalStroke.Color := gcWhite;
  FClearButton.Hint := 'Clear Filter';
  FClearButton.ShowHint := True;
  if Assigned(FRenderer) then
      FClearButton.Bitmaps.AddBitmap(FRenderer.CellAppearance.FilterClearIcon);
      
  if Assigned(FRenderer) then
    FClearButton.OnClick := DoClearClick;

  FClearButton.AdaptToStyle := AdaptToStyle;
  FClearButton.DataInteger := FCell.Column;

  case AType of
    gfetString: FEditor := TTMSFNCEdit.Create(Self);
    gfetBoolean:
    begin
      FEditor := TCheckBox.Create(Self);
      (FEditor as TCheckBox).Width := 20;
    end;
    gfetDateTime:
    begin
      FEditor := TTMSFNCDataGridDatePicker.Create(Self);
      {$IFDEF FMXLIB}
      (FEditor as TTMSFNCDataGridDatePicker).IsEmpty := True;
      {$ELSE}
      (FEditor as TTMSFNCDataGridDatePicker).Date := 0;
      {$ENDIF}
    end;
    gfetFloat: FEditor := TTMSFNCDataGridSpinBox.Create(Self);
    gfetNumber: FEditor := TTMSFNCDataGridSpinBox.Create(Self);
  end;

  if Assigned(FEditor) then
  begin
    {$IFDEF FMXLIB}
    FEditor.Stored := False;
    {$ENDIF}

    FEditor.Parent := Self;

    if FEditor is TTMSFNCEdit then
    begin
      {$IFDEF FMXLIB}
      (FEditor as TTMSFNCEdit).OnChangeTracking := DoEditorChange
      {$ELSE}
      (FEditor as TTMSFNCEdit).OnChange := @DoEditorChange
      {$ENDIF}
    end
    else if FEditor is TCheckBox then
    begin
      {$IFDEF FMXLIB}
      (FEditor as TCheckBox).OnChange := @DoEditorChange
      {$ELSE}
      (FEditor as TCheckBox).OnClick := DoEditorChange
      {$ENDIF}
    end
    else if FEditor is TTMSFNCDataGridDatePicker then
      (FEditor as TTMSFNCDataGridDatePicker).OnChange := @DoEditorChange
    else if FEditor is TTMSFNCDataGridSpinBox then
      (FEditor as TTMSFNCDataGridSpinBox).OnChange := @DoEditorChange;

    if FEditor is TTMSFNCCustomControl then
      (FEditor as TTMSFNCCustomControl).AdaptToStyle := AdaptToStyle;
  end;
end;

destructor TTMSFNCDataGridFilterPanel.Destroy;
begin
  FreeAndNil(FEditorTimer);
  FreeAndNil(FEditor);
  FreeAndNil(FTypeListBox);
  FreeAndNil(FClearButton);
  FreeAndNil(FTypeButton);
  FreeAndNil(FTypeListBox);
  inherited;
end;

procedure TTMSFNCDataGridFilterPanel.DoClearClick(Sender: TObject);
begin
  if not Assigned(FRenderer) then
    Exit;

  if Assigned(FEditor) then
  begin
    FBlockChange := True;
    if FEditor is TTMSFNCEdit then
      (FEditor as TTMSFNCEdit).Text := ''
    else if FEditor is TTMSFNCDataGridCheckBox then
    begin
      {$IFDEF FMXLIB}
      (FEditor as TTMSFNCDataGridCheckBox).IsChecked := False;
      {$ELSE}
      (FEditor as TTMSFNCDataGridCheckBox).Checked := False;
      {$ENDIF}
    end
    else if FEditor is TTMSFNCDataGridDatePicker then
    begin
      {$IFDEF FMXLIB}
      (FEditor as TTMSFNCDataGridDatePicker).IsEmpty := True;
      {$ELSE}
      (FEditor as TTMSFNCDataGridDatePicker).Date := 0;
      {$ENDIF}
    end
    else if FEditor is TTMSFNCDataGridSpinBox then
    begin
      (FEditor as TTMSFNCDataGridSpinBox).Text := '';
    end;

    FBlockChange := False;
  end;

  FRenderer.DoFilterClearClick(Sender);
end;

procedure TTMSFNCDataGridFilterPanel.DoEditorChange(Sender: TObject);
begin
  if FBlockChange then
    Exit;

  FEditorTime := 0;
  EditorTimer.Enabled := True;
end;

procedure TTMSFNCDataGridFilterPanel.DoEditorTimerChanged(Sender: TObject);
var
  v: TTMSFNCDataGridCellValue;
  fdt: TTMSFNCDataGridDataFilterType;
begin
  Inc(FEditorTime);
  if FEditorTime >= FILTERDELAY then
  begin
    FEditorTimer.Enabled := False;

    if not Assigned(FRenderer) or not Assigned(FEditor) or FBlockChange then
      Exit;

    v := GetEditorValue(FEditor);

    fdt := gftContains;
    if Assigned(FTypeButton) and FTypeButton.Visible and (FTypeListBox.ItemIndex <> -1) then
      fdt := TTMSFNCDataGridDataFilterType(FTypeListBox.Items[FTypeListBox.ItemIndex].DataInteger);

    FRenderer.ApplyFilterFromValue(FCell.Column, v, fdt);
  end;
end;

procedure TTMSFNCDataGridFilterPanel.DoTypeButtonClick(Sender: TObject);
begin
  if Assigned(FTypeButton) then
    FTypeButton.DropDown;
end;

procedure TTMSFNCDataGridFilterPanel.DoTypeListBoxSelected(Sender: TObject; AItem: TTMSFNCListBoxItem);
begin
  if Assigned(FTypeButton) then
    FTypeButton.CloseDropDown;

  DoEditorChange(Sender);
end;

function TTMSFNCDataGridFilterPanel.GetDefaultFilterType(
  AType: TTMSFNCDataGridFilterEditorType): TTMSFNCDataGridDataFilterType;
begin
  case AType of
    gfetString, gfetCustom: Result := gftContains;
    gfetBoolean, gfetDateTime, gfetFloat, gfetNumber: Result := gftEqual;
    else
      Result := gftContains;
  end;
end;

function TTMSFNCDataGridFilterPanel.GetEditorTimer: TTimer;
begin
  if not Assigned(FEditorTimer) then
  begin
    FEditorTimer := TTimer.Create(nil);
    FEditorTimer.Enabled := False;
    FEditorTimer.Interval := 1;
    FEditorTimer.OnTimer := DoEditorTimerChanged;
  end;

  Result := FEditorTimer;
end;

function TTMSFNCDataGridFilterPanel.ItemIndexByType(
  AType: TTMSFNCDataGridDataFilterType): Integer;
var
  I: Integer;
  d: Integer;
begin
  Result := -1;
  for I := 0 to TypeListBox.Items.Count - 1 do
  begin
    d := TypeListBox.Items[I].DataInteger;
    if d = Integer(AType) then
    begin
      Result := I;
      break;
    end;
  end;
end;

procedure TTMSFNCDataGridFilterPanel.Notification(AComponent: TComponent;
  Operation: TOperation);
begin
  inherited;
  if (Operation = opRemove) and not (csDestroying in ComponentState) then
  begin
    if (AComponent = FClearButton) then
      FClearButton := nil;

    if (AComponent = FTypeButton) then
      FTypeButton := nil;

    if (AComponent = FEditor) then
      FEditor := nil;

    if (AComponent = FTypeListBox) then
      FTypeListBox := nil;
  end;
end;

procedure TTMSFNCDataGridFilterPanel.Reset;
begin
  FBlockChange := True;
  if Assigned(FTypeListBox) then
    FTypeListBox.ItemIndex := ItemIndexByType(GetDefaultFilterType(FType));

  if Assigned(FEditor) then
    SetEditorValue(FEditor, TValueToCellValue(TValue.Empty));

  FBlockChange := False;
end;

procedure TTMSFNCDataGridFilterPanel.SetFilter(
  AFilter: TTMSFNCDataGridDataFilterData);
begin
  if Assigned(AFilter) then
  begin
    FBlockChange := True;
    if Assigned(FTypeListBox) then
      FTypeListBox.ItemIndex := ItemIndexByType(AFilter.&Type);

    if Assigned(FEditor) then
    begin
      if AFilter.DataString <> '' then
        SetEditorValue(FEditor, AFilter.DataString)
      else
        SetEditorValue(FEditor, AFilter.StripCondition);
    end;
    FBlockChange := False;
  end;
end;

procedure TTMSFNCDataGridFilterPanel.UpdateControlAfterResize;
begin
  inherited;

  if Assigned(FTypeButton) then
  begin
    FTypeButton.Left := 5;
    FTypeButton.Top := Round((Height - FTypeButton.Height) / 2);
  end;

  if Assigned(FClearButton) then
  begin
    FClearButton.Left := Width - FClearButton.Width - 5;
    FClearButton.Top := Round((Height - FClearButton.Height) / 2);
  end;

  if Assigned(FEditor) then
  begin
    {$IFDEF FMXLIB}
    FEditor.Position.X := 5;
    FEditor.Position.Y := Round((Height - FEditor.Height) / 2);
    {$ELSE}
    FEditor.Left := 5;
    FEditor.Top := (Height - FEditor.Height) div 2;
    {$ENDIF}

    if Assigned(FClearButton) and AutoHideControls then
      FClearButton.Visible := Width - FClearButton.Width - 15 > 85;

    if Assigned(FClearButton) and FClearButton.Visible then
      FEditor.Width := Width - FClearButton.Width - 15
    else
      FEditor.Width := Width - 5;

    if Assigned(FTypeButton) and AutoHideControls then
      FTypeButton.Visible := FEditor.Width > 85;

    if Assigned(FTypeButton) and FTypeButton.Visible then
    begin
      {$IFDEF FMXLIB}
      FEditor.Position.X := FEditor.Position.X + FTypeButton.Width + 5;
      {$ELSE}
      FEditor.Left := FEditor.Left + FTypeButton.Width + 5;
      {$ENDIF}
      FEditor.Width := FEditor.Width - FTypeButton.Width - 5;
    end;
  end;
end;

{ TComboBoxSafe }

constructor TComboBoxSafe.Create(AOwner: TComponent);
begin
  inherited;
  {$IFDEF VCLLIB}
  FSafeItemIndex := -1;
  {$ENDIF}
end;

{$IFDEF VCLLIB}
procedure TComboBoxSafe.CreateWnd;
var
  LOnChange: TNotifyEvent;
  I: Integer;
begin
  inherited;
  LOnChange := OnChange;
  OnChange := nil;
  try
    Items.Clear;
    for I := 0 to Length(FSafeItems) - 1 do
      Items.Add(FSafeItems[I]);
    ItemIndex := FSafeItemIndex;
  finally
    OnChange := LOnChange;
  end;
end;

procedure TComboBoxSafe.DestroyWnd;
begin
  FSafeItemIndex := ItemIndex;
  FSafeItems := Items.ToStringArray;
  inherited;
end;
{$ENDIF}

function TComboBoxSafe.GetSafeItemIndex: Integer;
begin
  {$IFDEF VCLLIB}
  if WindowHandle <> 0 then
  {$ENDIF}
    Result := ItemIndex
  {$IFDEF VCLLIB}
  else
    Result := FSafeItemIndex;
  {$ENDIF}
end;

function TComboBoxSafe.GetSafeItems: TArray<string>;
begin
  {$IFDEF VCLLIB}
  if WindowHandle <> 0 then
  {$ENDIF}
    Result := Items.ToStringArray
  {$IFDEF VCLLIB}
  else
    Result := FSafeItems;
  {$ENDIF}
end;

procedure TComboBoxSafe.SetSafeItemIndex(const AValue: Integer);
begin
  {$IFDEF VCLLIB}
  if WindowHandle <> 0 then
  {$ENDIF}
    ItemIndex := AValue
  {$IFDEF VCLLIB}
  else
    FSafeItemIndex := AValue;
  {$ENDIF}
end;

procedure TComboBoxSafe.SetSafeItems(const AValue: TArray<string>);
var
  I: Integer;
begin
  {$IFDEF VCLLIB}
  if WindowHandle <> 0 then
  begin
  {$ENDIF}
    Items.Clear;
    for I := 0 to Length(AValue) - 1 do
      Items.Add(AValue[I]);
  {$IFDEF VCLLIB}
  end
  else
    FSafeItems := AValue;
  {$ENDIF}
end;

initialization
begin
  {$IFDEF WEBLIB}
  TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDEXPAND);
  TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDCOLLAPSE);
  TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDFILTER);
  TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDFILTERACTIVE);
  TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDFILTERCLEAR);
  TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDSORTASC);
  TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDSORTDESC);
  TTMSFNCBitmap.CreateFromResource(TTMSFNCDATAGRIDFILTERTYPE);
  {$ENDIF}
end;

end.
